// Message bundles used by ``useT()`` (see ``I18nContext``).
//
// Keys are dot-delimited so they can later be nested or extracted by a
// generator. Adding a new locale is a matter of copying ``en`` and translating
// the values; any missing key falls back to English.

export const MESSAGES = {
  en: {
    'nav.notifications': 'Notifications',
    'nav.search.placeholder': 'Search…',
    'nav.search.placeholder.guest': 'Find a book or writer…',
    'nav.editor': 'Editor',
    'nav.profile': 'Profile',
    'nav.settings': 'Settings',
    'nav.help': 'Help',
    'nav.contact': 'Contact us',
    'nav.curiosities': 'Curiosities',
    'nav.games': 'Brain games',
    'nav.messages': 'Messages',
    'nav.login': 'Log in',
    'nav.register': 'Register',
    'nav.logout': 'Log out',
    'nav.open_menu': 'Open menu',
    'nav.close_menu': 'Close menu',
    'nav.open_search': 'Open search',
    'nav.close_search': 'Close search',
    'nav.overview': 'Dashboard',
    'nav.view_profile': 'View profile',
    'search.section.users': 'Writers',
    'search.section.books': 'Books',
    'search.section.posts': 'Discussions',
    'search.tag.book': 'Book',
    'search.tag.discussion': 'Discussion',
    'search.no_results': 'No results found for “{query}”.',
    'publish.toast.published_promoted': 'Published — your project is now public so readers can find it.',
    'nav.manage_work': 'Manage my work',
    'nav.browse': 'Browse the catalogue',
    'nav.forum': 'Ask a question',
    'nav.goals': 'Set and track my goals',
    'nav.profile_menu': 'Profile menu',

    'landing.hero.headline_prefix': 'Unleash Your',
    'landing.hero.headline_accent': 'Words',
    'landing.hero.description':
      'A focused space to write, share, and read in-progress stories. Edit alone or with collaborators in real time, publish chapters as you finish them, and explore what the rest of the community is working on right now.',
    'landing.cta.start_writing': 'Start Writing',
    'landing.cta.reading': 'Reading',

    'notifications.all_caught_up': 'All caught up',
    'notifications.mark_all_read': 'Mark all read',
    'notifications.refresh': 'Refresh',
    'notifications.empty.title': 'Nothing here yet',
    'notifications.empty.description':
      'Collaboration updates, goal achievements, and system messages will appear here.',
    'notifications.empty.unread.title': 'No unread notifications',
    'notifications.empty.unread.description':
      "You're all caught up — switch to All to revisit anything.",
    'notifications.open': 'Open',
    'notifications.mark_read': 'Mark read',
    'notifications.delete': 'Delete',
    'notifications.unread': 'Unread',
    'notifications.unread_count': '{count} unread',
    'notifications.error.load': 'Notifications unavailable. Try again.',
    'notifications.filter.all': 'All',
    'notifications.filter.unread': 'Unread',

    'analysis.title': 'Writing Report',
    'analysis.subtitle': 'Score, metrics & personal suggestions.',
    'analysis.refresh': 'Refresh',
    'analysis.analysing': 'Analysing…',
    'analysis.empty':
      'Start typing and click Refresh for your first report.',
    'analysis.error': 'Analysis service unavailable. Try again.',
    'analysis.score': 'score',
    'analysis.section.glance': 'At a glance',
    'analysis.section.suggestions': 'Personal suggestions',
    'analysis.section.top_words': 'Most frequent words',
    'analysis.section.tone': 'Tone',
    'analysis.metric.words': 'Words',
    'analysis.metric.sentences': 'Sentences',
    'analysis.metric.avg_sentence': 'Avg sentence',
    'analysis.metric.avg_sentence_unit': 'words',
    'analysis.metric.unique': 'Unique',
    'analysis.metric.reading': 'Reading',
    'analysis.metric.reading_unit': 'min',
    'analysis.metric.flesch': 'Flesch ease',
    'analysis.close': 'Close analysis',
    'analysis.gauge.aria': 'Overall score: {score} out of 100',
    'analysis.bar.readability': 'Readability',
    'analysis.bar.sentence_rhythm': 'Sentence rhythm',
    'analysis.bar.vocabulary': 'Vocabulary variety',
    'analysis.bar.style': 'Style',
    'analysis.sentiment.positive': 'positive',
    'analysis.sentiment.neutral': 'neutral',
    'analysis.sentiment.negative': 'negative',
    'analysis.sentiment.line': 'Sentiment is {label} (polarity {polarity}).',
    'analysis.empty_state':
      'Start typing and click “Refresh” to generate your first report.',
    'analysis.placeholder.title': 'No report yet',
    'analysis.placeholder.description':
      'Write a paragraph in the editor, then run the report to see your score, metrics, and personalised suggestions.',

    'sprint.title': 'Writing sprint',
    'sprint.subtitle': 'Focused burst with live word count',
    'sprint.start': 'Start sprint',
    'sprint.stop': 'Stop & save',
    'sprint.pause': 'Pause',
    'sprint.resume': 'Resume',
    'sprint.again': 'New sprint',
    'sprint.paused': 'Paused',
    'sprint.close': 'Close sprint',
    'sprint.collapse': 'Collapse sprint',
    'sprint.expand': 'Expand sprint',
    'sprint.custom_label': 'Custom minutes',
    'sprint.goal_label': 'Word goal (optional)',
    'sprint.goal_progress': 'Goal',
    'sprint.duration': 'Duration',
    'sprint.minutes_short': 'min',
    'sprint.words_suffix': 'words',
    'sprint.wpm_suffix': 'wpm',
    'sprint.toast.done':
      'Sprint done — {words} words in {minutes} min ({wpm} wpm).',
    'sprint.toast.goal_hit':
      'Goal hit — {words} words in {minutes} min. Nice run!',
    'sprint.toast.empty': 'Sprint finished. No words counted this round.',

    'focus.title': 'Focus mode',
    'focus.exit': 'Exit focus (Esc)',
    'focus.words': 'words',
    'focus.stat.elapsed': 'elapsed',
    'focus.stat.session': 'this session',
    'focus.toggle.typewriter': 'Typewriter mode',
    'focus.toggle.hide_stats': 'Hide stats',
    'focus.toggle.show_stats': 'Show stats',
    'focus.toast.session_summary':
      'Wrote {words} words in {elapsed}.',

    'editor.analyse': 'Analyse',
    'editor.coherence': 'Coherence',
    'editor.focus': 'Focus',
    'editor.sprint': 'Sprint',
    'editor.export': 'Export',
    'editor.export.txt': 'Plain text (.txt)',
    'editor.export.md': 'Markdown (.md)',
    'editor.export.html': 'Web page (.html)',
    'editor.export.pdf': 'PDF document (.pdf)',
    'editor.export.docx': 'Word document (.doc)',
    'editor.export.png': 'Image (.png)',
    'editor.export.fallback_name': 'chapter',
    'editor.export.html_lang': 'en',
    'editor.mention.title': 'Reference a planner module',
    'editor.mention.placeholder_hint': 'type a name',
    'editor.mention.no_match': 'No matching module — keep typing or open the Planner to add one.',
    'editor.mention.empty': 'This project has no planner modules yet — open the Planner to add some.',
    'editor.mention.loading': 'Loading the planner library…',
    'editor.mention.nav': 'navigate',
    'editor.mention.insert': 'insert',
    'editor.mention.cancel': 'cancel',
    'editor.export.toast_failed': 'Chapter export failed. Try again.',
    'project_export.action.open': 'Export project',
    'project_export.title': 'Export project',
    'project_export.close': 'Close',
    'project_export.cancel': 'Cancel',
    'project_export.download': 'Download',
    'project_export.busy': 'Preparing…',
    'project_export.intro': 'Bundles all {count} chapters of this project for download.',
    'project_export.no_chapters': 'This project has no chapters yet — add at least one to export.',
    'project_export.mode_label': 'How should the export be packaged?',
    'project_export.mode.merged.title': 'Single document',
    'project_export.mode.merged.description': 'Merge every chapter into one file (PDF, Word, HTML, …).',
    'project_export.mode.zip.title': 'ZIP archive',
    'project_export.mode.zip.description': 'One file per chapter inside a .zip.',
    'project_export.format_label': 'Format',
    'project_export.format.pdf': 'PDF document (.pdf)',
    'project_export.format.doc': 'Word document (.doc)',
    'project_export.format.html': 'Web page (.html)',
    'project_export.format.md': 'Markdown (.md)',
    'project_export.format.txt': 'Plain text (.txt)',
    'project_export.format.png': 'Image (.png)',
    'project_export.options_label': 'Include in the document',
    'project_export.option.cover': 'Cover page',
    'project_export.option.cover_hint': 'Title page with the project title, description, and author.',
    'project_export.option.toc': 'Table of contents',
    'project_export.option.toc_hint': 'A list of chapters before the body.',
    'project_export.option.credits': 'Credits page',
    'project_export.option.credits_hint': 'Owner and collaborators with their roles.',
    'project_export.option.pagination': 'Page breaks between sections',
    'project_export.option.pagination_hint': 'Start each chapter on a new page (and keep cover, contents, and credits on their own). Turn off for a continuous document.',
    'project_export.fallback_project': 'project',
    'project_export.chapter_word': 'Chapter',
    'project_export.contents': 'Contents',
    'project_export.credits': 'Credits',
    'project_export.by': 'by',
    'project_export.role.owner': 'Owner',
    'project_export.role.editor': 'Editor',
    'project_export.role.reader': 'Reader',
    'project_export.toast.success': 'Export ready — check your downloads.',
    'project_export.toast.failed': 'Project export failed. Try again.',
    'project_export.toast.no_chapters': 'Add at least one chapter before exporting.',
    'editor.sync.connected': 'Synced',
    'editor.sync.connecting': 'Syncing…',
    'editor.sync.offline': 'Offline',
    'editor.sync.tooltip.connected': 'Changes saved.',
    'editor.sync.tooltip.connecting': 'Connecting to the collaborative server…',
    'editor.sync.tooltip.offline': 'You are offline. Edits will sync when reconnected.',

    'editor.presence.editing_now': 'Editing now',
    'editor.presence.solo': 'Solo',
    'editor.presence.solo_tooltip': "You're the only one editing right now.",
    'editor.presence.aria_one': '{count} other person editing',
    'editor.presence.aria_many': '{count} other people editing',

    'history.title': 'Activity',
    'history.subtitle': 'Publishing, sharing, restores',
    'history.refresh': 'Refresh activity',
    'history.loading': 'Loading recent activity…',
    'history.empty.title': 'Nothing has happened here yet',
    'history.empty.hint':
      "Publishing this story, inviting a co-writer, restoring an earlier draft — every change to this project will show up here as you go.",
    'history.error': "Activity unavailable",
    'history.relative.just_now': 'just now',
    'history.relative.minutes': '{n} min ago',
    'history.relative.hours': '{n} h ago',
    'history.relative.days': '{n} d ago',

    'editor.menubar.font': 'Font',
    'editor.menubar.zoom': 'Zoom',
    'editor.menubar.line_spacing': 'Spacing',
    'editor.menubar.lists': 'Lists',
    'editor.menubar.lists.bullet': 'Bullet list',
    'editor.menubar.lists.ordered': 'Numbered list',
    'editor.menubar.lists.task': 'Task list',
    'editor.menubar.indent_right': 'Indent right',
    'editor.menubar.indent_left': 'Indent left',

    'editor.menubar.image': 'Insert image',
    'editor.menubar.image.tab.upload': 'Upload',
    'editor.menubar.image.tab.url': 'From URL',
    'editor.menubar.image.upload.cta': 'Choose a file',
    'editor.menubar.image.upload.hint': 'PNG, JPG, GIF, or WebP, up to 10 MB.',
    'editor.menubar.image.uploading': 'Uploading…',
    'editor.menubar.image.url.label': 'Image URL',
    'editor.menubar.image.url.placeholder': 'https://example.com/photo.jpg',
    'editor.menubar.image.alt': 'Alt text',
    'editor.menubar.image.alt.placeholder': 'Describe the image for screen readers',
    'editor.menubar.image.insert': 'Insert image',
    'editor.menubar.image.cancel': 'Cancel',
    'editor.menubar.image.error.invalid_url': 'Enter an http(s) image URL.',
    'editor.menubar.image.error.upload': 'Upload failed. Please try again.',
    'editor.menubar.image.error.too_large': 'File is too large (max 10 MB).',

    'editor.menubar.link.title.insert': 'Insert link',
    'editor.menubar.link.title.edit': 'Edit link',
    'editor.menubar.link.url': 'Web address',
    'editor.menubar.link.url.placeholder': 'https://example.com',
    'editor.menubar.link.text': 'Display text',
    'editor.menubar.link.text.placeholder': 'Optional — defaults to the URL',
    'editor.menubar.link.insert': 'Insert link',
    'editor.menubar.link.remove': 'Remove link',
    'editor.menubar.link.cancel': 'Cancel',
    'editor.menubar.link.error.invalid': 'Enter an http(s) URL.',

    'settings.appearance.title': 'Appearance',
    'settings.appearance.theme': 'Theme',
    'settings.appearance.label': 'Color theme',
    'settings.appearance.current': 'Theme (currently {label}). Click to change.',
    'settings.appearance.theme.light': 'Light',
    'settings.appearance.theme.light.desc': 'Clean, focused, easy on the eyes.',
    'settings.appearance.theme.dark': 'Dark',
    'settings.appearance.theme.dark.desc': 'Low-glare surfaces for long writing sessions.',
    'settings.appearance.theme.playful': 'Playful',
    'settings.appearance.theme.playful.desc': 'Plumescript signature look — sticker shadows, candy palette, and bubbly Baloo type.',
    'settings.appearance.theme.hc': 'Clarity',
    'settings.appearance.theme.hc.desc':
      'Pure white background, near-black text, vivid focus rings and forced link underlines. WCAG AAA contrast.',
    'settings.appearance.theme.hc-dark': 'Variance',
    'settings.appearance.theme.hc-dark.desc':
      'Pure black background, near-white text, vivid focus rings and forced link underlines. WCAG AAA contrast.',
    'settings.appearance.subtitle':
      'Pick a theme that fits how you write.',
    'settings.appearance.theme_hint':
      'Saved to this device. Changes apply immediately across the app.',

    'settings.user.title': 'Account',
    'settings.user.subtitle': 'Update your personal information.',
    'settings.user.personal_info': 'Personal information',
    'settings.user.firstname': 'First name',
    'settings.user.lastname': 'Last name',
    'settings.user.email': 'Email',
    'settings.user.language': 'Language',
    'settings.user.language_placeholder': 'Language',
    'settings.user.notifications': 'Notifications',
    'settings.user.report_alerts': 'Receive weekly updates about your content.',
    'settings.user.collab_alerts': 'Receive notifications for collaboration requests.',
    'settings.user.publication_alerts': 'Get notified when chapters or projects publish.',
    'settings.notifications.subtitle':
      'Choose which notifications show up. Changes save automatically.',
    'settings.notifications.publication.label': 'Publication updates',
    'settings.notifications.publication.description':
      'New chapters in books you read, public projects you follow.',
    'settings.notifications.collaboration.label': 'Collaboration',
    'settings.notifications.collaboration.description':
      'Invites, comments, and replies on shared projects.',
    'settings.notifications.report.label': 'Reports & alerts',
    'settings.notifications.report.description':
      'Weekly digest of your writing stats and any moderation flags.',
    'settings.notifications.save_failed':
      'Save failed — try again in a moment.',

    // Engagement / retention / conversion email channel
    'settings.engagement_emails.title': 'Engagement emails',
    'settings.engagement_emails.subtitle':
      'Personalized nudges that help you finish your projects. Weekly by default, granular control behind Manage.',
    'settings.engagement_emails.summary.loading': 'Loading preferences…',
    'settings.engagement_emails.summary.enabled': 'On',
    'settings.engagement_emails.summary.disabled': 'Off',
    'settings.engagement_emails.summary.quiet_hours': 'Quiet {start}–{end}',
    'settings.engagement_emails.summary.categories': '{active}/{total} categories',
    'settings.engagement_emails.summary.snoozed_until': 'Snoozed until {date}',
    'settings.engagement_emails.summary.manage': 'Manage',
    'settings.engagement_emails.master.label': 'Send engagement emails',
    'settings.engagement_emails.master.description':
      'Turn off to stop every email below. Transactional mail (password resets, security alerts) still goes through.',
    'settings.engagement_emails.frequency.label': 'Frequency',
    'settings.engagement_emails.frequency.description':
      'How often we batch your nudges. The same nudge will not repeat within a single window.',
    'settings.engagement_emails.frequency.option.never': 'Never',
    'settings.engagement_emails.frequency.option.daily': 'Daily',
    'settings.engagement_emails.frequency.option.weekly': 'Weekly',
    'settings.engagement_emails.frequency.option.biweekly': 'Biweekly',
    'settings.engagement_emails.frequency.option.monthly': 'Monthly',
    'settings.engagement_emails.category.writing_activity.label': 'Writing activity',
    'settings.engagement_emails.category.writing_activity.description':
      'Streak about to break, goal short of target, dormant project — keeps your momentum.',
    'settings.engagement_emails.category.planner_insights.label': 'Planner & coherence',
    'settings.engagement_emails.category.planner_insights.description':
      'Reminders for a missing logline, no protagonist card, half-filled structure beats, or a pending coherence check.',
    'settings.engagement_emails.category.community.label': 'Community',
    'settings.engagement_emails.category.community.description':
      'Unread comments, new followers, forum replies, idle message threads.',
    'settings.engagement_emails.category.inspiration.label': 'Inspiration & tips',
    'settings.engagement_emails.category.inspiration.description':
      'Weekly writing prompt plus AI preset highlights. Opt-in.',
    'settings.engagement_emails.category.billing.label': 'Account & billing',
    'settings.engagement_emails.category.billing.description':
      'Quota near-limit alerts, paid-milestone recognition. Trial expiry reminders come from this channel.',
    'settings.engagement_emails.quiet_hours.label': 'Quiet hours',
    'settings.engagement_emails.quiet_hours.description':
      "Never send during these hours in your local time. We'll wait for the next eligible window.",
    'settings.engagement_emails.quiet_hours.start': 'Start',
    'settings.engagement_emails.quiet_hours.end': 'End',
    'settings.engagement_emails.snooze.label': 'Vacation mode',
    'settings.engagement_emails.snooze.description':
      'Pause every engagement email for a set window — handy before a trip or a sprint.',
    'settings.engagement_emails.snooze.7d': '7 days',
    'settings.engagement_emails.snooze.30d': '30 days',
    'settings.engagement_emails.snooze.90d': '90 days',
    'settings.engagement_emails.snooze.resume': 'Resume now',
    'settings.engagement_emails.snooze.until': 'Paused until {date}',
    'settings.engagement_emails.open_tracking.label': 'Help us measure usefulness',
    'settings.engagement_emails.open_tracking.description':
      'Adds a tiny invisible pixel so we know if our emails are landing. Pixel only, no other data.',
    'settings.engagement_emails.preview.button': 'Preview next email',
    'settings.engagement_emails.preview.modal_title': 'Next engagement email — preview',
    'settings.engagement_emails.preview.sending': 'Composing…',
    'settings.engagement_emails.preview.empty': 'Nothing to send right now',
    'settings.engagement_emails.preview.empty_description':
      "No signals matched on your current state. Keep writing — we'll surface a nudge when there's something useful to say.",
    'settings.engagement_emails.history.button': 'See what we’ve sent you',
    'settings.engagement_emails.history.modal_title': 'Engagement email history',
    'settings.engagement_emails.history.loading': 'Loading history…',
    'settings.engagement_emails.history.empty_title': 'No emails sent yet',
    'settings.engagement_emails.history.empty_description':
      'Your sent log will appear here once the first engagement email goes out.',
    'settings.engagement_emails.history.no_subject': '(welcome)',
    'settings.engagement_emails.history.signal_count': '{count} nudge(s) in this email',
    'settings.engagement_emails.save_failed':
      'Save failed — try again in a moment.',
    'settings.user.save': 'Save changes',
    'settings.user.save_success': 'Settings saved.',
    'settings.user.danger_zone': 'Danger zone',
    'settings.user.reset': 'Reset',

    'settings.security.title': 'Security',
    'settings.security.subtitle': 'Password, verification, and recent sign-ins.',
    'settings.security.verify_account': 'Verify your account',
    'settings.security.verify_subtitle':
      'Confirm you control this email so you can comment, post, and start conversations.',
    'settings.security.change_password': 'Change password',
    'settings.security.change_password_subtitle':
      "Pick a new password — you'll be emailed a confirmation when it changes.",
    'settings.security.last_login': 'Last sign-in',
    'settings.security.last_login_subtitle':
      'When and where you most recently signed in.',
    'settings.security.last_login.when': 'When',
    'settings.security.last_login.where': 'Where',
    'settings.security.loading': 'Loading…',
    'settings.security.unknown_date': "Last sign-in date unavailable.",
    'settings.security.location_unknown': 'Unknown location',
    'settings.security.sign_out': 'Sign out',

    'settings.verify.unverified_title': 'Email not verified',
    'settings.verify.unverified_subtitle':
      "We'll email a 6-digit code. Enter it here to unlock posting and messaging.",
    'settings.verify.send_code': 'Send verification code',
    'settings.verify.resend': 'Send a new code',
    'settings.verify.cooldown': 'Wait {seconds}s before retrying',
    'settings.verify.code_label': '6-digit verification code',
    'settings.verify.confirm': 'Verify account',
    'settings.verify.sent':
      'Verification code sent. Check your spam folder if you don’t see it. Expires in 15 minutes.',
    'settings.verify.confirmed': 'Account verified.',
    'settings.verify.invalid_token': 'Invalid or expired code. Request a new one.',
    'settings.verify.request_failed': "Code send failed. Try again in a minute.",
    'settings.verify.confirm_failed': "Code verification failed. Try again.",
    'settings.verify.rate_limited': 'Too many attempts. Wait a minute and retry.',
    'settings.verify.verified_title': 'Email verified',
    'settings.verify.verified_subtitle':
      'You can comment, post, and start conversations. Thanks for confirming.',
    'settings.verify.required_toast':
      'Verify your email before posting or messaging — open Settings → Security.',

    'settings.ai_usage.title': 'AI usage',
    'settings.ai_usage.subtitle':
      'Daily limits. Reset at midnight ET.',

    'settings.billing.title': 'Billing',
    'settings.billing.subtitle': 'Subscription, plan, and payments.',
    'settings.billing.plan': 'Plan',
    'settings.billing.current_subscription': 'Your current plan',
    'settings.billing.per_month': '/month',
    'settings.billing.plan_free': 'Free',
    'settings.billing.plan_basic': 'Standard',
    'settings.billing.plan_premium': 'Premium',
    'settings.billing.badge_free': 'Free',
    'settings.billing.badge_basic': 'Essential',
    'settings.billing.badge_premium': 'Pro',
    'settings.billing.description_free': 'Core writing tools, free forever.',
    'settings.billing.description_basic': 'Higher daily AI limits.',
    'settings.billing.description_premium': 'Highest daily AI limits.',
    'settings.billing.upgrade': 'Upgrade',
    'settings.billing.manage': 'Manage subscription',
    'settings.billing.money_back.title': '30-day refund guarantee',
    'settings.billing.money_back.body':
      'Full refund within 30 days. No forms.',
    'settings.billing.payment_method': 'Payment method',
    'settings.billing.no_card': 'No card saved',
    'settings.billing.card_placeholder': 'Card number',
    'settings.billing.manage_cards': 'Manage cards',
    'settings.billing.cancel_plan': 'Cancel plan',
    'settings.billing.payments_disabled': "Payments aren't enabled here.",
    // Sheet titles for the four billing-side modals (migrated
    // from hand-rolled scaffolding to the canonical Sheet
    // component for modal consistency + mobile a11y).
    'settings.billing.payment_modal_title': 'Add a payment method',
    'settings.billing.billing_modal_title': 'Change plan',
    'settings.billing.invoices_modal_title': 'Past invoices',
    'settings.billing.cancel_modal_title': 'Cancel subscription',
    'settings.billing.billing_disabled': "Billing isn't available here.",
    'settings.billing.free_tier_only_title': "You're on the Free tier",
    'settings.billing.free_tier_only_subtitle':
      "Paid plans aren't available yet — you have full access to the free tier and the AI quotas above.",
    'settings.billing.history.title': 'Billing history',
    'settings.billing.history.subtitle':
      'Past charges, invoices, and refunds for your subscription.',
    'settings.billing.history.cta': 'View past invoices',

    'settings.member_since.title': 'Member since',
    'settings.member_since.subtitle':
      'When you joined the writing community.',
    'settings.member_since.unknown': 'Unknown',

    'settings.password.current_placeholder': 'Current password',
    'settings.password.new_placeholder': 'New password',
    'settings.password.confirm_placeholder': 'Confirm new password',
    'settings.password.strength.0': 'Very weak',
    'settings.password.strength.1': 'Weak',
    'settings.password.strength.2': 'Fair',
    'settings.password.strength.3': 'Good',
    'settings.password.strength.4': 'Strong',
    'settings.password.strength.5': 'Excellent',
    'settings.password.criteria.title': 'Security criteria',
    'settings.password.criteria.min_length': 'At least 8 characters',
    'settings.password.criteria.needs_upper': 'One uppercase letter',
    'settings.password.criteria.needs_lower': 'One lowercase letter',
    'settings.password.criteria.needs_digit': 'One digit',
    'settings.password.criteria.needs_symbol': 'One special character (!@#$%^&*)',
    'settings.password.criteria.contains_identity': 'Must not contain your identifier',
    'settings.password.mismatch': "The two passwords don't match.",
    'settings.password.weak_error': "The password doesn't meet the security criteria.",
    'settings.password.invalid_current': 'Current password is incorrect.',
    'settings.password.generic_error': 'Something went wrong. Try again.',
    'settings.password.updated': 'Password updated.',
    'settings.password.show': 'Show password',
    'settings.password.hide': 'Hide password',
    'settings.password.submit': 'Update password',
    'settings.password.forgot.title': 'Forgot password?',
    'settings.password.forgot.description': "We'll send a reset link to {email}.",
    'settings.password.forgot.description_no_email': 'Sign in first to receive a reset link.',
    'settings.password.forgot.cta': 'Send reset link',
    'settings.password.forgot.sent':
      "If an account exists with that email, a reset link has been sent. Check your spam folder if you don't see it. The link expires in 1 hour.",
    'settings.password.forgot.no_email_error': 'No email address attached to this account.',
    'settings.password.forgot.send_failed': 'Reset link send failed. Try again.',

    'settings.payment.title': 'Manage payment methods',
    'settings.payment.subtitle': 'Add or update your payment methods.',
    'settings.payment.saved_title': 'Your saved cards',
    'settings.payment.default_badge': 'Default',
    'settings.payment.expires': 'Expires',
    'settings.payment.selected_options': 'Options for the selected card:',
    'settings.payment.set_default': 'Set as default',
    'settings.payment.delete': 'Delete',
    'settings.payment.confirm_delete': 'Confirm?',
    'settings.payment.no_methods_title': 'No saved payment method.',
    'settings.payment.no_methods_description': 'Add a card to enable payments.',
    'settings.payment.add_title': 'Add a new card',
    'settings.payment.stripe_not_ready': 'Loading the payment form…',
    'settings.payment.delete_error': "Delete failed. Try again.",
    'settings.payment.update_error': 'Payment method update failed.',
    'settings.payment.processing': 'Processing…',
    'settings.payment.submit': 'Save new card',
    'settings.payment.field.number': 'Card number',
    'settings.payment.field.expiry': 'Expiry',
    'settings.payment.field.cvc': 'CVC',

    'settings.transaction.title': 'Choose your plan',
    'settings.transaction.subtitle': 'Pick what fits how you write.',
    'settings.transaction.basic.name': 'Standard',
    // ``Standard`` superseded the prior "Basic" label. The description
    // is the string Stripe stamps on the invoice + on
    // User.stripe_subscription_description (which quota.tier_for_email
    // substring-matches). We rename it too so new invoices read
    // "Standard"; quota.py matches both ``basic`` and ``standard``
    // so writers who subscribed pre-rename keep their tier.
    'settings.transaction.basic.description': 'Standard tier subscription',
    'settings.transaction.basic.features':
      'Text editor|Publication and content management|Discussion forums and messaging',
    'settings.transaction.premium.name': 'Premium',
    'settings.transaction.premium.description': 'Premium tier subscription',
    'settings.transaction.premium.features':
      'Basic features|Custom themes|Exclusive avatars and badges|Priority access to new features',
    'settings.transaction.period': '/month',
    'settings.transaction.billing_address': 'Billing address',
    'settings.transaction.methods_title': 'Saved payment methods',
    'settings.transaction.ssl_note': 'SSL 256-bit secure payment',
    'settings.transaction.no_methods': 'No saved payment method.',
    'settings.transaction.processing': 'Processing…',
    'settings.transaction.pay_now': 'Pay now',
    'settings.transaction.promo_code.label': 'Promo code (optional)',
    'settings.transaction.promo_code.placeholder': 'Enter a code',
    'settings.transaction.promo_code.invalid': 'That promo code isn’t valid or has been revoked. Leave the field empty to continue without one.',
    'settings.transaction.current_plan': 'Current plan',

    'settings.invoices.title': 'Billing history',
    'settings.invoices.subtitle':
      'Past charges, receipts, and downloadable PDFs for your subscription.',
    'settings.invoices.loading': 'Loading invoices…',
    'settings.invoices.error': 'Invoices unavailable. Try again.',
    'settings.invoices.empty.title': 'No invoices yet',
    'settings.invoices.empty.description':
      "You haven't been charged yet — once a paid plan is active your invoices will appear here.",
    'settings.invoices.untitled': 'Invoice {id}',
    'settings.invoices.download_pdf': 'Download PDF',
    'settings.invoices.view_hosted': 'Open invoice online',
    'settings.invoices.status.paid': 'Paid',
    'settings.invoices.status.open': 'Open',
    'settings.invoices.status.uncollectible': 'Uncollectible',
    'settings.invoices.status.void': 'Void',
    'settings.invoices.status.draft': 'Draft',
    'settings.transaction.already_subscribed': "You're already subscribed to this plan.",
    'settings.transaction.address_required': 'Please fill in the billing address.',
    'settings.transaction.payment_failed': 'Payment failed. Please try another payment method.',
    'settings.transaction.terms_note': 'By proceeding with the payment, you accept our terms and conditions of sale.',
    'settings.transaction.success_title': 'Payment confirmed, {name}.',
    'settings.transaction.success_subscription': 'Your subscription has been successfully activated.',
    'settings.transaction.success_payment': 'Your payment has been completed.',
    'settings.transaction.success_thanks': 'Thank you for your subscription.',
    'settings.transaction.discover': 'Discover other features',

    'settings.cancel.title': 'Cancel subscription',
    'settings.cancel.warning': 'You are about to cancel your subscription.',
    'settings.cancel.current_plan': 'Current plan',
    'settings.cancel.price': 'Price',
    'settings.cancel.period_end': 'Period end',
    'settings.cancel.error': "Cancellation failed. Try again.",
    'settings.cancel.cancel': 'Keep my subscription',
    'settings.cancel.canceling': 'Canceling…',
    'settings.cancel.confirm': 'Confirm cancellation',

    'error_boundary.title': 'Something went wrong.',
    'error_boundary.body':
      'The page hit an unexpected error. You can try again, or come back in a moment.',
    'error_boundary.retry': 'Try again',

    'error_page.code_label': 'Error',
    'error_page.title': 'Something went wrong.',
    'error_page.body':
      "We couldn't load this page. The team has been notified — try again, or head back home.",
    'error_page.retry': 'Try again',
    'error_page.go_home': 'Back to home',

    'not_found.eyebrow': '404',
    'not_found.title': 'Page not found',
    'not_found.body':
      "The page you were looking for doesn't exist or has moved.",
    'not_found.go_home': 'Back to home',

    'content_filter.error.generic': "That input isn't allowed. Please try a different value.",
    'content_filter.error.too_short': 'That value is too short.',
    'content_filter.error.too_long': 'That value is too long.',
    'content_filter.error.invalid_chars':
      'Use only letters, numbers, hyphens, dots, and underscores.',
    'content_filter.error.reserved':
      'That name is reserved. Please choose another.',
    'content_filter.error.pii':
      "Don't put email addresses or phone numbers in this field.",
    'content_filter.error.url_like':
      "Links aren't allowed in this field.",
    'content_filter.error.profanity':
      'Please choose something without profanity.',
    'content_filter.error.slur':
      'That word is not allowed.',

    // Plan picker — three-card layout. Free is shown as a peer
    // (not a "trial"); paid tiers buy more daily AI headroom on
    // minor features only. Core writing is always uncapped.
    'settings.transaction.free.name': 'Free',
    'settings.transaction.free.tagline': 'Everything to write. Forever.',
    'settings.transaction.basic.tagline': 'Daily AI helpers, comfortable headroom.',
    'settings.transaction.premium.tagline': 'Full AI, every chapter.',
    'settings.transaction.popular': 'Most popular',
    'settings.transaction.forever': 'Forever',
    'settings.transaction.daily_quotas': 'Daily AI quotas',
    'settings.transaction.no_paywalls': 'Every feature, every plan. Paid tiers only raise daily AI limits.',
    'settings.transaction.billing_period_aria': 'Billing period',
    'settings.transaction.currency_aria': 'Currency',
    'settings.transaction.billing.monthly': 'Monthly',
    'settings.transaction.billing.annual': 'Annual',
    // ``{total}`` carries its own currency symbol + amount (e.g.,
    // "CA$60") so the template stays neutral — no hardcoded "$".
    'settings.transaction.annual_total': 'Billed {total} per year',
    'settings.transaction.always_included_label': 'Always included, every tier',
    'settings.transaction.always_included':
      'Unlimited writing & projects|Publish & share chapters|Forum, comments & messages|Reading the catalogue|All themes & avatars|Notifications & profile editing',
    'settings.transaction.feature.daily': '{n}/day',
    'settings.transaction.feature.ai_grammar': 'grammar checks',
    'settings.transaction.feature.ai_inspiration': 'inspiration lookups',
    'settings.transaction.feature.ai_autocomplete': 'auto-complete suggestions',
    'settings.transaction.feature.ai_image': 'image generations',
    'settings.transaction.feature.dictionary': 'spell-check runs',
    'settings.transaction.feature.ai_planner_text': 'planner AI drafts',
    'settings.transaction.feature.ai_planner_image': 'planner illustrations',
    'settings.transaction.feature.ai_summarize': 'chapter summaries',
    'settings.transaction.feature.ai_tts_download': 'audio downloads',
    'settings.transaction.feature.ai_coherence': 'coherence checks',
    'settings.transaction.feature.ai_critique': 'project critiques',
    'settings.transaction.feature.ai_tropes': 'trope suggestions',

    // Today's-usage panel inside Settings.
    'settings.quota.today': "Today's AI usage",
    'settings.quota.loading': 'Loading usage…',
    'settings.quota.unavailable': 'Usage temporarily unavailable.',
    'settings.quota.reset_note': 'Resets daily at midnight ET.',

    // Paywall toast — fires when an AI call hits the daily ceiling.
    'paywall.toast.title': "You've used today's free AI",
    'paywall.toast.body':
      "You've used {used} of {limit} {feature} today. Upgrade to keep going, or come back tomorrow.",
    'paywall.toast.cta': 'See plans →',
    'paywall.toast.trial_cta': 'Or try Premium free for 7 days',
    'paywall.toast.trial_pending': 'Starting your trial…',
    'paywall.toast.trial_granted': '✓ Trial started. Premium quotas unlocked for 7 days.',
    'paywall.toast.trial_already_used': 'Your free trial has already been used.',
    'paywall.toast.trial_unavailable': "Free trial isn't available right now. You can still subscribe directly.",
    'paywall.toast.money_back': '30-day money-back guarantee on any paid plan.',

    'common.close': 'Close',
    'common.save': 'Save',
    'common.cancel': 'Cancel',
    'common.loading': 'Loading…',
    'common.undo': 'Undo',
    // Generic error fallbacks — used by ``util/friendlyError.js`` to
    // turn axios-style "Request failed with status code 500" or
    // raw "Network Error" into something readable. Backend-typed
    // errors (``err.response.data.error``) still pass through
    // verbatim so the writer sees the specific message when one
    // exists.
    'common.error.network': 'Connection lost. Check your network and try again.',
    'common.error.generic': 'Something went wrong. Please try again.',
    // Locale-aware display quotes — EN uses curly “ ”, FR uses guillemets
    // with non-breaking spaces («  …  »). Use everywhere we wrap
    // a user-supplied title in JSX text (planner health duplicates,
    // coherence/critique evidence excerpts, etc.) so French copy doesn't
    // render with English-style quotes.
    'common.quote_open': '“',
    'common.quote_close': '”',

    'auth.loading': 'Loading…',
    'auth.toast.session_expired': 'Your session has expired — please sign in again.',
    'auth.login.title': 'Login',
    'auth.login.subtitle': 'Sign in to continue',
    'auth.email_label': 'Email address',
    'auth.email_placeholder': 'example@example.com',
    'auth.password_label': 'Password',
    'auth.password_placeholder': '********',
    'auth.remember_me': 'Remember me',
    'auth.forgot_password': 'Forgot password?',
    'auth.login_cta': 'Login',
    'auth.no_account': "Don't have an account?",
    'auth.sign_up': 'Sign up',
    'auth.have_account': 'Already have an account?',
    'auth.log_in': 'Log in',

    'auth.signup.title': 'Register',
    'auth.signup.subtitle': 'Create an account to start writing.',
    'auth.fullname_label': 'Full Name',
    'auth.username_label': 'Username',
    'auth.create_password_label': 'Create a secure password',
    'auth.confirm_password_label': 'Confirm your password',
    'auth.terms_prefix': 'By signing up, you agree to our',
    'auth.terms': 'Terms',
    'auth.signup_cta': 'Get Started',
    'auth.fill_all_fields': 'Please fill in all fields.',
    'auth.passwords_dont_match': 'Passwords do not match.',
    'auth.weak_password': 'Choose a longer password ({min}+ characters).',
    'auth.terms_required': 'Please accept the Terms to continue.',
    'auth.user_already_exists': 'Email or username already in use.',
    'auth.signup_failed': 'Registration failed.',
    'auth.invalid_credentials': 'Invalid email or password.',
    'auth.signin_failed': 'Sign-in failed.',
    'auth.show_password': 'Show password',
    'auth.hide_password': 'Hide password',
    'auth.feature.secure': 'Secure',
    'auth.feature.interactive': 'Interactive',
    'auth.feature.friendly': 'Friendly',

    'auth.reset.title': 'Password Reset',
    'auth.reset.placeholder': 'Email address',
    'auth.reset.request_hint':
      "Enter the email on your account. We'll send you a one-hour code.",
    'auth.reset.confirm_hint':
      'Paste the code from the email and choose a new password.',
    'auth.reset.send': 'Send reset code',
    'auth.reset.sent':
      "If that email is registered, we've sent a 6-digit reset code. Check your spam folder if you don't see it. It expires in 1 hour.",
    'auth.reset.cooldown': 'Wait {seconds}s before retrying',
    'auth.reset.have_token': 'I already have a code',
    'auth.reset.resend': 'Send a new code',
    'auth.reset.token_label': '6-digit reset code',
    'auth.reset.token_placeholder': '000000',
    'auth.reset.new_password_label': 'New password',
    'auth.reset.new_password_placeholder': 'New password (8+ characters)',
    'auth.reset.confirm': 'Set new password',
    'auth.reset.confirmed': 'Password updated. You can sign in now.',
    'auth.reset.invalid_token':
      'That code is invalid or expired. Request a new one.',
    'auth.reset.weak_password': 'Password is too weak:',
    'auth.reset.request_failed':
      "Reset failed to start. Try again in a minute.",
    'auth.reset.confirm_failed':
      "Password update failed. Try again in a minute.",
    'auth.reset.rate_limited': 'Too many attempts. Wait a minute and retry.',
    'auth.reset.no_account': "Don't have an account?",
    'auth.reset.register': 'Register',
    'auth.reset.now': 'now.',
    'auth.reset.page_title': 'Reset your password',
    'auth.reset.page_subtitle':
      'Enter a new password — the email and code from the link are already filled in.',
    'auth.reset.confirm_password_label': 'Confirm new password',
    'auth.reset.confirm_password_placeholder': 'Type the new password again',
    'auth.reset.password_mismatch': "Passwords don't match.",
    'auth.reset.back_to_login_prefix': 'Remember your password?',
    'auth.reset.back_to_login': 'Back to sign in',

    'goal.page.title': 'Goals',
    'goal.page.subtitle': 'Plan your writing sessions and track your streaks.',
    'goal.new': 'New goal',
    'goal.period.daily': 'Daily',
    'goal.period.weekly': 'Weekly',
    'goal.period.project': 'Per project',
    'goal.target_words': 'Target words',
    'goal.streak_days': 'Streak (days)',
    'goal.active': 'Active',
    'goal.save': 'Save',
    'goal.cancel': 'Cancel',
    'goal.delete': 'Delete',
    'goal.empty.title': 'No goals yet',
    'goal.empty.description':
      'Set a daily or weekly word target and your streak will update automatically after each writing sprint.',
    'goal.confirm_delete': 'This goal and its progress history will be permanently removed.',
    'goal.confirm_delete_title': 'Delete this goal?',

    'help.title': 'Help center',
    'help.intro':
      'Short answers to the questions that come up most. For anything else, the community forum reaches the team and other writers fastest.',
    'help.sections.getting_started': 'Getting started',
    'help.sections.writing': 'Writing & editing',
    'help.sections.collab': 'Collaboration',
    'help.sections.billing': 'Billing',
    'help.contact': 'Question we haven\'t answered? Send a note.',
    'help.page.title': 'Find your way around Plumescript',
    'help.cards.essentials.title': 'Get started',
    'help.cards.essentials.body':
      'Create an account, confirm your email with the 6-digit code, and start writing. No setup, no plug-ins.',
    'help.cards.essentials.body_more':
      'Drafts save every few seconds. Two writers can edit the same chapter without overwriting each other. The public reader works without an account, and any chapter exports to PDF, Word, HTML, Markdown, plain text, or image in one click. Commenting, messaging, and starting forum threads each require a verified email — one 6-digit code unlocks all three.',
    'help.cards.essentials.cta': 'Learn more',
    'help.cards.essentials.cta_less': 'Show less',
    'help.cards.collab.title': 'Write together',
    'help.cards.collab.body':
      'Invite a co-writer by email. You both edit the same chapter live, see each other\'s cursor, and can\'t overwrite one another. Inline comments, voice + video rooms, and a thread per paragraph keep every conversation in one place.',
    'help.cards.collab.cta': 'Start writing now',
    'help.cards.transform.title': 'AI that waits to be asked',
    'help.cards.transform.body':
      'Tone analysis, readability scores, synonym and citation lookup, and an AI co-writer. Every suggestion can be accepted, rewritten, or ignored. Nothing runs without your prompt.',
    'help.cards.transform.cta': 'Discover more',
    'help.cards.enhance.title': 'Tune the page',
    'help.cards.enhance.body':
      'Focus mode for long sessions, timed sprints for short ones, six themes (Light, Dark, Playful, Warm, plus two WCAG-AAA high-contrast variants), and reading-prefs sliders for text size, line height, and letter spacing.',
    'help.cards.enhance.cta': 'Read our guide',
    'help.newsletter.title': 'Stay in the loop',
    'help.newsletter.description': 'A short monthly note covering new features, writer tips, and community highlights.',
    'help.newsletter.placeholder': 'Enter your email to join our newsletter',
    'help.newsletter.submit': 'Subscribe',
    'help.newsletter.submitting': 'Working…',
    'help.newsletter.unsubscribe': 'Unsubscribe',
    'help.newsletter.subscribed_state': 'Subscription active.',
    'help.newsletter.thanks': 'Thanks. We’ll be in touch.',
    'help.newsletter.thanks_unsubscribe': 'Unsubscribed. You’ll stop receiving issues from now on.',
    'help.newsletter.error.invalid': 'That email doesn’t look right.',
    'help.newsletter.error.generic': 'Subscription update failed. Try again in a minute.',

    'report.button': 'Report',
    'report.modal.title': 'Report this',
    'report.modal.description': 'Tell us what’s wrong. The moderation team reviews every report.',
    'report.field.kind': 'Why are you reporting this?',
    'report.field.reason': 'Reason',
    'report.field.reason.placeholder': 'Short summary (required)',
    'report.field.note': 'Anything else?',
    'report.field.note.placeholder': 'Optional context — links, screenshots, etc.',
    'report.cancel': 'Cancel',
    'report.submit': 'Submit report',
    'report.submitting': 'Submitting…',
    'report.toast.filed': 'Report submitted. Thank you.',
    'report.toast.already_filed': 'You already reported this — we’re on it.',
    'report.toast.failed': 'Report submission failed. Try again in a minute.',
    'report.kind.spam': 'Spam or scam',
    'report.kind.harassment': 'Harassment or hate',
    'report.kind.illegal': 'Illegal content',
    'report.kind.copyright': 'Copyright violation',
    'report.kind.other': 'Other',

    'help.quickstart.guest.heading': 'Three ways in',
    'help.quickstart.guest.subheading': 'Try the platform without an account first. Most of it is open to read.',
    'help.quickstart.guest.signup.title': 'Open a free account',
    'help.quickstart.guest.signup.description': 'Write your first chapter, save where you are, and invite a co-writer when you’re ready.',
    'help.quickstart.guest.signup.cta': 'Sign up',
    'help.quickstart.guest.browse.title': 'Browse the catalogue',
    'help.quickstart.guest.browse.description': 'Read what other writers have published. No account needed to start exploring.',
    'help.quickstart.guest.browse.cta': 'Browse stories',
    'help.quickstart.guest.games.title': 'Warm up with a word game',
    'help.quickstart.guest.games.description': 'Tiny reading and writing puzzles to loosen the creative muscles before a session.',
    'help.quickstart.guest.games.cta': 'Play games',

    'help.quickstart.logged.heading': 'Pick up where you left off',
    'help.quickstart.logged.subheading': 'Three places writers spend most of their time.',
    'help.quickstart.logged.start.title': 'Start a new project',
    'help.quickstart.logged.start.description': 'A fresh book, short story, or serial. Pick a cover, add a description, grow chapters as you go.',
    'help.quickstart.logged.start.cta': 'Create project',
    'help.quickstart.logged.collab.title': 'Open your workspace',
    'help.quickstart.logged.collab.description': 'Forum threads, shared tasks, the writers in your circle. The community side of Plumescript, all in one place.',
    'help.quickstart.logged.collab.cta': 'Open workspace',
    'help.quickstart.logged.goals.title': 'Track your writing',
    'help.quickstart.logged.goals.description': 'Set a daily word goal, watch your streak climb, see the weekly view at a glance.',
    'help.quickstart.logged.goals.cta': 'Open my goals',

    'help.faq.heading': 'Five questions, before you start',
    'help.faq.intro': 'The ones writers ask most often when they first land on Plumescript.',
    'help.faq.q1': 'Do I need an account to read?',
    'help.faq.a1': 'No. The catalogue, the reader, the forum, the help page, and the word games are open to anyone. Writing and following others need a free account. Commenting, messaging another writer, or starting a forum thread also asks you to verify your email. One 6-digit code from your inbox unlocks all three.',
    'help.faq.q2': 'How does writing together work?',
    'help.faq.a2': "From a project, open the share dialog and invite a co-writer by email. Edits sync the moment they're typed. Two writers can type in the same chapter at once, see each other's cursor, and never overwrite one another. Drafts save themselves every couple of seconds.",
    'help.faq.q3': 'I forgot my password. Can I get back in?',
    'help.faq.a3': 'Yes. On the sign-in page, click "Forgot password?", enter your email, and we send a one-hour reset link plus a 6-digit code. Click the link to land on a pre-filled reset page. If the link doesn\'t work, paste the code into the same form. The link strips itself from the address bar after it\'s read, so a shared device doesn\'t leak it.',
    'help.faq.q4': 'Is what I write private by default?',
    'help.faq.a4': 'Yes. Every project starts private. Only you can see it. You can flip a project to "shared" (visible to the people you invite) or "public" (listed in the catalogue) any time from the project edit screen. Publishing the first chapter of a private project also auto-promotes the project to public, so readers can actually find it.',
    'help.faq.q5': 'What is the impact of generating an AI image?',
    'help.faq.a5': 'Free for you. Not free in energy terms. One generated cover takes roughly as much electricity as charging your phone from empty to full, plus a glass of water for cooling at the data center. Tiny by itself, real once thousands of users hit "generate" on a whim. Use it on covers you actually plan to keep. Suggestions and tone analysis run on much lighter models and add a comparatively negligible footprint.',

    'help.shortcuts.heading': 'Keyboard shortcuts',
    'help.shortcuts.intro': 'A handful of shortcuts for the things you do most often inside the editor.',
    'help.shortcuts.bold': 'Bold',
    'help.shortcuts.italic': 'Italic',
    'help.shortcuts.link': 'Insert link',
    'help.shortcuts.undo': 'Undo',
    'help.shortcuts.redo': 'Redo',
    'help.shortcuts.save': 'Save current chapter',
    'help.shortcuts.send': 'Send message',
    'help.shortcuts.search': 'Focus search',
    'help.shortcuts.hint': 'On macOS, replace Ctrl with ⌘ Cmd.',

    'help.contact.title': 'Still have a question?',
    'help.contact.description':
      'The forum is the fastest path — other writers and the team both watch it. Or write to us directly. Every message is read.',
    'help.contact.cta': 'Visit the forum',
    'help.contact.submitting': 'Sending…',
    'help.contact.thanks':
      'Message received. A confirmation is on its way and we\'ll reply as soon as we can.',
    'help.contact.field.name': 'Your name',
    'help.contact.field.name_placeholder': 'Your name (optional)',
    'help.contact.field.email': 'Your email',
    'help.contact.field.email_placeholder': 'name@example.com',
    'help.contact.field.subject': 'Subject',
    'help.contact.field.subject_placeholder': 'What can we help with?',
    'help.contact.field.body': 'Message',
    'help.contact.field.body_placeholder':
      'A few sentences is plenty. Include any links or screenshots that would help.',
    'help.contact.error.invalid_email':
      "That email doesn't look right. Please double-check and try again.",
    'help.contact.error.too_short':
      'Please share a few more details so we can help.',
    'help.contact.error.too_long':
      'That message is a bit long. Please trim it under 8,000 characters.',
    'help.contact.error.rate_limited':
      "You've sent a few messages already. Please wait an hour before sending another.",
    'help.contact.error.generic':
      "Send failed. Please try again, or email us directly.",

    'contact.seo.title': 'Contact us',
    'contact.seo.description':
      'Talk to the Plumescript team. What we believe, what we’re building, and how to reach us.',
    'contact.hero.tag': 'A small bilingual team',
    'contact.hero.title': 'Talk to us',
    'contact.hero.body':
      'Plumescript is a small bilingual team based in Québec. We read every message that comes in. The rest of this page covers what we’re building, what we believe, and the fastest ways to reach us.',
    'contact.hero.cta_primary': 'Send a message',
    'contact.hero.cta_secondary': 'Email us directly',
    'contact.trust.reply.title': 'We do read these',
    'contact.trust.reply.body':
      'Every message lands in a real inbox someone on the team checks. No auto-router, no triage queue.',
    'contact.trust.privacy.title': 'Yours, always',
    'contact.trust.privacy.body':
      'Your message stays between us. Never sold, never used to train models.',
    'contact.trust.bilingual.title': 'EN · FR, either way',
    'contact.trust.bilingual.body':
      'Write to us in whichever language is easier. We answer in the same.',
    'contact.mission.eyebrow': 'Our mission',
    'contact.mission.title': 'Give writers more room',
    'contact.mission.body':
      'Hand the writer more control over their work. Versioning that lets them roll back, collaboration that doesn’t overwrite, AI that waits to be asked, and a quiet page when none of the above is needed.',
    'contact.vision.eyebrow': "Where we're going",
    'contact.vision.title': 'One tool, every chapter',
    'contact.vision.body':
      'A reading-and-writing platform that scales from one person scribbling at midnight to a small team shipping a serial. No tool-switching, no lost presence, no drift between drafts.',
    'contact.values.eyebrow': 'What we value',
    'contact.values.title': 'What writing here feels like',
    'contact.values.intro':
      'It’s 11 PM. You open Plumescript. The page reads in your language, the menu uses the keyboard you actually use, and nobody asked you to translate yourself. Two paragraphs in, your co-writer’s cursor blinks down the page, and you keep typing past each other without ever colliding. The autosave timestamp ticks. The privacy notice still says the same thing it did last week. You ask the AI for a softer ending. It offers three. You take none, and that’s fine. Four quiet commitments keep this room steady.',
    'contact.values.inclusion.title': 'Inclusion',
    'contact.values.inclusion.body':
      'The page meets you in your language and your keyboard. No detour to fit in.',
    'contact.values.collaboration.title': 'Collaboration',
    'contact.values.collaboration.body':
      'Two cursors in one chapter. Each move is yours. Neither overwrites the other.',
    'contact.values.responsibility.title': 'Responsibility',
    'contact.values.responsibility.body':
      'Autosaves you can read. A privacy notice that says the same thing tomorrow.',
    'contact.values.innovation.title': 'Innovation',
    'contact.values.innovation.body':
      'Suggestions you can accept, refuse, or ignore. Tools that wait for your call.',
    'contact.info.title': 'Direct contact',
    'contact.info.intro':
      'Need a faster channel? Either of these reaches us in minutes.',
    'contact.info.email_label': 'Email',
    'contact.info.email_value': 'support@plumescript.ca',
    'contact.info.email_hint':
      'Write to us in either language. We read every message that comes in.',
    'contact.info.community_label': 'Community',
    'contact.info.community_value': 'The forum',
    'contact.info.community_hint':
      'Public questions, feature ideas, and writing-craft chatter all live in /forum.',
    'contact.info.community_cta': 'Open the forum',
    'contact.form.title': 'Send a message',
    'contact.form.intro':
      'The form is for everything else. Bugs you hit, ideas you have, anything we should know. A confirmation lands in your inbox the moment you send.',
    'contact.form.submit': 'Send message',
    'contact.form.submitting': 'Sending…',
    'contact.closer.title': 'Thanks for reading this far',
    'contact.closer.body':
      'If something’s urgent, drop "urgent" in the subject. We’ll see it. Otherwise we work the inbox in order.',

    'help.discover.title': 'What Plumescript does',
    'help.discover.intro': "A quick rundown of what's in the box. Most of it works without an account. Sign up when you're ready to write your own.",
    'help.discover.point1': 'Real-time collaborative editor. Multiple writers can type in the same chapter, see each other\'s cursor, and never overwrite each other.',
    'help.discover.point2': 'Public catalogue and reader so anyone can discover and read stories without an account.',
    'help.discover.point3': 'AI cover generation, tone analysis, writing suggestions, plus studio and personal-voice narration, all sent to third-party providers only when you ask.',
    'help.discover.point4': 'Goals, sprints, and a focus mode for the writers who like a bit of pressure.',
    'help.discover.point5': 'Forum, messages, and reactions for the social side of writing. Verify your email once and they all unlock.',
    'help.discover.point6': 'Milestone titles for active engagement and paid membership tenure, displayed on your public profile.',
    'help.discover.close': 'Close',
    'help.discover.browse': 'Browse the catalogue',

    'editor.guest.title': 'Log in to edit this story',
    'editor.guest.description': "The editor needs an account to load and sync your changes. You can still read this story without one — open the public reader instead, or sign in to start writing.",
    'editor.guest.login_cta': 'Log in to edit',
    'editor.guest.reader_cta': 'Open the reader',

    'editor.basic.title': 'Scratch editor',
    'editor.basic.subtitle': 'Write something now. Your draft is saved on this device — no account needed.',
    'editor.basic.saved_locally': 'Saved on this device',
    'editor.basic.title_placeholder': 'Untitled draft',
    'editor.basic.body_placeholder': 'Start writing…',
    'editor.basic.stats.words': 'words',
    'editor.basic.stats.chars': 'characters',
    'editor.basic.stats.reading': '{minutes} min read',
    'editor.basic.action.copy': 'Copy',
    'editor.basic.action.download': 'Download .txt',
    'editor.basic.action.clear': 'Clear',
    'editor.basic.toast.copied': 'Copied to clipboard.',
    'editor.basic.toast.copy_failed': "Clipboard unavailable.",
    'editor.basic.toast.cleared': 'Draft cleared.',
    'editor.basic.confirm_clear': 'The current draft will be erased. This cannot be undone.',
    'editor.basic.confirm_clear_title': 'Clear the draft?',
    'editor.basic.confirm_clear_action': 'Clear',
    'editor.basic.upgrade.title': 'Want to keep going?',
    'editor.basic.upgrade.description': 'Create a free account to save drafts to the cloud, work across devices, share with collaborators, and publish to the public catalogue.',
    'editor.basic.upgrade.cta': 'Create a free account',

    'terms.eyebrow': 'Legal',
    'terms.title': 'Terms of Service',
    'terms.subtitle': 'A plain-English summary of how Plumescript works, what we promise, and what we ask in return.',
    'terms.last_updated': 'Last updated {date}',
    'terms.toc.label': 'On this page',

    'terms.section.intro.title': 'About these terms',
    'terms.section.intro.body':
      "Plumescript is a writing platform: an editor, a story planner, a public reader catalogue, a community forum, and a few AI helpers around them. These terms cover the website, the editor, the public catalogue, and any service we host on plumescript.ca.\n\nThey are written in plain English to set honest expectations, not to maximise our protection. If anything reads ambiguously, take the most generous interpretation for you — and reach out before relying on the strict wording.",

    'terms.section.account.title': 'Your account',
    'terms.section.account.body':
      "The public reader, catalogue, forum, games, and help pages are open to everyone. A free account unlocks writing, importing manuscripts, and following other writers. Verifying your email — we send a 6-digit code — additionally unlocks commenting, messaging, and starting forum threads.\n\nForgot your password? Request a reset link from the sign-in page; the email contains a one-tap link that prefills the form. One person, one account: don't share credentials or operate accounts on behalf of someone else.",

    'terms.section.content.title': 'Your content stays yours',
    'terms.section.content.body':
      "Everything you write — projects, chapters, planner notes, comments, forum posts — stays yours. We only store and process it to run the app, sync your edits across devices, and keep backups so you don't lose work.\n\nPublishing a project to the public catalogue grants us (and other readers) a non-exclusive licence to display it inside the app. Unpublishing, archiving, or deleting a project revokes that licence going forward.\n\nImporting a manuscript — drag a .docx, .pdf, .epub, .txt, or .md into the Planner — places it in your private project, just like prose you typed yourself. If you import a published book to study its structure, mark the project as study or research so it stays out of your main listings, and read the next section first.",

    'terms.section.use.title': 'Acceptable use',
    'terms.section.use.body':
      "Use the platform for lawful personal or collaborative writing. Don't post or upload anything you don't have the right to share, anything illegal where you live, or anything whose purpose is to harass, threaten, or impersonate someone.\n\nImported content is your responsibility. The import feature will happily accept a .epub of someone else's novel — that doesn't grant you the right to redistribute it. Importing a copyrighted work for private study or analysis is between you and the rights holder; publishing or sharing a project containing content you didn't write or licence violates these terms, and we'll act on credible takedown requests.\n\nDon't break or scrape the service: no automated abuse, no credential stuffing, no exploiting bugs to read private projects. Found a security issue? Please report it — we handle good-faith disclosures generously.",

    'terms.section.ai.title': 'AI features',
    'terms.section.ai.body':
      "Several features use third-party AI providers. When you trigger one, the relevant text is sent to the provider so it can return a result. We do not use your private writing or your voice samples to train models.\n\nWhat gets sent, by feature:\n• Planner suggestions, character extraction, manuscript analysis — the relevant chapter or planner excerpt.\n• Coherence checker and project critique — your planner export plus the chapter you choose to review.\n• Tone, rewrite, continue, and chat tools — the passage you highlighted or the question you typed.\n• Cover image generation — your title, genre, and a short prompt.\n• Pro narrator — the text of the chapter to narrate.\n• Personal narrator — the chapter text plus the voice sample you uploaded to clone from. Samples stay private to your account; delete them any time from Settings.\n\nImage generation has a real physical footprint: roughly a full phone charge of electricity and a glass of cooling water at the data centre, per cover. The generation modal restates this before you spend a credit, so the choice stays yours. Text and narration AI features run on much lighter models with a comparatively negligible footprint.\n\nDaily quotas apply per feature across free and paid tiers. The remaining count is always visible before you spend one.",

    'terms.section.termination.title': 'Ending the relationship',
    'terms.section.termination.body':
      "Delete your account any time from Settings. We'll remove your private projects, planner data, and forum posts within 30 days.\n\nWe may suspend or terminate accounts that violate these terms or abuse the service. We may also reclaim accounts that have sat dormant for more than two years — but we'll always email you first so you have a chance to come back and keep your work.",

    'terms.section.liability.title': 'What we promise, where we draw the line',
    'terms.section.liability.body':
      "Responsibility means saying what we will and won't carry. We promise honest effort: a privacy notice that says the same thing tomorrow, autosaves you can read, suggestions you can refuse, and prompt attention when something breaks. Plumescript is still maturing — some features will move, some will be reshaped, some will retire. We'll tell you when that happens, and we won't surprise-charge you for it.\n\nA small ask in return: keep your own copy of anything truly important. Export your projects, save your manuscripts. We back up the service carefully, but no platform is immune to a bad day, and you should never be one of ours away from your work.\n\nIf something we did or didn't do causes a real loss, our remedy is bounded by what you've paid us in the prior 12 months. For free-tier writers that figure is zero — not because your work is worth nothing, but because we want the Free tier to stay genuinely free, no fine-print toll.\n\nPaid plans are processed by Stripe. You can manage or cancel any time from Settings → Billing; cancellations take effect at the end of the current period. No retention call, no dark pattern.",

    'terms.section.contact.title': 'Questions?',
    'terms.section.contact.body':
      "If anything is unclear, the fastest way to reach us is the community forum. For privacy questions or rights / takedown requests, email support@plumescript.ca — we respond within a few business days.",

    'terms.footer.note': 'These terms apply to every page on plumescript.ca.',
    'terms.footer.back_help': 'Back to Help',
    'terms.footer.signup': 'Sign up',

    'guest_banner.headline': 'New here?',
    'guest_banner.subhead': 'Create a free account to start writing, save progress, and collaborate.',
    'guest_banner.signup': 'Sign up free',
    'guest_banner.login': 'Log in',
    'guest_banner.dismiss': 'Dismiss',

    'forum.guest.title': 'Want to ask a question?',
    'forum.guest.description': 'Anyone can read the forum, but you need an account to post or reply.',
    'forum.guest.signup': 'Sign up to ask',
    'forum.guest.login': 'Log in',
    'forum.guest.reply_prompt': 'Sign up or log in to add a reply.',

    'profile.stats.stories': 'Stories',
    'profile.stats.followers': 'Followers',
    'profile.stats.following': 'Following',
    'profile.follow': 'Follow',
    'profile.unfollow': 'Unfollow',
    'profile.message': 'Message',
    'profile.tabs.projects': 'Projects',
    'profile.tabs.following': 'Following',
    'profile.tabs.activity': 'Activity',
    'profile.empty.projects': 'No projects published yet.',
    'profile.empty.followers': 'No connections yet.',
    'profile.empty.activity': 'Nothing to show yet.',
    'profile.empty.projects.title': 'No projects yet',
    'profile.empty.projects.description':
      'Public projects appear here once a writer publishes their first chapter.',
    'profile.empty.following.title': 'No connections yet',
    'profile.empty.following.description':
      'Writers this profile follows will show up here as a curated reading shortlist.',
    'profile.empty.activity.title': 'Quiet for now',
    'profile.empty.activity.description':
      'Recent publications, ratings, and follows will appear here as the writer keeps creating.',
    'profile.edit': 'Edit profile',
    'profile.bio.placeholder': 'No bio yet.',
    'profile.edit.title': 'Edit your profile',
    'profile.edit.username': 'Username',
    'profile.edit.username.placeholder': 'How others see you',
    'profile.edit.picture': 'Avatar',
    'profile.edit.picture.upload': 'Upload',
    'profile.edit.picture.uploading': 'Uploading…',
    'profile.edit.picture.url': 'Or paste an image URL',
    'profile.edit.bio': 'Bio',
    'profile.edit.bio.placeholder': 'Tell readers a little about yourself.',
    'profile.edit.cancel': 'Cancel',
    'profile.edit.save': 'Save',
    'profile.edit.saving': 'Saving…',
    'profile.edit.toast.saved': 'Profile updated.',
    'profile.edit.toast.failed': 'Profile update failed. Try again.',
    'profile.edit.toast.username_taken': 'That username is already taken.',
    'profile.edit.toast.upload_failed': 'Avatar upload failed.',
    'profile.card.discover': 'Discover',
    'profile.card.last_activity': 'Last activity',
    'profile.card.no_description': 'No description',
    'profile.card.edit': 'Open',
    'profile.card.read': 'Read',
    'profile.card.parts': '{count} parts',
    'profile.card.visibility.public': 'Public',
    'profile.card.visibility.private': 'Private',
    'profile.card.visibility.shared': 'Shared',
    'profile.card.archived': 'Archived',
    'profile.card.published': 'Published',
    'profile.card.draft': 'Draft',
    'profile.card.last_edited': 'Last edited {date}',
    'profile.card.last_read': 'Last read {date}',
    'profile.back': 'Back',
    'profile.activity.project_created': 'Created project {project}',
    'profile.activity.project_updated': 'Updated project {project}',
    'profile.activity.chapter_published': 'Published “{chapter}” in {project}',
    'profile.activity.chapter_updated': 'Updated “{chapter}” in {project}',
    'profile.activity.forum_posted': 'Posted on the forum — {title}',
    'profile.activity.rating_given': 'Rated {project} {stars} ★',
    'profile.activity.follow_added': 'Started following {target}',
    'profile.titles.heading': 'Title',
    'profile.titles.newcomer': 'Newcomer',
    'profile.titles.storyteller': 'Storyteller',
    'profile.titles.prolific': 'Prolific',
    'profile.titles.wordsmith': 'Wordsmith',
    'profile.titles.voice': 'Voice',
    'profile.titles.legend': 'Legend',
    'profile.titles.critic': 'Critic',
    'profile.titles.forum_voice': 'Forum Voice',
    'profile.titles.connector': 'Connector',
    'profile.titles.beloved': 'Beloved',
    'profile.titles.settler': 'Settler',
    'profile.titles.voyager': 'Voyager',
    'profile.titles.chronicler': 'Chronicler',
    'profile.titles.veteran': 'Veteran',
    'profile.titles.elder': 'Elder',
    'profile.titles.founding_quill': 'Founding Quill',
    'profile.titles.member_1m': 'Friend of the House',
    'profile.titles.member_3m': 'Steady Hand',
    'profile.titles.member_6m': 'Long Reader',
    'profile.titles.member_1y': 'Year-One Patron',
    'profile.titles.member_3y': 'Pillar',
    'profile.titles.member_5y': 'Cornerstone',
    // Extended ladder titles -- recognise long-haul activity
    // on the existing counters (published chapters, words,
    // ratings given, forum threads, projects, followers,
    // following, distinct readers).
    'profile.titles.sage': 'Sage',
    'profile.titles.ink_trail': 'Ink Trail',
    'profile.titles.library': 'Living Library',
    'profile.titles.patron': 'Patron',
    'profile.titles.forum_master': 'Forum Master',
    'profile.titles.architect': 'Archive Architect',
    'profile.titles.beacon': 'Beacon',
    'profile.titles.compass': 'Compass',
    'profile.titles.cherished': 'Cherished',
    // Creative-condition titles -- combinations of axes the
    // simple thresholds don't reach.
    'profile.titles.balanced_voice': 'Balanced Voice',
    'profile.titles.renaissance': 'Renaissance Writer',
    // AI utilisation bucket — rewards heavy use of the AI surfaces.
    'profile.titles.ai_apprentice': 'AI Apprentice',
    'profile.titles.ai_adept': 'AI Adept',
    'profile.titles.tech_wizard': 'Tech Wizard',
    'profile.titles.ai_archmage': 'AI Archmage',
    'profile.titles.planner_alchemist': 'Planner Alchemist',
    'profile.titles.grammar_guru': 'Grammar Guru',
    'profile.titles.inspiration_seeker': 'Inspiration Seeker',
    'profile.titles.image_summoner': 'Image Summoner',
    'profile.titles.coherence_keeper': 'Coherence Keeper',
    'profile.titles.toast.failed': 'Title update failed. Try again.',

    'settings.milestones.title': 'Achievements',
    'settings.milestones.subtitle':
      'Titles unlocked by engagement and tenure.',
    'settings.milestones.loading': 'Loading…',
    'settings.milestones.unavailable':
      'Achievements unavailable right now.',
    'settings.milestones.current': 'Current',
    'settings.milestones.next': 'Next:',
    'settings.milestones.threshold_days': '{days} d',
    'settings.milestones.flip_to': 'Switch to {name}',

    'settings.milestones.engagement.title': 'Engagement',
    'settings.milestones.engagement.subtitle': '',
    'settings.milestones.engagement.tenure': '{days} sign-in days',
    'settings.milestones.engagement.next_threshold': '{days} d to go',
    'settings.milestones.engagement.empty':
      'First title unlocks after 7 sign-in days.',
    'settings.milestones.engagement.maxed': 'All engagement titles unlocked.',

    'settings.milestones.membership.title': 'Paid membership',
    'settings.milestones.membership.subtitle': '',
    'settings.milestones.membership.tenure': '{days} days on a paid plan',
    'settings.milestones.membership.next_threshold': '{days} d to go',
    'settings.milestones.membership.empty':
      'First title unlocks one month after activation.',
    'settings.milestones.membership.maxed':
      'All paid-membership titles unlocked.',
    'settings.milestones.membership.free_tier':
      'Upgrade to a paid plan to start earning these titles.',

    'settings.recent_activity.title': 'Recent activity',
    'settings.recent_activity.subtitle':
      'The latest action on this account.',
    'settings.recent_activity.loading': 'Loading…',
    'settings.recent_activity.empty':
      'No recent activity yet.',
    'settings.recent_activity.unavailable':
      'Recent activity unavailable right now.',
    'settings.recent_activity.kind.project_created':
      'Created project “{title}”',
    'settings.recent_activity.kind.project_updated':
      'Updated project “{title}”',
    'settings.recent_activity.kind.chapter_published':
      'Published “{chapter}” in {project}',
    'settings.recent_activity.kind.chapter_updated':
      'Edited “{chapter}” in {project}',
    'settings.recent_activity.kind.forum_posted':
      'Posted in forum: “{title}”',
    'settings.recent_activity.kind.rating_given':
      'Rated {title} {stars}★',
    'settings.recent_activity.kind.follow_added':
      'Followed @{username}',

    'footer.label': 'Site footer',
    'footer.tagline': 'A focused space to write, share, and read in-progress stories. Free to read, no account needed.',
    'footer.discover': 'Discover',
    'footer.account': 'Account',
    'footer.preferences': 'Preferences',
    'footer.rights': 'All rights reserved.',
    'footer.terms': 'Terms',
    'footer.contact': 'Contact',
    'a11y.skip_to_main': 'Skip to main content',
    'a11y.main_landmark': 'Main content',
    'a11y.primary_nav': 'Primary navigation',
    'a11y.reading_prefs.title': 'Reading preferences',
    'a11y.reading_prefs.subtitle':
      'Make long-form text comfortable to read. Saved on this device only.',
    'a11y.reading_prefs.open': 'Open reading preferences',
    'a11y.reading_prefs.open_hint': 'Font size, line height, dyslexia-friendly font, focus mode and more.',
    'a11y.reading_prefs.intro':
      'Adjust text size, line height, letter spacing, and link emphasis. Each control updates the page immediately.',
    'a11y.reading_prefs.font_scale': 'Text size',
    'a11y.reading_prefs.line_height': 'Line height',
    'a11y.reading_prefs.letter_spacing': 'Letter spacing',
    'a11y.reading_prefs.underline_links': 'Always underline links',
    'a11y.reading_prefs.reset': 'Reset to defaults',
    'home.seo.description': 'Browse stories from the Plumescript community — books, serials and works in progress, free to read without an account.',
    'home.welcome': 'Welcome back',
    'home.quick_actions': 'Quick actions',
    'home.cta.new_project': 'Start a new project',
    'home.cta.open_editor': 'Open the editor',
    'home.cta.goals': 'Set a writing goal',
    'home.cta.collab': 'Find collaborators',
    'home.recent_projects': 'Recent projects',
    'home.empty.projects': 'No projects yet. Your first story starts here.',
    'home.section.community': 'From the community',
    'home.section.explore': 'Explore',
    'home.welcome_back': 'Welcome back, {name}.',
    'home.tagline': 'Crafting worlds with words, one story at a time.',
    'home.guest.eyebrow': 'Discover',
    'home.guest.title': 'Read freely, no account needed.',
    'home.guest.subtitle': 'Sign up when you’re ready to write your own.',
    'home.guest.cta_signup': 'Sign up',
    'home.guest.cta_browse': 'Browse',
    'home.start_writing': 'Start writing',
    'home.start_reading': 'Start reading',
    'home.your_stories.title': 'Your stories',
    'home.your_stories.subtitle': 'Catch up on the stories you love.',
    'home.recommended.title': 'Recommended for you',
    'home.recommended.subtitle': 'Stories picked for the way you write.',

    'browse.catalogue.title': 'Discover the catalogue',
    'browse.catalogue.subtitle':
      'A curated selection of public projects from across the platform.',
    'browse.catalogue.filter.all': 'All',
    // Catalogue category presets — expanded set so the browse
    // filter rail surfaces the genres writers actually pick instead
    // of the original three-row "Fiction / Bio / Non-fiction" stub.
    'browse.catalogue.filter.novel': 'Novel',
    'browse.catalogue.filter.short_story': 'Short story',
    'browse.catalogue.filter.poetry': 'Poetry',
    'browse.catalogue.filter.essay': 'Essay',
    'browse.catalogue.filter.memoir': 'Memoir',
    'browse.catalogue.filter.screenplay': 'Screenplay',
    'browse.catalogue.filter.children': "Children's",
    'browse.catalogue.filter.comic': 'Comic / Graphic novel',
    'browse.catalogue.filter.non_fiction': 'Non-fiction',
    'browse.catalogue.filter.fantasy': 'Fantasy',
    'browse.catalogue.filter.scifi': 'Sci-fi',
    'browse.catalogue.filter.mystery': 'Mystery',
    'browse.catalogue.filter.romance': 'Romance',
    // Locale filter chip strip — shared by Browse + Search + Forum.
    'locale_filter.aria': 'Filter by language',
    'locale_filter.fr': 'French',
    'locale_filter.en': 'English',
    'locale_filter.all': 'All languages',
    // Forum card chrome — extracted from hardcoded strings in
    // QuestionCard for i18n + mobile-tight phrasing.
    'forum.card.new_badge': 'New',
    'forum.card.replies': '{count} replies',
    'forum.card.edit': 'Edit',
    'forum.card.delete': 'Delete',
    // Jitsi video-call toasts — replaced the silent failure where a
    // load error left the writer staring at an empty box.
    'messages.jitsi.toast.sleep_hours': 'Video calls are paused overnight (11 PM – 7 AM ET). Try again in the morning.',
    'messages.jitsi.toast.load_failed': "Video call connection failed. Check your network and try again.",
    'browse.catalogue.untitled': 'Untitled',
    'browse.catalogue.prev': 'Previous',
    'browse.catalogue.next': 'Next',
    'browse.catalogue.empty.title': 'Nothing in this category yet',
    'browse.catalogue.empty.description':
      'Try a different filter — new public stories land here every day.',
    'browse.catalogue.search.placeholder': 'Search the catalogue…',
    'browse.catalogue.search.clear': 'Clear search',
    'browse.catalogue.sort.label': 'Sort',
    'browse.catalogue.sort.popular': 'Most popular',
    'browse.catalogue.sort.newest': 'Newest',
    'browse.catalogue.sort.rated': 'Highest rated',
    'browse.catalogue.sort.title': 'Title (A–Z)',
    'browse.catalogue.advanced': 'Filters',
    'browse.catalogue.advanced.audience': 'Audience',
    'browse.catalogue.advanced.language': 'Language',
    'browse.catalogue.advanced.min_rating': 'Minimum rating',
    'browse.catalogue.advanced.any': 'Any',
    'browse.catalogue.advanced.reset': 'Reset filters',

    'home.book.modal.loading': 'Loading project details…',
    'home.book.modal.section.details': 'Story details',
    'home.book.modal.section.characters': 'Main characters',
    'home.book.modal.section.tags': 'Tags',
    'home.book.modal.section.notes': 'Story notes',
    'home.book.modal.section.toc': 'Table of contents · {count}',
    'home.book.modal.detail.genre': 'Genre',
    'home.book.modal.detail.category': 'Category',
    'home.book.modal.detail.audience': 'Audience',
    'home.book.modal.detail.language': 'Language',
    'home.book.modal.untitled_chapter': 'Chapter {index}',
    'home.book.modal.words': '{count} words',
    'home.status.complete': 'Complete',
    'home.status.in_progress': 'In progress',
    'home.view_all': 'View all',
    'home.loading': 'Loading…',
    'home.empty.your_stories.title': 'Your shelf is empty',
    'home.empty.your_stories.description': 'Start reading a story and it will show up here so you can pick up where you left off.',
    'home.empty.your_stories.cta': 'Find something to read',
    'home.empty.recommended.title': 'No picks for you yet',
    'home.empty.recommended.description': "We'll recommend stories based on what the community publishes. Check back once more writers share their work.",
    'home.empty.recommended.cta': 'Explore the community',
    'home.empty.signed_out.title': 'Sign in to see your reading shelf',
    'home.empty.signed_out.description': 'Your reading progress lives behind the sign-in wall. Public picks stay browsable below.',
    'home.empty.signed_out.cta': 'Sign in',
    'home.book.last_read': 'Last read {date}',
    'home.book.not_started': 'Not started yet',
    'home.book.parts_left': '{count} parts left',
    'home.book.parts_total': '{count} parts',
    'home.book.chapter_of': 'Chapter {read} of {total}',
    'home.book.finished': 'Finished',
    'home.book.no_chapters': 'No chapters yet',
    'home.book.by': 'By {author}',
    'home.book.state.new': 'New',
    'home.book.state.reading': 'Reading',
    'home.book.state.finished': 'Finished',
    'home.book.modal.status.ongoing': 'Ongoing',
    'home.book.modal.status.complete': 'Complete',
    'home.book.modal.total_parts': 'Total: {count} parts',
    'home.book.modal.discover': 'Discover',
    'home.book.modal.add_to_shelf': 'Add to reading list',
    'home.book.modal.in_shelf': 'Added to your list',
    'home.book.modal.already_in_shelf': 'Already in your list',
    'home.book.modal.shelf_failed': "Reading list update failed.",
    'home.book.modal.close': 'Close',
    'home.welcome.avatar_link': 'Open your profile',

    'messages.title': 'Your messages',
    'messages.recent_connections': 'Recent connections',
    'messages.recent_group': 'Recent group',
    'messages.add_contacts': 'Start a new conversation',
    'messages.search_contacts': 'Search contacts…',
    'messages.compose_placeholder': 'Write a message…',
    'messages.send': 'Send',
    'messages.empty.title': 'No conversations yet',
    'messages.empty.description':
      'Search for a collaborator and start your first thread.',
    'messages.empty.cta': 'Start a conversation',
    'messages.empty.select.title': 'Pick a conversation',
    'messages.empty.select.description':
      'Choose a contact or group from the list to read the messages here.',
    'messages.empty.thread.title': 'Say hello',
    'messages.empty.thread.description':
      'No messages here yet — be the first to write something.',
    'messages.sender.send': 'Send',
    'messages.sender.attach': 'Attach a file',
    'messages.sender.emoji': 'Add an emoji',
    'messages.sender.link': 'Insert a link',
    'messages.sender.font': 'Text style',
    'messages.sender.format': 'Format text',
    'messages.sender.format.heading': 'Format selection',
    'messages.sender.format.bold': 'Bold',
    'messages.sender.format.italic': 'Italic',
    'messages.sender.format.strike': 'Strikethrough',
    'messages.sender.format.code': 'Inline code',
    'messages.sender.audio': 'Record audio',
    'messages.sender.video': 'Record video',
    'messages.sender.remove_attachment': 'Remove attachment',
    'messages.sender.link.title': 'Insert link',
    'messages.sender.link.url': 'URL',
    'messages.sender.link.url.placeholder': 'https://example.com',
    'messages.sender.link.text': 'Display text',
    'messages.sender.link.text.placeholder': 'Optional — defaults to the URL',
    'messages.sender.link.add': 'Insert',
    'messages.sender.link.cancel': 'Cancel',
    'messages.sender.link.invalid': 'Enter an http(s) URL.',
    'messages.sender.recording.stop_audio': 'Stop recording',
    'messages.sender.recording.stop_video': 'Stop recording',
    'messages.recorder.video.title': 'Video recorder',
    'messages.recorder.video.start': 'Start recording',
    'messages.recorder.video.stop': 'Stop recording',
    'messages.recorder.video.save': 'Use this take',
    'messages.recorder.video.re_record': 'Re-record',
    'messages.recorder.video.error': 'Camera unavailable ({message}).',
    'messages.recorder.video.unsupported': 'Your browser doesn\'t support inline video playback.',
    'messages.recorder.audio.start': 'Start recording',
    'messages.recorder.audio.pause': 'Pause',
    'messages.recorder.audio.resume': 'Resume',
    'messages.recorder.audio.stop': 'Stop',
    'messages.recorder.audio.send': 'Send recording',
    'messages.recorder.audio.timer': 'Recording duration',
    'messages.sender.upload_failed': 'Attachment upload failed. Try again.',
    'messages.add': 'Add',
    'messages.new_chat': 'New chat',
    'messages.call.video': 'Start video call',
    'messages.call.audio': 'Start voice call',
    'messages.bubble.read': 'Read',
    'messages.bubble.delivered': 'Delivered',
    'messages.modal.close': 'Close',
    'messages.modal.no_contact': 'No matching contact found.',
    'messages.modal.start': 'Start the conversation',
    'messages.voice_call': 'Voice call',
    'messages.video_call': 'Video call',
    'messages.leave_call': 'Leave call',
    'messages.bubble.react': 'React',
    'messages.bubble.preview': 'Preview',
    'messages.bubble.share': 'Share',
    'messages.bubble.download': 'Download',
    'messages.bubble.share.facebook': 'Share to Facebook',
    'messages.bubble.share.twitter': 'Share to X',
    'messages.bubble.share.linkedin': 'Share to LinkedIn',
    'messages.bubble.share.download': 'Download file',
    'messages.bubble.attachment.audio': 'Audio',
    'messages.bubble.attachment.video': 'Video',
    'messages.bubble.attachment.image': 'Image',
    'messages.bubble.attachment.file': 'File',
    'messages.bubble.reactions.title': 'Reactions',
    'messages.bubble.reactions.more': '+{count} more',
    'messages.bubble.read_by': 'Read by {name}',
    'messages.bubble.unread_by': 'Not yet seen by {name}',
    'messages.bubble.preview_title': 'Attachment preview',

    'collab.title': 'Dashboard',
    'collab.subtitle':
      'Browse open projects, find writers, and set up shared goals.',
    'collab.explore.title': 'Explore projects',
    'collab.writers.title': 'Writers you may like',
    'collab.calendar.title': 'Upcoming sessions',
    'collab.filter.all': 'All genres',
    'collab.cta.open': 'Open project',
    'collab.cta.invite': 'Invite to project',
    'collab.calendar': 'Calendar',
    'collab.urgent_tasks': 'Tasks to do',
    'collab.current_time': 'Current time',
    'collab.discover_book': 'Discover new book',
    'collab.daily_quote': 'Daily quote',
    'collab.demo_seed.toast': 'Sample project added — open it to explore the planner and the editor.',
    'collab.premium_weekend.title': "Premium weekend is live — studio-grade Pro voices and higher AI quotas all weekend.",
    'collab.premium_weekend.body': "Goes back to normal at {ends}. Make the most of it.",
    'collab.weather': 'Weather',
    'collab.from_writers': 'From other writers',
    'collab.view_all_writers': 'See all writers',
    'collab.weather_in': 'Weather in {city}, {province}',
    'collab.humidity': 'Humidity: {value}%',
    'collab.wind_speed': 'Wind: {value} km/h',
    'collab.engage': 'Engage with writing',
    'collab.writers_near': 'Writers in your neighbourhood',
    'collab.loading': 'Loading…',
    'collab.empty.books.title': 'Nothing public yet',
    'collab.empty.books.description': 'Public projects from other writers will appear here.',
    'collab.empty.goals.title': 'No tasks to do',
    'collab.empty.goals.description': 'Capture a task above, or open Goals to plan your week.',
    'collab.empty.goals.cta': 'Open Goals',
    'collab.tasks.quick_add.placeholder': 'New task…',
    'collab.tasks.quick_add.aria': 'Capture a new task',
    'collab.tasks.quick_add.submit': 'Add',
    'collab.tasks.quick_add.done': 'Task added to your kanban.',
    'collab.tasks.quick_add.failed': "Add failed — try again.",
    'collab.tasks.quick_complete.title': 'Mark as done',
    'collab.tasks.quick_complete.aria': 'Mark “{title}” as done',
    'collab.tasks.quick_complete.done': 'Task completed.',
    'collab.tasks.quick_complete.failed': "Complete failed — try again.",
    'collab.empty.posts.title': 'No discussions yet',
    'collab.empty.posts.description': 'Ask a question on the forum to kick off a thread.',
    'collab.empty.posts.cta': 'Open the forum',
    'collab.empty.writers.title': 'No writers to show',
    'collab.empty.writers.description': 'Follow and discover other authors as the community grows.',
    'collab.empty.quote.title': 'No quote right now',
    'collab.empty.quote.description': "Quote unavailable. Try again in a minute.",
    'collab.empty.weather.title': 'Weather unavailable',
    'collab.empty.weather.description': "Weather or location unavailable.",

    'calendar.prev_month': 'Previous month',
    'calendar.next_month': 'Next month',

    'forum.collaboration': 'Collaboration',
    'forum.heading': 'Forum',
    'forum.new_question.title': 'Topic',
    'forum.new_question.title_placeholder': 'Enter your question',
    'forum.new_question.description': 'Description',
    'forum.new_question.description_placeholder': 'Describe your question in detail',
    'forum.new_question.category': 'Category',
    'forum.new_question.category_placeholder': 'Select a category',
    'forum.new_question.date': 'Date',
    'forum.new_question.tags': 'Tags',
    'forum.new_question.tags_placeholder': 'Separate tags with commas',
    'forum.new_question.submit': 'Publish question',
    'forum.toast.post_published': 'Posted.',
    'forum.toast.post_failed': "Couldn't publish. Check your connection and try again.",
    'forum.category.general': 'General',
    'forum.category.technical': 'Technical',
    'forum.category.other': 'Other',
    'forum.filter.popular_topics': 'Popular topics',
    'forum.filter.topic.writing_tips': 'Writing tips',
    'forum.filter.topic.publishing': 'Publishing advice',
    'forum.filter.topic.inspiration': 'Inspiration',
    'forum.filter.topic.community': 'Community',
    'forum.filter.writing_style': 'Writing style',
    'forum.filter.all_categories': 'All categories',
    'forum.search.placeholder': 'Search a question…',
    'forum.search.button': 'Search',
    'forum.post.back': 'Back',
    'forum.post.reply': 'Reply',
    'forum.post.edit': 'Edit',
    'forum.post.delete': 'Delete',
    'forum.post.delete_confirm': 'The post and all its replies will be permanently removed.',
    'forum.post.delete_confirm_title': 'Delete this post?',
    'forum.post.fill_required': 'Please fill in Title, Description, and Category.',
    'forum.reply.heading': 'Answers',
    'forum.reply.sort.newest': 'Newest',
    'forum.reply.sort.oldest': 'Oldest',
    'forum.reply.sort.popular': 'Most popular',
    'forum.reply.add_heading': 'Add an answer',
    'forum.reply.placeholder': 'Write your answer…',
    'forum.reply.send': 'Send',
    'forum.reply.emoji': 'Insert emoji',
    'forum.reply.attach_image': 'Attach image',
    'forum.reply.attachment_staged': 'Image ready',
    'forum.reply.attachment_clear': 'Remove image',
    'forum.reply.attachment_alt': 'Attachment',
    'forum.reply.attachment_link': 'View attachment',
    'forum.reply.like': 'Like',
    'forum.attachment.upload_failed': "Couldn't upload that image — try again.",
    'forum.reaction.toggle_failed': "Couldn't update reaction — try again.",
    'forum.reply.cancel': 'Cancel',
    'forum.reply.update': 'Update',
    'forum.modal.new_title': 'Ask a new question',
    'forum.modal.edit_title': 'Edit question',
    'forum.modal.title': 'Title',
    'forum.modal.title_placeholder': 'Give your question a clear title',
    'forum.modal.description': 'Description',
    'forum.modal.description_placeholder': 'Describe your question in detail…',
    'forum.modal.category': 'Category',
    'forum.modal.tags': 'Tags',
    'forum.modal.tags_placeholder': 'Enter tags separated by commas',
    'forum.modal.cancel': 'Cancel',
    'forum.modal.publish': 'Publish',
    'forum.modal.update': 'Update',
    'forum.empty.title': 'No questions yet',
    'forum.empty.description': 'Be the first to ask the community — post a question from the side panel.',

    'editor.save': 'Save',
    'editor.publish': 'Publish',
    'editor.preview': 'Preview',
    'editor.grammar': 'Grammar',
    'editor.share': 'Share',
    'editor.chapter_title_placeholder': 'Chapter title',
    'editor.upload_cover': 'Upload cover image',
    'editor.back_to_projects': 'Back to projects',
    'editor.prompt_hint.title': 'Scene prompt ready',
    'editor.prompt_hint.title_named': 'Scene prompt for {name}',
    'editor.prompt_hint.copy': 'Copy',
    'editor.prompt_hint.copied': 'Prompt copied',
    'editor.prompt_hint.dismiss': 'Dismiss',
    'editor.editing_kicker': 'Editing project',
    'editor.part': 'Part {n}',
    'editor.story_title': 'Story title',
    'editor.write_here': 'Write your story here',
    'editor.grammar_check': 'Grammar check',
    'editor.format': 'Format',
    'editor.inspiration': 'Inspiration',
    'editor.auto_suggestions': 'Auto suggestions',
    'editor.ai_assistant': 'AI assistant',

    'book.toc.title': 'Table of contents',
    'book.read_chapter': 'Read chapter',
    'book.next_chapter': 'Next chapter',
    'book.prev_chapter': 'Previous chapter',
    'book.by_author': 'By {author}',
    'book.your_comments': 'Your comments',
    'book.other_comments': 'Other comments',
    'book.comment_placeholder': 'Write your comment',
    'book.guest.signup_prompt': 'Sign up to save, rate, and comment.',
    'book.comment.submit_failed': "Comment post failed. Please try again.",
    'book.guest.section_eyebrow': 'Reader',
    'book.guest.section_title': 'Enjoying the story?',
    'book.guest.section_description': 'Reading is free — no account needed. Sign up to comment, rate, save it to your shelf, and follow this writer for new chapters.',
    'book.guest.signup_action': 'Sign up free',
    'book.guest.login': 'Log in',
    'book.guest.do_now': 'Or continue without an account',
    'book.guest.share_copy': 'Copy link',
    'book.guest.share_copied': 'Link copied to clipboard.',
    'book.guest.share_failed': "Clipboard unavailable.",
    'book.guest.see_author': "See more by @{handle}",
    'book.guest.browse_more': 'Browse more stories',
    'book.no_comments': 'No comments yet.',
    'book.parts_of': 'Part {current} of {total}',
    'book.continue': 'Continue to next part',
    'book.mark_finished': 'Mark as finished',
    'book.related.heading': 'More books by this author',
    'book.related.subheading': 'Other stories by the same writer',
    'book.related.view_all': 'View all',
    'book.bookmark': 'Add to shelf',
    'book.bookmark.added': 'Remove from shelf',
    'book.bookmark.toast.added': 'Added to your shelf.',
    'book.bookmark.toast.removed': 'Removed from your shelf.',
    'book.bookmark.toast.failed': 'Shelf update failed. Try again.',
    'book.favorite': 'Add to favourites',
    'book.favorite.added': 'Remove from favourites',
    'book.favorite.toast.added': 'Added to your favourites.',
    'book.favorite.toast.removed': 'Removed from your favourites.',
    'book.favorite.toast.failed': 'Favourites update failed. Try again.',
    'book.alt.hero': 'Cover image',
    'book.alt.author': 'Author avatar',
    'book.alt.rating': 'Rating',
    'book.alt.views': 'Views',
    'book.alt.comments': 'Comments',
    'book.alt.send': 'Send comment',
    'book.alt.readers': 'Readers',

    'work.tab.organisations': 'Organisations',
    'work.tab.projects': 'Projects',
    'work.back_to_dashboard': 'Back to dashboard',
    'work.add_new': 'Add new',
    'work.header.projects_subtitle': 'Every book you’re writing.',
    'work.header.orgs_subtitle': 'Groups of related projects.',
    'work.header.count_aria': '{n} items',
    'work.bulk.select_row': 'Select row',
    'work.bulk.selected_count': '{n} selected',
    'work.bulk.cancel': 'Cancel',
    'work.bulk.delete': 'Delete {n}',
    'work.bulk.confirm_delete': 'Confirm delete {n}',
    'work.bulk.working': 'Deleting…',
    'work.bulk.toast.deleted': '{n} moved to trash.',
    'work.bulk.toast.delete_failed': "Deleting {n} failed — try again.",
    'work.filter': 'Filter',
    'work.view_more': 'View more',
    'work.summary': 'Summary',
    'work.projects_empty': 'No projects yet — use “Add new” to create your first one.',
    'work.projects_empty.title': 'No projects yet',
    'work.projects_empty.description': 'Group your stories under an organisation. Start your first project to begin writing.',
    'work.projects_empty.cta': 'Create a project',
    'work.orgs_empty.title': 'No organisations yet',
    'work.orgs_empty.description': 'Organisations let you group projects — by series, client, or theme. Make your first one to get started.',
    'work.orgs_empty.cta': 'Create an organisation',
    'work.modal.preset.label': 'Or pick a preset cover',
    'work.modal.preset.pick': 'Pick this preset cover',
    'work.modal.preset.hint': 'Click the image above to upload your own.',
    'work.modal.ai_cover': 'Generate cover with AI',
    'work.modal.cover.upload_label': 'Upload cover image',
    'work.unfiled': 'Unfiled',
    'work.edit_org_info': 'Edit organisation information',
    'work.modal.new_org': 'New organisation',
    'work.modal.edit_org': 'Edit organisation',
    'work.modal.new_project': 'New project',
    'work.modal.edit_project': 'Edit project',
    'work.modal.name.org': 'Organisation name',
    'work.modal.name.project': 'Project name',
    'work.modal.name_placeholder.org': 'Enter organisation name',
    'work.modal.name_placeholder.project': 'Enter project name',
    'work.modal.description.org': 'Organisation description',
    'work.modal.description.project': 'Project description',
    'work.modal.description_placeholder.org': 'Enter organisation description',
    'work.modal.description_placeholder.project': 'Enter project description',
    'work.modal.save': 'Save',
    'work.modal.saving': 'Saving…',
    'work.modal.cancel': 'Cancel',
    'work.modal.delete_org': 'Delete organisation',
    'work.modal.delete_project': 'Delete project',
    'work.confirm.delete_project_title': 'Delete this project?',
    'work.confirm.delete_project_message': 'The project, its chapters and its history will be permanently removed. This cannot be undone.',
    'work.confirm.delete_org_title': 'Delete this organisation?',
    'work.confirm.delete_org_message': 'The organisation will be removed. Projects inside it stay intact and move to your unfiled list.',
    'work.confirm.delete_action': 'Delete',
    'work.share_project': 'Share project',
    'work.share_org': 'Share organisation',
    'work.view_history': 'View activity',
    'work.history_modal.title_for': 'Activity — {name}',
    'work.shared_with': 'Shared · {count}',
    'work.shared_bucket.name': 'Shared with me',
    'work.shared_bucket.description':
      "Projects you've been invited to that don't belong to one of your organisations.",
    'work.modal.delete_confirm_org': 'Delete this organisation and every project inside it? This cannot be undone.',
    'work.modal.delete_confirm_project': 'Delete this project? This cannot be undone.',
    'work.modal.danger.org': 'Delete this organisation permanently.',
    'work.modal.danger.project': 'Delete this project permanently.',
    'work.modal.upload_failed': "Cover image upload failed. Your changes weren't saved. Try again without a cover, or contact your admin.",
    'work.modal.upload_unavailable': "Uploads aren't configured on this server yet. Save without a cover for now.",

    'create_project.title': 'New project',
    'create_project.subtitle':
      'Set up the basics — you can refine everything later.',
    'create_project.project_title': 'Project title',
    'create_project.description': 'Short description',
    'create_project.genre': 'Genre',
    'create_project.cover': 'Cover image',
    'create_project.save_cta': 'Create project',
    'create_project.cancel': 'Cancel',
    'create_project.section.title': 'Title',
    'create_project.section.description': 'Public pitch',
    'create_project.section.description_hint': 'Back-cover blurb readers see in the catalogue.',
    'create_project.section.characters': 'Main characters',
    'create_project.section.characters_hint': 'Names here become starter character cards in the Planner.',
    'create_project.section.plot_points': 'Plot points',
    'create_project.section.scenarios': 'Scenarios',
    'create_project.section.notes': 'Notes',
    'create_project.section.chapters': 'Chapters',
    'create_project.section.settings': 'Settings',
    'create_project.add_character': 'Add character',
    'create_project.add_chapter': 'Add chapter',
    'create_project.save_draft': 'Save draft',
    'create_project.org': 'Organisation',
    'create_project.proj': 'Project',
    'create_project.danger_zone': 'Danger zone',
    'create_project.section.tags': 'Tags',
    'create_project.section.category': 'Category',
    'create_project.section.category_hint': 'The broad category readers use to browse (Fiction, Non-fiction, Children’s, …). The Planner’s Genre setting is the narrative sub-class (Fantasy, Mystery, Romance, …).',
    'create_project.section.target_audience': 'Target audience',
    'create_project.section.language': 'Language',
    'create_project.section.copyright': 'Copyright',
    'create_project.section.toc': 'Table of contents',
    'create_project.section.story_note': 'Story note',
    'create_project.story_details': 'Story details',
    'create_project.add_photo': 'Add a photo',
    'create_project.image_placeholder': 'Click to add a cover image',
    'create_project.add_more_characters': 'Add more characters',
    'create_project.character_placeholder': 'Character {index}',
    'create_project.save': 'Save',
    'create_project.saving': 'Saving…',
    'create_project.add_new': 'Add new',
    'create_project.delete_project': 'Delete project',
    'create_project.danger.description_project': 'Delete this project permanently.',
    'create_project.prompt.chapter_name': 'Enter the chapter name',
    'create_project.prompt.rename_chapter': 'Enter the new chapter name',
    'create_project.rename_chapter': 'Rename chapter',
    'create_project.delete_chapter': 'Delete chapter',
    'create_project.confirm_delete': 'Delete',
    'create_project.no_chapters.title': 'No chapters yet',
    'create_project.no_chapters.description': 'Click “Add new” to create your first chapter.',
    'create_project.reorder_chapter': 'Drag to reorder chapter',
    'create_project.chapter_n': 'Chapter {n}',
    'create_project.bulk.select_row': 'Select chapter',
    'create_project.bulk.selected_count': '{n} chapter(s) selected',
    'create_project.bulk.cancel': 'Cancel',
    'create_project.bulk.delete': 'Delete {n}',
    'create_project.bulk.confirm_delete': 'Confirm delete {n}',
    'create_project.bulk.working': 'Deleting…',
    'create_project.bulk.toast.deleted': '{n} chapter(s) moved to trash.',
    'create_project.bulk.toast.delete_failed': "Deleting {n} chapter(s) failed — try again.",
    'create_project.toast.chapter_reorder_failed': 'Reorder failed. Try again.',
    'create_project.confirm.delete_chapter': 'Are you sure you want to delete this chapter?',
    'create_project.confirm.delete_project': 'The project, its chapters and its history will be permanently removed. This cannot be undone.',
    'create_project.confirm.delete_project_title': 'Delete this project?',
    'create_project.toast.project_saved': 'Project updated.',
    'create_project.toast.project_save_failed': 'Project save failed. Try again.',
    'create_project.toast.project_deleted': 'Project deleted.',
    'create_project.toast.project_delete_failed': 'Project delete failed. Try again.',
    'create_project.toast.cover_upload_failed': 'Cover upload failed. Try again.',
    'create_project.toast.chapter_added': 'Chapter added.',
    'create_project.toast.chapter_add_failed': 'Adding chapter failed. Try again.',
    'create_project.toast.chapter_renamed': 'Chapter updated.',
    'create_project.toast.chapter_rename_failed': 'Chapter update failed. Try again.',
    'create_project.toast.chapter_deleted': 'Chapter deleted.',
    'create_project.toast.chapter_delete_failed': 'Chapter delete failed. Try again.',
    'create_project.category.biography': 'Biography',
    'create_project.category.fiction': 'Fiction',
    'create_project.category.non_fiction': 'Non-fiction',
    'create_project.category.novel': 'Novel',
    'create_project.category.short_story': 'Short story',
    'create_project.category.poetry': 'Poetry',
    'create_project.category.fantasy': 'Fantasy',
    'create_project.category.science_fiction': 'Science fiction',
    'create_project.category.romance': 'Romance',
    'create_project.category.mystery': 'Mystery',
    'create_project.category.thriller': 'Thriller',
    'create_project.category.horror': 'Horror',
    'create_project.category.historical_fiction': 'Historical fiction',
    'create_project.category.young_adult': 'Young adult',
    'create_project.category.childrens': "Children's book",
    'create_project.category.graphic_novel': 'Graphic novel',
    'create_project.category.drama': 'Drama',
    'create_project.category.screenplay': 'Screenplay',
    'create_project.category.memoir': 'Memoir',
    'create_project.category.autobiography': 'Autobiography',
    'create_project.category.self_help': 'Self-help',
    'create_project.category.history': 'History',
    'create_project.category.essay': 'Essay',
    'create_project.category.science': 'Science',
    'create_project.category.business': 'Business',
    'create_project.category.travel': 'Travel',
    'create_project.category.placeholder': 'Search categories…',
    'create_project.category.clear': 'Clear category',
    'create_project.category.no_match': 'No category matches your search.',
    'create_project.category.show_more': 'Show {count} more',
    'create_project.audience.children': 'Children',
    'create_project.audience.teenagers': 'Teenagers',
    'create_project.audience.adults': 'Adults',
    'create_project.audience.elders': 'Elders',
    'create_project.language.english': 'English',
    'create_project.language.french': 'French',
    'create_project.copyright.all_rights': 'All rights reserved',
    'create_project.copyright.creative_commons': 'Creative Commons',

    'comments.title': 'Comments',
    'comments.show_resolved': 'Resolved',
    'comments.placeholder': 'Write a comment…',
    'comments.post': 'Post',
    'comments.post_reply': 'Reply',
    'comments.posting': 'Posting…',
    'comments.compose_new': 'New comment',
    'comments.compose_reply': 'Replying…',
    'comments.cancel_reply': 'Cancel reply',
    'comments.replying_to': 'Replying to',
    'comments.submit_hint': 'Ctrl/⌘ + Enter to post',
    'comments.clear': 'Clear',
    'comments.reply': 'Reply',
    'comments.edit': 'Edit',
    'comments.delete': 'Delete',
    'comments.save': 'Save',
    'comments.cancel': 'Cancel',
    'comments.resolve': 'Resolve',
    'comments.unresolve': 'Reopen',
    'comments.resolved': 'Resolved',
    'comments.unknown_author': 'Unknown',
    'comments.confirm_delete': 'The comment and any replies threaded under it will be permanently removed.',
    'comments.confirm_delete_title': 'Delete this comment?',
    'comments.empty.title': 'No comments yet',
    'comments.empty.description':
      'Select text in the editor and click the comment icon to start a thread.',
    'comments.toast.create_failed': 'Comment post failed. Try again.',
    'comments.toast.update_failed': 'Comment update failed. Try again.',
    'comments.toast.delete_failed': 'Comment delete failed. Try again.',
    'comments.toast.resolve_failed': 'Comment update failed. Try again.',
    'comments.toast.verification_required': 'Verify your email to leave comments — tap to open Settings.',

    'editor.preview.label': 'Preview',
    'editor.preview.back': 'Back to edit',
    'editor.preview.untitled': 'Untitled chapter',
    'editor.preview.empty.title': 'Nothing to preview yet',
    'editor.preview.empty.description':
      'Start writing in the editor and your chapter will appear here as it would to a reader.',

    'share.button': 'Share',
    'share.title': 'Share chapter',
    'share.close': 'Close',
    'share.done': 'Done',
    'share.search_placeholder': 'Search by name or email',
    'share.section.collaborators': 'Collaborators',
    'share.section.invite': 'Invite others',
    'share.empty.collaborators': 'No collaborators yet. Invite someone below.',
    'share.empty.invite': 'No other users to invite.',
    'share.empty.no_match': 'No users match your search.',
    'share.role.editor': 'Editor',
    'share.role.reader': 'Commenter',
    'share.action.make_editor': 'Change to editor',
    'share.action.make_reader': 'Change to commenter',
    'share.action.remove': 'Remove access',
    'share.action.invite_editor': 'Invite as editor',
    'share.action.invite_reader': 'Invite as commenter',
    'share.toast.fetch_failed': 'Collaborators unavailable. Try again.',
    'share.toast.role_updated': 'Access updated.',
    'share.toast.role_failed': 'Access update failed. Try again.',
    'share.toast.removed': 'Collaborator removed.',
    'share.toast.remove_failed': 'Remove failed. Try again.',
    'share.title.project': 'Share project',
    'share.title.organisation': 'Share organisation',

    'publish.button.publish': 'Publish',
    'publish.button.published': 'Published',
    'publish.close': 'Close',
    'publish.cancel': 'Cancel',
    'publish.confirm_publish': 'Publish chapter',
    'publish.confirm_unpublish': 'Unpublish',
    'publish.working': 'Working…',
    'publish.dialog.publish_title': 'Publish this chapter?',
    'publish.dialog.unpublish_title': 'Unpublish this chapter?',
    'publish.dialog.publish_body':
      'Readers and commenters on this project will be able to read this chapter once it’s published. You can unpublish it at any time.',
    'publish.dialog.unpublish_body':
      'This chapter will go back to draft and disappear from the public reader view. Collaborators with editor access keep their access.',
    'publish.toast.published': 'Chapter published.',
    'publish.toast.unpublished': 'Chapter moved back to draft.',
    'publish.toast.failed': 'Publication status update failed. Try again.',
    'publish.toast.no_chapter': 'Save the chapter before publishing it.',
    'publish.success.title': 'Chapter published',
    'publish.success.body':
      'Share the read link, open the public page, or jump back into editing.',
    'publish.success.copy': 'Copy link',
    'publish.success.copied_short': 'Copied',
    'publish.success.copied': 'Link copied to clipboard.',
    'publish.success.copy_failed': "Copy failed.",
    'publish.success.open_read': 'Open read page',
    'publish.success.continue_editing': 'Continue editing',

    'versions.title': 'Versions',
    'versions.subtitle': 'Roll back to an earlier draft',
    'versions.loading': 'Looking through earlier drafts…',
    'versions.empty.title': 'Nothing to roll back to yet',
    'versions.empty.hint':
      "We save a snapshot every few minutes while you write — they'll show up here as you go. Or click Save now to mark this exact moment yourself.",
    'versions.save_point': 'Save now',
    'versions.restore': 'Restore',
    'versions.confirm_restore': 'Replace current draft',
    'versions.cancel': 'Keep current',
    'versions.working': 'Restoring…',
    'versions.author_unknown': 'Unknown author',
    'versions.word_count': '{count} words',
    'versions.toast.saved': 'Saved a snapshot of where you are.',
    'versions.toast.save_failed': "Version save failed. Try again.",
    'versions.toast.restored':
      'Restored. Your previous draft was saved as a version, so you can switch back.',
    'versions.toast.restore_failed':
      "Restore failed. Try again.",

    'trash.title': 'Trash',
    'trash.subtitle': 'Deleted projects and chapters. Restore or purge.',
    'trash.open_button': 'Open trash',
    'trash.loading': 'Loading trash…',
    'trash.empty': 'Nothing in the trash.',
    'trash.empty_hint': 'Items are kept for 30 days, then purged.',
    'trash.summary_count': '{n} item(s) in the trash',
    'trash.empty_all': 'Empty trash',
    'trash.confirm_empty_all': 'Permanently delete all {n}?',
    'trash.bulk.select_all': 'Select all in this section',
    'trash.bulk.select_row': 'Select row',
    'trash.bulk.selected_count': '{n} selected',
    'trash.bulk.clear': 'Clear selection',
    'trash.bulk.purge': 'Delete {n} permanently',
    'trash.bulk.confirm_purge': 'Confirm delete {n}',
    'trash.toast.bulk_purged': 'Permanently deleted {n} item(s).',
    'trash.toast.bulk_purge_failed': "Deleting {n} item(s) failed — try again.",
    'trash.section.projects': 'Projects · {count}',
    'trash.section.projects_subtitle':
      'Restoring a project also makes its chapters visible again.',
    'trash.section.chapters': 'Chapters · {count}',
    'trash.section.chapters_subtitle':
      'Chapters in deleted projects: restore the project first.',
    'trash.in_project': 'In {project}',
    'trash.parent_in_trash': 'Restore the parent project first.',
    'trash.deleted_when': 'Deleted {when}',
    'trash.restore': 'Restore',
    'trash.purge': 'Delete forever',
    'trash.confirm_purge': 'Delete forever',
    'trash.cancel': 'Cancel',
    'trash.working': 'Deleting…',
    'trash.toast.restored': 'Restored.',
    'trash.toast.restore_failed': "Restore failed.",
    'trash.toast.restore_parent_first':
      'Restore the parent project first, then this chapter.',
    'trash.toast.purged': 'Permanently deleted.',
    'trash.toast.purge_failed': "Delete failed.",

    'editor.save.state.save_now': 'Save',
    'editor.save.state.saving': 'Saving…',
    'editor.save.state.just_saved': 'Saved',
    'editor.save.state.saved': 'Saved',
    'editor.save.state.saved_at': 'Saved {time} ago',
    'editor.save.state.error': 'Retry save',
    'editor.save.toast.failed': 'Chapter save failed. Try again.',
    'editor.save.toast.no_chapter': "This chapter hasn't been saved yet. Reload the project page.",

    'grammar.title': 'Grammar & spelling',
    'grammar.run': 'Check now',
    'grammar.running': 'Checking…',
    'grammar.clean': 'No issues found.',
    'grammar.counts': '{grammar} grammar · {spelling} spelling',
    'grammar.no_suggestions': 'No suggestions — dismiss if intentional.',
    'grammar.empty.title': 'Your writing is clean.',
    'grammar.empty.description': 'No grammar or spelling issues were found in this chapter.',
    'grammar.source.grammar': 'Grammar',
    'grammar.source.spelling': 'Spelling',
    'grammar.description.possible_typo': 'Possible typo — not in the dictionary.',
    'grammar.action.apply': 'Apply “{suggestion}”',
    'grammar.action.dismiss': 'Dismiss',
    'grammar.toast.no_issues': 'Your writing looks clean.',
    'grammar.toast.stale': 'The text changed since the last check — run it again.',
    'grammar.toast.failed': 'Grammar check failed. Try again.',

    'format.title': 'Formatting',
    'format.subtitle': 'Insert a writing template or tidy your typography.',
    'format.close': 'Close',
    'format.cancel': 'Cancel',
    'format.apply_template': 'Insert template',
    'format.preview': 'Preview',
    'format.replace_chapter':
      'Replace the whole chapter (default: insert at the cursor)',
    'format.section.templates': 'Templates',
    'format.section.tidy': 'Tidy typography',
    'format.tidy.selection': 'Fix selected text',
    'format.tidy.chapter': 'Fix whole chapter',
    'format.tidy.description':
      'Smart quotes, em-dashes, ellipses, and trimmed spaces. Runs on your text only — formatting is preserved.',
    'format.toast.template_applied': 'Template inserted.',
    'format.toast.tidied_selection': 'Selection tidied.',
    'format.toast.tidied_chapter': 'Chapter tidied.',
    'format.toast.no_selection': 'Select some text first.',

    'format.template.novel_chapter.name': 'Novel chapter',
    'format.template.novel_chapter.summary': 'Title, opening hook, and three scene breaks.',
    'format.template.novel_chapter.title': 'Chapter title',
    'format.template.novel_chapter.hook': 'Open with a hook — set the scene and stakes.',
    'format.template.novel_chapter.scene': 'Scene {n}',
    'format.template.novel_chapter.scene_body': 'Write this scene…',

    'format.template.essay.name': 'Essay',
    'format.template.essay.summary': 'Classic five-paragraph structure with headers.',
    'format.template.essay.title': 'Essay title',
    'format.template.essay.intro': 'Introduction',
    'format.template.essay.intro_body':
      'Introduce the topic and state your thesis.',
    'format.template.essay.body': 'Body paragraph {n}',
    'format.template.essay.body_prompt': 'Make one supporting argument here.',
    'format.template.essay.conclusion': 'Conclusion',
    'format.template.essay.conclusion_body':
      'Restate the thesis in light of the arguments and close with a broader implication.',

    'format.template.dialogue_scene.name': 'Dialogue scene',
    'format.template.dialogue_scene.summary': 'Two-character exchange with a setting line.',
    'format.template.dialogue_scene.title': 'Scene',
    'format.template.dialogue_scene.setting': 'Describe the setting in a single line.',
    'format.template.dialogue_scene.char_a': 'Character A',
    'format.template.dialogue_scene.char_b': 'Character B',
    'format.template.dialogue_scene.line': 'Line of dialogue.',

    'format.template.outline.name': 'Outline',
    'format.template.outline.summary': 'Nested bullet structure for planning.',
    'format.template.outline.title': 'Outline',
    'format.template.outline.point': 'Main point {n}',
    'format.template.outline.detail': 'Supporting detail.',

    'format.template.three_act.name': 'Three-act structure',
    'format.template.three_act.summary': 'Setup, confrontation, resolution headers.',
    'format.template.three_act.setup': 'Act I — Setup',
    'format.template.three_act.setup_body':
      'Introduce the world, the protagonist, and the inciting incident.',
    'format.template.three_act.confrontation': 'Act II — Confrontation',
    'format.template.three_act.confrontation_body':
      'Raise the stakes through rising action and the midpoint turn.',
    'format.template.three_act.resolution': 'Act III — Resolution',
    'format.template.three_act.resolution_body':
      'Deliver the climax and the denouement.',

    'format.template.blog_post.name': 'Blog post',
    'format.template.blog_post.summary': 'Hook, why, how, and a takeaway.',
    'format.template.blog_post.title': 'Blog post title',
    'format.template.blog_post.hook':
      'Open with a sentence that promises what the reader will learn.',
    'format.template.blog_post.why': 'Why this matters',
    'format.template.blog_post.why_body':
      'Explain the problem and why the reader should care right now.',
    'format.template.blog_post.how': 'How it works',
    'format.template.blog_post.how_body':
      'Walk through the approach in concrete, ordered steps.',
    'format.template.blog_post.takeaway': 'Takeaway',
    'format.template.blog_post.takeaway_body':
      'Summarise the one thing the reader should remember.',

    'inspiration.title': 'Inspiration',
    'inspiration.subtitle':
      'Synonyms and curated quotes to unblock your draft.',
    'inspiration.close': 'Close',
    'inspiration.placeholder': 'Search a word or theme…',
    'inspiration.hint': 'Press Enter to search.',
    'inspiration.section.synonyms': 'Synonyms',
    'inspiration.section.synonyms_empty':
      "No synonyms found for this term in this language.",
    'inspiration.section.citations': 'Quotes',
    'inspiration.section.citations_empty':
      'No quotes matched — try a broader theme.',
    'inspiration.empty.title': 'Search something to begin',
    'inspiration.empty.description':
      'Or highlight a word in the editor before opening this panel — it pre-fills the search.',
    'inspiration.action.insert': 'Insert',
    'inspiration.action.copy': 'Copy',
    'inspiration.toast.synonym_inserted': 'Synonym inserted.',
    'inspiration.toast.quote_inserted': 'Quote inserted as a blockquote.',
    'inspiration.toast.copied': 'Copied to clipboard.',
    'inspiration.toast.copy_failed': 'Copy failed. Try again.',
    'inspiration.toast.failed': 'Inspiration unavailable. Try again.',

    'chatai.title': 'AI Assistant',
    'chatai.subtitle': 'Ask, continue, rewrite, or outline.',
    'chatai.close': 'Close',
    'chatai.reset': 'Clear conversation',
    'chatai.greeting': 'Pick a mode above and type a prompt to get started.',
    'chatai.send': 'Send',
    'chatai.sending': 'Working…',
    'chatai.submit_hint': 'Ctrl/⌘ + Enter to send',
    'chatai.use_context': 'Include chapter as context',
    'chatai.empty_response': '(no text returned)',
    'chatai.rewrite_of': 'Rewrite: “{text}”',
    'chatai.continue_of': 'Continue from the cursor',
    'chatai.mode.ask': 'Ask',
    'chatai.mode.continue': 'Continue',
    'chatai.mode.rewrite': 'Rewrite',
    'chatai.mode.outline': 'Outline',
    'chatai.hint.ask': 'Free-form question. Toggle chapter context if the answer should reference your draft.',
    'chatai.hint.continue': 'Generates the next paragraph from where your cursor sits. Prompt is optional.',
    'chatai.hint.rewrite': 'Rewrites the highlighted selection. Add a tone hint in the prompt if you want.',
    'chatai.hint.outline': 'Generates a structured outline for the topic you type below.',
    'chatai.placeholder.ask': 'Ask the assistant…',
    'chatai.placeholder.continue': 'Optional: tone or direction hint…',
    'chatai.placeholder.rewrite': 'Optional: tone (clearer, tighter, formal)…',
    'chatai.action.copy': 'Copy',
    'chatai.action.insert': 'Insert',
    'chatai.pro_lane.label': 'Pro',
    'chatai.pro_lane.tooltip':
      'Generated with Pro — your subscription uses our most precise model for Continue, Rewrite, and Outline.',
    'chatai.toast.inserted': 'Inserted into the editor.',
    'chatai.toast.copied': 'Copied to clipboard.',
    'chatai.toast.copy_failed': 'Copy failed. Try again.',
    'chatai.toast.select_text': 'Highlight some text in the editor first.',
    'chatai.toast.empty_chapter': 'Your chapter is empty — write a little first.',
    'chatai.toast.failed': 'The assistant could not respond. Try again.',

    'flyingmenu.action.rewrite': 'Rewrite selection',
    'flyingmenu.action.continue': 'Continue from selection',
    'flyingmenu.toast.rewrote': 'Selection rewritten.',
    'flyingmenu.toast.rewrote_pro': 'Rewritten with Pro.',
    'flyingmenu.toast.continued': 'Continuation appended.',
    'flyingmenu.toast.continued_pro': 'Continued with Pro.',
    'flyingmenu.toast.empty': 'No response — try again.',
    'flyingmenu.toast.failed': 'AI request failed. Try again.',

    'goal.kanban.title': 'Tasks',
    'goal.kanban.tab_label': 'Tasks',
    'goal.kanban.subtitle':
      'Drag cards between stages to track your writing plan.',
    'planner.tab_label': 'Plan',
    'planner.signature.kicker': 'Story Planner',
    'planner.signature.untitled_project': 'Untitled project',
    'planner.signature.coverage_label': 'Beats filled',
    'planner.signature.coverage_tooltip': '{pct}% of your structure beats have notes.',
    // Advanced suggestion panel — AI-ranked story tropes. The
    // panel is branded as "Advanced suggestions" to writers; the
    // per-suggestion shape under the hood is still a trope.
    'planner.tropes.open': 'Advanced suggestions',
    'planner.tropes.panel_title': 'Advanced suggestions',
    'planner.tropes.subtitle_ai': 'Tailored to your project by the assistant.',
    'planner.tropes.subtitle_curated': 'From the curated catalogue.',
    'planner.tropes.ai_badge': 'AI-tailored',
    'planner.tropes.refresh': 'Refresh suggestions',
    'planner.tropes.loading': 'Reading your planner…',
    'planner.tropes.dismiss': 'Dismiss this suggestion',
    'planner.tropes.dismissed_toast': 'Hidden for this project.',
    'planner.tropes.apply': 'Add to planner',
    'planner.tropes.apply_update': 'Fill in "{title}"',
    'planner.tropes.apply_update_generic': 'Fill in existing card',
    'planner.tropes.apply_delete': 'Remove "{title}"',
    'planner.tropes.apply_delete_generic': 'Remove existing card',
    'planner.tropes.apply_change_kind': 'Reclassify "{title}" as {kind}',
    'planner.tropes.apply_change_kind_generic': 'Reclassify as {kind}',
    'planner.tropes.apply_merge': 'Merge "{source}" into "{target}"',
    'planner.tropes.apply_merge_generic': 'Merge two cards',
    'planner.tropes.applied_toast': 'Added "{title}" to your planner.',
    // Slice 7: polish keys.
    'planner.tropes.applied_pill': 'Applied to "{title}"',
    'planner.tropes.applied_pill_tooltip': "This suggestion is already in your planner — you applied it earlier.",
    'planner.tropes.quota.pill': '{remaining} AI left today',
    'planner.tropes.quota.tooltip': 'Used {used} of {limit} AI suggestions today.',
    'planner.tropes.reset_dismissed': 'Show {count} dismissed',
    'planner.tropes.dismissed_reset': 'Dismissed list cleared. Hit Refresh to see them again.',
    'planner.tropes.updated_toast': 'Updated "{title}".',
    'planner.tropes.deleted_toast': 'Removed "{title}". Restore within 30 days from trash.',
    'planner.tropes.reclassified_toast': 'Reclassified "{title}".',
    'planner.tropes.merged_toast': 'Merged "{source}" into "{target}".',
    'planner.tropes.undone_toast': 'Reverted.',
    'planner.tropes.undo_failed': "Undo failed — try removing it manually from the planner.",
    'planner.tropes.apply_failed': "Adding the suggestion failed — try again.",
    'planner.tropes.apply_unsupported_kind': 'This suggestion needs a multi-step apply — coming soon.',
    'planner.tropes.tier_label': 'Tier {tier}',
    'planner.tropes.tier_tooltip': 'Tier {tier} — 1 universal, 2 genre-flavoured, 3 niche.',
    'planner.tropes.footer_count': 'Showing {shown} of {total} suggestions in the catalogue.',
    'planner.tropes.empty.title': "No suggestions yet",
    'planner.tropes.empty.description': "Try a refresh, or add a logline and a few characters so the assistant has more to work with.",
    'planner.tropes.quota.title': 'Out of AI suggestions for today',
    'planner.tropes.quota.description': "You've hit the daily AI cap. You can still browse the curated catalogue without using your quota.",
    'planner.tropes.quota.show_curated': 'Show curated suggestions',
    'planner.tropes.error.title': "Suggestions unavailable",
    'planner.tropes.error.description': 'The assistant might be busy. Try again in a moment.',
    'planner.tropes.error.retry': 'Try again',
    // applies_to_kinds chips — locale labels for the planner kind set.
    'planner.tropes.kind.character': 'character',
    'planner.tropes.kind.location': 'location',
    'planner.tropes.kind.object': 'object',
    'planner.tropes.kind.event': 'event',
    'planner.tropes.kind.scene': 'scene',
    'planner.tropes.kind.magic_system': 'magic system',
    'planner.tropes.kind.structure': 'structure',
    'planner.tropes.kind.theme': 'theme',
    'planner.tropes.kind.relationship': 'relationship',
    'planner.snippets.open': 'Library',
    'planner.snippets.title': 'Reusable snippets',
    'planner.snippets.subtitle': 'Drop a saved archetype, magic-system or scene into this project.',
    'planner.snippets.empty.title': 'No snippets yet',
    'planner.snippets.empty.description': 'Save any card via "Save as snippet" to reuse it across projects.',
    'planner.snippets.empty.mine.title': 'No snippets yet',
    'planner.snippets.empty.mine.description': 'Save any card via "Save as snippet" to reuse it across projects.',
    'planner.snippets.empty.marketplace.title': 'No public snippets yet',
    'planner.snippets.empty.marketplace.description': 'Be the first to publish a reusable archetype. Open one of your snippets and tap the share icon to put it on the marketplace.',
    'planner.snippets.tab.mine': 'Mine',
    'planner.snippets.tab.marketplace': 'Marketplace',
    'planner.snippets.tab.classics': 'Classics',
    'planner.snippets.mine_hint': 'Your private snippet library. Share one with the marketplace via the share icon.',
    'planner.snippets.marketplace_hint': 'Curated and community snippets you can drop straight into this project.',
    'planner.snippets.classics_hint': 'Modules from enriched public-domain classics — characters, locations, world rules, beats — ready to study or remix.',
    'planner.snippets.empty.classics.title': 'No classics in this language yet',
    'planner.snippets.empty.classics.description': 'Classics are enriched per language. Switch UI language or check back later.',
    'planner.snippets.search_placeholder': 'Search by title, summary or book',
    'planner.snippets.search_empty.title': 'No matches',
    'planner.snippets.search_empty.description': 'Nothing matches "{query}". Try a shorter search or clear it to browse everything.',
    'planner.snippets.over_cap_hint': 'Showing the first {shown} of {total} — narrow the search to see more.',
    'planner.snippets.featured': 'Featured',
    'planner.snippets.yours': 'Yours',
    'planner.snippets.anonymous': 'anon',
    'planner.snippets.publish': 'Share to marketplace',
    'planner.snippets.unpublish': 'Make private',
    'planner.snippets.published': 'Shared to the marketplace.',
    'planner.snippets.unpublished': 'Snippet is private again.',
    'planner.snippets.import': 'Import here',
    'planner.snippets.delete': 'Delete',
    'planner.snippets.imported': 'Snippet added to this project.',
    'planner.coherence.button': 'Check coherence',
    'planner.coherence.tooltip': 'Compare a chapter against your planner with AI.',
    'planner.coherence.title': 'Coherence check',
    'planner.coherence.subtitle': 'Compare a chapter against your planner. Surfaces contradictions, drift from what you set up earlier, and ideas you may have missed.',
    'planner.coherence.chapter': 'Chapter to review',
    'planner.coherence.run': 'Run check',
    'planner.coherence.running': 'Reviewing…',
    'planner.coherence.summary': 'Reviewer summary',
    'planner.coherence.no_flags': 'No issues flagged — the chapter is coherent with your planner.',
    'planner.coherence.no_chapters': 'No chapters to review yet.',
    'planner.coherence.untitled_chapter': 'Chapter {id}',
    'planner.coherence.failed': 'The coherence check failed — try again in a moment.',
    'planner.coherence.quota_exceeded': 'You’ve used today’s coherence checks.',
    'planner.coherence.truncated': 'The chapter or planner was truncated for the AI — long projects may miss late-chapter context.',
    // Severity badges on each flag card. Used as the category
    // label when the model didn't return one of its own.
    'planner.coherence.severity.error': 'Contradiction',
    'planner.coherence.severity.warning': 'Drift',
    'planner.coherence.severity.info': 'Opportunity',
    // Canonical category vocabulary returned by the FastAPI router
    // (see _COHERENCE_CATEGORIES in routers/planner.py). Anything
    // outside this set falls through the t() humanize fallback.
    'planner.coherence.category.character': 'Character',
    'planner.coherence.category.voice': 'Voice',
    'planner.coherence.category.tone': 'Tone',
    'planner.coherence.category.pacing': 'Pacing',
    'planner.coherence.category.plot': 'Plot',
    'planner.coherence.category.continuity': 'Continuity',
    'planner.coherence.category.worldbuilding': 'Worldbuilding',
    'planner.coherence.category.motif': 'Motif',
    'planner.coherence.category.magic_rules': 'Magic rules',
    'planner.coherence.category.structure': 'Structure',
    'planner.coherence.category.other': 'Other',
    'planner.coherence.category.parse_fallback': 'Unreadable response',
    'planner.coherence.parse_fallback_message':
      'The reviewer returned an unreadable response. Try again — most parse errors clear on a second run. The raw text is in the evidence box below.',

    // Project-wide critique — sister of coherence but scoped to the
    // whole project. Premium-only; the BE quota returns 0 for free
    // and basic, surfacing the upgrade CTA via the same QuotaExceeded
    // path the rest of the FE already handles.
    'planner.project_critique.button': 'Critique whole project',
    'planner.project_critique.tooltip': 'Run an AI developmental edit across every chapter and your plan.',
    'planner.project_critique.title': 'Project critique',
    'planner.project_critique.subtitle': "A developmental pass over every chapter and your plan. Surfaces dropped plot threads, characters that went nowhere, structure beats you haven't hit, and repetition across chapters.",
    'planner.project_critique.pro_badge': 'Pro',
    'planner.project_critique.pro_tooltip': 'Premium feature — your subscription uses our most precise model.',
    'planner.project_critique.run': 'Run critique',
    'planner.project_critique.rerun': 'Run again',
    'planner.project_critique.running': 'Reviewing your project…',
    'planner.project_critique.wait.title': 'Reading your story',
    'planner.project_critique.wait.eta': 'Usually 15-25 seconds',
    'planner.project_critique.wait.stats': 'Across {count} planner cards',
    'planner.project_critique.wait.stage_reading': 'Reading your planner…',
    'planner.project_critique.wait.stage_analysing': 'Analysing chapter summaries…',
    'planner.project_critique.wait.stage_cross_checking': 'Cross-checking for plot threads…',
    'planner.project_critique.wait.stage_compiling': 'Compiling findings…',
    'planner.project_critique.preflight.no_preset': "Pick a Genre + Structure preset from the planner header to unlock structural findings. Without one the review will skip that category.",
    'planner.project_critique.preflight.thin_corpus': "Only {count} chapters drafted so far — the review will lean on planner findings until more prose is written.",
    // Tier-4 §14 — DNA preflight, BLOCKS opening the critique modal
    // when the Story DNA looks malformed enough that the AI will
    // produce useless output. Distinct from the in-modal preflights
    // above, which are inline warnings.
    'planner.project_critique.dna_preflight.title': 'Your Story DNA may mislead the critique',
    'planner.project_critique.dna_preflight.intro': 'Issues we spotted',
    'planner.project_critique.dna_preflight.hint': 'Try “Draft from chapters” in Notes (free) to repair the DNA before spending Pro quota.',
    'planner.project_critique.dna_preflight.proceed': 'Proceed anyway',
    'planner.project_critique.dna_preflight.fix_first': 'Fix it first',
    'planner.project_critique.dna_preflight.flag.logline_short': 'logline too short',
    'planner.project_critique.dna_preflight.flag.logline_no_period': 'logline ends mid-thought',
    'planner.project_critique.dna_preflight.flag.synopsis_short': 'synopsis too thin',
    'planner.project_critique.dna_preflight.flag.synopsis_includes_act_marker': 'synopsis contains structure outline',
    'planner.project_critique.dna_preflight.flag.theme_truncated': 'theme cuts off mid-word',
    'planner.project_critique.dna_preflight.flag.tone_empty': 'tone empty',
    'planner.project_critique.summary': 'Reviewer summary',
    'planner.project_critique.failed': 'The critique failed — try again in a moment.',
    'planner.project_critique.truncated': 'Your project was truncated for the AI — long projects may miss late-chapter context.',
    'planner.project_critique.premium_required_title': 'Project critique is a Pro feature.',
    'planner.project_critique.premium_required_body': 'Upgrade to run an AI developmental edit across your whole project, with categorized debt and concrete fixes.',
    'planner.project_critique.upgrade_cta': 'See pricing',
    'planner.project_critique.parse_fallback_message': 'The reviewer returned an unreadable response. Try the critique again — most parse errors clear on a second run.',
    'planner.project_critique.clean_title': 'Nothing flagged.',
    'planner.project_critique.clean_body': 'The reviewer found no load-bearing debt across the four categories. Keep going.',
    'planner.project_critique.category.plot_holes': 'Plot holes',
    'planner.project_critique.category.character_debt': 'Character debt',
    'planner.project_critique.category.structural_gaps': 'Structural gaps',
    'planner.project_critique.category.pacing_repetition': 'Pacing & repetition',
    'planner.project_critique.category_clean': 'Nothing flagged in this category.',
    'planner.project_critique.count': '{n}',
    'planner.project_critique.apply.add_label': 'Add to planner',
    'planner.project_critique.apply.update_label': 'Update card',
    'planner.project_critique.apply.chapter_label': 'Rewrite chapter',
    'planner.project_critique.apply.add_tooltip': 'Adds a new {kind} card titled "{title}" to your planner.',
    'planner.project_critique.apply.update_tooltip': 'Updates the existing "{title}" card with the suggested change.',
    'planner.project_critique.apply.chapter_tooltip': "Rewrites chapter {n} following the reviewer's instruction. Uses one AI credit.",
    'planner.project_critique.apply.running': 'Applying…',
    'planner.project_critique.apply.done': 'Applied',
    'planner.project_critique.apply.added': 'Added "{title}" to the planner.',
    'planner.project_critique.apply.updated': 'Updated "{title}".',
    'planner.project_critique.apply.chapter_rewritten': "Chapter {n} rewritten — open the editor to review.",
    'planner.project_critique.apply.failed': "Apply failed ({reason}).",
    'planner.project_critique.scope_chapter': 'Chapter {n}',
    'planner.project_critique.scope_project': 'Project-wide',
    'planner.project_critique.show_fix': 'Show suggested fix',

    'planner.actions.drag': 'Drag to reorder',
    'planner.card.cross_refs_tooltip': '{count} linked card(s)',
    'planner.card.age_unit': 'yo',
    'planner.card.placeholder': 'Placeholder',
    'planner.card.placeholder_tooltip': "Marked as a deliberate stub — the health card's thin-modules check skips this one.",
    // Tier-6 §17 — live "appears in N chapters" chip + drawer
    'planner.card.appears.count': 'in {count} chapters',
    'planner.card.appears.none': 'not yet in any chapter',
    'planner.card.appears.tooltip': 'Mentioned in {count} chapters — click to see which.',
    'planner.card.appears.none_tooltip': "This character hasn't been named in any chapter prose yet.",
    'planner.card.appears.modal_title': "Where {name} appears",
    'planner.card.appears.modal_subtitle': '{count} chapters mention this character. Click a row to jump to the editor.',
    'planner.card.appears.untitled_chapter': 'Chapter {n} (untitled)',
    'planner.card.appears.close': 'Close',
    // [Removed: planner.family_tree.* keys — FamilyTreeView component
    // was deleted as low-value graph chrome. Kind labels for the
    // ``family`` and ``dynasty`` planner kinds live under
    // planner.kind.* and are still present.]
    'planner.timeline.toggle': 'Timeline',
    'planner.timeline.title': 'Project chronology',
    'planner.timeline.dated_suffix': 'dated',
    'planner.timeline.undated_suffix': 'undated',
    'planner.timeline.undated': 'no date',
    'planner.timeline.hint': 'Cards alternate above and below the spine. Edit a card to set or refine its date — the timeline reflows automatically.',
    'planner.timeline.empty.title': 'No events to chart yet',
    'planner.timeline.empty.description': 'Create event, chronology, war, festival, treaty, catastrophe, or chapter-plan cards with a date — they line up here in chronological order.',
    'planner.map.toggle': 'Map',
    'planner.map.title': 'World map',
    'planner.map.empty.title': 'Pin your world on a map',
    'planner.map.empty.description': 'Upload a sketch, a hand-drawn map, or any image and drag your locations onto it. Coordinates save with each location card so they survive when you resize the map.',
    'planner.map.upload_cta': 'Upload a map',
    'planner.map.uploading': 'Uploading…',
    'planner.map.uploaded': 'Map uploaded — start pinning locations.',
    'planner.map.upload_failed': 'Map upload failed — try a smaller image.',
    'planner.map.replace': 'Replace',
    'planner.map.clear': 'Clear',
    'planner.map.clear_confirm': 'Remove the current map? The pinned coordinates on each location will stay so a new map can re-use them.',
    'planner.map.pinned_suffix': 'pinned',
    'planner.map.unplaced': 'Locations not yet pinned',
    'planner.map.pin_to_map': 'Pin to map',
    'planner.map.pin_hint': 'Click a chip to drop the pin at the centre, then drag it to the right spot.',
    // [Removed: planner.graph.* keys — RelationshipGraph component
    // was deleted as low-value graph chrome. Character relationships
    // surface through the Mentioned-by panel in the module drawer
    // (Slice C) and the Connections tab's orphan + red-link
    // worklists.]
    'planner.corkboard.toggle': 'Corkboard',
    'planner.corkboard.list_view': 'List',
    'planner.corkboard.scene_index': 'Scene {index}',
    'planner.corkboard.empty.title': 'No scenes pinned yet',
    'planner.corkboard.empty.description': 'Add scene cards under Narration > Scene — they’ll appear on the corkboard here, drag to reorder.',
    'planner.loading': 'Loading the planner…',
    'planner.loading_modules': 'Loading cards…',
    'planner.search.placeholder': 'Filter cards in this category…',
    'planner.module.untitled': 'Untitled',
    'planner.category.rail': 'Planner categories',
    'planner.category.switch_aria': 'Switch category',
    'planner.category.pick': 'Pick a category',
    'planner.category.world': 'World & Geography',
    'planner.category.society': 'Civilisation & Society',
    'planner.category.characters': 'Characters',
    'planner.category.creatures': 'Creatures & Nature',
    'planner.category.systems': 'Systems',
    'planner.category.narration': 'Narration',
    'planner.category.objects': 'Objects & Inventory',
    'planner.category.lore': 'Documentation & Lore',
    'planner.category.art_direction': 'Art Direction & Media',
    'planner.category.metadata': 'Metadata',
    'planner.category.unknown': 'Other',
    'planner.empty_project.title': 'Pick a project to start planning',
    'planner.empty_project.description':
      'The Story Planner is per-project — choose one to see its world, characters, and narration laid out in one place.',
    'planner.empty_project.no_projects_title': 'Create your first project',
    'planner.empty_project.no_projects_description':
      'The planner attaches to a project. Create one and you’ll land back here with the canvas ready.',
    'planner.empty_project.create_cta': 'Create a new project',
    'planner.project.switch': 'Project',
    'planner.empty_category.title': 'Nothing here yet',
    'planner.empty_category.title_named': '{category} is empty',
    'planner.empty_category.description':
      'Tap a chip below to add your first card, drop in a saved snippet, or let the AI get this category started.',
    'planner.empty_category.suggest': 'Suggest a starter set',
    'planner.empty_category.add_first': 'Add your first {kind}',
    'planner.kind.empty': 'No {kind} yet.',
    // Footer chip strip on category views — replaces the per-empty-kind
    // placeholder rows. Empty kinds collapse so the eye scans only
    // populated sections; this footer keeps the +Add affordance for
    // every unused kind a click away.
    'planner.add_more_kinds.label': 'Add another type',
    'planner.add_more_kinds.hint': '{count} more available for this category',
    'planner.project.pick': 'Pick a project',
    'planner.preset.pick': 'Choose a writing type',
    'planner.preset.title': 'Apply a preset',
    'planner.preset.subtitle':
      'Each writing type lights up the most useful kinds and flavors AI suggestions. Pick a structure to seed locked beats under Narration.',
    'planner.preset.apply': 'Apply',
    'planner.preset.genre': 'Writing type',
    'planner.preset.structure': 'Plot structure',
    'planner.preset.literary': 'Literary fiction',
    'planner.preset.fantasy': 'Fantasy',
    'planner.preset.scifi': 'Science fiction',
    'planner.preset.mystery': 'Mystery / Thriller',
    'planner.preset.romance': 'Romance',
    'planner.preset.memoir': 'Memoir / Nonfiction',
    'planner.preset.screenplay': 'Screenplay',
    'planner.preset.poetry': 'Poetry',
    'planner.preset.children': "Children's fiction",
    'planner.preset.essay': 'Essay / Article',
    'planner.structure.pick': 'Plot structure',
    'planner.structure.three_act': 'Three-act',
    'planner.structure.five_act': 'Five-act',
    'planner.structure.heros_journey': "Hero's journey",
    'planner.structure.save_the_cat': 'Save the Cat',
    'planner.structure.freytag': "Freytag's pyramid",
    'planner.structure.kishotenketsu': 'Kishōtenketsu',
    'planner.structure.snowflake': 'Snowflake method',
    'planner.structure.in_media_res': 'In media res',
    'planner.structure.fichtean_curve': 'Fichtean curve',
    'planner.structure.seven_point': 'Seven-point',
    // Tier-5 §16 — writer-defined structure preset. The picker shows
    // this as a regular button; the textarea reveals when it's
    // selected.
    'planner.structure.custom': 'Custom structure',
    'planner.preset.custom.label': 'Your acts and beats',
    'planner.preset.custom.placeholder': 'List your structural framework — one act or beat per line. Example:\n\n1. Setup — protagonist in their world\n2. Inciting incident — magic dragon attacks\n3. Rising action — recruiting allies\n4. Climax — the final spell',
    'planner.preset.custom.hint': '{used} / {max} characters. The critique reads this verbatim and compares your chapters against the framework you defined.',
    'planner.dna.logline': 'Logline',
    'planner.dna.logline_placeholder': 'The one-sentence pitch.',
    'planner.dna.synopsis': 'Synopsis',
    'planner.dna.synopsis_placeholder': 'A short paragraph capturing the story spine.',
    'planner.dna.theme': 'Theme',
    'planner.dna.theme_placeholder': 'What the story is really about.',
    'planner.dna.tone': 'Tone',
    'planner.dna.tone_placeholder': 'How it feels — bittersweet, dreadful, hopeful…',
    'planner.dna.target_words': 'Target word count',
    'planner.dna.toggle': 'Story DNA',
    'planner.dna.toggle_aria': 'Show or hide the logline, synopsis, theme, and tone fields.',
    'planner.logline_strip.aria': 'Project logline. Click to edit in Story DNA.',
    'planner.logline_strip.empty_cta': 'Add a logline — one sentence that captures your story.',
    'planner.logline_strip.empty_tooltip': "A one-sentence pitch. Visible everywhere in the Planner so your story's spine stays in sight.",
    'planner.status.stub': 'Stub — title only',
    'planner.status.draft': 'Draft — partially filled',
    'planner.status.complete': 'Complete — summary, body and metadata set',
    'planner.filter.all': 'All',
    'planner.filter.stub': 'Stubs',
    'planner.filter.draft': 'Drafts',
    'planner.filter.complete': 'Complete',
    'planner.filter.aria': 'Filter cards by completion',
    'planner.spotlight.aria': 'Suggested next steps for this project',
    'planner.spotlight.heading': 'Next steps:',
    'planner.spotlight.no_logline': 'Add a logline',
    'planner.spotlight.no_preset': 'Pick a genre and structure preset',
    'planner.spotlight.stubs': '{count} blank cards to fill in',
    'planner.spotlight.low_coverage': 'Beat coverage {pct}% — fill in more beats',
    'planner.ai.collapse': 'Collapse AI assistant',
    'planner.ai.expand': 'Expand AI assistant',
    'planner.search.clear': 'Clear search',
    'planner.density.aria': 'Card display density',
    'planner.density.grid': 'Grid view',
    'planner.density.list': 'List view',
    'planner.import.button': 'Import',
    'planner.import.title': 'Import a manuscript',
    'planner.import.title.manuscript': 'Import a manuscript',
    'planner.import.title.side_doc': 'Import side documentation',
    'planner.import.intro': 'Drop a Word document or PDF — we’ll read it and pull out the chapters, characters, and locations we find. You decide what to import.',
    // Slice import-11 (side-doc kinds). The picker lets a writer
    // tell us what shape the dropped file actually has -- a full
    // manuscript, a character bible, or a world/location sheet.
    // The default ``manuscript`` matches the original behaviour.
    'planner.import.kind.label': 'What kind of document is this?',
    'planner.import.kind.manuscript.label': 'Manuscript',
    'planner.import.kind.manuscript.desc': 'Prose with chapters. We extract the chapters, characters, locations, and project DNA.',
    'planner.import.kind.side_doc.label': 'Reference document',
    'planner.import.kind.side_doc.desc': 'Notes, reference sheets, world-building documents, magic systems, factions. We split it into sections and route each one to the right planner category.',
    'planner.import.kind.multifile_lock': 'When you drop several files, each one is treated as a chapter of the manuscript.',
    'planner.import.review_intro_sheet': 'Tick the entries to import. Each one becomes a planner card in its detected category, with the parsed summary, body, and role.',
    'planner.import.entry_kind_tooltip': 'Detected card type. You can change it later from the card itself.',
    'planner.import.chars': 'chars',
    // Section headers for side-doc review pane, one per planner
    // category. Same names the planner sidebar uses so the
    // import review reads like the writer's own planner.
    'planner.import.entry_category.characters': 'Characters',
    'planner.import.entry_category.world': 'World & locations',
    'planner.import.entry_category.society': 'Society & cultures',
    'planner.import.entry_category.systems': 'Systems & magic',
    'planner.import.entry_category.creatures': 'Creatures',
    'planner.import.entry_category.narration': 'Narration & notes',
    'planner.import.entry_category.objects': 'Objects & items',
    'planner.import.entry_category.lore': 'Lore & history',
    'planner.import.entry_category.art_direction': 'Art direction',
    'planner.import.entry_category.metadata': 'Metadata',
    'planner.import.entry_category._other': 'Other ({cat})',
    'planner.import.drop_prompt': 'Drop a file here, or click to browse',
    'planner.import.drop_more_or_click': 'Drop more files, or click to add',
    'planner.import.multi_hint': 'Each file becomes a chapter, in alphabetical order.',
    'planner.import.accepts': 'Accepts {accepted}',
    'planner.import.files_picked': 'Files picked ({n})',
    'planner.import.analyze_n_files': 'Analyze {n} files',
    'planner.import.analyzing_n': 'Analyzing {current}/{total}…',
    'planner.import.analyzing_file': 'Reading {filename} ({current}/{total})',
    'planner.import.remove_file': 'Remove file',
    'planner.import.or': 'or',
    'planner.import.paste_label': 'Paste text directly',
    'planner.import.paste_placeholder': 'Paste your draft, outline, or reference notes here…',
    'planner.import.analyze': 'Analyze',
    'planner.import.analyzing': 'Analyzing…',
    'planner.import.cancel': 'Cancel',
    'planner.import.back': 'Back',
    'planner.import.close': 'Close',
    'planner.import.commit_n': 'Import {n} entries',
    'planner.import.committing': 'Importing…',
    'planner.import.review_intro': 'Tick what to keep. Pre-selected lists default to everything we detected.',
    'planner.import.section.metadata': 'Project details',
    'planner.import.section.chapters': 'Chapters',
    'planner.import.section.characters': 'Characters',
    'planner.import.section.locations': 'Locations',
    'planner.import.metadata_hint': 'Detected from the prose before the first chapter. Ticked fields fill in your project; unticked fields stay as they are.',
    'planner.import.meta.title': 'Title',
    'planner.import.meta.logline': 'Logline',
    'planner.import.meta.synopsis': 'Synopsis',
    'planner.import.meta.genre': 'Genre',
    'planner.import.meta.theme': 'Theme',
    'planner.import.meta.has_current': 'Your project already has a value — ticking will overwrite: “{current}”',
    // Slice import-10 uplift: inferred role badges on character
    // rows in the review pane. Role is suggested from chapter
    // coverage rank (top -> protagonist; next two -> supporting).
    'planner.import.role.protagonist': 'Protagonist',
    'planner.import.role.supporting': 'Supporting',
    'planner.import.role.antagonist': 'Antagonist',
    'planner.import.role.mentor': 'Mentor',
    'planner.import.role.rival': 'Rival',
    'planner.import.role.ally': 'Ally',
    'planner.import.role.foil': 'Foil',
    'planner.import.role.deuteragonist': 'Deuteragonist',
    'planner.import.role.love interest': 'Love interest',
    'planner.import.section_empty': 'Nothing detected in this category.',
    'planner.import.no_entries_title': 'No entries detected in this document.',
    'planner.import.no_entries_hint': 'No usable sections were found. Go back and try a different file, or paste the relevant excerpt directly.',
    'planner.import.sidedoc_fallback': "This doesn't look like reference documentation, but we found manuscript content (chapters, characters, locations). Pick from below to import them as a manuscript.",
    'planner.import.ai_uplift_button': 'AI uplift: extract planner entries',
    'planner.import.ai_uplift_running': 'Extracting entries…',
    'planner.import.ai_uplift_done': 'AI uplift found {n} entries. Review and pick what to keep.',
    'planner.import.ai_uplift_no_entries': "AI uplift found no structured entries in this text.",
    'planner.import.ai_uplift_no_text': 'No source text to analyse. Paste text or re-import the file.',
    'planner.import.ai_uplift_error': 'AI uplift failed. Please try again.',
    'planner.import.ai_uplift_quota': "Daily planner-AI quota reached — try again later or upgrade.",
    'planner.import.select_all': 'Select all',
    'planner.import.clear_all': 'Clear',
    'planner.import.words': 'words',
    'planner.import.mentions': 'mentions',
    'planner.import.truncated_warning': 'Your document was very long — we read the first 2 million characters. Re-import the rest separately if needed.',
    'planner.import.done_title': 'Imported',
    'planner.import.done_summary': '{chapters} chapter(s), {characters} character(s), and {locations} location(s) added.',
    'planner.import.error_missing_input': 'Drop a file or paste some text first.',
    'planner.import.error_unsupported_format': 'Unsupported file type. Accepts {accepted}.',
    'planner.import.error_too_large': 'File too large ({max_mb} MB max).',
    'planner.import.error_analyze': 'File unreadable. Try a different format or paste the text directly.',
    'planner.import.error_commit': 'Save failed — try again in a moment.',
    'planner.import.error_nothing_picked': 'Tick at least one entry to import.',
    'planner.import.mark_study.label': 'Mark this as a study / research project',
    'planner.import.mark_study.description': 'Keeps the project out of your main work list. Useful when importing a published book (yours or someone else’s) to study its structure.',
    'planner.import.mark_study.already': 'This project is already marked as a study project.',
    'planner.import.structure.title': 'Structure',
    'planner.import.structure.intro': 'A quick look at the shape of the manuscript before you import.',
    'planner.import.structure.chapters': 'Chapters',
    'planner.import.structure.total_words': 'Total words',
    'planner.import.structure.mean_chapter': 'Avg / chapter',
    'planner.import.structure.range': 'Range',
    'planner.import.structure.words_unit': 'words',
    'planner.import.structure.pace_label': 'Chapter pacing:',
    'planner.import.structure.pace_even': 'fairly even',
    'planner.import.structure.pace_mixed': 'mixed',
    'planner.import.structure.pace_uneven': 'very uneven',
    'planner.import.structure.coverage_label': 'Character coverage',
    'planner.import.structure.coverage_row': '{present}/{total} · {pct}%',
    'planner.import.fallback_chapter_title': 'Imported chapter',
    // Re-import diff (slice import-6). Per-section badge + per-row
    // tag. Hidden when nothing duplicates.
    'planner.import.diff_summary': '{new} new · {dup} already in project',
    'planner.import.already_imported': 'In project',
    // Templates the import enrichment loop sends to the planner
    // /suggest endpoint. Localised so a French writer's enrichment
    // run actually asks the model in French -- otherwise the
    // instruction came through in English and the model echoed
    // English back, ignoring the project's locale.
    'planner.import.enrich_prompt.character':
      'Describe the character "{name}" in a short paragraph: who they are, their apparent role, and any traits that surface. Use only what is grounded in this excerpt from the source manuscript; if the excerpt doesn\'t say something, leave it out.\n\nExcerpt:\n{excerpt}',
    'planner.import.enrich_prompt.location':
      'Describe the location "{name}" in a short, evocative paragraph. Use only what is grounded in this excerpt from the source manuscript; if the excerpt doesn\'t say something, leave it out.\n\nExcerpt:\n{excerpt}',
    'planner.import.enrich_title': 'Enrich with AI',
    'planner.import.enrich_description': 'Adds a generated paragraph to each imported card, drawing on your manuscript text. Uses one AI credit per card.',
    'planner.import.enrich_cap_note': 'Limited to {cap} cards per run — open the AI assistant to enrich the rest later.',
    'planner.import.enrich_n': 'Enrich {n} with AI',
    'planner.import.enrich_progress': 'Enriching {current} of {total}…',
    'planner.import.enrich_cancel': 'Stop',
    'planner.import.enrich_done': 'Enrichment finished',
    'planner.import.enrich_stopped_quota': 'AI quota reached',
    'planner.import.enrich_summary': '{success} enriched, {failed} skipped.',
    'planner.header.preset_combined': 'Preset: {genre} · {structure}',
    'planner.header.preset_pick': 'Pick preset',
    'planner.actions.save': 'Save',
    'planner.actions.delete': 'Move to trash',
    'planner.actions.add_kind': 'Add {kind}',
    'planner.actions.save_as_snippet': 'Save as snippet',
    'planner.actions.export': 'Export planner',
    'planner.actions.export_md': 'Markdown (.md)',
    'planner.actions.export_txt': 'Plain text (.txt)',
    'planner.actions.export_doc': 'Word (.doc)',
    'planner.actions.export_pdf': 'PDF',
    'planner.actions.export_html': 'HTML',
    'planner.actions.export_failed': "Planner export failed.",
    'planner.actions.export_xrefs.label': "Include cross-references",
    'planner.actions.export_xrefs.hint': "Adds a References / Referenced-by block under each module.",
    'planner.actions.export_appendix.label': "Add connectivity appendix",
    'planner.actions.export_appendix.hint': "Appends top hubs, red-link worklist, and the full adjacency map at the end.",
    'planner.actions.export_notes.label': "Include project notes",
    'planner.actions.export_notes.hint': "Adds your freeform Notes-tab content near the top of the export.",
    'planner.search.empty': 'Nothing matches that search.',
    'planner.drawer.new': 'New {kind}',
    'planner.drawer.edit': 'Edit {kind}',
    'planner.drawer.title': 'Title',
    'planner.drawer.dupe.warning': "Same title already exists as:",
    'planner.drawer.dupe.open': "Open the existing card",
    'planner.drawer.summary': 'Summary',
    'planner.drawer.summary_placeholder': 'A one-liner shown on the card.',
    'planner.drawer.body': 'Body',
    'planner.drawer.metadata': 'Details',
    'planner.metadata.list_placeholder': 'One per line',
    'planner.metadata.ref_field_hint': "Links to another planner module — type a title or use Pick.",
    'planner.metadata.ref_field_placeholder': "Type a module title to link…",
    'planner.metadata.ref_field_list_placeholder': "One module title per line — each becomes a link.",
    'planner.metadata.ref_field_pick': "Pick",
    'planner.health.empty_state.title': "Get started with cross-references",
    'planner.health.empty_state.body': "Your planner has modules but no cross-references yet — every module is an island. Cross-references turn the planner into a connected web: open a module, find a field with a link icon (mentor, rival, faction, located_in…), and type the title of the related module.",
    'planner.health.empty_state.example': "Example: in your protagonist's character module, fill the \"mentor\" field with the title of another character. That single edit fills the Project Map, the Mentioned-by panel on both modules, and the Health metrics here.",
    'planner.health.empty_state.next_step': "Tip: characters, factions, and locations carry the most ref-fields. Start there and the rest of the web will grow on its own.",
    'planner.health.xref_detect.title': "Auto-detect cross-references from prose",
    'planner.health.xref_detect.subtitle': "Scans every module's title, summary, and body for mentions of other module titles, then proposes ref-field values to wire them up. No AI quota — pure pattern matching.",
    'planner.health.xref_detect.button': "Detect cross-references",
    'planner.health.xref_detect.scanning': "Scanning…",
    'planner.health.xref_detect.modal_title': "Cross-reference proposals",
    'planner.health.xref_detect.summary': "Found {values} proposed values across {modules} modules (scanned {scanned}).",
    'planner.health.xref_detect.summary_empty': "No new cross-references found in the prose. Scanned {scanned} modules.",
    'planner.health.xref_detect.sample_label': "Sample (showing {shown} of {total})",
    'planner.health.xref_detect.apply_hint': "Apply writes the full plan into your modules' metadata. You can undo any single change by editing the module directly.",
    'planner.health.xref_detect.apply': "Apply all ({count})",
    'planner.health.xref_detect.applying': "Applying…",
    'planner.health.xref_detect.applied': "Wrote {fields} cross-references across {modules} modules.",
    'planner.health.xref_detect.scan_failed': "Planner scan failed — try again in a moment.",
    'planner.health.xref_detect.apply_failed': "Some modules didn't update. Refresh and check the metrics.",
    // Cross-linking (Slice B chips). State-specific hover hints:
    // "folded" = auto-matched after accent/ligature folding (low
    // confidence). "ambiguous" = >1 candidate, writer picks (Slice D).
    // "red" = no module matches this value — Slice D wires Create.
    'planner.cross_linking.chip.folded_hint': 'Auto-matched (low confidence — accent or ligature fold)',
    'planner.cross_linking.chip.ambiguous_hint': '{count} possible matches — pick one',
    'planner.cross_linking.chip.red_hint': 'No module matches — could become a {kinds}',
    'planner.cross_linking.chip.repin_hint': 'Change pin or pick a different module',
    // Slice C — Mentioned-by panel labels.
    'planner.cross_linking.mentioned_by.title': 'Mentioned by',
    'planner.cross_linking.mentioned_by.uncategorised': 'Other',
    'planner.cross_linking.mentioned_by.via': 'via {via}',
    // Slice D — picker modal.
    'planner.cross_linking.picker.title_resolved': 'Change link',
    'planner.cross_linking.picker.title_ambiguous': 'Choose a module',
    'planner.cross_linking.picker.title_red': 'Link or create a module',
    'planner.cross_linking.picker.close': 'Close',
    'planner.cross_linking.picker.candidates': 'Possible matches',
    'planner.cross_linking.picker.search_label': 'Pick an existing module',
    'planner.cross_linking.picker.search_placeholder': 'Search by title…',
    'planner.cross_linking.picker.no_results': 'No matches in this project for these kinds.',
    'planner.cross_linking.picker.create_label': 'Create a new module instead',
    'planner.cross_linking.picker.create_title_placeholder': 'Title for the new module',
    'planner.cross_linking.picker.create_as': 'Create as {kind}',
    'planner.cross_linking.picker.title_required': 'A title is required to create a module.',
    'planner.cross_linking.picker.created': 'Created — saved & linked.',
    'planner.cross_linking.picker.create_failed': 'Module creation failed. Try again.',
    'planner.cross_linking.picker.pinned': 'Pinned. Save the drawer to commit.',
    // Slice E — relationship-suggestion inline action in the
    // Mentioned-by panel. Surfaces only on character→character
    // reciprocal pairs not yet wrapped in a relationship module.
    'planner.cross_linking.suggest_rel.cta': 'Make rel.',
    'planner.cross_linking.suggest_rel.hint': 'These two characters reference each other — wrap as a relationship module',
    'planner.cross_linking.suggest_rel.created': 'Relationship created — open it from the planner to rename and describe.',
    'planner.cross_linking.suggest_rel.failed': 'Relationship creation failed. Try again.',
    // Slice F — project map view.
    'planner.view.cards': 'Cards',
    'planner.view.map': 'Map',
    // Rework Slice 1 — primary planner tabs.
    'planner.tab.aria': 'Planner view',
    'planner.tab.modules': 'Modules',
    'planner.tab.connections': 'Analytics',
    'planner.tab.notes': 'Notes',
    'planner.connections.sub_aria': 'Connections view',
    'planner.connections.tab_intro': "What's off about the planner, how things connect, and where to drill in.",
    'planner.connections.sub.map': 'Map',
    'planner.connections.sub.health': 'Health',
    'planner.connections.sub.orphans': 'Unlinked',
    'planner.connections.sub.red_links': 'Broken mentions',
    'planner.connections.loading': "Reading your project's connections…",
    'planner.connections.error_title': "Connections unavailable",
    'planner.connections.error_hint': 'Try refreshing — the rest of the planner keeps working.',
    'planner.overflow.title': 'More planner tools',
    'planner.overflow.snippets': 'Snippets library',
    'planner.overflow.tropes': 'Trope suggestions',
    'planner.overflow.coherence': 'Run coherence check',
    'planner.overflow.critique': 'Project critique',
    'planner.overflow.import': 'Import…',
    'planner.notes.title': 'Plan notes',
    'planner.notes.body': "Open the Story DNA editor in the header to edit your logline, synopsis, theme, and tone.",
    'planner.notes.open_editor': 'Open Story DNA editor',
    // Rework Slice 8 — Notes inline editor.
    'planner.notes.dna.title': 'Story DNA',
    'planner.notes.dna.subtitle': "The structured spine of your project — the four lines AI reasoning leans on most.",
    'planner.notes.field.logline': 'Logline',
    'planner.notes.field.logline_placeholder': "One sentence: the protagonist, their goal, and what's in the way.",
    'planner.notes.field.synopsis': 'Synopsis',
    'planner.notes.field.synopsis_placeholder': "A paragraph or two — the story beats end to end.",
    'planner.notes.field.theme': 'Theme',
    'planner.notes.field.theme_placeholder': "The idea the story is wrestling with (love, betrayal, identity…).",
    'planner.notes.field.tone': 'Tone',
    'planner.notes.field.tone_placeholder': "How it should feel to read (lyrical, dry, urgent…).",
    'planner.notes.freeform.title': 'Plan notes',
    'planner.notes.freeform.subtitle': "The catch-all — running thoughts, open questions, todos. Saved as you type.",
    'planner.notes.freeform.label': 'Notes',
    'planner.notes.freeform.placeholder': "What's on your mind for this project?",
    'planner.notes.freeform.hint': 'Markdown supported · auto-saves on blur',
    'planner.notes.saving': 'Saving…',
    'planner.notes.saved': 'Saved',
    'planner.notes.save_failed': "Save failed — try again.",
    'planner.notes.draft.button': 'Draft from my chapters',
    'planner.notes.draft.tooltip': 'Generate a candidate from your chapter prose and your plan. Uses one AI credit.',
    'planner.notes.draft.candidate_label': 'AI candidate',
    'planner.notes.draft.apply': 'Apply',
    'planner.notes.draft.discard': 'Discard',
    'planner.notes.draft.applied': 'Applied — your draft is now the field value.',
    'planner.notes.draft.failed': "Draft failed — try again in a moment.",
    'planner.notes.draft.quota_exceeded': 'Daily AI cap reached. You can still type the field yourself.',
    // Slice 3 — drawer tiering.
    'planner.drawer.more_details': '▸ More details ({count})',
    // Slice 5 — FirstStepsCard onboarding.
    'planner.first_steps.title': 'Welcome to your planner',
    'planner.first_steps.body': "The planner is where you keep the things in your head — characters, places, magic rules — so you can write without forgetting them. Pick a starting point:",
    'planner.first_steps.add_protagonist': 'Add your protagonist',
    'planner.first_steps.add_setting': 'Add the main setting',
    'planner.first_steps.start_preset': 'Start from a structure preset',
    'planner.first_steps.dismiss_aria': 'Dismiss this welcome card',
    // Stage D — at-scale banner (40+ modules).
    'planner.at_scale.title': "{modules} modules · {chapters} chapters",
    'planner.at_scale.bottom_title': "Across your planner — {modules} modules, {chapters} chapters",
    'planner.at_scale.loading': "Checking what's loose…",
    'planner.at_scale.clean': "Everything's wired up — no orphans, no broken refs.",
    'planner.at_scale.loose_ends': "{count} loose ends ({orphans} orphans, {red_links} broken refs).",
    'planner.at_scale.loose_ends_short': "{count} loose ends — {orphans} unlinked · {red_links} broken",
    'planner.at_scale.show_loose': "Show me what's loose",
    'planner.at_scale.review_loose': "Review loose ends",
    // Stage B — cross-refs CTA strip on Modules tab.
    'planner.cross_refs_strip.title': "Your modules aren't wired up yet",
    'planner.cross_refs_strip.body': "Cross-references turn your modules into a connected web — open Connections to detect them from your prose or wire them by hand.",
    'planner.cross_refs_strip.cta': "Open Connections",
    // Stage C — Continue where you left off chip.
    'planner.continue.label': "Recently edited",
    'planner.footer.heading': "Something missing?",
    'planner.health.title': "{count} planner inconsistencies to review",
    'planner.health.clean_title': "Plan is clean",
    'planner.health.clean_hint': "Every health check passed. Add or edit a module to surface new findings.",
    'planner.health.open': "Open this module",
    'planner.health.more': "+ {count} more",
    'planner.health.section.duplicates': "Duplicates",
    'planner.health.duplicates.hint': "Same title, different kinds — likely duplicates.",
    'planner.health.duplicates.across': "→",
    'planner.health.section.role_conflicts': "Role conflicts",
    'planner.health.role_conflicts.hint': "Confirm if intentional, or relabel a card.",
    'planner.health.role_conflicts.line': "{count} share “{role}” (max {expected}).",
    'planner.health.section.stale_orphans': "Stale orphans",
    'planner.health.stale_orphans.hint': "No references, no recent edits.",
    'planner.health.stale_orphans.tooltip': "No references · {days} days ago",
    'planner.health.section.unmentioned_characters': "Missing or underused cast",
    'planner.health.unmentioned_characters.hint': "Planned characters that don't appear in the prose, plus high-stakes roles below their expected chapter count.",
    // Tier-6 §17 augmentation — underused badge + tooltip
    'planner.health.unmentioned.underused_badge': '{count} ch.',
    'planner.health.unmentioned.underused_tooltip': "Appears in {count} chapter(s) — below what a {role} role usually carries.",
    'planner.health.section.metadata_gaps': "Missing critique fields",
    'planner.health.metadata_gaps.hint': "Voice, relationships, and motivations feed every critique.",
    'planner.health.metadata_gaps.missing': "missing",
    'planner.health.metadata_gaps.key.voice_sample': "voice",
    'planner.health.metadata_gaps.key.relationships': "relationships",
    'planner.health.metadata_gaps.key.motivations': "motivations",
    'planner.health.section.thin_modules': "Thin cards",
    'planner.health.thin_modules.hint': "Empty body and short summary.",
    'planner.health.thin_modules.tooltip': "Body {body}c · Summary {summary}c",
    'planner.health.tile.duplicates': "Duplicates",
    'planner.health.tile.semantic_dupes': "Same summary",
    'planner.health.section.semantic_dupes': "Same-summary duplicates ({count})",
    'planner.health.semantic_dupes.hint': "Different titles, identical summary — likely a copy-paste. Open one to differentiate.",
    // Tier-4 — orphan-drain merge candidates
    'planner.health.tile.orphan_merges': "Merge candidates",
    'planner.health.section.orphan_merges': "Orphans that may merge ({count})",
    'planner.health.orphan_merges.hint': "These orphans share heavy content overlap with a referenced module of the same kind. Open both to compare and merge or differentiate.",
    'planner.health.orphan_merges.arrow': "matches {overlap}% →",
    // Tier-4 — narration-only names (backward extract)
    'planner.health.tile.narration_names': "In prose only",
    'planner.health.section.narration_names': "Names in prose but not in the planner ({count})",
    'planner.health.narration_names.hint': "These capitalized names appear in your chapters repeatedly but aren't recorded as planner cards. Add the recurring ones so cross-linking and critique can see them.",
    'planner.health.narration_names.tooltip': "{mentions} mentions across {chapters} chapters",
    'planner.health.tile.role_conflicts': "Roles",
    'planner.health.tile.unmentioned': "Unused cast",
    'planner.health.tile.metadata_gaps': "Meta gaps",
    'planner.health.tile.thin_modules': "Thin",
    'planner.health.tile.stale_orphans': "Stale",
    'planner.health.bulk_archive.select': "Select for bulk archive",
    'planner.health.bulk_archive.action': "Archive {count}",
    'planner.health.bulk_archive.done': "Archived {count} module(s).",
    'planner.health.bulk_archive.failed': "Archive failed — try again in a moment.",
    'planner.health.merge.action': "Merge",
    'planner.health.merge.multi_action': "Fold {count} into 1st",
    'planner.health.merge.multi_tooltip': "Folds the {count} later cards in this group into the first listed ({target}). One operation.",
    'planner.health.merge.tooltip': "Fold the {from} card into the {into} card. Cross-references update, the {from} card is archived.",
    'planner.health.merge.done': "Merged — references updated, old card archived.",
    'planner.health.merge.failed': "Merge failed — try again in a moment.",
    'planner.health.role_conflicts.dismiss': "Intentional",
    'planner.health.role_conflicts.dismiss_tooltip': "Mark this role count as intentional — the warning hides for this project.",
    'planner.health.write_scene.action': "Write a scene",
    'planner.health.write_scene.tooltip': "Open the writing space and start a scene with {name}.",
    'planner.health.write_scene.prompt': "Write a scene where {name} ({role}) enters the story for the first time. Keep their voice and motivations from the planner; introduce them to the reader through action, not exposition.",
    'planner.health.write_scene.clipboard': "Scene prompt copied — paste into the AI assist when you open a chapter.",
    'planner.health.fill.action': "AI fill",
    'planner.health.fill.tooltip_metadata': "Open this character and auto-fill voice sample / relationships / motivations with AI.",
    'planner.health.fill.tooltip_thin': "Open this card and auto-fill every empty field with AI.",
    'planner.continue.title': "Open the module you were last editing",
    'planner.continue.edited_ago': "edited {ago}",
    'planner.signature.untitled_module': "Untitled module",
    // Coherence/Critique gating tooltip (overflow menu).
    'planner.overflow.coherence_disabled': "Add {count} more chapter(s) before running the coherence check — it reads your draft prose to find drift.",
    'planner.overflow.critique_disabled': "Add {count} more chapter(s) before running Critique — it needs real chapter content to flag structural debt.",
    // Rework Slice 2 — flat grid + CategoryRail.
    'planner.category.section_label': 'Categories',
    'planner.group_by_kind.label': 'Group by type',
    'planner.select_mode.label': 'Select',
    'planner.select_mode.off_tooltip': 'Switch to select mode — pick multiple cards to archive in one batch.',
    'planner.select_mode.on_tooltip': 'Exit select mode and clear selection.',
    'planner.bulk_archive.selected_count': '{count} selected',
    'planner.bulk_archive.clear': 'Clear',
    'planner.bulk_archive.action': 'Archive {count}',
    'planner.bulk_archive.done': '{count} card(s) archived. Restore from the trash any time.',
    'planner.bulk_archive.failed': "Archive failed — try again in a moment.",
    'planner.group_by_kind.on_tooltip': 'Showing one section per type — click to flatten',
    'planner.group_by_kind.off_tooltip': 'Flat grid sorted by recent activity — click to group by type',
    // Rework Slice 4 — KindPicker.
    'planner.kind_picker.button_label': 'New',
    'planner.kind_picker.button_title': 'Add a new module — search or pick a type',
    'planner.kind_picker.title': 'Add a new module',
    'planner.kind_picker.search_placeholder': 'Describe what you want to add — character, city, scene…',
    'planner.kind_picker.clear': 'Clear search',
    'planner.kind_picker.results': 'Results ({count})',
    'planner.kind_picker.no_results': "No matching type. Browse the categories below or try a different keyword.",
    'planner.kind_picker.popular': 'Popular for this project',
    'planner.kind_picker.all_categories': 'All categories',
    'planner.kind_picker.category_count': '{count} types',
    // [Removed: planner.project_map.* and planner.map.loading/error
    // keys — ProjectMap component was deleted (Connections tab is
    // now worklist-driven via Orphans + Red links). The MapView
    // pin-on-image surface under the World category is unaffected
    // and continues to use planner.map.toggle.]
    // Slice H — project health view.
    'planner.view.health': 'Health',
    'planner.health.loading': 'Reading the planner\'s pulse…',
    'planner.health.error_title': 'Project health unavailable',
    'planner.health.error_hint': 'Try refreshing — the planner kept working in cards view.',
    'planner.health.metric.resolution_rate': 'Links wired',
    'planner.health.metric.resolution_rate_sub': '{resolved} of {instances} mentions linked',
    'planner.health.metric.edges_per_module': 'Avg links per card',
    'planner.health.metric.edges_per_module_sub': 'Higher = a richer web',
    'planner.health.metric.orphans': 'Unlinked cards',
    'planner.health.metric.orphans_sub': 'Nobody mentions them',
    'planner.health.metric.red_links': 'Broken mentions',
    'planner.health.metric.red_links_sub': 'Names that match no card',
    'planner.health.orphans.title': 'Modules with no incoming references',
    'planner.health.orphans.empty': 'Every module is referenced by at least one other — the web is fully connected.',
    'planner.health.orphans.show_more': '+ {count} more — show all',
    'planner.health.orphans.show_more_categories': '+ {categories} more categories ({orphans} orphans) — show all',
    'planner.health.red_links.title': 'Unresolved references (worklist)',
    'planner.health.red_links.empty': 'Every reference resolves to a real module — no worklist items.',
    'planner.health.red_links.open_referrer': 'Open the referencing module to pick or create the target',
    'planner.health.red_links.more': '+ {count} more references (open one to drill in)',
    'planner.health.red_links.why_aria': 'Why does this reference fail?',
    'planner.health.red_links.why.not_found': 'No module by this name exists — create one with kind {kinds} via the referencing module.',
    'planner.health.red_links.why.kind_mismatch': 'A module called “{suggestion_title}” exists but its kind ({suggestion_kind}) isn’t in {kinds}.',
    'planner.health.red_links.why.too_short': 'Too short to auto-match — the resolver only matches names of 4 characters or more.',
    'planner.health.red_links.why.open_suggestion': 'Open “{title}”',
    'planner.metadata.setting': 'Setting',
    'planner.metadata.pov': 'POV',
    // Auto-added planner.metadata.* — covers every field id
    // declared in writeon-django planner_presets._field. Without
    // these, missing keys fall through to the humanized id
    // (looks English even when locale=fr).
    'planner.metadata.abilities': 'Abilities',
    'planner.metadata.access_rules': 'Access rules',
    'planner.metadata.activation': 'Activation',
    'planner.metadata.aftermath': 'Aftermath',
    'planner.metadata.age': 'Age',
    'planner.metadata.anchor_date': 'Anchor date',
    'planner.metadata.answer': 'Answer',
    'planner.metadata.appearance': 'Appearance',
    'planner.metadata.attributed_to': 'Attributed to',
    'planner.metadata.author': 'Author',
    'planner.metadata.authority': 'Authority',
    'planner.metadata.axioms': 'Axioms',
    'planner.metadata.behavior': 'Behavior',
    'planner.metadata.biography': 'Biography',
    'planner.metadata.biome': 'Biome',
    'planner.metadata.capital': 'Capital',
    'planner.metadata.cause': 'Cause',
    'planner.metadata.character': 'Character',
    'planner.metadata.clergy': 'Clergy',
    'planner.metadata.climax': 'Climax',
    'planner.metadata.conflict': 'Conflict',
    'planner.metadata.context': 'Context',
    'planner.metadata.costs': 'Costs',
    'planner.metadata.creation_myth': 'Creation myth',
    'planner.metadata.culture': 'Culture',
    'planner.metadata.currency': 'Currency',
    'planner.metadata.daily_life': 'Daily life',
    'planner.metadata.damage_kind': 'Damage kind',
    'planner.metadata.date': 'Date',
    'planner.metadata.description': 'Description',
    'planner.metadata.districts': 'Districts',
    'planner.metadata.effects': 'Effects',
    'planner.metadata.emotional_states': 'Emotional states',
    'planner.metadata.end_state': 'End state',
    'planner.metadata.evolution': 'Evolution',
    'planner.metadata.exceptions': 'Exceptions',
    'planner.metadata.festivals': 'Festivals',
    'planner.metadata.flaws': 'Flaws',
    'planner.metadata.form': 'Form',
    'planner.metadata.founder': 'Founder',
    'planner.metadata.function': 'Function',
    'planner.metadata.giver': 'Giver',
    'planner.metadata.goal': 'Goal',
    'planner.metadata.government': 'Government',
    'planner.metadata.habitat': 'Habitat',
    'planner.metadata.hazards': 'Hazards',
    'planner.metadata.heroes': 'Heroes',
    'planner.metadata.history': 'History',
    'planner.metadata.image_url': 'Image URL',
    'planner.metadata.impact': 'Impact',
    'planner.metadata.inciting_incident': 'Inciting incident',
    'planner.metadata.inhabitants': 'Inhabitants',
    'planner.metadata.kind': 'Kind',
    'planner.metadata.landmarks': 'Landmarks',
    'planner.metadata.languages': 'Languages',
    'planner.metadata.leadership': 'Leadership',
    'planner.metadata.limitations': 'Limitations',
    'planner.metadata.limits': 'Limits',
    'planner.metadata.link': 'Link',
    'planner.metadata.locked': 'Locked',
    'planner.metadata.logline': 'Logline',
    'planner.metadata.manifestations': 'Manifestations',
    'planner.metadata.members': 'Members',
    'planner.metadata.methods': 'Methods',
    'planner.metadata.months': 'Months',
    'planner.metadata.motivations': 'Motivations',
    'planner.metadata.nature': 'Nature',
    'planner.metadata.notable_features': 'Notable features',
    'planner.metadata.notable_members': 'Notable members',
    'planner.metadata.objective': 'Objective',
    'planner.metadata.origin': 'Origin',
    'planner.metadata.origin_myth': 'Origin myth',
    'planner.metadata.outcome': 'Outcome',
    'planner.metadata.pantheon': 'Pantheon',
    'planner.metadata.penalty': 'Penalty',
    'planner.metadata.period': 'Period',
    'planner.metadata.physiology': 'Physiology',
    'planner.metadata.population': 'Population',
    'planner.metadata.portrait_url': 'Portrait URL',
    'planner.metadata.powers': 'Powers',
    'planner.metadata.practices': 'Practices',
    'planner.metadata.practitioners': 'Practitioners',
    'planner.metadata.present_characters': 'Present characters',
    'planner.metadata.primary_industries': 'Primary industries',
    'planner.metadata.psychology': 'Psychology',
    'planner.metadata.purpose': 'Purpose',
    'planner.metadata.reputations': 'Reputations',
    'planner.metadata.requires': 'Requires',
    'planner.metadata.research_state': 'Research state',
    'planner.metadata.resolution': 'Resolution',
    'planner.metadata.reward': 'Reward',
    'planner.metadata.rival_factions': 'Rival factions',
    'planner.metadata.role': 'Role',
    'planner.metadata.ruler': 'Ruler',
    'planner.metadata.rules': 'Rules',
    'planner.metadata.sample_phrases': 'Sample phrases',
    'planner.metadata.scope': 'Scope',
    'planner.metadata.script': 'Script',
    'planner.metadata.seasons': 'Seasons',
    'planner.metadata.secrets': 'Secrets',
    'planner.metadata.side_effects': 'Side effects',
    'planner.metadata.sides': 'Sides',
    'planner.metadata.source': 'Source',
    'planner.metadata.speakers': 'Speakers',
    'planner.metadata.special': 'Special',
    'planner.metadata.stages': 'Stages',
    'planner.metadata.stakes': 'Stakes',
    'planner.metadata.start_state': 'Start state',
    'planner.metadata.statement': 'Statement',
    'planner.metadata.succession': 'Succession',
    'planner.metadata.summary': 'Summary',
    'planner.metadata.system': 'System',
    'planner.metadata.taboos': 'Taboos',
    'planner.metadata.threat_level': 'Threat level',
    'planner.metadata.trade_routes': 'Trade routes',
    'planner.metadata.traits': 'Traits',
    'planner.metadata.trajectory': 'Trajectory',
    'planner.metadata.turning_points': 'Turning points',
    'planner.metadata.type': 'Type',
    'planner.metadata.value': 'Value',
    'planner.metadata.values': 'Values',
    'planner.metadata.vibe': 'Vibe',
    'planner.metadata.voice_sample': 'Voice sample',
    'planner.metadata.weather_patterns': 'Weather patterns',
    'planner.metadata.word_target': 'Word target',
    'planner.chip.role': 'role',
    'planner.chip.age': 'age',
    'planner.chip.type': 'type',
    'planner.chip.pov': 'POV',
    'planner.chip.setting': 'where',
    'planner.chip.population': 'pop.',
    'planner.chip.rules': 'rules',
    'planner.chip.motivations': 'wants',
    'planner.chip.flaws': 'flaws',
    'planner.chip.locked': 'beat',
    // [Removed: planner.family_tree.{dynasty,family} — kind labels
    // already live under planner.kind.dynasty / .family.]
    'planner.ai.title': 'AI assistant',
    'planner.ai.fab': 'AI',
    'planner.ai.pill_label': 'Assist',
    'planner.ai.quota_label': 'Today’s AI usage',
    'planner.ai.suggest': 'Advanced',
    'planner.ai.suggest_for_kind': 'Get an advanced suggestion',
    'planner.ai.run_freeform': 'Run freeform prompt',
    'planner.ai.freeform_placeholder': 'Describe what you need (e.g. "a mentor who challenges the protagonist").',
    'planner.ai.active_kind': 'Active category: {kind}',
    'planner.ai.no_kind': 'Pick a category to enable AI presets.',
    'planner.ai.no_presets': 'No presets for this category yet — use the free-form box below.',
    'planner.ai.result': 'Suggestion',
    'planner.ai.insert': 'Insert as new card',
    'planner.ai.dismiss': 'Dismiss',
    'planner.ai.pick_project': 'Pick a project to enable AI suggestions.',
    'planner.ai.quota_exceeded': 'You’ve hit your AI quota for today.',
    'planner.ai.failed': 'AI suggestion failed — try again in a moment.',
    'planner.ai.kind_armed': 'AI ready for {kind} — open the assistant on the right.',
    'planner.ai.try_preset': 'Use the AI assistant on the right to get started in this category.',
    'planner.ai.suggested_title': 'Advanced {kind} suggestion',
    'planner.ai.preset.character_quick_sketch': 'Quick character sketch',
    'planner.ai.preset.character_foil': 'Generate a foil',
    'planner.ai.preset.character_voice_sample': 'Voice sample',
    'planner.ai.preset.character_backstory': 'Backstory paragraph',
    'planner.ai.preset.character_secret': 'Hidden secret',
    'planner.ai.preset.city_describe': 'Describe in sensory detail',
    'planner.ai.preset.region_expand': 'Expand the region',
    'planner.ai.preset.magic_rules_design': 'Design magic rules',
    'planner.ai.preset.magic_cost': 'Pick a magic cost',
    'planner.ai.preset.technology_rules_design': 'Design tech rules',
    'planner.ai.preset.plot_raise_stakes': 'Raise the stakes',
    'planner.ai.preset.plot_twist': 'Suggest a plot twist',
    'planner.ai.preset.scene_punchy_opener': 'Punchy opener',
    'planner.ai.preset.scene_transition': 'Bridge to next scene',
    'planner.toast.saved': 'Saved.',
    'planner.toast.save_failed': 'Save failed — try again.',
    'planner.toast.trashed': 'Moved to trash (30-day window to restore).',
    'planner.toast.delete_failed': 'Delete failed — try again.',
    'planner.toast.conflict': 'Someone else edited this card — refreshed to the latest.',
    'planner.toast.snippet_saved': 'Saved to your snippet library.',
    'planner.toast.snippet_failed': 'Snippet save failed — try again.',
    'planner.toast.load_failed': 'Planner unavailable — try refreshing.',
    'planner.toast.preset_applied': 'Preset applied.',
    'planner.toast.preset_failed': 'Preset apply failed — try again.',
    'planner.confirm.delete': 'Move this card to trash? You can restore it within 30 days.',
    'planner.confirm.delete_title': 'Move this card to trash?',
    'planner.confirm.delete_action': 'Move to trash',
    'planner.snippets.confirm.delete_title': 'Delete this snippet?',
    'planner.snippets.confirm.delete_message': 'This snippet will be permanently removed from your library. Cards already imported into projects keep working.',
    'planner.map.clear_confirm_title': 'Remove the current map?',
    'planner.map.clear_action': 'Remove map',
    'planner.image.prompt_placeholder': 'Describe the image to generate…',
    'planner.image.generate': 'Generate',
    'planner.image.hint': 'Upload a file or generate one — your choice.',
    'planner.image.generated': 'Image generated and attached.',
    'planner.image.failed': 'Image generation failed — try a different prompt.',
    'planner.image.quota_exceeded': "You've used today's image generations.",
    'planner.image.upload_cta': 'Upload an image',
    'planner.image.upload_hint': 'PNG, JPEG, WebP, or GIF.',
    'planner.image.upload_button': 'Upload file',
    'planner.image.replace_cta': 'Replace',
    'planner.image.uploading': 'Uploading…',
    'planner.image.uploaded': 'Image uploaded.',
    'planner.image.upload_failed': "Image upload failed — try again.",
    'planner.image.remove': 'Remove image',
    // --- In-page chapter reader (text-to-speech). The reader
    // narrates the active chapter, highlights the current sentence,
    // and rolls into the next chapter when one finishes.
    'reader.button.open': 'Open the reader',
    'reader.button.play': 'Read aloud',
    'reader.button.pause': 'Pause reading',
    'reader.button.resume': 'Resume reading',
    'reader.button.stop': 'Stop',
    'reader.button.prev': 'Previous sentence',
    'reader.button.next': 'Next sentence',
    'reader.unsupported': 'Your browser does not support read-aloud yet.',
    'reader.panel.title': 'Reader',
    'reader.panel.status.idle': 'Ready — pick where to start.',
    'reader.panel.status.playing': 'Reading…',
    'reader.panel.status.paused': 'Paused.',
    'reader.label.speed': 'Speed',
    'reader.label.voice': 'Narrator',
    'reader.label.markers': 'Jump within this chapter',
    'reader.label.chapters': 'Chapters',
    'reader.marker.beginning': 'Beginning',
    'reader.marker.end': 'End',
    'reader.marker.chapter': 'Chapter {n}',
    // Install-help banner — surfaced when the picker fell into
    // degraded mode (no voice matches the chapter's language).
    // Short and instruction-only; we don't want writers reading a
    // tutorial, we want them closing the panel and getting back
    // to writing.
    'reader.install.title': 'No {language} voice installed',
    'reader.install.body': "The reader is using your default voice, which will mispronounce {language}. To get a proper {language} voice on this device:",
    'reader.install.dismiss': 'Hide this hint',
    // Instructions stay deliberately generic — specific menu paths
    // break across OS versions, locales, and OEM skins. We name
    // the right Settings app for the platform and tell the writer
    // to use its built-in search for stable terms ("voices",
    // "spoken content", "text-to-speech"). Then restart the
    // browser so the new voice is exposed to the Web Speech API.
    'reader.install.steps.windows': 'Open Windows Settings and use its search bar to look for "voices" or "speech". Find the section for adding voices in your target language. Once it finishes downloading, restart your browser.',
    'reader.install.steps.mac': 'Open System Settings and search for "spoken content" or "voices". Pick a voice in your target language and let macOS download it, then reload this page.',
    'reader.install.steps.ios': 'Open the Settings app and search for "voices" or "spoken content". Find your target language, choose a voice and download it. Switch back to your browser.',
    'reader.install.steps.android': 'Open Android Settings and search for "text-to-speech". Open your active engine and look for an option to install or update voice data — exact wording varies by phone manufacturer.',
    'reader.install.steps.linux': "Install a text-to-speech engine package that includes voices in your target language. ``espeak-ng`` and ``speech-dispatcher`` with ``mbrola`` voices are common starting points; your distribution's package manager handles the rest.",
    'reader.install.steps.generic': "Open your device's settings and search for \"voices\" or \"text-to-speech\". Look for an option to install a voice in your target language, then reload this page.",
    // Plumescript Narrator — branded label for the canonical voice
    // pick (Aria/Sylvie/Samantha/Amélie depending on platform +
    // locale). When the writer is on the canonical voice we apply a
    // per-voice storytelling tuning (slightly slower + warmer pitch)
    // automatically; their slider override always wins.
    'reader.plumescript_narrator': 'Plumescript Narrator',
    'reader.plumescript_badge_tooltip': "Curated narrator pick tuned for storytelling — your sliders always override it.",
    'reader.plumescript_narrator_hint': "Picked for storytelling on this device — tweak the speed any time.",
    // Backend TTS tier (Plumescript Pro — Polly Generative voices,
    // streamed from the AI service rather than the browser's local
    // speechSynthesis). Tier toggle copy + voice picker labels +
    // status messages for synth, polling, and cache hits.
    // Personal voice clone settings panel — upload reference clip,
    // manage existing samples, surface in the Reader's Pro tier.
    'settings.personal_voice.title': 'Personal voice',
    'settings.personal_voice.subtitle': 'Record a reference clip and the narrator can read your work back in your own voice. Uses your daily narration quota.',
    'settings.personal_voice.open': 'Open voice samples',
    'settings.personal_voice.open_hint': 'Record a new sample, rename or delete existing samples.',
    'settings.personal_voice.your_samples': 'Your voice samples',
    'settings.personal_voice.empty': 'No samples yet. Add one below to unlock the Personal narrator in the Reader.',
    'settings.personal_voice.loading': 'Loading samples…',
    'settings.personal_voice.add_title': 'Add a new sample',
    'settings.personal_voice.add_hint': "Record 10–20 seconds of clean speech (one voice, minimal background). We'll clone your timbre for future chapters.",
    'settings.personal_voice.audio_hint': 'No file selected. Drop in a .wav, .mp3 or .webm.',
    'settings.personal_voice.default_name': 'Sample',
    'settings.personal_voice.confirm_delete': 'Delete this voice sample? This cannot be undone.',
    'settings.personal_voice.field.reference_text': 'What you said in the clip',
    'settings.personal_voice.field.reference_text_optional': '(optional)',
    'settings.personal_voice.field.reference_text_placeholder': 'Leave blank — we\'ll transcribe the clip for you',
    'settings.personal_voice.field.reference_text_hint': 'Typing the exact words gives the best voice clone. Leave blank and we\'ll auto-transcribe the clip on save (adds ~30 s).',
    'settings.personal_voice.field.name': 'Name (optional)',
    'settings.personal_voice.field.name_placeholder': 'e.g. Studio mic, Quiet room',
    'settings.personal_voice.field.audio': 'Audio clip',
    'settings.personal_voice.action.pick_file': 'Choose audio file',
    'settings.personal_voice.action.record': 'Record',
    'settings.personal_voice.action.stop_recording': 'Stop recording',
    'settings.personal_voice.action.clear': 'Clear',
    'settings.personal_voice.action.save': 'Save sample',
    'settings.personal_voice.action.saving': 'Saving…',
    'settings.personal_voice.action.saved': 'Saved',
    'settings.personal_voice.action.rename': 'Rename',
    'settings.personal_voice.action.save_name': 'Save name',
    'settings.personal_voice.action.cancel': 'Cancel',
    'settings.personal_voice.action.delete': 'Delete sample',
    'settings.personal_voice.error.load': "Samples unavailable. Try again.",
    'settings.personal_voice.error.submit': "Sample not saved. Check your connection and try again.",
    'settings.personal_voice.error.file_too_large': 'That file is too large. Keep it under 10 MB.',
    'settings.personal_voice.error.mic_denied': "Microphone access was denied. Allow it in your browser settings, or upload an audio file instead.",
    'settings.personal_voice.error.record_unsupported': "Recording isn't available in this browser. Upload an audio file instead.",
    'settings.personal_voice.error.record_failed': "Recorder failed to start. Try again or upload a file.",
    'settings.personal_voice.disabled.no_audio': 'Record or upload an audio sample first.',
    'settings.personal_voice.disabled.no_reference_text': 'Add reference text so the system knows what was spoken.',
    // Planner AI-Update — fills empty fields + drift-corrects non-
    // empty fields against the planner + chapter state. Surfaced as
    // a button at the top of ModuleDrawer.
    'planner.ai_update.button': 'Update with AI',
    'planner.ai_update.running': 'Updating…',
    'planner.ai_update.applied': 'AI applied — {filled} filled, {updated} synced. Review and Save.',
    'planner.ai_update.no_change': 'AI had nothing to add — your module is already aligned with the planner.',
    'planner.ai_update.failed': "Planner unavailable. Try again.",
    'planner.ai_update.quota_exceeded': 'Daily planner-AI limit reached. Upgrade or try again tomorrow.',
    'planner.ai_update.badge.filled': 'AI filled',
    'planner.ai_update.badge.updated': 'AI synced',
    'planner.ai_update.badge.filled_tooltip': 'AI suggested this value to fill an empty field. Edit or Save to keep.',
    'planner.ai_update.badge.updated_tooltip': "AI suggested this update so the module matches what your chapters have established. Edit or Save to keep.",
    'planner.ai_update.hint': 'Reads your planner structure, sibling modules, and the chapters that mention this entry — then fills empty fields and refreshes existing ones. Counts toward your daily AI quota.',
    'planner.ai_update.per_field_hint': 'Tap the ✨ next to any field to focus the AI on just that field.',
    'planner.ai_update.improve_field': 'Improve this field with AI',
    'planner.ai_update.field_improved': 'AI refined this field. Review and Save.',
    'planner.ai_update.no_context': 'Add some planner content or chapter summaries first — the AI has nothing to ground its suggestions in.',
    'planner.ai_update.diff.was': 'Was:',
    'planner.ai_update.diff.empty': 'empty',
    'planner.ai_update.diff.more': 'more',
    'planner.ai_update.diff.less': 'less',
    'reader.tier.label': 'Narration',
    'reader.tier.browser': 'Standard',
    'reader.tier.browser_hint': "Uses the voices already on this device — instant, free, works offline.",
    'reader.tier.pro': 'Pro',
    'reader.tier.pro_hint': "Studio-grade narration. Counts toward your daily narration quota.",
    'reader.tier.personal': 'My voice',
    'reader.tier.personal_hint': "Your own voice, cloned from a Settings sample. Counts toward your daily narration quota.",
    'reader.tier.pro_preview.title': 'Studio-grade narration',
    'reader.tier.pro_preview.body':
      'Pro voices add natural prosody, accent, and pacing far beyond your device’s built-in synth. Available on the Premium plan.',
    'reader.tier.pro_preview.sample':
      '"Au sommet de la colline, le vent murmurait des histoires anciennes — et Ragaboum écoutait, le cœur battant."',
    'reader.tier.pro_preview.cta_premium': 'Upgrade to Premium',
    'reader.tier.pro_preview.cta_login': 'Sign in to listen',
    'reader.personal.download.aria': 'Download narration audio',
    'reader.personal.download.title': 'Save this narration as MP3',
    'reader.personal.label': 'Voice sample',
    'reader.personal.empty': "Upload a sample in Settings → Personal voice to unlock this tier.",
    'reader.personal.tip.proper_nouns': 'Tip: foreign-looking proper nouns (e.g. "Fuego") may be pronounced in their native accent. To force the cloned voice\'s native accent, spell them phonetically (e.g. "Fwego").',
    'reader.personal.bg.badge.title': 'Generating your narration…',
    'reader.personal.bg.badge.hint': 'feel free to navigate away — we’ll let you know',
    'reader.personal.bg.badge.return': 'Return to the reader',
    'reader.personal.bg.badge.cancel': 'Cancel this generation',
    'reader.personal.bg.toast.ready': 'Your personal narration is ready — tap the reader to play.',
    'reader.personal.bg.toast.timeout': 'Narration interrupted after 20 min. Tap retry.',
    'reader.personal.bg.toast.failed': "Narration generation failed — please try again.",
    'reader.personal.bg.toast.quota': "You've used today's personal-narrator quota.",
    'reader.personal.bg.pending.title': 'Generating in background',
    'reader.personal.bg.pending.subtitle': "We'll keep working even if you leave this page. A toast will let you know when it's ready.",
    'reader.personal.bg.ready.title': 'Your narration is ready',
    'reader.personal.bg.ready.subtitle': 'Press play to listen.',
    'reader.personal.bg.failed.timeout': 'Narration timed out after 20 min. Tap retry.',
    'reader.personal.bg.failed.generic': "Narration generation failed. Please try again.",
    'reader.personal.bg.failed.retry': 'Retry',
    'reader.personal.bg.cancel': 'Cancel',
    'reader.personal.bg.dismiss': 'Dismiss',
    'reader.pro.label': 'Pro voice',
    'reader.pro.loading_voices': "Loading voices…",
    'reader.pro.no_voices': "No Pro voices available right now.",
    'reader.pro.no_locale_match': "No Pro voice matches this chapter's language — showing all voices. Quality may suffer.",
    'reader.pro.no_locale_match_plan': "No Pro voice for this chapter's language is included in your plan — showing your plan's full voice set.",
    'reader.pro.plan_locked': "Voices for this language need a higher plan. Upgrade to unlock the studio narrators.",
    'reader.pro.sample.cta': "Try a Pro voice once — free",
    'reader.pro.sample.pending': "Unlocking your sample…",
    'reader.pro.sample.granted': "✓ Premium unlocked for 10 minutes. Hit play to hear the Pro voice.",
    'reader.pro.sample.unavailable': "Sample isn't available right now — the upgrade link still works.",
    'reader.pro.download.label': "Download chapter as MP3",
    'reader.pro.download.started': "Download starting — your chapter audiobook is on the way.",
    'reader.pro.download.failed': "Chapter download failed. Try again.",
    'reader.pro.download.quota_exhausted': "Daily download limit reached. Resets at midnight ET; Premium gets more.",
    'reader.pro.engine.generative': "Studio (Generative) — most lifelike per sentence",
    'reader.pro.engine.neural': "Live (Neural) — natural prosody",
    'reader.pro.engine.long_form': "Storyteller (Long-form) — recommended for chapter narration",
    'reader.pro.engine.standard': "Standard — fastest, lowest quality",
    'reader.pro.engine.legend': "Storyteller is recommended for chapter playback — coherent multi-minute pacing built for audiobook narration. Studio is the most lifelike per sentence but resets between chunks. Live is the everyday choice; Standard is the fastest.",
    'profile.tier.premium_title': "Premium member",
    'profile.tier.basic_title': "Member",
    'profile.role.admin_title': "Plumescript Admin · official account",
    'profile.role.admin_aria': "Plumescript Admin",
    'profile.role.team_title': "Plumescript team · official account",
    'profile.role.team_aria': "Plumescript team member",
    'settings.appearance.theme.warm': 'Warm',
    'settings.appearance.theme.warm.desc': 'Oxblood walnut and terracotta tan lit by burnt sienna and mulled plum, over warm vellum cream — a russet-bound edition of a literary press, all red-orange-brown variants for maximum warmth.',
    'settings.appearance.theme.charbon': 'Charbon',
    'settings.appearance.theme.charbon.desc': "Charcoal Sketch — sooty charcoal-paper canvas with a hot-charcoal red CTA, aged-brass accent, and ivory-chalk ink. An artist's sketchpad in a lamplit studio: sooty paper under a warm hot-coal halo glowing in the bottom-right corner.",
    'settings.appearance.theme.ocean': 'Ocean',
    'settings.appearance.theme.ocean.desc': 'Deep Sea — mid-tone saturated blue canvas with a bright cerulean-wave CTA, reef-turquoise accent, and sea-foam ink. Submerged at mid-depth in tropical water: sunlight filters down from above (bright cerulean glow at the top), deeper blue fades in below. Confidently blue and dark, but not abyssal-near-black — an in-between depth.',
    'settings.appearance.theme.nature': 'Nature',
    'settings.appearance.theme.nature.desc': "Mossbank — deep forest-green canvas dappled with sunlight through canopy, autumn-bronze accent, and parchment-cream ink. A writer's cabin in the deep woods.",
    'settings.appearance.theme.premium_locked': '{label} is part of Premium — upgrade in Settings → Billing to unlock.',
    'reader.pro.status.loading': "Preparing audio…",
    'reader.pro.status.long_wait': "Still rendering ({seconds}s)…",
    'reader.pro.status.cached': "Cached — instant playback",
    'reader.pro.status.cached_short': "Cached",
    'reader.pro.status.cached_tooltip': "We already had this narration prepared — instant playback, no synthesis cost.",
    'reader.pro.status.error': "Narrator unavailable. Try again.",
    'reader.pro.status.quota': "Daily narration limit reached. Upgrade or try again tomorrow.",
    'reader.pro.status.now_playing': "Now playing",
    'reader.pro.status.paused': "Paused",
    'reader.pro.stage.queued': "Queued",
    'reader.pro.stage.synthesizing': "Synthesizing voice…",
    'reader.pro.stage.almost_ready': "Almost ready — finishing up",
    'reader.pro.scrubber': "Seek narration position",
    // --- Catalogue kind labels (94). Backend ships each as
    // ``label_i18n: planner.kind.<id>``; without these entries the
    // t() humanize fallback produced English-ish strings in BOTH
    // EN and FR. Adding them grounds each language. Some IDs reuse
    // the same English word (e.g. ``custom``); FR disambiguates.
    'planner.kind.universe': 'Universe',
    'planner.kind.dimension': 'Dimension',
    'planner.kind.kingdom': 'Kingdom',
    'planner.kind.region': 'Region',
    'planner.kind.country': 'Country',
    'planner.kind.city': 'City',
    'planner.kind.district': 'District',
    'planner.kind.location': 'Location',
    'planner.kind.map': 'Map',
    'planner.kind.climate': 'Climate',
    'planner.kind.calendar': 'Calendar',
    'planner.kind.environmental_condition': 'Environmental condition',
    'planner.kind.culture': 'Culture',
    'planner.kind.religion': 'Religion',
    'planner.kind.mythology': 'Mythology',
    'planner.kind.language': 'Language',
    'planner.kind.custom': 'Custom',
    'planner.kind.tradition': 'Tradition',
    'planner.kind.law': 'Law',
    'planner.kind.economy': 'Economy',
    'planner.kind.trade': 'Trade',
    'planner.kind.political_system': 'Political system',
    'planner.kind.organisation': 'Organisation',
    'planner.kind.faction': 'Faction',
    'planner.kind.alliance': 'Alliance',
    'planner.kind.war': 'War',
    'planner.kind.treaty': 'Treaty',
    'planner.kind.catastrophe': 'Catastrophe',
    'planner.kind.festival': 'Festival',
    'planner.kind.character': 'Character',
    'planner.kind.family': 'Family',
    'planner.kind.dynasty': 'Dynasty',
    'planner.kind.relationship': 'Relationship',
    'planner.kind.social_class': 'Social class',
    'planner.kind.race': 'Race',
    'planner.kind.power': 'Power',
    'planner.kind.ability': 'Ability',
    'planner.kind.skill': 'Skill',
    'planner.kind.creature': 'Creature',
    'planner.kind.fauna': 'Fauna',
    'planner.kind.flora': 'Flora',
    'planner.kind.ecosystem': 'Ecosystem',
    'planner.kind.natural_resource': 'Natural resource',
    'planner.kind.magic_system': 'Magic system',
    'planner.kind.scientific_system': 'Scientific system',
    'planner.kind.technology': 'Technology',
    'planner.kind.world_rule': 'World rule',
    'planner.kind.energy': 'Energy',
    'planner.kind.resource_system': 'Resource system',
    'planner.kind.balance': 'Balance',
    'planner.kind.note': 'Note',
    'planner.kind.chronology': 'Chronology',
    'planner.kind.event': 'Event',
    'planner.kind.plot': 'Plot',
    'planner.kind.conflict': 'Conflict',
    'planner.kind.story_arc': 'Story arc',
    'planner.kind.beat': 'Beat',
    'planner.kind.chapter_plan': 'Chapter plan',
    'planner.kind.quest': 'Quest',
    'planner.kind.mission': 'Mission',
    'planner.kind.dialogue': 'Dialogue',
    'planner.kind.scene': 'Scene',
    'planner.kind.alternative_ending': 'Alternative ending',
    'planner.kind.narrative_variant': 'Narrative variant',
    'planner.kind.narrative_flow': 'Narrative flow',
    'planner.kind.object': 'Object',
    'planner.kind.artefact': 'Artefact',
    'planner.kind.magical_object': 'Magical object',
    'planner.kind.inventory': 'Inventory',
    'planner.kind.equipment': 'Equipment',
    'planner.kind.weapon': 'Weapon',
    'planner.kind.armor': 'Armor',
    'planner.kind.vehicle': 'Vehicle',
    'planner.kind.document': 'Document',
    'planner.kind.letter': 'Letter',
    'planner.kind.in_world_book': 'In-world book',
    'planner.kind.archive': 'Archive',
    'planner.kind.journal': 'Journal',
    'planner.kind.quote': 'Quote',
    'planner.kind.proverb': 'Proverb',
    'planner.kind.riddle': 'Riddle',
    'planner.kind.code': 'Code',
    'planner.kind.philosophical_inspiration': 'Philosophical inspiration',
    'planner.kind.illustration': 'Illustration',
    'planner.kind.concept_art': 'Concept art',
    'planner.kind.visual_reference': 'Visual reference',
    'planner.kind.inspiration': 'Inspiration',
    'planner.kind.music': 'Music',
    'planner.kind.sound_ambiance': 'Sound ambiance',
    'planner.kind.media': 'Media',
    'planner.kind.tag': 'Tag',
    'planner.kind.keyword': 'Keyword',
    'planner.kind.collection': 'Collection',
    'planner.kind.statistic': 'Statistic',
    // --- Enumerated option values surfaced by metadata pickers
    // (role of a character, sub-type of a location, threat level
    // of a creature, etc.). Backend serves these as ``label_i18n``
    // on each option entry.
    'planner.options.role.protagonist': 'Protagonist',
    'planner.options.role.antagonist': 'Antagonist',
    'planner.options.role.mentor': 'Mentor',
    'planner.options.role.ally': 'Ally',
    'planner.options.role.foil': 'Foil',
    'planner.options.role.supporting': 'Supporting',
    'planner.options.role.minor': 'Minor',
    'planner.options.role.rival': 'Rival',
    'planner.options.role.deuteragonist': 'Deuteragonist',
    'planner.options.role.love interest': 'Love interest',
    'planner.options.location.poi': 'Point of interest',
    'planner.options.location.dungeon': 'Dungeon',
    'planner.options.location.interior': 'Interior',
    'planner.options.location.building': 'Building',
    'planner.options.location.natural': 'Natural',
    'planner.options.threat.harmless': 'Harmless',
    'planner.options.threat.wary': 'Wary',
    'planner.options.threat.dangerous': 'Dangerous',
    'planner.options.threat.deadly': 'Deadly',
    'planner.options.rel.friendship': 'Friendship',
    'planner.options.rel.love': 'Love',
    'planner.options.rel.rivalry': 'Rivalry',
    'planner.options.rel.enemy': 'Enemy',
    'planner.options.rel.kin': 'Kin',
    'planner.options.rel.mentor': 'Mentor',
    'planner.options.conflict.cosmic': 'Cosmic',
    'planner.options.conflict.environmental': 'Environmental',
    'planner.options.conflict.internal': 'Internal',
    'planner.options.conflict.interpersonal': 'Interpersonal',
    'planner.options.conflict.societal': 'Societal',
    'planner.options.locked.no': 'No',
    'planner.options.locked.yes': 'Yes',
    'goal.kanban.stage.todo': 'To do',
    'goal.kanban.stage.pending': 'Pending',
    'goal.kanban.stage.progress': 'In progress',
    'goal.kanban.stage.done': 'Done',
    'goal.kanban.add_task': 'Add task',
    'goal.kanban.empty': 'No tasks here yet.',
    'goal.kanban.empty.title': 'No tasks here',
    'goal.kanban.empty.description': 'Add a task to this column to start organising your work.',
    'goal.kanban.loading': 'Loading tasks…',
    'goal.kanban.error.title_required': 'A title is required to save the task.',
    'goal.kanban.error.save_failed': 'Something went wrong. Try again.',
    'goal.kanban.modal.title': 'Title',
    'goal.kanban.modal.title_placeholder': 'Enter task title',
    'goal.kanban.modal.tags': 'Tags',
    'goal.kanban.modal.tags_placeholder': 'Enter tags separated by spaces',
    'goal.kanban.modal.progress': 'Progress',
    'goal.kanban.modal.description': 'Description',
    'goal.kanban.modal.description_placeholder': 'Enter task description',
    'goal.kanban.modal.due_date': 'Due date',
    'goal.kanban.modal.saving': 'Saving…',
    'goal.kanban.modal.save_task': 'Create task',
    'goal.kanban.modal.save_changes': 'Save changes',
    'goal.kanban.modal.close': 'Close',
    'goal.kanban.card.in_list': 'In list',
    'goal.kanban.card.due_date': 'Due date',
    'goal.kanban.card.edit': 'Edit card',
    'goal.kanban.card.delete': 'Delete card',
    'goal.kanban.card.no_deadline': 'No date',

    'image_generation.title': 'Generate image with AI',
    'image_generation.subtitle': 'Describe what you want, then optionally overlay clear text.',
    'image_generation.action.open': 'Generate with AI',
    'image_generation.prompt.label': 'Prompt',
    'image_generation.prompt.placeholder': 'A misty harbour at dusk, oil-painting style…',
    'image_generation.image_type.label': 'Type',
    'image_generation.image_type.book_cover': 'Book cover',
    'image_generation.image_type.chapter_cover': 'Chapter cover',
    'image_generation.image_type.custom': 'Custom',
    'image_generation.aspect.label': 'Aspect ratio',
    'image_generation.aspect.auto': 'Default for type',
    'image_generation.action.generate': 'Generate',
    'image_generation.action.regenerate': 'Regenerate',
    'image_generation.action.use': 'Use this image',
    'image_generation.action.cancel': 'Cancel',
    'image_generation.empty.title': 'No image yet',
    'image_generation.empty.description': 'Describe the cover, scene, or character you want, then click Generate to see a preview here. The model produces clear imagery only — any project info is added separately on the next step.',
    'image_generation.generating.title': 'Generating your image…',
    'image_generation.generating.description': 'This usually takes a few seconds.',
    'image_generation.overlay.toggle': 'Add project info on top of the image',
    'image_generation.overlay.title.label': 'Title',
    'image_generation.overlay.title.placeholder': 'Primary line (e.g. book title)',
    'image_generation.overlay.subtitle.label': 'Subtitle',
    'image_generation.overlay.subtitle.placeholder': 'Optional second line (e.g. author)',
    'image_generation.overlay.clear': 'Clear text',
    'image_generation.overlay.position.label': 'Position',
    'image_generation.overlay.position.top': 'Top',
    'image_generation.overlay.position.center': 'Center',
    'image_generation.overlay.position.bottom': 'Bottom',
    'image_generation.overlay.color.label': 'Text color',
    'image_generation.overlay.color.auto.toggle': 'Auto-match cover',
    'image_generation.overlay.color.auto.hint': 'Pick a contrasting tone based on the cover area under the text',
    'image_generation.overlay.background.toggle': 'Add background bar',
    'image_generation.overlay.background.mode.label': 'Backdrop',
    'image_generation.overlay.background.mode.gradient': 'Soft gradient',
    'image_generation.overlay.background.mode.solid': 'Solid bar',
    'image_generation.overlay.background.mode.none': 'None',
    'image_generation.overlay.background.color.label': 'Backdrop tone',
    'image_generation.toast.failed': 'Image generation failed. Please try again.',
    'image_generation.toast.empty_prompt': 'Please write a prompt first.',
    'image_generation.toast.applied': 'Image added.',
    'image_generation.clean.action': 'Clean any leftover text',
    'image_generation.clean.hint':
      'Detects letter-shaped regions and paints over them. No extra AI call, no extra cost.',
    'image_generation.toast.clean_applied': 'Image cleaned.',
    'image_generation.toast.clean_noop': 'Image already looks clean — nothing to remove.',
    'image_generation.toast.clean_failed': 'Image cleanup failed. Try again.',
    'image_generation.note.cost':
      'Each image is roughly a phone full charge of electricity plus a glass of cooling water at the data center. Generate one you intend to keep.',

    'games.page.title': 'Brain games for writers',
    'games.page.subtitle':
      'A handful of small word puzzles to warm up before a writing session. Plays in your browser, no account, no scoring leaderboard — just a quick reset.',
    'games.back': 'Back to games',
    'games.scramble.title': 'Word Scramble',
    'games.scramble.desc': 'Unscramble writing-themed words. Build a streak.',
    'games.typing.title': 'Typing Sprint',
    'games.typing.desc': 'Type a sentence in 30 seconds — measure your WPM and accuracy.',
    'games.rhyme.title': 'Rhyme Finder',
    'games.rhyme.desc': 'Find as many words as you can that rhyme with the target.',
    // LiteraryDevices + Charade kept here because the
    // components still ship in Games.jsx for a future "literary
    // curiosities" surface to mount. The keys are reachable
    // via the t() humanize fallback either way; explicit
    // entries keep the labels clean once they land elsewhere.
    'games.literary_devices.title': 'Literary Devices',
    'games.literary_devices.desc': 'Read a short sentence and pick the device it uses.',
    'games.charade.title': 'Charade',
    'games.charade.desc': 'Solve a short writer-themed riddle in one word.',

    'curiosities.page.title': 'A craft study',
    'curiosities.page.kicker': 'Practice the craft',
    'curiosities.page.subtitle': 'Small daily lessons that sharpen the writer’s ear. A few minutes, repeated often — that’s how craft compounds.',
    'curiosities.philosophy': 'Practice deepens craft. Each lesson takes a few minutes and is designed to be replayed — the goal isn’t to finish them, it’s to keep noticing.',
    'curiosities.lesson_kicker': 'Lesson {number}',
    'curiosities.duration_short': '~ 3 min',
    'curiosities.start_lesson': 'Begin the lesson',
    'curiosities.more_soon': 'More lessons coming as the surface grows.',
    'curiosities.back': 'Back to lessons',

    // Shared quiz UI
    'curiosities.quiz.next': 'Next',
    'curiosities.quiz.skip': 'Skip',
    'curiosities.quiz.wrong_with_answer': 'Not quite — the answer was: {answer}',

    // Lesson 1: Literary Devices (already wired in Games)
    'curiosities.lesson.literary_devices.title': 'Literary devices',
    'curiosities.lesson.literary_devices.curriculum': 'Read a sentence; name the device behind it. Builds the vocabulary you reach for when revising — metaphor, personification, alliteration, oxymoron, and more.',

    // Lesson 2: Active vs Passive Voice
    'curiosities.lesson.active_voice.title': 'Active vs passive voice',
    'curiosities.lesson.active_voice.curriculum': 'Given a passive-voice sentence, pick the rewrite that flips it into the stronger, more direct active voice. The single highest-leverage revision habit.',
    'curiosities.lesson.active_voice.prompt': 'Pick the active-voice rewrite:',

    // Lesson 3: First lines
    'curiosities.lesson.first_lines.title': 'First lines',
    'curiosities.lesson.first_lines.curriculum': 'Recognise the novel from its opening sentence. Trains the ear for the rhythm and concision that great openings rely on.',
    'curiosities.lesson.first_lines.prompt': 'Which novel does this open?',

    // Lesson 4: Verb strength
    'curiosities.lesson.verb_strength.title': 'Stronger verbs',
    'curiosities.lesson.verb_strength.curriculum': 'Replace a verb-plus-adverb with a single, more vivid verb. The fastest way to tighten flat prose.',
    'curiosities.lesson.verb_strength.prompt': 'Pick the strongest single-verb rewrite:',

    // Lesson 5: Show, don’t tell
    'curiosities.lesson.show_dont_tell.title': 'Show, don’t tell',
    'curiosities.lesson.show_dont_tell.curriculum': 'Given a flat “telling” sentence, pick the rewrite that conveys the same state through action or sensation.',
    'curiosities.lesson.show_dont_tell.prompt': 'Pick the version that shows rather than tells:',

    // Lesson 6: Point of view
    'curiosities.lesson.pov.title': 'Point of view',
    'curiosities.lesson.pov.curriculum': 'Identify the narrative point of view at work in a sentence. First, second, third-limited, third-omniscient — each makes a different promise to the reader.',
    'curiosities.lesson.pov.prompt': 'Which point of view is this sentence written in?',
    'curiosities.pov.first': 'First person',
    'curiosities.pov.second': 'Second person',
    'curiosities.pov.third_limited': 'Third person — limited',
    'curiosities.pov.third_omni': 'Third person — omniscient',

    // Lesson 7: Punctuation rhythm
    'curiosities.lesson.punctuation.title': 'Punctuation rhythm',
    'curiosities.lesson.punctuation.curriculum': 'Pick the punctuation mark that best fits a specific rhetorical effect. Pacing, emphasis, voice — all carried by these tiny marks.',
    'curiosities.lesson.punctuation.prompt': 'Which mark fits best?',
    'curiosities.punct.comma': 'Comma  ,',
    'curiosities.punct.commas': 'Commas  , ,',
    'curiosities.punct.semicolon': 'Semicolon  ;',
    'curiosities.punct.colon': 'Colon  :',
    'curiosities.punct.em_dash': 'Em-dash  —',
    'curiosities.punct.em_dashes': 'Em-dashes  — —',
    'curiosities.punct.parentheses': 'Parentheses  ( )',
    'curiosities.punct.period': 'Period  .',
    'curiosities.punct.ellipsis': 'Ellipsis  …',
    'curiosities.punct.exclamation': 'Exclamation mark  !',
    'curiosities.punct.question_mark': 'Question mark  ?',

    // Lesson 8: Synonym precision
    'curiosities.lesson.synonym_precision.title': 'Synonym precision',
    'curiosities.lesson.synonym_precision.curriculum': 'Pick the synonym that fits the context most precisely. Trains the ear for register, intensity, and connotation — synonyms are never truly interchangeable.',
    'curiosities.lesson.synonym_precision.prompt': 'Which word fits the blank most precisely?',

    // Lesson 9: Dialogue tags
    'curiosities.lesson.dialogue_tags.title': 'Dialogue tags',
    'curiosities.lesson.dialogue_tags.curriculum': 'Pick the tag verb that fits the line of speech. Plain “said” is usually the best invisible choice; specific verbs earn their place only when they add something the dialogue doesn’t.',
    'curiosities.lesson.dialogue_tags.prompt': 'Which dialogue tag fits best?',

    // Lesson 10: Closing lines
    'curiosities.lesson.closing_lines.title': 'Closing lines',
    'curiosities.lesson.closing_lines.curriculum': 'Recognise the novel from its closing sentence. Pairs with First lines — opening sets the contract, closing pays it off.',
    'curiosities.lesson.closing_lines.prompt': 'Which novel does this close?',
    // --- Doc-derived lessons (Ecriture_en_jeux.docx) ---
    // Lessons 11-14 extend the craft sequence with ChoiceQuiz
    // pools. Each lesson's pool sprinkles in a game-flavoured entry
    // (tongue-twister, charade, riddle, cipher) so the original
    // game spirit persists as a variant of the craft question.
    'curiosities.lesson.rhythm.title': 'Rhythm',
    'curiosities.lesson.rhythm.curriculum': "Short sentences run. Long sentences linger. The length of a sentence is a speed dial — three-word jabs put the reader inside a chase; one unbroken breath holds them inside a slow realisation. The fastest way to write a scene that drags is to fill it with sentences that are all the same size. Vary the length on purpose, and the page starts to breathe.",
    'curiosities.lesson.rhythm.prompt': 'Which version matches the moment?',
    'curiosities.lesson.concrete_nouns.title': 'Concrete nouns',
    'curiosities.lesson.concrete_nouns.curriculum': "The noun half of the strong-words pair (lesson 04 covered verbs). Bird → cardinal; tree → oak; drink → gin over the cube. Specificity is what separates description that fades from description that stays — the named thing earns its place.",
    'curiosities.lesson.concrete_nouns.prompt': 'Pick the version that names the thing:',
    'curiosities.lesson.subtext.title': 'Subtext',
    'curiosities.lesson.subtext.curriculum': "Lesson 09 covered dialogue attribution. This one goes one layer deeper: what the line actually MEANS. Subtext is the gap between the words and the intent — sarcasm, deflection, a half-concealed verdict. Trains the writer to hear the second voice.",
    'curiosities.lesson.subtext.prompt': 'Pick what is really being said:',
    'curiosities.lesson.scene_vs_summary.title': 'Scene vs summary',
    'curiosities.lesson.scene_vs_summary.curriculum': "The structural macro lever. Lesson 10 closes a chapter; this one asks how scenes connect within and between them. Scene = dramatise, live the moment. Summary = compress, age the routine. The lever that separates 200-page bloat from 200-page propulsion.",
    'curiosities.lesson.scene_vs_summary.prompt': 'Pick the right mode for this moment:',
    // Apply-to-your-writing extension — closes the gap between puzzle
    // (the mean) and craft (the purpose). Each lesson 11-14 mounts
    // ``ApplyToYourWriting`` underneath its puzzle UI with a
    // lesson-specific apply_prompt + a curated master example.
    'games.anagram.title': 'Anagram Hunt',
    'games.anagram.desc': 'How many real words can you make from the letter rack?',
    'games.chain.title': 'Word Chain',
    'games.chain.desc': 'Each word starts with the last letter of the one before it.',
    'games.memory.title': 'Memory Lines',
    'games.memory.desc': 'Read a sentence — then retype it from memory.',
    'games.synonym.title': 'Synonym Match',
    'games.synonym.desc': 'Pair each word with its synonym, against the clock.',
    'games.rsvp.title': 'Speed Read',
    'games.rsvp.desc': 'Words flash one at a time — answer a comprehension prompt.',
    'games.cubes.title': 'Story Cubes',
    'games.cubes.desc': 'Roll three random words and write a sentence using all of them.',

    'games.common.score': 'Score',
    'games.common.streak': 'Streak',
    'games.common.streak_value': 'Streak: {streak}',
    'games.common.skip': 'Skip',
    'games.common.add': 'Add',
    'games.common.start': 'Start',
    'games.common.submit': 'Submit',
    'games.common.play_again': 'Play again',
    'games.common.try_again': 'Try again',
    'games.common.reset': 'Reset',
    'games.common.new_round': 'New round',

    'games.feedback.correct': 'Correct',
    'games.feedback.added': 'Added',
    'games.feedback.linked': 'Linked',
    'games.feedback.try_again': 'Try again',

    'games.scramble.placeholder': 'Type the unscrambled word',
    'games.scramble.check': 'Check',

    'games.typing.time_left': 'Time left',
    'games.typing.hint': 'Type the sentence below',
    'games.typing.placeholder': 'Start typing here…',
    'games.typing.result_summary': 'WPM {wpm} · {acc}% accuracy',

    'games.literary_devices.prompt': 'Which device does this sentence use?',
    'games.literary_devices.feedback.correct': 'Correct.',
    'games.literary_devices.feedback.wrong': 'Not quite — this is {device}.',
    'games.literary_devices.device.metaphor': 'Metaphor',
    'games.literary_devices.device.simile': 'Simile',
    'games.literary_devices.device.personification': 'Personification',
    'games.literary_devices.device.alliteration': 'Alliteration',
    'games.literary_devices.device.hyperbole': 'Hyperbole',
    'games.literary_devices.device.onomatopoeia': 'Onomatopoeia',
    'games.literary_devices.device.oxymoron': 'Oxymoron',
    'games.literary_devices.device.repetition': 'Repetition',
    'games.literary_devices.device.irony': 'Irony',
    'games.literary_devices.device.anaphora': 'Anaphora',
    'games.literary_devices.device.assonance': 'Assonance',
    'games.literary_devices.device.litotes': 'Litotes',
    'games.literary_devices.device.metonymy': 'Metonymy',
    'games.literary_devices.device.understatement': 'Understatement',
    'games.literary_devices.device.rhetorical_question': 'Rhetorical question',
    'games.literary_devices.device.symbolism': 'Symbolism',
    'games.literary_devices.device.allusion': 'Allusion',

    'games.charade.prompt': 'What am I?',
    'games.charade.placeholder': 'Your answer…',
    'games.charade.reveal': 'Reveal',
    'games.charade.feedback.correct': 'Solved.',
    'games.charade.feedback.wrong': 'Not it — try another word.',
    'games.charade.feedback.revealed': 'The answer was “{answer}”.',

    'games.rhyme.target_label': 'Target word',
    'games.rhyme.found': 'Found',
    'games.rhyme.hint_label': 'words rhyming with the target',
    'games.rhyme.placeholder': 'A word that rhymes with “{word}”',
    'games.rhyme.new_target': 'New target',
    'games.rhyme.hint': 'Hint',
    'games.rhyme.abandon': 'Give up',
    'games.rhyme.complete': 'You found them all.',
    'games.rhyme.error.same_word': 'A word doesn\'t rhyme with itself.',
    'games.rhyme.error.invalid': 'Letters only, 2+ chars.',
    'games.rhyme.error.duplicate': 'Already found.',
    'games.rhyme.error.not_a_rhyme': "Doesn't rhyme — or isn't on the curated list.",

    'games.anagram.found': 'Found',
    'games.anagram.hint': 'Make words from the letters below',
    'games.anagram.placeholder': 'Type a word…',
    'games.anagram.new_rack': 'New rack',
    'games.anagram.error.duplicate': 'Already found',
    'games.anagram.error.invalid_letters': 'Letters not in rack',
    'games.anagram.error.not_in_list': "Not in this puzzle's word list",

    'games.chain.status': 'Time: {remaining}s · Next letter: {letter}',
    'games.chain.so_far': 'Chain so far:',
    'games.chain.placeholder': 'A word starting with “{letter}”',
    'games.chain.error.letters_only': 'Letters only, 2+ chars',
    'games.chain.error.duplicate': 'Already used',
    'games.chain.error.wrong_start': 'Must start with “{letter}”',
    'games.chain.error.not_a_word': "That doesn't look like a real word — try another.",

    'games.memory.intro': 'A sentence will flash for a few seconds. Read it, then retype it from memory.',
    'games.memory.placeholder': 'Type the sentence you just saw…',
    'games.memory.accuracy': 'Accuracy',
    'games.memory.match_kind': 'Character-level match',
    'games.memory.original': 'Original:',
    'games.memory.yours': 'Yours:',
    'games.memory.empty': '(empty)',
    'games.memory.helps_remember': 'This helps remember:',
    'games.memory.phase.memorize': 'Memorize',
    'games.memory.phase.recall': 'From memory',

    'games.synonym.pairs': 'Pairs',
    'games.synonym.hint': 'Click a word, then its synonym',
    'games.synonym.done_in': 'Done in {seconds}s',

    'games.rsvp.speed': 'Speed:',
    'games.rsvp.wpm_option': '{value} wpm',
    'games.rsvp.intro': 'Words flash one at a time. After the sentence, pick the word that appeared.',
    'games.rsvp.question': 'Which word was in the sentence?',
    'games.rsvp.correct': 'Correct.',
    'games.rsvp.incorrect': 'Not quite — the answer was “{word}”.',
    'games.rsvp.another': 'Another sentence',

    'games.cubes.intro': 'Write a sentence (or two) using all three words.',
    'games.cubes.placeholder': 'Once upon a lantern, the detective…',
    'games.cubes.word_count': '{count} words',
    'games.cubes.all_used': '· all three used ✓',
    'games.cubes.reroll': 'Reroll',
    'games.cubes.validate': 'Check words',
    'games.cubes.validating': 'Checking…',
    'games.cubes.unknown_intro': "Words that aren't in our dictionary (still your call to keep them):",
  },

  fr: {
    'nav.notifications': 'Notifications',
    'nav.search.placeholder': 'Rechercher…',
    'nav.search.placeholder.guest': 'Trouver un livre ou un auteur…',
    'nav.editor': 'Éditeur',
    'nav.profile': 'Profil',
    'nav.settings': 'Paramètres',
    'nav.help': 'Aide',
    'nav.contact': 'Nous joindre',
    'nav.curiosities': 'Curiosités',
    'nav.games': 'Jeux pour l’esprit',
    'nav.messages': 'Messages',
    'nav.login': 'Connexion',
    'nav.register': 'Inscription',
    'nav.logout': 'Déconnexion',
    'nav.open_menu': 'Ouvrir le menu',
    'nav.close_menu': 'Fermer le menu',
    'nav.open_search': 'Ouvrir la recherche',
    'nav.close_search': 'Fermer la recherche',
    'nav.overview': 'Tableau de bord',
    'nav.view_profile': 'Voir mon profil',
    'search.section.users': 'Écrivains',
    'search.section.books': 'Livres',
    'search.section.posts': 'Discussions',
    'search.tag.book': 'Livre',
    'search.tag.discussion': 'Discussion',
    'search.no_results': 'Aucun résultat pour « {query} ».',
    'publish.toast.published_promoted': 'Publié — votre projet est désormais public pour que les lecteurs puissent le trouver.',
    'nav.manage_work': 'Gérer mes travaux',
    'nav.browse': 'Parcourir le catalogue',
    'nav.forum': 'Poser une question',
    'nav.goals': 'Définir et suivre mes objectifs',
    'nav.profile_menu': 'Menu du profil',

    'landing.hero.headline_prefix': 'Libérez vos',
    'landing.hero.headline_accent': 'mots',
    'landing.hero.description':
      "Un espace pour écrire, partager et lire des histoires en cours. Écrivez en solo ou à plusieurs en temps réel, publiez vos chapitres au fur et à mesure, et explorez ce que la communauté est en train de créer.",
    'landing.cta.start_writing': 'Commencer à écrire',
    'landing.cta.reading': 'Lire',

    'notifications.all_caught_up': 'Tout est à jour',
    'notifications.mark_all_read': 'Tout marquer lu',
    'notifications.refresh': 'Rafraîchir',
    'notifications.empty.title': 'Aucune notification',
    'notifications.empty.description':
      'Les mises à jour de collaboration, objectifs atteints et messages système apparaîtront ici.',
    'notifications.empty.unread.title': 'Aucune non lue',
    'notifications.empty.unread.description':
      'Vous êtes à jour — repassez sur Toutes pour revoir une notification.',
    'notifications.open': 'Ouvrir',
    'notifications.mark_read': 'Marquer lu',
    'notifications.delete': 'Supprimer',
    'notifications.unread': 'Non lue',
    'notifications.unread_count': '{count} non lues',
    'notifications.error.load': 'Notifications indisponibles. Réessayez.',
    'notifications.filter.all': 'Toutes',
    'notifications.filter.unread': 'Non lues',

    'analysis.title': "Rapport d'écriture",
    'analysis.subtitle': 'Score, statistiques et conseils personnalisés.',
    'analysis.refresh': 'Rafraîchir',
    'analysis.analysing': 'Analyse…',
    'analysis.empty':
      'Commencez à écrire puis cliquez sur « Rafraîchir » pour votre premier rapport.',
    'analysis.error': "Service d'analyse indisponible. Réessayez.",
    'analysis.score': 'score',
    'analysis.section.glance': 'En bref',
    'analysis.section.suggestions': 'Conseils personnalisés',
    'analysis.section.top_words': 'Mots les plus fréquents',
    'analysis.section.tone': 'Tonalité',
    'analysis.metric.words': 'Mots',
    'analysis.metric.sentences': 'Phrases',
    'analysis.metric.avg_sentence': 'Phrase moyenne',
    'analysis.metric.avg_sentence_unit': 'mots',
    'analysis.metric.unique': 'Uniques',
    'analysis.metric.reading': 'Lecture',
    'analysis.metric.reading_unit': 'min',
    'analysis.metric.flesch': 'Lisibilité',
    'analysis.close': "Fermer l'analyse",
    'analysis.gauge.aria': 'Score global : {score} sur 100',
    'analysis.bar.readability': 'Lisibilité',
    'analysis.bar.sentence_rhythm': 'Rythme des phrases',
    'analysis.bar.vocabulary': 'Variété du vocabulaire',
    'analysis.bar.style': 'Style',
    'analysis.sentiment.positive': 'positive',
    'analysis.sentiment.neutral': 'neutre',
    'analysis.sentiment.negative': 'négative',
    'analysis.sentiment.line': 'La tonalité est {label} (polarité {polarity}).',
    'analysis.empty_state':
      "Commencez à écrire puis cliquez sur « Rafraîchir » pour générer votre premier rapport.",
    'analysis.placeholder.title': 'Aucun rapport',
    'analysis.placeholder.description':
      "Écrivez un paragraphe dans l'éditeur, puis lancez le rapport pour voir le score, les statistiques et des conseils personnalisés.",

    'sprint.title': "Sprint d'écriture",
    'sprint.subtitle': 'Session chronométrée avec compteur de mots',
    'sprint.start': 'Lancer le sprint',
    'sprint.stop': 'Arrêter & sauvegarder',
    'sprint.pause': 'Pause',
    'sprint.resume': 'Reprendre',
    'sprint.again': 'Nouveau sprint',
    'sprint.paused': 'En pause',
    'sprint.close': 'Fermer le sprint',
    'sprint.collapse': 'Réduire le sprint',
    'sprint.expand': 'Agrandir le sprint',
    'sprint.custom_label': 'Minutes personnalisées',
    'sprint.goal_label': 'Objectif de mots (facultatif)',
    'sprint.goal_progress': 'Objectif',
    'sprint.duration': 'Durée',
    'sprint.minutes_short': 'min',
    'sprint.words_suffix': 'mots',
    'sprint.wpm_suffix': 'mpm',
    'sprint.toast.done':
      'Sprint terminé — {words} mots en {minutes} min ({wpm} mpm).',
    'sprint.toast.goal_hit':
      "Objectif atteint — {words} mots en {minutes} min. Bien joué !",
    'sprint.toast.empty': 'Sprint terminé. Aucun mot compté ce tour.',

    'focus.title': 'Mode concentration',
    'focus.exit': 'Quitter (Échap)',
    'focus.words': 'mots',
    'focus.stat.elapsed': 'écoulé',
    'focus.stat.session': 'cette session',
    'focus.toggle.typewriter': 'Mode machine à écrire',
    'focus.toggle.hide_stats': 'Masquer les stats',
    'focus.toggle.show_stats': 'Afficher les stats',
    'focus.toast.session_summary':
      '{words} mots écrits en {elapsed}.',

    'editor.analyse': 'Analyser',
    'editor.coherence': 'Cohérence',
    'editor.focus': 'Focus',
    'editor.sprint': 'Sprint',
    'editor.export': 'Exporter',
    'editor.export.txt': 'Texte brut (.txt)',
    'editor.export.md': 'Markdown (.md)',
    'editor.export.html': 'Page web (.html)',
    'editor.export.pdf': 'Document PDF (.pdf)',
    'editor.export.docx': 'Document Word (.doc)',
    'editor.export.png': 'Image (.png)',
    'editor.export.fallback_name': 'chapitre',
    'editor.export.html_lang': 'fr',
    'editor.mention.title': 'Mentionner un élément du plan',
    'editor.mention.placeholder_hint': 'tapez un nom',
    'editor.mention.no_match': 'Aucun module correspondant — continuez à taper ou ouvrez le plan pour en créer un.',
    'editor.mention.empty': "Ce projet n'a encore aucun élément de plan — ouvrez le plan pour en ajouter.",
    'editor.mention.loading': "Chargement de la bibliothèque…",
    'editor.mention.nav': 'naviguer',
    'editor.mention.insert': 'insérer',
    'editor.mention.cancel': 'annuler',
    'editor.export.toast_failed': "Export du chapitre impossible. Réessayez.",
    'project_export.action.open': 'Exporter le projet',
    'project_export.title': 'Exporter le projet',
    'project_export.close': 'Fermer',
    'project_export.cancel': 'Annuler',
    'project_export.download': 'Télécharger',
    'project_export.busy': 'Préparation…',
    'project_export.intro': 'Regroupe les {count} chapitres de ce projet pour téléchargement.',
    'project_export.no_chapters': "Ce projet n'a pas encore de chapitre — ajoutez-en au moins un pour exporter.",
    'project_export.mode_label': "Comment souhaitez-vous empaqueter l'export ?",
    'project_export.mode.merged.title': 'Document unique',
    'project_export.mode.merged.description': 'Fusionner tous les chapitres en un seul fichier (PDF, Word, HTML, …).',
    'project_export.mode.zip.title': 'Archive ZIP',
    'project_export.mode.zip.description': 'Un fichier par chapitre dans une archive .zip.',
    'project_export.format_label': 'Format',
    'project_export.format.pdf': 'Document PDF (.pdf)',
    'project_export.format.doc': 'Document Word (.doc)',
    'project_export.format.html': 'Page web (.html)',
    'project_export.format.md': 'Markdown (.md)',
    'project_export.format.txt': 'Texte brut (.txt)',
    'project_export.format.png': 'Image (.png)',
    'project_export.options_label': 'Inclure dans le document',
    'project_export.option.cover': 'Page de couverture',
    'project_export.option.cover_hint': 'Page de titre avec le titre du projet, la description et l’auteur.',
    'project_export.option.toc': 'Table des matières',
    'project_export.option.toc_hint': 'Une liste des chapitres avant le contenu.',
    'project_export.option.credits': 'Page de crédits',
    'project_export.option.credits_hint': 'Propriétaire et collaborateurs avec leurs rôles.',
    'project_export.option.pagination': 'Sauts de page entre les sections',
    'project_export.option.pagination_hint': 'Commencer chaque chapitre sur une nouvelle page (et garder couverture, sommaire et crédits sur leurs propres pages). Désactivez pour un document continu.',
    'project_export.fallback_project': 'projet',
    'project_export.chapter_word': 'Chapitre',
    'project_export.contents': 'Sommaire',
    'project_export.credits': 'Crédits',
    'project_export.by': 'par',
    'project_export.role.owner': 'Propriétaire',
    'project_export.role.editor': 'Éditeur',
    'project_export.role.reader': 'Lecteur',
    'project_export.toast.success': 'Export prêt — consultez vos téléchargements.',
    'project_export.toast.failed': "Export du projet impossible. Réessayez.",
    'project_export.toast.no_chapters': 'Ajoutez au moins un chapitre avant l’export.',
    'editor.sync.connected': 'Synchronisé',
    'editor.sync.connecting': 'Synchronisation…',
    'editor.sync.offline': 'Hors ligne',
    'editor.sync.tooltip.connected': 'Modifications enregistrées.',
    'editor.sync.tooltip.connecting': 'Connexion au serveur collaboratif…',
    'editor.sync.tooltip.offline': 'Vous êtes hors ligne. Les modifications seront synchronisées dès le retour de la connexion.',

    'editor.presence.editing_now': 'En train d’écrire',
    'editor.presence.solo': 'En solo',
    'editor.presence.solo_tooltip': 'Vous écrivez en solo pour le moment.',
    'editor.presence.aria_one': '{count} autre personne en train d’écrire',
    'editor.presence.aria_many': '{count} autres personnes en train d’écrire',

    'history.title': 'Activité',
    'history.subtitle': 'Publications, partages, restaurations',
    'history.refresh': "Actualiser l'activité",
    'history.loading': "Chargement de l'activité récente…",
    'history.empty.title': "Rien ne s'est encore passé ici",
    'history.empty.hint':
      "Publier ce récit, inviter un co-auteur, restaurer un brouillon antérieur — chaque changement sur ce projet apparaîtra ici au fil du temps.",
    'history.error': "Activité indisponible",
    'history.relative.just_now': "à l'instant",
    'history.relative.minutes': 'il y a {n} min',
    'history.relative.hours': 'il y a {n} h',
    'history.relative.days': 'il y a {n} j',

    'editor.menubar.font': 'Police',
    'editor.menubar.zoom': 'Zoom',
    'editor.menubar.line_spacing': 'Interligne',
    'editor.menubar.lists': 'Listes',
    'editor.menubar.lists.bullet': 'Liste à puces',
    'editor.menubar.lists.ordered': 'Liste numérotée',
    'editor.menubar.lists.task': 'Liste de tâches',
    'editor.menubar.indent_right': 'Augmenter le retrait',
    'editor.menubar.indent_left': 'Diminuer le retrait',

    'editor.menubar.image': 'Insérer une image',
    'editor.menubar.image.tab.upload': 'Téléverser',
    'editor.menubar.image.tab.url': 'Depuis une URL',
    'editor.menubar.image.upload.cta': 'Choisir un fichier',
    'editor.menubar.image.upload.hint': 'PNG, JPG, GIF ou WebP, jusqu’à 10 Mo.',
    'editor.menubar.image.uploading': 'Téléversement…',
    'editor.menubar.image.url.label': "URL de l'image",
    'editor.menubar.image.url.placeholder': 'https://exemple.com/photo.jpg',
    'editor.menubar.image.alt': 'Texte alternatif',
    'editor.menubar.image.alt.placeholder': 'Décrivez l’image pour les lecteurs d’écran',
    'editor.menubar.image.insert': 'Insérer l’image',
    'editor.menubar.image.cancel': 'Annuler',
    'editor.menubar.image.error.invalid_url': 'Entrez une URL d’image en http(s).',
    'editor.menubar.image.error.upload': 'Téléversement impossible. Réessayez.',
    'editor.menubar.image.error.too_large': 'Fichier trop volumineux (10 Mo max).',

    'editor.menubar.link.title.insert': 'Insérer un lien',
    'editor.menubar.link.title.edit': 'Modifier le lien',
    'editor.menubar.link.url': 'Adresse web',
    'editor.menubar.link.url.placeholder': 'https://exemple.com',
    'editor.menubar.link.text': 'Texte affiché',
    'editor.menubar.link.text.placeholder': 'Optionnel — l’URL est utilisée par défaut',
    'editor.menubar.link.insert': 'Insérer le lien',
    'editor.menubar.link.remove': 'Supprimer le lien',
    'editor.menubar.link.cancel': 'Annuler',
    'editor.menubar.link.error.invalid': 'Entrez une URL en http(s).',

    'settings.appearance.title': 'Apparence',
    'settings.appearance.theme': 'Thème',
    'settings.appearance.label': 'Thème de couleurs',
    'settings.appearance.current': 'Thème (actuellement {label}). Cliquez pour changer.',
    'settings.appearance.theme.light': 'Clair',
    'settings.appearance.theme.light.desc': 'Net, sobre, reposant pour les yeux.',
    'settings.appearance.theme.dark': 'Sombre',
    'settings.appearance.theme.dark.desc': "Surfaces à faible éblouissement pour les longues sessions d'écriture.",
    'settings.appearance.theme.playful': 'Ludique',
    'settings.appearance.theme.playful.desc': "Identité Plumescript — ombres « stickers », palette bonbon et typographie ronde Baloo.",
    'settings.appearance.theme.hc': 'Clarté',
    'settings.appearance.theme.hc.desc':
      "Fond blanc pur, texte presque noir, anneaux de focus vifs et liens systématiquement soulignés. Contraste WCAG AAA.",
    'settings.appearance.theme.hc-dark': 'Contraste',
    'settings.appearance.theme.hc-dark.desc':
      "Fond noir pur, texte presque blanc, anneaux de focus vifs et liens systématiquement soulignés. Contraste WCAG AAA.",
    'settings.appearance.subtitle':
      "Choisissez un thème adapté à votre façon d'écrire.",
    'settings.appearance.theme_hint':
      "Enregistré sur cet appareil. Les changements s'appliquent immédiatement à toute l'application.",

    'settings.user.title': 'Compte',
    'settings.user.subtitle': 'Mettez à jour vos informations personnelles.',
    'settings.user.personal_info': 'Informations personnelles',
    'settings.user.firstname': 'Prénom',
    'settings.user.lastname': 'Nom',
    'settings.user.email': 'E-mail',
    'settings.user.language': 'Langue',
    'settings.user.language_placeholder': 'Langue',
    'settings.user.notifications': 'Notifications',
    'settings.user.report_alerts': 'Recevoir des mises à jour hebdomadaires sur votre contenu.',
    'settings.user.collab_alerts': 'Recevoir des notifications pour les demandes de collaboration.',
    'settings.user.publication_alerts': 'Recevoir une notification quand un chapitre ou un projet est publié.',
    'settings.notifications.subtitle':
      "Choisissez les types d'alertes que vous recevez. Les changements sont sauvegardés automatiquement.",
    'settings.notifications.publication.label': 'Publications',
    'settings.notifications.publication.description':
      'Nouveaux chapitres dans les livres que vous lisez, projets publics que vous suivez.',
    'settings.notifications.collaboration.label': 'Collaboration',
    'settings.notifications.collaboration.description':
      'Invitations, commentaires et réponses sur les projets partagés.',
    'settings.notifications.report.label': 'Rapports et alertes',
    'settings.notifications.report.description':
      "Résumé hebdomadaire de vos statistiques d'écriture et signalements éventuels.",
    'settings.notifications.save_failed':
      'Sauvegarde impossible — réessayez dans un instant.',

    'settings.engagement_emails.title': 'Rappels par courriel',
    'settings.engagement_emails.subtitle':
      "Messages personnalisés pour vous aider à terminer vos projets. Hebdomadaires par défaut — réglages détaillés via Gérer.",
    'settings.engagement_emails.summary.loading': 'Chargement des préférences…',
    'settings.engagement_emails.summary.enabled': 'Activés',
    'settings.engagement_emails.summary.disabled': 'Désactivés',
    'settings.engagement_emails.summary.quiet_hours': 'Silence {start}–{end}',
    'settings.engagement_emails.summary.categories': '{active} / {total} catégories',
    'settings.engagement_emails.summary.snoozed_until': "En pause jusqu'au {date}",
    'settings.engagement_emails.summary.manage': 'Gérer',
    'settings.engagement_emails.master.label': 'Recevoir les rappels par courriel',
    'settings.engagement_emails.master.description':
      "Désactivez pour couper tous les courriels ci-dessous. Les messages transactionnels (mot de passe, sécurité) continuent d'être envoyés.",
    'settings.engagement_emails.frequency.label': 'Fréquence',
    'settings.engagement_emails.frequency.description':
      "À quelle cadence regrouper vos rappels. Un même message ne part jamais deux fois dans la même fenêtre.",
    'settings.engagement_emails.frequency.option.never': 'Jamais',
    'settings.engagement_emails.frequency.option.daily': 'Quotidienne',
    'settings.engagement_emails.frequency.option.weekly': 'Hebdomadaire',
    'settings.engagement_emails.frequency.option.biweekly': 'Toutes les deux semaines',
    'settings.engagement_emails.frequency.option.monthly': 'Mensuelle',
    'settings.engagement_emails.category.writing_activity.label': "Activité d'écriture",
    'settings.engagement_emails.category.writing_activity.description':
      "Série en péril, objectif sous la cible, projet dormant — pour garder votre élan.",
    'settings.engagement_emails.category.planner_insights.label': 'Plan & cohérence',
    'settings.engagement_emails.category.planner_insights.description':
      "Accroche manquante, protagoniste sans fiche, structure à moitié remplie, rappel pour lancer une vérification de cohérence.",
    'settings.engagement_emails.category.community.label': 'Communauté',
    'settings.engagement_emails.category.community.description':
      'Commentaires non lus, nouveaux abonnés, réponses sur le forum, fils de discussion inactifs.',
    'settings.engagement_emails.category.inspiration.label': 'Inspiration & conseils',
    'settings.engagement_emails.category.inspiration.description':
      "Défi d'écriture hebdomadaire et préréglages IA mis en avant. Désactivé par défaut.",
    'settings.engagement_emails.category.billing.label': 'Compte & facturation',
    'settings.engagement_emails.category.billing.description':
      "Alertes de quota proche de la limite, étapes clés de votre abonnement. Les rappels de fin d'essai passent aussi par ce canal.",
    'settings.engagement_emails.quiet_hours.label': 'Heures silencieuses',
    'settings.engagement_emails.quiet_hours.description':
      "Aucun envoi pendant cette plage horaire dans votre fuseau local. Le prochain message attend la fenêtre suivante.",
    'settings.engagement_emails.quiet_hours.start': 'Début',
    'settings.engagement_emails.quiet_hours.end': 'Fin',
    'settings.engagement_emails.snooze.label': 'Mode vacances',
    'settings.engagement_emails.snooze.description':
      "Mettre tous les rappels en pause pour une période — utile avant un voyage ou pendant un sprint d'écriture.",
    'settings.engagement_emails.snooze.7d': '7 jours',
    'settings.engagement_emails.snooze.30d': '30 jours',
    'settings.engagement_emails.snooze.90d': '90 jours',
    'settings.engagement_emails.snooze.resume': 'Reprendre maintenant',
    'settings.engagement_emails.snooze.until': "En pause jusqu'au {date}",
    'settings.engagement_emails.open_tracking.label': "Nous aider à mesurer l'utilité",
    'settings.engagement_emails.open_tracking.description':
      "Ajoute un pixel invisible pour savoir si vos courriels sont ouverts. Pixel seul — aucune autre donnée collectée.",
    'settings.engagement_emails.preview.button': 'Aperçu du prochain courriel',
    'settings.engagement_emails.preview.modal_title': 'Aperçu — prochain rappel',
    'settings.engagement_emails.preview.sending': 'Composition en cours…',
    'settings.engagement_emails.preview.empty': "Rien à envoyer pour l'instant",
    'settings.engagement_emails.preview.empty_description':
      "Aucun signal ne correspond à votre état actuel. Continuez d'écrire — un rappel partira dès qu'il y aura quelque chose d'utile à dire.",
    'settings.engagement_emails.history.button': 'Voir les courriels déjà envoyés',
    'settings.engagement_emails.history.modal_title': 'Historique des envois',
    'settings.engagement_emails.history.loading': "Chargement de l'historique…",
    'settings.engagement_emails.history.empty_title': 'Aucun courriel envoyé',
    'settings.engagement_emails.history.empty_description':
      "Votre journal d'envois apparaîtra ici dès le premier courriel.",
    'settings.engagement_emails.history.no_subject': '(bienvenue)',
    'settings.engagement_emails.history.signal_count': '{count} rappel(s) dans ce courriel',
    'settings.engagement_emails.save_failed':
      'Sauvegarde impossible — réessayez dans un instant.',
    'settings.user.save': 'Enregistrer les modifications',
    'settings.user.save_success': 'Paramètres enregistrés.',
    'settings.user.danger_zone': 'Zone de danger',
    'settings.user.reset': 'Réinitialiser',

    'settings.security.title': 'Sécurité',
    'settings.security.subtitle': 'Mot de passe, vérification et connexions récentes.',
    'settings.security.verify_account': 'Vérifier votre compte',
    'settings.security.verify_subtitle':
      'Confirmez que vous contrôlez cette adresse e-mail pour pouvoir commenter, publier et démarrer des conversations.',
    'settings.security.change_password': 'Changer le mot de passe',
    'settings.security.change_password_subtitle':
      "Choisissez un nouveau mot de passe — un e-mail de confirmation vous sera envoyé après modification.",
    'settings.security.last_login': 'Dernière connexion',
    'settings.security.last_login_subtitle':
      'Date et lieu de votre dernière connexion.',
    'settings.security.last_login.when': 'Quand',
    'settings.security.last_login.where': 'Où',
    'settings.security.loading': 'Chargement…',
    'settings.security.unknown_date': 'Date de dernière connexion indisponible.',
    'settings.security.location_unknown': 'Emplacement inconnu',
    'settings.security.sign_out': 'Se déconnecter',

    'settings.verify.unverified_title': 'E-mail non vérifié',
    'settings.verify.unverified_subtitle':
      'Nous vous enverrons un code à 6 chiffres. Saisissez-le ici pour débloquer la publication et la messagerie.',
    'settings.verify.send_code': 'Envoyer le code de vérification',
    'settings.verify.resend': 'Envoyer un nouveau code',
    'settings.verify.cooldown': 'Attendez {seconds} s avant de réessayer',
    'settings.verify.code_label': 'Code de vérification à 6 chiffres',
    'settings.verify.confirm': 'Vérifier le compte',
    'settings.verify.sent':
      "Code de vérification envoyé. Pensez à vérifier votre dossier spam si vous ne le voyez pas. Expire dans 15 minutes.",
    'settings.verify.confirmed': 'Compte vérifié.',
    'settings.verify.invalid_token': 'Code invalide ou expiré. Demandez-en un nouveau.',
    'settings.verify.request_failed':
      "Envoi du code impossible. Réessayez dans une minute.",
    'settings.verify.confirm_failed':
      "Vérification du code impossible. Réessayez.",
    'settings.verify.rate_limited':
      'Trop de tentatives. Patientez une minute et réessayez.',
    'settings.verify.verified_title': 'E-mail vérifié',
    'settings.verify.verified_subtitle':
      'Vous pouvez commenter, publier et lancer des conversations.',
    'settings.verify.required_toast':
      'Vérifiez votre e-mail avant de publier ou commenter — Paramètres → Sécurité.',

    'settings.ai_usage.title': "Utilisation IA",
    'settings.ai_usage.subtitle':
      "Limites quotidiennes. Réinitialisation à minuit (heure de l'Est).",

    'settings.billing.title': 'Facturation',
    'settings.billing.subtitle': 'Abonnement, plan et paiements.',
    'settings.billing.plan': 'Plan',
    'settings.billing.current_subscription': 'Votre plan actuel',
    'settings.billing.per_month': '/mois',
    'settings.billing.plan_free': 'Gratuit',
    'settings.billing.plan_basic': 'Standard',
    'settings.billing.plan_premium': 'Premium',
    'settings.billing.badge_free': 'Gratuit',
    'settings.billing.badge_basic': 'Essentiel',
    'settings.billing.badge_premium': 'Pro',
    'settings.billing.description_free': "Outils d'écriture de base, gratuit à vie.",
    'settings.billing.description_basic': 'Limites IA quotidiennes plus élevées.',
    'settings.billing.description_premium': 'Limites IA quotidiennes les plus élevées.',
    'settings.billing.upgrade': 'Mettre à niveau',
    'settings.billing.manage': 'Gérer mon abonnement',
    'settings.billing.money_back.title': 'Satisfait ou remboursé sous 30 jours',
    'settings.billing.money_back.body':
      'Remboursement intégral sous 30 jours. Sans formulaire.',
    'settings.billing.payment_method': 'Mode de paiement',
    'settings.billing.no_card': 'Aucune carte enregistrée',
    'settings.billing.card_placeholder': 'Numéro de carte',
    'settings.billing.manage_cards': 'Gérer les cartes',
    'settings.billing.cancel_plan': 'Annuler le plan',
    'settings.billing.history.title': 'Historique de facturation',
    'settings.billing.history.subtitle':
      'Paiements passés, factures et remboursements de votre abonnement.',
    'settings.billing.history.cta': 'Voir les factures',

    'settings.member_since.title': 'Membre depuis',
    'settings.member_since.subtitle':
      "Date à laquelle vous avez rejoint la communauté d'écriture.",
    'settings.member_since.unknown': 'Inconnu',
    'settings.billing.payments_disabled': "Les paiements ne sont pas activés ici.",
    'settings.billing.payment_modal_title': 'Ajouter un moyen de paiement',
    'settings.billing.billing_modal_title': "Changer d'offre",
    'settings.billing.invoices_modal_title': 'Factures passées',
    'settings.billing.cancel_modal_title': "Annuler l'abonnement",
    'settings.billing.billing_disabled': "La facturation n'est pas disponible ici.",
    'settings.billing.free_tier_only_title': "Vous êtes sur l'offre gratuite",
    'settings.billing.free_tier_only_subtitle':
      "Les abonnements payants ne sont pas encore disponibles — vous avez un accès complet à l'offre gratuite et aux quotas IA ci-dessus.",

    'settings.password.current_placeholder': 'Mot de passe actuel',
    'settings.password.new_placeholder': 'Nouveau mot de passe',
    'settings.password.confirm_placeholder': 'Confirmer le nouveau mot de passe',
    'settings.password.strength.0': 'Très faible',
    'settings.password.strength.1': 'Faible',
    'settings.password.strength.2': 'Moyen',
    'settings.password.strength.3': 'Correct',
    'settings.password.strength.4': 'Bon',
    'settings.password.strength.5': 'Excellent',
    'settings.password.criteria.title': 'Critères de sécurité',
    'settings.password.criteria.min_length': 'Au moins 8 caractères',
    'settings.password.criteria.needs_upper': 'Une majuscule',
    'settings.password.criteria.needs_lower': 'Une minuscule',
    'settings.password.criteria.needs_digit': 'Un chiffre',
    'settings.password.criteria.needs_symbol': 'Un caractère spécial (!@#$%^&*)',
    'settings.password.criteria.contains_identity': 'Ne doit pas contenir votre identifiant',
    'settings.password.mismatch': 'Les deux mots de passe ne correspondent pas.',
    'settings.password.weak_error': 'Le mot de passe ne respecte pas les critères de sécurité.',
    'settings.password.invalid_current': 'Le mot de passe actuel est incorrect.',
    'settings.password.generic_error': 'Une erreur est survenue. Réessayez.',
    'settings.password.updated': 'Mot de passe mis à jour.',
    'settings.password.show': 'Afficher le mot de passe',
    'settings.password.hide': 'Masquer le mot de passe',
    'settings.password.submit': 'Mettre à jour le mot de passe',
    'settings.password.forgot.title': 'Mot de passe oublié ?',
    'settings.password.forgot.description': 'Un lien de réinitialisation sera envoyé à {email}.',
    'settings.password.forgot.description_no_email': 'Connectez-vous pour recevoir un lien de réinitialisation.',
    'settings.password.forgot.cta': 'Recevoir un lien de réinitialisation',
    'settings.password.forgot.sent':
      'Si un compte existe avec cette adresse, un lien a été envoyé. Vérifiez aussi vos courriers indésirables. Le lien expire dans 1 heure.',
    'settings.password.forgot.no_email_error': 'Aucune adresse e-mail associée à ce compte.',
    'settings.password.forgot.send_failed': "Envoi impossible pour le moment. Réessayez.",

    'settings.payment.title': 'Modes de paiement',
    'settings.payment.subtitle': 'Ajoutez ou mettez à jour vos cartes.',
    'settings.payment.saved_title': 'Cartes enregistrées',
    'settings.payment.default_badge': 'Par défaut',
    'settings.payment.expires': 'Expire',
    'settings.payment.selected_options': 'Options pour la carte sélectionnée :',
    'settings.payment.set_default': 'Définir par défaut',
    'settings.payment.delete': 'Supprimer',
    'settings.payment.confirm_delete': 'Confirmer ?',
    'settings.payment.no_methods_title': 'Aucun mode de paiement enregistré.',
    'settings.payment.no_methods_description': 'Ajoutez une carte pour activer les paiements.',
    'settings.payment.add_title': 'Nouvelle carte',
    'settings.payment.stripe_not_ready': 'Chargement du formulaire de paiement…',
    'settings.payment.delete_error': 'Suppression impossible. Réessayez.',
    'settings.payment.update_error': 'Mise à jour du mode de paiement impossible.',
    'settings.payment.processing': 'Traitement…',
    'settings.payment.submit': 'Enregistrer la nouvelle carte',
    'settings.payment.field.number': 'Numéro de carte',
    'settings.payment.field.expiry': 'Expiration',
    'settings.payment.field.cvc': 'CVC',

    'settings.transaction.title': 'Choisissez votre plan',
    'settings.transaction.subtitle': 'Choisissez celui qui vous convient.',
    'settings.transaction.basic.name': 'Standard',
    'settings.transaction.basic.description': 'Abonnement Standard',
    'settings.transaction.basic.features':
      'Éditeur de texte|Publication et gestion de contenu|Forums de discussion et messagerie',
    'settings.transaction.premium.name': 'Premium',
    'settings.transaction.premium.description': 'Abonnement Premium',
    'settings.transaction.premium.features':
      'Fonctionnalités de base|Thèmes personnalisés|Avatars et badges exclusifs|Accès prioritaire aux nouveautés',
    'settings.transaction.period': '/mois',
    'settings.transaction.billing_address': 'Adresse de facturation',
    'settings.transaction.methods_title': 'Modes de paiement enregistrés',
    'settings.transaction.ssl_note': 'Paiement sécurisé SSL 256 bits',
    'settings.transaction.no_methods': 'Aucun mode de paiement enregistré.',
    'settings.transaction.processing': 'Traitement…',
    'settings.transaction.pay_now': 'Payer maintenant',
    'settings.transaction.promo_code.label': 'Code promo (facultatif)',
    'settings.transaction.promo_code.placeholder': 'Entrez un code',
    'settings.transaction.promo_code.invalid': 'Ce code promo n’est pas valide ou a été révoqué. Laissez le champ vide pour continuer sans code.',
    'settings.transaction.current_plan': 'Plan actuel',

    'settings.invoices.title': 'Historique de facturation',
    'settings.invoices.subtitle':
      'Paiements passés, reçus et PDF téléchargeables de votre abonnement.',
    'settings.invoices.loading': 'Chargement des factures…',
    'settings.invoices.error': 'Factures indisponibles. Réessayez.',
    'settings.invoices.empty.title': 'Aucune facture',
    'settings.invoices.empty.description':
      "Aucune facture pour l'instant — vos factures apparaîtront ici dès qu'un plan payant sera actif.",
    'settings.invoices.untitled': 'Facture {id}',
    'settings.invoices.download_pdf': 'Télécharger le PDF',
    'settings.invoices.view_hosted': 'Ouvrir la facture en ligne',
    'settings.invoices.status.paid': 'Payée',
    'settings.invoices.status.open': 'En attente',
    'settings.invoices.status.uncollectible': 'Irrécouvrable',
    'settings.invoices.status.void': 'Annulée',
    'settings.invoices.status.draft': 'Brouillon',
    'settings.transaction.already_subscribed': 'Vous avez déjà ce plan.',
    'settings.transaction.address_required': "Renseignez l'adresse de facturation.",
    'settings.transaction.payment_failed': 'Paiement refusé. Essayez une autre carte.',
    'settings.transaction.terms_note': 'En procédant au paiement, vous acceptez nos conditions générales de vente.',
    'settings.transaction.success_title': 'Paiement confirmé, {name}.',
    'settings.transaction.success_subscription': 'Votre abonnement a été activé avec succès.',
    'settings.transaction.success_payment': 'Votre paiement a été effectué avec succès.',
    'settings.transaction.success_thanks': 'Merci pour votre abonnement.',
    'settings.transaction.discover': "Découvrez d'autres fonctionnalités",

    'settings.cancel.title': "Annuler l'abonnement",
    'settings.cancel.warning': "Vous êtes sur le point d'annuler votre abonnement.",
    'settings.cancel.current_plan': 'Plan actuel',
    'settings.cancel.price': 'Prix',
    'settings.cancel.period_end': 'Fin de période',
    'settings.cancel.error': "Annulation de l'abonnement impossible. Réessayez.",
    'settings.cancel.cancel': 'Garder mon abonnement',
    'settings.cancel.canceling': 'Annulation en cours…',
    'settings.cancel.confirm': "Confirmer l'annulation",

    'error_boundary.title': "Quelque chose s'est mal passé.",
    'error_boundary.body':
      "La page a rencontré une erreur inattendue. Vous pouvez réessayer, ou revenir dans un instant.",
    'error_boundary.retry': 'Réessayer',

    'error_page.code_label': 'Erreur',
    'error_page.title': "Quelque chose s'est mal passé.",
    'error_page.body':
      "Cette page n'a pas pu se charger. L'équipe a été prévenue — réessayez ou revenez à l'accueil.",
    'error_page.retry': 'Réessayer',
    'error_page.go_home': "Retour à l'accueil",

    'not_found.eyebrow': '404',
    'not_found.title': 'Page introuvable',
    'not_found.body':
      "La page que vous cherchiez n'existe pas ou a été déplacée.",
    'not_found.go_home': "Retour à l'accueil",

    'content_filter.error.generic': "Cette valeur n'est pas autorisée. Essayez-en une autre.",
    'content_filter.error.too_short': 'Cette valeur est trop courte.',
    'content_filter.error.too_long': 'Cette valeur est trop longue.',
    'content_filter.error.invalid_chars':
      'Utilisez uniquement des lettres, chiffres, tirets, points et tirets bas.',
    'content_filter.error.reserved':
      'Ce nom est réservé. Choisissez-en un autre.',
    'content_filter.error.pii':
      "Pas d'adresses e-mail ni de numéros de téléphone dans ce champ.",
    'content_filter.error.url_like':
      "Les liens ne sont pas autorisés ici.",
    'content_filter.error.profanity':
      'Évitez les grossièretés.',
    'content_filter.error.slur':
      "Ce mot n'est pas autorisé.",

    'settings.transaction.free.name': 'Gratuit',
    'settings.transaction.free.tagline': 'Tout pour écrire. Pour toujours.',
    'settings.transaction.basic.tagline': 'Outils IA quotidiens, marge confortable.',
    'settings.transaction.premium.tagline': "IA complète, à chaque chapitre.",
    'settings.transaction.popular': 'Le plus populaire',
    'settings.transaction.forever': 'Pour toujours',
    'settings.transaction.daily_quotas': 'Quotas IA quotidiens',
    'settings.transaction.no_paywalls': 'Toutes les fonctions à chaque niveau. Le payant lève uniquement les limites quotidiennes.',
    'settings.transaction.billing_period_aria': 'Période de facturation',
    'settings.transaction.currency_aria': 'Devise',
    'settings.transaction.billing.monthly': 'Mensuel',
    'settings.transaction.billing.annual': 'Annuel',
    'settings.transaction.annual_total': 'Facturé {total} par an',
    'settings.transaction.always_included_label': 'Inclus à chaque niveau',
    'settings.transaction.always_included':
      "Écriture & projets illimités|Publication et partage de chapitres|Forum, commentaires & messages|Lecture du catalogue|Tous les thèmes & avatars|Notifications & édition du profil",
    'settings.transaction.feature.daily': '{n}/jour',
    'settings.transaction.feature.ai_grammar': "vérifications grammaticales",
    'settings.transaction.feature.ai_inspiration': "recherches d'inspiration",
    'settings.transaction.feature.ai_autocomplete': "suggestions d'auto-complétion",
    'settings.transaction.feature.ai_image': "générations d'images",
    'settings.transaction.feature.dictionary': "passes de correction orthographique",
    'settings.transaction.feature.ai_planner_text': "brouillons IA du plan",
    'settings.transaction.feature.ai_planner_image': "illustrations du plan",
    'settings.transaction.feature.ai_summarize': "résumés de chapitre",
    'settings.transaction.feature.ai_tts_download': "téléchargements audio",
    'settings.transaction.feature.ai_coherence': "vérifications de cohérence",
    'settings.transaction.feature.ai_critique': "critiques de projet",
    'settings.transaction.feature.ai_tropes': "suggestions de tropes",

    'settings.quota.today': "Utilisation IA aujourd'hui",
    'settings.quota.loading': "Chargement de l'utilisation…",
    'settings.quota.unavailable': "Utilisation temporairement indisponible.",
    'settings.quota.reset_note': "Réinitialisation quotidienne à minuit (heure de l'Est).",

    'paywall.toast.trial_cta': 'Ou essayez Premium gratuitement pendant 7 jours',
    'paywall.toast.trial_pending': 'Démarrage de votre essai…',
    'paywall.toast.trial_granted': '✓ Essai démarré. Quotas Premium débloqués pendant 7 jours.',
    'paywall.toast.trial_already_used': 'Vous avez déjà utilisé votre essai gratuit.',
    'paywall.toast.trial_unavailable': 'Essai gratuit indisponible pour le moment. Vous pouvez toujours vous abonner directement.',
    'paywall.toast.money_back': 'Satisfait ou remboursé sous 30 jours, tous forfaits payants.',
    'paywall.toast.title': "Vous avez utilisé votre IA gratuite du jour",
    'paywall.toast.body':
      "{used} sur {limit} {feature} utilisés aujourd'hui. Passez à un forfait supérieur, ou revenez demain.",
    'paywall.toast.cta': 'Voir les forfaits →',

    'common.close': 'Fermer',
    'common.save': 'Enregistrer',
    'common.cancel': 'Annuler',
    'common.loading': 'Chargement…',
    'common.undo': 'Annuler',
    'common.error.network': 'Connexion perdue. Vérifiez votre réseau et réessayez.',
    'common.error.generic': 'Une erreur est survenue. Réessayez.',
    'common.quote_open': '« ',
    'common.quote_close': ' »',

    'auth.loading': 'Chargement…',
    'auth.toast.session_expired': 'Votre session a expiré — veuillez vous reconnecter.',
    'auth.login.title': 'Connexion',
    'auth.login.subtitle': 'Connectez-vous pour continuer',
    'auth.email_label': 'Adresse e-mail',
    'auth.email_placeholder': 'exemple@exemple.com',
    'auth.password_label': 'Mot de passe',
    'auth.password_placeholder': '********',
    'auth.remember_me': 'Se souvenir de moi',
    'auth.forgot_password': 'Mot de passe oublié ?',
    'auth.login_cta': 'Se connecter',
    'auth.no_account': "Vous n'avez pas de compte ?",
    'auth.sign_up': "S'inscrire",
    'auth.have_account': 'Déjà un compte ?',
    'auth.log_in': 'Se connecter',

    'auth.signup.title': 'Inscription',
    'auth.signup.subtitle': 'Créez un compte pour commencer à écrire.',
    'auth.fullname_label': 'Nom complet',
    'auth.username_label': "Nom d'utilisateur",
    'auth.create_password_label': 'Créez un mot de passe sécurisé',
    'auth.confirm_password_label': 'Confirmez votre mot de passe',
    'auth.terms_prefix': "En vous inscrivant, vous acceptez nos",
    'auth.terms': 'conditions',
    'auth.signup_cta': 'Commencer',
    'auth.fill_all_fields': 'Remplissez tous les champs.',
    'auth.passwords_dont_match': 'Les mots de passe ne correspondent pas.',
    'auth.weak_password':
      'Mot de passe trop court ({min} caractères minimum).',
    'auth.terms_required': 'Acceptez les conditions pour continuer.',
    'auth.user_already_exists': "Cette adresse e-mail ou ce nom est déjà utilisé.",
    'auth.signup_failed': 'Inscription impossible. Réessayez.',
    'auth.invalid_credentials': 'E-mail ou mot de passe incorrect.',
    'auth.signin_failed': 'Connexion impossible. Réessayez.',
    'auth.show_password': 'Afficher le mot de passe',
    'auth.hide_password': 'Masquer le mot de passe',
    'auth.feature.secure': 'Sécurisé',
    'auth.feature.interactive': 'Interactif',
    'auth.feature.friendly': 'Convivial',

    'auth.reset.title': 'Réinitialisation du mot de passe',
    'auth.reset.placeholder': 'Adresse e-mail',
    'auth.reset.request_hint':
      "Saisissez l'e-mail de votre compte. Un code valable 1 heure vous sera envoyé.",
    'auth.reset.confirm_hint':
      'Collez le code reçu par e-mail et choisissez un nouveau mot de passe.',
    'auth.reset.send': 'Envoyer le code',
    'auth.reset.sent':
      "Si cet e-mail est enregistré, un code à 6 chiffres vient d'être envoyé. Vérifiez aussi vos courriers indésirables. Le code expire dans 1 heure.",
    'auth.reset.cooldown': 'Attendez {seconds} s avant de réessayer',
    'auth.reset.have_token': "J'ai déjà un code",
    'auth.reset.resend': 'Renvoyer un code',
    'auth.reset.token_label': 'Code à 6 chiffres',
    'auth.reset.token_placeholder': '000000',
    'auth.reset.new_password_label': 'Nouveau mot de passe',
    'auth.reset.new_password_placeholder': 'Nouveau mot de passe (8+ caractères)',
    'auth.reset.confirm': 'Définir le nouveau mot de passe',
    'auth.reset.confirmed':
      'Mot de passe mis à jour. Vous pouvez vous connecter.',
    'auth.reset.invalid_token':
      'Ce code est invalide ou expiré. Demandez-en un nouveau.',
    'auth.reset.weak_password': 'Mot de passe trop faible :',
    'auth.reset.request_failed':
      'Réinitialisation impossible. Réessayez dans une minute.',
    'auth.reset.confirm_failed':
      'Mise à jour impossible. Réessayez dans une minute.',
    'auth.reset.rate_limited':
      'Trop de tentatives. Patientez une minute.',
    'auth.reset.no_account': "Vous n'avez pas de compte ?",
    'auth.reset.register': 'Inscrivez-vous',
    'auth.reset.now': 'maintenant.',
    'auth.reset.page_title': 'Réinitialiser votre mot de passe',
    'auth.reset.page_subtitle':
      "Saisissez un nouveau mot de passe — l'e-mail et le code sont déjà remplis.",
    'auth.reset.confirm_password_label': 'Confirmer le nouveau mot de passe',
    'auth.reset.confirm_password_placeholder':
      'Confirmez le nouveau mot de passe',
    'auth.reset.password_mismatch': 'Les mots de passe ne correspondent pas.',
    'auth.reset.back_to_login_prefix': 'Mot de passe retrouvé ?',
    'auth.reset.back_to_login': 'Retour à la connexion',

    'goal.page.title': 'Objectifs',
    'goal.page.subtitle':
      "Planifiez vos sessions d'écriture et suivez vos séries.",
    'goal.new': 'Nouvel objectif',
    'goal.period.daily': 'Quotidien',
    'goal.period.weekly': 'Hebdomadaire',
    'goal.period.project': 'Par projet',
    'goal.target_words': 'Mots cibles',
    'goal.streak_days': 'Série (jours)',
    'goal.active': 'Actif',
    'goal.save': 'Enregistrer',
    'goal.cancel': 'Annuler',
    'goal.delete': 'Supprimer',
    'goal.empty.title': "Aucun objectif pour l'instant",
    'goal.empty.description':
      'Définissez une cible quotidienne ou hebdomadaire — votre série se mettra à jour après chaque sprint.',
    'goal.confirm_delete': "L'objectif et son historique seront supprimés définitivement.",
    'goal.confirm_delete_title': 'Supprimer cet objectif ?',

    'help.title': "Centre d'aide",
    'help.intro':
      "Des réponses courtes aux questions les plus fréquentes. Pour le reste, le forum communautaire reste le chemin le plus rapide vers l'équipe et les autres auteurs.",
    'help.sections.getting_started': 'Premiers pas',
    'help.sections.writing': 'Écriture et édition',
    'help.sections.collab': 'Collaboration',
    'help.sections.billing': 'Facturation',
    'help.contact': "Une question sans réponse ? Écrivez-nous.",
    'help.page.title': 'Trouvez vos repères dans Plumescript',
    'help.cards.essentials.title': 'Premiers pas',
    'help.cards.essentials.body':
      "Créez un compte, confirmez votre adresse avec le code à 6 chiffres, et commencez à écrire. Aucune configuration, aucun plug-in.",
    'help.cards.essentials.body_more':
      "Les brouillons se sauvegardent toutes les quelques secondes. Deux auteurs peuvent écrire dans le même chapitre sans s'écraser. Le lecteur public fonctionne sans compte, et tout chapitre s'exporte en PDF, Word, HTML, Markdown, texte brut ou image en un clic. Commenter, envoyer un message ou ouvrir un sujet sur le forum nécessite une adresse vérifiée — un seul code à 6 chiffres débloque les trois.",
    'help.cards.essentials.cta_less': 'Réduire',
    'help.cards.essentials.cta': 'En savoir plus',
    'help.cards.collab.title': 'Écrire à plusieurs',
    'help.cards.collab.body':
      "Invitez un co-auteur par e-mail. Vous écrivez tous les deux dans le même chapitre en direct, voyez le curseur de l'autre, et ne pouvez pas vous écraser. Commentaires en ligne, salles audio + vidéo, et un fil de discussion par paragraphe : tout reste au même endroit.",
    'help.cards.collab.cta': 'Commencer à écrire',
    'help.cards.transform.title': "Une IA qui attend qu'on lui demande",
    'help.cards.transform.body':
      "Analyse de ton, scores de lisibilité, recherche de synonymes et de citations, et un co-auteur IA. Chaque suggestion peut être acceptée, réécrite ou ignorée. Rien ne se déclenche sans votre demande.",
    'help.cards.transform.cta': 'Découvrir',
    'help.cards.enhance.title': 'Ajustez la page',
    'help.cards.enhance.body':
      "Mode focus pour les longues sessions, sprints chronométrés pour les courtes, six thèmes (Clair, Sombre, Ludique, Chaud, plus deux variantes haut contraste WCAG AAA), et des réglages de lecture (taille, hauteur de ligne, espacement).",
    'help.cards.enhance.cta': 'Lire le guide',
    'help.newsletter.title': 'Restez au courant',
    'help.newsletter.description': "Un court billet mensuel sur les nouveautés, des conseils d'écriture et les coups de cœur de la communauté.",
    'help.newsletter.placeholder':
      'Entrez votre adresse e-mail pour recevoir notre infolettre',
    'help.newsletter.submit': "S'abonner",
    'help.newsletter.submitting': 'En cours…',
    'help.newsletter.unsubscribe': 'Se désabonner',
    'help.newsletter.subscribed_state': "Abonnement actif.",
    'help.newsletter.thanks': 'Merci. À très bientôt.',
    'help.newsletter.thanks_unsubscribe': "Désabonnement effectué. Vous ne recevrez plus les prochains numéros.",
    'help.newsletter.error.invalid': 'Cet e-mail ne semble pas valide.',
    'help.newsletter.error.generic': "Abonnement non mis à jour. Réessayez dans une minute.",

    'report.button': 'Signaler',
    'report.modal.title': 'Signaler ce contenu',
    'report.modal.description': "Dites-nous ce qui ne va pas. L'équipe de modération examine chaque signalement.",
    'report.field.kind': 'Pourquoi signalez-vous ?',
    'report.field.reason': 'Raison',
    'report.field.reason.placeholder': 'Court résumé (obligatoire)',
    'report.field.note': 'Autre information ?',
    'report.field.note.placeholder': 'Contexte facultatif — liens, captures, etc.',
    'report.cancel': 'Annuler',
    'report.submit': 'Envoyer le signalement',
    'report.submitting': 'Envoi…',
    'report.toast.filed': 'Signalement envoyé. Merci.',
    'report.toast.already_filed': 'Vous avez déjà signalé — on s’en occupe.',
    'report.toast.failed': "Envoi du signalement impossible. Réessayez plus tard.",
    'report.kind.spam': 'Spam ou arnaque',
    'report.kind.harassment': 'Harcèlement ou haine',
    'report.kind.illegal': 'Contenu illégal',
    'report.kind.copyright': "Atteinte au droit d'auteur",
    'report.kind.other': 'Autre',

    'help.quickstart.guest.heading': 'Trois portes d’entrée',
    'help.quickstart.guest.subheading': "Essayez la plateforme sans compte d'abord. La majeure partie est ouverte à la lecture.",
    'help.quickstart.guest.signup.title': 'Ouvrir un compte gratuit',
    'help.quickstart.guest.signup.description': "Écrivez votre premier chapitre, sauvegardez votre travail, invitez un co-auteur le moment venu.",
    'help.quickstart.guest.signup.cta': "S'inscrire",
    'help.quickstart.guest.browse.title': 'Parcourez le catalogue',
    'help.quickstart.guest.browse.description': "Lisez ce que d'autres auteurs ont publié. Aucun compte requis pour commencer à explorer.",
    'help.quickstart.guest.browse.cta': 'Parcourir',
    'help.quickstart.guest.games.title': "Échauffez-vous avec un jeu de mots",
    'help.quickstart.guest.games.description': "Petits casse-tête de lecture et d'écriture pour délier les muscles créatifs avant une session.",
    'help.quickstart.guest.games.cta': 'Jouer',

    'help.quickstart.logged.heading': 'Reprenez là où vous étiez',
    'help.quickstart.logged.subheading': 'Trois endroits où les auteurs passent le plus de temps.',
    'help.quickstart.logged.start.title': 'Démarrer un nouveau projet',
    'help.quickstart.logged.start.description': "Un nouveau livre, une nouvelle ou un feuilleton. Choisissez une couverture, ajoutez une description, et faites grandir les chapitres au fil de l'écriture.",
    'help.quickstart.logged.start.cta': 'Créer le projet',
    'help.quickstart.logged.collab.title': "Ouvrir votre espace de travail",
    'help.quickstart.logged.collab.description': "Sujets du forum, tâches partagées, les auteurs de votre cercle. Le côté communautaire de Plumescript, regroupé.",
    'help.quickstart.logged.collab.cta': "Ouvrir l'espace",
    'help.quickstart.logged.goals.title': "Suivez votre écriture",
    'help.quickstart.logged.goals.description': "Fixez un objectif quotidien de mots, voyez votre série monter, gardez la vue hebdo à portée de regard.",
    'help.quickstart.logged.goals.cta': 'Ouvrir mes objectifs',

    'help.faq.heading': 'Cinq questions, avant de commencer',
    'help.faq.intro': "Celles que les auteurs nous posent le plus souvent en arrivant sur Plumescript.",
    'help.faq.q1': 'Faut-il un compte pour lire ?',
    'help.faq.a1': "Non. Le catalogue, le lecteur, le forum, la page d'aide et les jeux de mots sont ouverts à tous. Écrire et suivre d'autres auteurs demandent un compte gratuit. Commenter, envoyer un message ou ouvrir un sujet sur le forum demandent en plus de vérifier votre adresse. Un code à 6 chiffres débloque les trois.",
    'help.faq.q2': 'Comment fonctionne l’écriture à plusieurs ?',
    'help.faq.a2': "Depuis un projet, ouvrez la fenêtre de partage et invitez un co-auteur par e-mail. Les modifications se synchronisent dès qu'on tape. Deux auteurs peuvent écrire dans le même chapitre en même temps, voir le curseur de l'autre, et personne ne s'écrase. Les brouillons se sauvegardent tout seuls toutes les quelques secondes.",
    'help.faq.q3': "J'ai oublié mon mot de passe. Comment me reconnecter ?",
    'help.faq.a3': "Sur la page de connexion, cliquez sur « Mot de passe oublié ? », saisissez votre adresse e-mail, et nous envoyons un lien valable une heure ainsi qu'un code à 6 chiffres. Cliquez sur le lien pour ouvrir un formulaire prérempli. Si le lien ne fonctionne pas, collez le code dans le même formulaire. Le lien est retiré de la barre d'adresse après lecture, pour qu'un appareil partagé ne le révèle pas.",
    'help.faq.q4': "Mes textes sont-ils privés par défaut ?",
    'help.faq.a4': "Oui. Chaque projet démarre en privé : personne d'autre que vous ne le voit. Vous pouvez le faire passer en « partagé » (visible aux personnes invitées) ou en « public » (publié dans le catalogue) à tout moment depuis l'écran d'édition. Publier le premier chapitre d'un projet privé le passe automatiquement en public, pour que les lecteurs puissent le trouver.",
    'help.faq.q5': "Quel est l'impact de générer une image IA ?",
    'help.faq.a5': "Gratuit pour vous, mais pas sans coût énergétique. Une couverture générée consomme à peu près l'équivalent d'une recharge complète de téléphone en électricité, plus un verre d'eau pour refroidir le centre de données. Anodin pris isolément, conséquent quand des milliers d'utilisateurs cliquent « générer » par curiosité. À réserver aux couvertures que vous comptez vraiment garder. Les suggestions et l'analyse du ton tournent sur des modèles bien plus légers, leur empreinte est comparativement négligeable.",

    'help.shortcuts.heading': 'Raccourcis clavier',
    'help.shortcuts.intro': "Une poignée de raccourcis pour ce que vous faites le plus souvent dans l'éditeur.",
    'help.shortcuts.bold': 'Gras',
    'help.shortcuts.italic': 'Italique',
    'help.shortcuts.link': 'Insérer un lien',
    'help.shortcuts.undo': 'Annuler',
    'help.shortcuts.redo': 'Rétablir',
    'help.shortcuts.save': 'Enregistrer le chapitre',
    'help.shortcuts.send': 'Envoyer le message',
    'help.shortcuts.search': 'Aller à la recherche',
    'help.shortcuts.hint': 'Sur macOS, remplacez Ctrl par ⌘ Cmd.',

    'help.contact.title': 'Une question reste sans réponse ?',
    'help.contact.field.name': 'Votre nom',
    'help.contact.field.name_placeholder': 'Votre nom (optionnel)',
    'help.contact.field.email': 'Votre adresse e-mail',
    'help.contact.field.email_placeholder': 'nom@exemple.com',
    'help.contact.field.subject': 'Sujet',
    'help.contact.field.subject_placeholder': 'Comment pouvons-nous vous aider ?',
    'help.contact.field.body': 'Message',
    'help.contact.field.body_placeholder':
      'Quelques phrases suffisent. Ajoutez des liens ou captures si utile.',
    'help.contact.submitting': 'Envoi…',
    'help.contact.thanks':
      "Message bien reçu. Une confirmation arrive, et nous vous répondrons dès que possible.",
    'help.contact.error.invalid_email':
      'Cette adresse ne semble pas valide. Vérifiez-la.',
    'help.contact.error.too_short':
      'Ajoutez quelques détails pour qu’on puisse vous aider.',
    'help.contact.error.too_long':
      'Message un peu long. Réduisez-le sous 8 000 caractères.',
    'help.contact.error.rate_limited':
      "Vous nous avez déjà écrit plusieurs fois. Merci d’attendre une heure avant de renvoyer.",
    'help.contact.error.generic':
      "Envoi impossible pour le moment. Réessayez, ou écrivez-nous directement.",
    'help.contact.description':
      "Le forum est le chemin le plus rapide — l'équipe et les autres auteurs y répondent. Ou écrivez-nous directement. Chaque message est lu.",
    'help.contact.cta': 'Aller sur le forum',

    'contact.seo.title': 'Nous contacter',
    'contact.seo.description':
      "Parler à l'équipe Plumescript. Ce que nous construisons, ce que nous croyons, et comment nous joindre.",
    'contact.hero.tag': 'Une petite équipe bilingue',
    'contact.hero.title': 'Parlez-nous',
    'contact.hero.body':
      "Plumescript est une petite équipe bilingue basée au Québec. Nous lisons chaque message qui arrive. Voici ce que nous construisons, ce que nous croyons, et les moyens les plus rapides de nous joindre.",
    'contact.hero.cta_primary': 'Envoyer un message',
    'contact.hero.cta_secondary': 'Nous écrire directement',
    'contact.trust.reply.title': 'Nous les lisons vraiment',
    'contact.trust.reply.body':
      "Chaque message arrive dans une vraie boîte que quelqu'un de l'équipe consulte. Pas de routage automatique, pas de file de tri.",
    'contact.trust.privacy.title': 'À vous, toujours',
    'contact.trust.privacy.body':
      "Votre message reste entre nous. Jamais revendu, jamais utilisé pour entraîner des modèles.",
    'contact.trust.bilingual.title': 'FR · EN, dans les deux sens',
    'contact.trust.bilingual.body':
      "Écrivez-nous dans la langue qui vous convient le mieux. Nous répondons dans la même.",
    'contact.mission.eyebrow': 'Notre mission',
    'contact.mission.title': 'Donner aux auteurs plus de marge',
    'contact.mission.body':
      "Redonner à l'auteur du contrôle sur son travail. Un versioning qui permet de revenir en arrière, une collaboration qui n'écrase pas, une IA qui attend qu'on lui demande, et une page calme quand rien de tout cela n'est nécessaire.",
    'contact.vision.eyebrow': 'Notre vision',
    'contact.vision.title': 'Un seul outil, chaque chapitre',
    'contact.vision.body':
      "Une plateforme de lecture et d'écriture qui passe d'une personne griffonnant à minuit à une petite équipe publiant un feuilleton. Pas de changement d'outil, pas de perte de présence, pas d'écart à gérer entre brouillons.",
    'contact.values.eyebrow': 'Ce que nous valorisons',
    'contact.values.title': 'À quoi ressemble écrire ici',
    'contact.values.intro':
      "Il est 23 h. Vous ouvrez Plumescript. La page se lit dans votre langue, le menu suit le clavier que vous utilisez vraiment, et personne ne vous a demandé de vous traduire. Deux paragraphes plus loin, le curseur de votre co-auteur clignote plus bas, et vous tapez chacun sans jamais vous heurter. L'horodatage d'auto-sauvegarde avance. La mention de confidentialité dit toujours la même chose que la semaine dernière. Vous demandez à l'IA une fin plus douce. Elle en propose trois. Vous n'en prenez aucune, et c'est très bien. Quatre engagements discrets gardent cette pièce stable.",
    'contact.values.inclusion.title': 'Inclusion',
    'contact.values.inclusion.body':
      "La page vous accueille dans votre langue et votre clavier. Aucun détour pour entrer.",
    'contact.values.collaboration.title': 'Collaboration',
    'contact.values.collaboration.body':
      "Deux curseurs dans un même chapitre. Chaque mouvement est le vôtre. Aucun n'écrase l'autre.",
    'contact.values.responsibility.title': 'Responsabilité',
    'contact.values.responsibility.body':
      "Des sauvegardes lisibles. Une politique qui dit demain ce qu'elle dit aujourd'hui.",
    'contact.values.innovation.title': 'Innovation',
    'contact.values.innovation.body':
      "Des suggestions à accepter, refuser ou ignorer. Des outils qui attendent votre signal.",
    'contact.info.title': 'Contact direct',
    'contact.info.intro':
      'Besoin d’un canal plus rapide ? Les deux ci-dessous nous joignent en quelques minutes.',
    'contact.info.email_label': 'E-mail',
    'contact.info.email_value': 'support@plumescript.ca',
    'contact.info.email_hint':
      "Écrivez-nous dans la langue de votre choix. Nous lisons chaque message qui arrive.",
    'contact.info.community_label': 'Communauté',
    'contact.info.community_value': 'Le forum',
    'contact.info.community_hint':
      "Questions publiques, idées de fonctionnalités, discussions sur l'écriture. Tout vit dans /forum.",
    'contact.info.community_cta': 'Ouvrir le forum',
    'contact.form.title': 'Envoyer un message',
    'contact.form.intro':
      "Le formulaire pour tout le reste. Bugs rencontrés, idées, ce qu'on devrait savoir. Un accusé de réception arrive dès l'envoi.",
    'contact.form.submit': 'Envoyer le message',
    'contact.form.submitting': 'Envoi…',
    'contact.closer.title': "Merci d'avoir lu jusqu'ici",
    'contact.closer.body':
      "Si quelque chose est urgent, indiquez « urgent » dans le sujet. Nous le verrons. Sinon nous traitons les messages dans l'ordre d'arrivée.",

    'help.discover.title': 'Ce que fait Plumescript',
    'help.discover.intro': "Un aperçu rapide de ce que contient la plateforme. La plupart des fonctions marchent sans compte. Inscrivez-vous quand vous voudrez écrire.",
    'help.discover.point1': "Éditeur collaboratif en temps réel. Plusieurs auteurs peuvent écrire dans le même chapitre, voir le curseur des autres, sans jamais s'écraser mutuellement.",
    'help.discover.point2': "Catalogue public et lecteur. N'importe qui peut découvrir et lire des récits sans compte.",
    'help.discover.point3': "Génération de couvertures par IA, analyse du ton, suggestions d'écriture, narration Pro et voix personnelle, envoyées à des fournisseurs tiers uniquement à la demande.",
    'help.discover.point4': "Objectifs, sprints chronométrés et mode focus pour les auteurs qui aiment un peu de pression.",
    'help.discover.point5': "Forum, messages et réactions pour le côté social de l'écriture. Vérifiez votre e-mail une fois et tout se débloque.",
    'help.discover.point6': "Titres de paliers pour l'engagement actif et la durée d'adhésion payante, affichés sur votre profil public.",
    'help.discover.close': 'Fermer',
    'help.discover.browse': 'Parcourir le catalogue',

    'editor.guest.title': 'Connectez-vous pour modifier ce récit',
    'editor.guest.description': "L'éditeur a besoin d'un compte pour charger et synchroniser vos modifications. Vous pouvez tout de même lire ce récit sans compte — ouvrez la vue lecture, ou connectez-vous pour commencer à écrire.",
    'editor.guest.login_cta': 'Se connecter',
    'editor.guest.reader_cta': 'Ouvrir la lecture',

    'editor.basic.title': 'Brouillon rapide',
    'editor.basic.subtitle': 'Écrivez tout de suite. Votre brouillon est sauvegardé sur cet appareil — aucun compte requis.',
    'editor.basic.saved_locally': 'Enregistré sur cet appareil',
    'editor.basic.title_placeholder': 'Brouillon sans titre',
    'editor.basic.body_placeholder': 'Commencez à écrire…',
    'editor.basic.stats.words': 'mots',
    'editor.basic.stats.chars': 'caractères',
    'editor.basic.stats.reading': '{minutes} min de lecture',
    'editor.basic.action.copy': 'Copier',
    'editor.basic.action.download': 'Télécharger .txt',
    'editor.basic.action.clear': 'Effacer',
    'editor.basic.toast.copied': 'Copié dans le presse-papiers.',
    'editor.basic.toast.copy_failed': "Presse-papiers indisponible. Réessayez.",
    'editor.basic.toast.cleared': 'Brouillon effacé.',
    'editor.basic.confirm_clear': 'Le brouillon actuel sera effacé. Cette action est irréversible.',
    'editor.basic.confirm_clear_title': 'Effacer le brouillon ?',
    'editor.basic.confirm_clear_action': 'Effacer',
    'editor.basic.upgrade.title': 'Envie de continuer ?',
    'editor.basic.upgrade.description': 'Créez un compte gratuit pour sauvegarder vos brouillons en ligne, écrire sur tous vos appareils, collaborer et publier dans le catalogue public.',
    'editor.basic.upgrade.cta': 'Créer un compte gratuit',

    'terms.eyebrow': 'Mentions légales',
    'terms.title': "Conditions d'utilisation",
    'terms.subtitle': "Un résumé en français clair du fonctionnement de Plumescript, de nos engagements et de ce que nous vous demandons en retour.",
    'terms.last_updated': "Dernière mise à jour : {date}",
    'terms.toc.label': 'Sur cette page',

    'terms.section.intro.title': 'À propos de ces conditions',
    'terms.section.intro.body':
      "Plumescript est une plateforme d'écriture : un éditeur, un plan narratif, un catalogue public de lecture, un forum communautaire, et quelques assistants IA autour. Ces conditions couvrent le site web, l'éditeur, le catalogue public, et tout service que nous hébergeons sur plumescript.ca.\n\nElles sont rédigées en français clair pour poser des attentes honnêtes, pas pour maximiser notre protection. Si une formulation vous semble ambiguë, prenez l'interprétation la plus généreuse — et écrivez-nous avant de vous fier à la lettre du texte.",

    'terms.section.account.title': 'Votre compte',
    'terms.section.account.body':
      "Le lecteur public, le catalogue, le forum, les jeux et la page d'aide sont accessibles sans compte. Un compte gratuit débloque l'écriture, l'import de manuscrits et le suivi d'autres auteurs. Vérifier votre adresse e-mail — nous envoyons un code à 6 chiffres — débloque en plus le commentaire, la messagerie et la création de sujets sur le forum.\n\nMot de passe oublié ? Demandez un lien de réinitialisation depuis la page de connexion ; l'e-mail contient un lien qui préremplit le formulaire en un clic. Un compte par personne : ne partagez pas vos identifiants et n'agissez pas au nom de quelqu'un d'autre.",

    'terms.section.content.title': 'Votre contenu vous appartient',
    'terms.section.content.body':
      "Tout ce que vous écrivez — projets, chapitres, notes du plan, commentaires, billets de forum — vous appartient. Nous le stockons et le traitons uniquement pour faire tourner l'application, synchroniser vos modifications entre appareils et conserver des sauvegardes pour que vous ne perdiez rien.\n\nPublier un projet dans le catalogue public nous accorde (ainsi qu'aux autres lecteurs) une licence non exclusive d'affichage dans l'application. Dépublier, archiver ou supprimer un projet révoque cette licence pour l'avenir.\n\nImporter un manuscrit — glissez un .docx, .pdf, .epub, .txt ou .md dans le plan — le place dans votre projet privé, comme du texte que vous auriez tapé vous-même. Si vous importez un livre publié pour en étudier la structure, marquez le projet comme étude ou recherche pour qu'il reste hors de votre liste principale, et lisez la section suivante avant.",

    'terms.section.use.title': 'Usage acceptable',
    'terms.section.use.body':
      "Utilisez la plateforme pour de l'écriture personnelle ou collaborative légale. Ne publiez et ne téléversez rien que vous n'ayez le droit de partager, rien d'illégal là où vous vivez, ni rien dont le but est de harceler, menacer ou usurper l'identité d'autrui.\n\nLe contenu importé relève de votre responsabilité. La fonction d'import accepte volontiers un .epub d'un roman écrit par quelqu'un d'autre — cela ne vous donne pas le droit de le redistribuer. Importer une œuvre protégée pour étude ou analyse privée est une affaire entre vous et l'ayant droit ; publier ou partager un projet contenant du texte que vous n'avez pas écrit ou licencié constitue une violation de ces conditions, et nous donnons suite aux demandes de retrait crédibles.\n\nN'essayez pas de casser ou d'aspirer le service : pas d'abus automatisé, pas de bourrage d'identifiants, pas d'exploitation de bugs pour lire des projets privés. Vous avez trouvé une faille de sécurité ? Signalez-la — nous traitons les divulgations responsables avec bienveillance.",

    'terms.section.ai.title': 'Fonctionnalités IA',
    'terms.section.ai.body':
      "Plusieurs fonctionnalités utilisent des fournisseurs d'IA tiers. Lorsque vous en déclenchez une, le texte concerné est envoyé au fournisseur pour qu'il renvoie un résultat. Nous n'utilisons ni vos écrits privés ni vos échantillons vocaux pour entraîner des modèles.\n\nCe qui est envoyé, par fonctionnalité :\n• Suggestions du plan, extraction de personnages, analyse de manuscrit — l'extrait pertinent du chapitre ou du plan.\n• Vérification de cohérence et critique de projet — l'export du plan et le chapitre que vous sélectionnez.\n• Ton, reformulation, continuation et conversation — le passage surligné ou la question saisie.\n• Génération de couverture — votre titre, le genre et un court prompt.\n• Narrateur Pro — le texte du chapitre à narrer.\n• Narrateur personnel — le texte du chapitre et l'échantillon vocal que vous avez téléversé pour le clonage. Les échantillons restent privés à votre compte ; supprimez-les à tout moment depuis Paramètres.\n\nLa génération d'image a une empreinte physique réelle : environ une recharge complète de téléphone en électricité et un verre d'eau pour refroidir le centre de données, par couverture. La fenêtre de génération le rappelle avant que vous ne dépensiez un crédit, pour que la décision reste la vôtre. Les fonctionnalités IA textuelles et de narration tournent sur des modèles bien plus légers, avec une empreinte comparativement négligeable.\n\nDes quotas quotidiens s'appliquent par fonctionnalité, sur les offres gratuite et payantes. Le compteur restant est toujours visible avant que vous n'en dépensiez un.",

    'terms.section.termination.title': 'Mettre fin à la relation',
    'terms.section.termination.body':
      "Supprimez votre compte à tout moment depuis Paramètres. Nous retirerons vos projets privés, les données du plan et vos billets de forum sous 30 jours.\n\nNous pouvons suspendre ou résilier les comptes qui enfreignent ces conditions ou abusent du service. Nous pouvons également récupérer les comptes inactifs depuis plus de deux ans — mais nous vous écrirons toujours d'abord pour vous laisser revenir et conserver votre travail.",

    'terms.section.liability.title': 'Ce que nous promettons, où nous traçons la ligne',
    'terms.section.liability.body':
      "La responsabilité, c'est dire ce que nous portons et ce que nous ne portons pas. Notre promesse est honnête : un avis de confidentialité qui dira la même chose demain, des sauvegardes automatiques lisibles, des suggestions que vous pouvez refuser, et une attention rapide quand quelque chose se casse. Plumescript est encore jeune — certaines fonctions vont bouger, d'autres seront reformées, d'autres retirées. Nous vous préviendrons quand cela arrive, et nous ne vous facturerons jamais par surprise.\n\nEn retour, un petit geste : gardez votre propre copie de ce qui compte vraiment. Exportez vos projets, sauvegardez vos manuscrits. Nos sauvegardes sont soigneuses, mais aucune plateforme n'est à l'abri d'une mauvaise journée, et vous ne devriez jamais en être à un clic de perdre votre travail.\n\nSi quelque chose que nous avons fait — ou n'avons pas fait — vous cause un préjudice réel, notre responsabilité est bornée à ce que vous nous avez versé au cours des 12 mois précédents. Pour les écrivain·e·s de l'offre gratuite, ce montant est nul — non parce que votre travail vaut zéro, mais parce que nous tenons à ce que l'offre gratuite reste réellement gratuite, sans péage caché.\n\nLes forfaits payants sont gérés par Stripe. Vous pouvez gérer ou résilier à tout moment depuis Paramètres → Facturation ; la résiliation prend effet à la fin de la période en cours. Pas d'appel de rétention, pas de procédé manipulateur.",

    'terms.section.contact.title': 'Des questions ?',
    'terms.section.contact.body':
      "Si quelque chose n'est pas clair, le forum communautaire reste le moyen le plus rapide de nous joindre. Pour les questions de confidentialité ou les demandes liées aux droits / retraits, écrivez à support@plumescript.ca — nous répondons sous quelques jours ouvrés.",

    'terms.footer.note': "Ces conditions s'appliquent à toutes les pages de plumescript.ca.",
    'terms.footer.back_help': "Retour à l'aide",
    'terms.footer.signup': "S'inscrire",

    'guest_banner.headline': 'Première visite ?',
    'guest_banner.subhead': 'Créez un compte gratuit pour écrire, sauvegarder et collaborer.',
    'guest_banner.signup': 'Inscription gratuite',
    'guest_banner.login': 'Se connecter',
    'guest_banner.dismiss': 'Fermer',

    'forum.guest.title': 'Envie de poser une question ?',
    'forum.guest.description': 'Tout le monde peut lire le forum, mais un compte est nécessaire pour publier ou répondre.',
    'forum.guest.signup': "S'inscrire pour poser",
    'forum.guest.login': 'Se connecter',
    'forum.guest.reply_prompt': "Inscrivez-vous ou connectez-vous pour répondre.",

    'profile.stats.stories': 'Récits',
    'profile.stats.followers': 'Abonnés',
    'profile.stats.following': 'Abonnements',
    'profile.follow': 'Suivre',
    'profile.unfollow': 'Ne plus suivre',
    'profile.message': 'Message',
    'profile.tabs.projects': 'Projets',
    'profile.tabs.following': 'Abonnements',
    'profile.tabs.activity': 'Activité',
    'profile.empty.projects': "Aucun projet publié pour l'instant.",
    'profile.empty.followers': 'Aucune relation pour le moment.',
    'profile.empty.activity': "Rien à afficher pour l'instant.",
    'profile.empty.projects.title': 'Aucun projet',
    'profile.empty.projects.description':
      'Les projets publics apparaîtront ici dès qu’un auteur publiera son premier chapitre.',
    'profile.empty.following.title': 'Aucune relation',
    'profile.empty.following.description':
      'Les auteurs suivis par ce profil apparaîtront ici comme une liste de lectures choisies.',
    'profile.empty.activity.title': 'Activité au repos',
    'profile.empty.activity.description':
      "Les publications récentes, notes et abonnements apparaîtront ici dès que l'auteur recommencera à créer.",
    'profile.edit': 'Modifier le profil',
    'profile.bio.placeholder': 'Aucune bio pour le moment.',
    'profile.edit.title': 'Modifier votre profil',
    'profile.edit.username': "Nom d'utilisateur",
    'profile.edit.username.placeholder': 'Comment les autres vous voient',
    'profile.edit.picture': 'Avatar',
    'profile.edit.picture.upload': 'Importer',
    'profile.edit.picture.uploading': 'Importation…',
    'profile.edit.picture.url': "Ou collez l'URL d'une image",
    'profile.edit.bio': 'Bio',
    'profile.edit.bio.placeholder': 'Présentez-vous en quelques mots.',
    'profile.edit.cancel': 'Annuler',
    'profile.edit.save': 'Enregistrer',
    'profile.edit.saving': 'Enregistrement…',
    'profile.edit.toast.saved': 'Profil mis à jour.',
    'profile.edit.toast.failed': 'Mise à jour impossible. Réessayez.',
    'profile.edit.toast.username_taken': 'Ce nom est déjà pris.',
    'profile.edit.toast.upload_failed': 'Import impossible. Réessayez.',
    'profile.card.discover': 'Découvrir',
    'profile.card.last_activity': 'Dernière activité',
    'profile.card.no_description': 'Aucune description',
    'profile.card.edit': 'Ouvrir',
    'profile.card.read': 'Lire',
    'profile.card.parts': '{count} chapitres',
    'profile.card.visibility.public': 'Public',
    'profile.card.visibility.private': 'Privé',
    'profile.card.visibility.shared': 'Partagé',
    'profile.card.archived': 'Archivé',
    'profile.card.published': 'Publié',
    'profile.card.draft': 'Brouillon',
    'profile.card.last_edited': 'Modifié {date}',
    'profile.card.last_read': 'Lu {date}',
    'profile.back': 'Retour',
    'profile.activity.project_created': 'A créé le projet {project}',
    'profile.activity.project_updated': 'A mis à jour le projet {project}',
    'profile.activity.chapter_published': 'A publié « {chapter} » dans {project}',
    'profile.activity.chapter_updated': 'A mis à jour « {chapter} » dans {project}',
    'profile.activity.forum_posted': 'A publié sur le forum — {title}',
    'profile.activity.rating_given': 'A noté {project} {stars} ★',
    'profile.activity.follow_added': 'Suit désormais {target}',
    'profile.titles.heading': 'Titre',
    'profile.titles.newcomer': 'Nouveau venu',
    'profile.titles.storyteller': 'Conteur',
    'profile.titles.prolific': 'Prolifique',
    'profile.titles.wordsmith': 'Plume affûtée',
    'profile.titles.voice': 'Voix',
    'profile.titles.legend': 'Légende',
    'profile.titles.critic': 'Critique',
    'profile.titles.forum_voice': 'Voix du forum',
    'profile.titles.connector': 'Lien social',
    'profile.titles.beloved': 'Adoré',
    'profile.titles.settler': 'Pionnier',
    'profile.titles.voyager': 'Voyageur',
    'profile.titles.chronicler': 'Chroniqueur',
    'profile.titles.veteran': 'Vétéran',
    'profile.titles.elder': 'Ancien',
    'profile.titles.founding_quill': 'Plume fondatrice',
    'profile.titles.member_1m': 'Ami de la maison',
    'profile.titles.member_3m': 'Présence assidue',
    'profile.titles.member_6m': 'Lecteur fidèle',
    'profile.titles.member_1y': 'Mécène de la première année',
    'profile.titles.member_3y': 'Pilier',
    'profile.titles.member_5y': 'Pierre angulaire',
    'profile.titles.sage': 'Sage',
    'profile.titles.ink_trail': "Sillage d'encre",
    'profile.titles.library': 'Bibliothèque vivante',
    'profile.titles.patron': 'Mécène',
    'profile.titles.forum_master': 'Maître du forum',
    'profile.titles.architect': 'Architecte des archives',
    'profile.titles.beacon': 'Phare',
    'profile.titles.compass': 'Boussole',
    'profile.titles.cherished': 'Aimé',
    'profile.titles.balanced_voice': 'Voix équilibrée',
    'profile.titles.renaissance': 'Écrivain renaissance',
    'profile.titles.ai_apprentice': 'Apprenti IA',
    'profile.titles.ai_adept': 'Adepte IA',
    'profile.titles.tech_wizard': 'Sorcier de la technique',
    'profile.titles.ai_archmage': 'Archimage IA',
    'profile.titles.planner_alchemist': 'Alchimiste du plan',
    'profile.titles.grammar_guru': 'Gourou de la grammaire',
    'profile.titles.inspiration_seeker': "Chercheur d'inspiration",
    'profile.titles.image_summoner': "Invocateur d'images",
    'profile.titles.coherence_keeper': 'Gardien de la cohérence',
    'profile.titles.toast.failed': 'Mise à jour du titre impossible. Réessayez.',

    'settings.milestones.title': 'Accomplissements',
    'settings.milestones.subtitle':
      'Titres débloqués par engagement et ancienneté.',
    'settings.milestones.loading': 'Chargement…',
    'settings.milestones.unavailable':
      'Accomplissements indisponibles pour le moment.',
    'settings.milestones.current': 'Actuel',
    'settings.milestones.next': 'Prochain :',
    'settings.milestones.threshold_days': '{days} j',
    'settings.milestones.flip_to': 'Passer à {name}',

    'settings.milestones.engagement.title': 'Engagement',
    'settings.milestones.engagement.subtitle': '',
    'settings.milestones.engagement.tenure': '{days} jours de connexion',
    'settings.milestones.engagement.next_threshold': 'plus que {days} j',
    'settings.milestones.engagement.empty':
      'Premier titre débloqué après 7 jours de connexion.',
    'settings.milestones.engagement.maxed':
      "Tous les titres d'engagement débloqués.",

    'settings.milestones.membership.title': 'Adhésion payante',
    'settings.milestones.membership.subtitle': '',
    'settings.milestones.membership.tenure': '{days} jours sur un forfait payant',
    'settings.milestones.membership.next_threshold': 'plus que {days} j',
    'settings.milestones.membership.empty':
      "Premier titre débloqué un mois après l'activation.",
    'settings.milestones.membership.maxed':
      "Tous les paliers d'adhésion payante débloqués.",
    'settings.milestones.membership.free_tier':
      'Passez à un forfait payant pour commencer à gagner ces titres.',

    'settings.recent_activity.title': 'Activité récente',
    'settings.recent_activity.subtitle':
      'La dernière action sur ce compte.',
    'settings.recent_activity.loading': 'Chargement…',
    'settings.recent_activity.empty':
      "Aucune activité récente pour l'instant.",
    'settings.recent_activity.unavailable':
      'Activité récente indisponible pour le moment.',
    'settings.recent_activity.kind.project_created':
      "Création du projet « {title} »",
    'settings.recent_activity.kind.project_updated':
      "Mise à jour du projet « {title} »",
    'settings.recent_activity.kind.chapter_published':
      "Publication de « {chapter} » dans {project}",
    'settings.recent_activity.kind.chapter_updated':
      "Modification de « {chapter} » dans {project}",
    'settings.recent_activity.kind.forum_posted':
      "Publication dans le forum : « {title} »",
    'settings.recent_activity.kind.rating_given':
      'Note de {stars}★ donnée à {title}',
    'settings.recent_activity.kind.follow_added':
      'Abonnement à @{username}',

    'footer.label': 'Pied de page',
    'footer.tagline': "Un espace pour écrire, partager et lire des histoires en cours. Lecture libre, sans compte.",
    'footer.discover': 'Découvrir',
    'footer.account': 'Compte',
    'footer.preferences': 'Préférences',
    'footer.rights': 'Tous droits réservés.',
    'footer.terms': 'Conditions',
    'footer.contact': 'Contact',
    'a11y.skip_to_main': 'Aller au contenu principal',
    'a11y.main_landmark': 'Contenu principal',
    'a11y.primary_nav': 'Navigation principale',
    'a11y.reading_prefs.title': 'Préférences de lecture',
    'a11y.reading_prefs.open': 'Ouvrir les préférences de lecture',
    'a11y.reading_prefs.open_hint': "Taille de police, interligne, police adaptée à la dyslexie, mode focus, etc.",
    'a11y.reading_prefs.subtitle':
      "Rendez les textes longs confortables à lire. Enregistré uniquement sur cet appareil.",
    'a11y.reading_prefs.intro':
      "Ajustez la taille du texte, la hauteur de ligne, l'espacement des lettres et la mise en valeur des liens. Chaque réglage met la page à jour immédiatement.",
    'a11y.reading_prefs.font_scale': 'Taille du texte',
    'a11y.reading_prefs.line_height': 'Hauteur de ligne',
    'a11y.reading_prefs.letter_spacing': 'Espacement des lettres',
    'a11y.reading_prefs.underline_links': 'Toujours souligner les liens',
    'a11y.reading_prefs.reset': 'Réinitialiser',
    'home.seo.description': "Explorez les histoires de la communauté Plumescript — livres, feuilletons et œuvres en cours, librement consultables sans compte.",
    'home.welcome': 'Bon retour parmi nous',
    'home.quick_actions': 'Actions rapides',
    'home.cta.new_project': 'Démarrer un projet',
    'home.cta.open_editor': "Ouvrir l'éditeur",
    'home.cta.goals': 'Définir un objectif',
    'home.cta.collab': 'Trouver des collaborateurs',
    'home.recent_projects': 'Projets récents',
    'home.empty.projects':
      "Pas encore de projet. Votre première histoire commence ici.",
    'home.section.community': 'Depuis la communauté',
    'home.section.explore': 'Explorer',
    'home.welcome_back': 'Bon retour, {name}.',
    'home.tagline': "Façonnez des mondes avec des mots, une histoire à la fois.",
    'home.guest.eyebrow': 'Découvrir',
    'home.guest.title': 'Lecture libre, sans compte.',
    'home.guest.subtitle': "Inscrivez-vous quand vous voudrez écrire.",
    'home.guest.cta_signup': "S'inscrire",
    'home.guest.cta_browse': 'Parcourir',
    'home.start_writing': "Commencer à écrire",
    'home.start_reading': "Commencer à lire",
    'home.your_stories.title': 'Vos histoires',
    'home.your_stories.subtitle': 'Reprenez les histoires que vous aimez.',
    'home.recommended.title': 'Recommandé pour vous',

    'browse.catalogue.title': 'Découvrez le catalogue',
    'browse.catalogue.subtitle':
      'Une sélection de projets publics issus de la plateforme.',
    'browse.catalogue.filter.all': 'Tout',
    // Catégories étendues — plus représentatives des genres
    // que les écrivains choisissent réellement.
    'browse.catalogue.filter.novel': 'Roman',
    'browse.catalogue.filter.short_story': 'Nouvelle',
    'browse.catalogue.filter.poetry': 'Poésie',
    'browse.catalogue.filter.essay': 'Essai',
    'browse.catalogue.filter.memoir': 'Mémoires',
    'browse.catalogue.filter.screenplay': 'Scénario',
    'browse.catalogue.filter.children': 'Jeunesse',
    'browse.catalogue.filter.comic': 'Bande dessinée',
    'browse.catalogue.filter.non_fiction': 'Documentaire',
    'browse.catalogue.filter.fantasy': 'Fantasy',
    'browse.catalogue.filter.scifi': 'Science-fiction',
    'browse.catalogue.filter.mystery': 'Polar',
    'browse.catalogue.filter.romance': 'Romance',
    // Bande de chips filtre langue — partagée par Browse, Search
    // et Forum. Le tableau de bord filtre silencieusement sur la
    // langue courante sans afficher ces chips.
    'locale_filter.aria': 'Filtrer par langue',
    'locale_filter.fr': 'Français',
    'locale_filter.en': 'Anglais',
    'locale_filter.all': 'Toutes les langues',
    'forum.card.new_badge': 'Nouveau',
    'forum.card.replies': '{count} réponses',
    'forum.card.edit': 'Modifier',
    'forum.card.delete': 'Supprimer',
    'messages.jitsi.toast.sleep_hours': "Les appels vidéo sont en pause la nuit (23h – 7h, heure de l'Est). Réessayez le matin.",
    'messages.jitsi.toast.load_failed': "Connexion à l'appel vidéo échouée. Vérifiez votre réseau et réessayez.",
    'browse.catalogue.untitled': 'Sans titre',
    'browse.catalogue.prev': 'Précédent',
    'browse.catalogue.next': 'Suivant',
    'browse.catalogue.empty.title': 'Rien dans cette catégorie',
    'browse.catalogue.empty.description':
      "Essayez un autre filtre — de nouvelles histoires publiques arrivent chaque jour.",
    'browse.catalogue.search.placeholder': 'Rechercher dans le catalogue…',
    'browse.catalogue.search.clear': 'Effacer la recherche',
    'browse.catalogue.sort.label': 'Trier',
    'browse.catalogue.sort.popular': 'Plus populaires',
    'browse.catalogue.sort.newest': 'Plus récents',
    'browse.catalogue.sort.rated': 'Mieux notés',
    'browse.catalogue.sort.title': 'Titre (A–Z)',
    'browse.catalogue.advanced': 'Filtres',
    'browse.catalogue.advanced.audience': 'Public',
    'browse.catalogue.advanced.language': 'Langue',
    'browse.catalogue.advanced.min_rating': 'Note minimum',
    'browse.catalogue.advanced.any': 'Tout',
    'browse.catalogue.advanced.reset': 'Réinitialiser',

    'home.book.modal.loading': 'Chargement des détails du projet…',
    'home.book.modal.section.details': 'Détails du projet',
    'home.book.modal.section.characters': 'Personnages principaux',
    'home.book.modal.section.tags': 'Étiquettes',
    'home.book.modal.section.notes': "Notes d'écriture",
    'home.book.modal.section.toc': 'Table des matières · {count}',
    'home.book.modal.detail.genre': 'Genre',
    'home.book.modal.detail.category': 'Catégorie',
    'home.book.modal.detail.audience': 'Public',
    'home.book.modal.detail.language': 'Langue',
    'home.book.modal.untitled_chapter': 'Chapitre {index}',
    'home.book.modal.words': '{count} mots',
    'home.recommended.subtitle': "Des histoires choisies selon votre style d'écriture.",
    'home.status.complete': 'Terminé',
    'home.status.in_progress': 'En cours',
    'home.view_all': 'Tout afficher',
    'home.loading': 'Chargement…',
    'home.empty.your_stories.title': 'Votre étagère est vide',
    'home.empty.your_stories.description': "Commencez à lire une histoire et elle apparaîtra ici pour reprendre où vous en étiez.",
    'home.empty.your_stories.cta': 'Trouver une lecture',
    'home.empty.recommended.title': 'Aucune recommandation pour le moment',
    'home.empty.recommended.description': 'Les recommandations arrivent à mesure que la communauté publie. Revenez quand d’autres auteurs partagent leur travail.',
    'home.empty.recommended.cta': 'Explorer la communauté',
    'home.empty.signed_out.title': 'Connectez-vous pour voir votre étagère',
    'home.empty.signed_out.description': 'Connectez-vous pour retrouver votre progression. Les sélections publiques restent accessibles ci-dessous.',
    'home.empty.signed_out.cta': 'Se connecter',
    'home.book.last_read': 'Lu {date}',
    'home.book.not_started': 'Pas encore commencé',
    'home.book.parts_left': '{count} chapitres restants',
    'home.book.parts_total': '{count} chapitres',
    'home.book.chapter_of': 'Chapitre {read} sur {total}',
    'home.book.finished': 'Terminé',
    'home.book.no_chapters': 'Aucun chapitre',
    'home.book.state.new': 'Nouveau',
    'home.book.state.reading': 'En cours',
    'home.book.state.finished': 'Terminé',
    'home.book.by': 'Par {author}',
    'home.book.modal.status.ongoing': 'En cours',
    'home.book.modal.status.complete': 'Terminé',
    'home.book.modal.total_parts': 'Total : {count} chapitres',
    'home.book.modal.discover': 'Découvrir',
    'home.book.modal.add_to_shelf': 'Ajouter à ma liste',
    'home.book.modal.in_shelf': 'Ajouté à votre liste',
    'home.book.modal.already_in_shelf': 'Déjà dans votre liste',
    'home.book.modal.shelf_failed': 'Mise à jour impossible. Réessayez.',
    'home.book.modal.close': 'Fermer',
    'home.welcome.avatar_link': 'Ouvrir mon profil',

    'messages.title': 'Vos messages',
    'messages.recent_connections': 'Connexions récentes',
    'messages.recent_group': 'Groupe récent',
    'messages.add_contacts': 'Lancer une nouvelle conversation',
    'messages.search_contacts': 'Rechercher des contacts…',
    'messages.compose_placeholder': 'Écrivez un message…',
    'messages.send': 'Envoyer',
    'messages.empty.title': 'Aucune conversation',
    'messages.empty.description':
      "Trouvez un collaborateur et démarrez votre première discussion.",
    'messages.empty.cta': 'Démarrer une conversation',
    'messages.empty.select.title': 'Choisissez une conversation',
    'messages.empty.select.description':
      'Sélectionnez un contact ou un groupe dans la liste pour lire les messages ici.',
    'messages.empty.thread.title': 'Dites bonjour',
    'messages.empty.thread.description':
      "Aucun message pour le moment — soyez le premier à écrire.",
    'messages.sender.send': 'Envoyer',
    'messages.sender.attach': 'Joindre un fichier',
    'messages.sender.emoji': 'Ajouter un émoji',
    'messages.sender.link': 'Insérer un lien',
    'messages.sender.font': 'Style du texte',
    'messages.sender.format': 'Mettre en forme',
    'messages.sender.format.heading': 'Mise en forme',
    'messages.sender.format.bold': 'Gras',
    'messages.sender.format.italic': 'Italique',
    'messages.sender.format.strike': 'Barré',
    'messages.sender.format.code': 'Code',
    'messages.sender.audio': "Enregistrer de l'audio",
    'messages.sender.video': 'Enregistrer une vidéo',
    'messages.sender.remove_attachment': 'Retirer la pièce jointe',
    'messages.sender.link.title': 'Insérer un lien',
    'messages.sender.link.url': 'URL',
    'messages.sender.link.url.placeholder': 'https://exemple.com',
    'messages.sender.link.text': 'Texte affiché',
    'messages.sender.link.text.placeholder': "Optionnel — l'URL est utilisée par défaut",
    'messages.sender.link.add': 'Insérer',
    'messages.sender.link.cancel': 'Annuler',
    'messages.sender.link.invalid': 'Entrez une URL en http(s).',
    'messages.sender.recording.stop_audio': "Arrêter l'enregistrement",
    'messages.sender.recording.stop_video': "Arrêter l'enregistrement",
    'messages.recorder.video.title': 'Enregistreur vidéo',
    'messages.recorder.video.start': "Démarrer l'enregistrement",
    'messages.recorder.video.stop': "Arrêter l'enregistrement",
    'messages.recorder.video.save': 'Garder cette prise',
    'messages.recorder.video.re_record': 'Réenregistrer',
    'messages.recorder.video.error': "Accès à la caméra impossible : {message}",
    'messages.recorder.video.unsupported': "Votre navigateur ne prend pas en charge la lecture vidéo intégrée.",
    'messages.recorder.audio.start': "Démarrer l'enregistrement",
    'messages.recorder.audio.pause': 'Pause',
    'messages.recorder.audio.resume': 'Reprendre',
    'messages.recorder.audio.stop': 'Arrêter',
    'messages.recorder.audio.send': "Envoyer l'enregistrement",
    'messages.recorder.audio.timer': "Durée d'enregistrement",
    'messages.sender.upload_failed': 'Import des pièces jointes impossible. Réessayez.',
    'messages.add': 'Ajouter',
    'messages.new_chat': 'Nouveau message',
    'messages.call.video': 'Démarrer un appel vidéo',
    'messages.call.audio': 'Démarrer un appel vocal',
    'messages.bubble.read': 'Lu',
    'messages.bubble.delivered': 'Distribué',
    'messages.modal.close': 'Fermer',
    'messages.modal.no_contact': 'Aucun contact trouvé.',
    'messages.modal.start': 'Démarrer la conversation',
    'messages.voice_call': 'Appel audio',
    'messages.video_call': 'Appel vidéo',
    'messages.leave_call': 'Quitter',
    'messages.bubble.react': 'Réagir',
    'messages.bubble.preview': 'Aperçu',
    'messages.bubble.share': 'Partager',
    'messages.bubble.download': 'Télécharger',
    'messages.bubble.share.facebook': 'Partager sur Facebook',
    'messages.bubble.share.twitter': 'Partager sur X',
    'messages.bubble.share.linkedin': 'Partager sur LinkedIn',
    'messages.bubble.share.download': 'Télécharger le fichier',
    'messages.bubble.attachment.audio': 'Audio',
    'messages.bubble.attachment.video': 'Vidéo',
    'messages.bubble.attachment.image': 'Image',
    'messages.bubble.attachment.file': 'Fichier',
    'messages.bubble.reactions.title': 'Réactions',
    'messages.bubble.reactions.more': '+{count} de plus',
    'messages.bubble.read_by': 'Lu par {name}',
    'messages.bubble.unread_by': 'Pas encore vu par {name}',
    'messages.bubble.preview_title': 'Aperçu de la pièce jointe',

    'collab.title': 'Tableau de bord',
    'collab.subtitle':
      "Parcourez les projets ouverts, trouvez des auteurs, partagez des objectifs.",
    'collab.explore.title': 'Explorer les projets',
    'collab.writers.title': 'Auteurs à découvrir',
    'collab.calendar.title': 'Sessions à venir',
    'collab.filter.all': 'Tous les genres',
    'collab.cta.open': 'Ouvrir le projet',
    'collab.cta.invite': 'Inviter au projet',
    'collab.calendar': 'Calendrier',
    'collab.urgent_tasks': 'Tâches à faire',
    'collab.current_time': 'Heure actuelle',
    'collab.discover_book': 'Découvrir un livre',
    'collab.daily_quote': 'Citation du jour',
    'collab.demo_seed.toast': "Projet d'exemple ajouté — ouvrez-le pour explorer le plan et l'éditeur.",
    'collab.premium_weekend.title': "Week-end Premium activé — voix Pro qualité studio et quotas IA élargis tout le week-end.",
    'collab.premium_weekend.body': "Retour à la normale le {ends}. Profitez-en.",
    'collab.weather': 'Météo',
    'collab.from_writers': "D'autres auteurs",
    'collab.view_all_writers': 'Voir tous les auteurs',
    'collab.weather_in': 'Météo à {city}, {province}',
    'collab.humidity': 'Humidité : {value} %',
    'collab.wind_speed': 'Vent : {value} km/h',
    'collab.engage': "S'engager dans l'écriture",
    'collab.writers_near': 'Auteurs autour de vous',
    'collab.loading': 'Chargement…',
    'collab.empty.books.title': 'Rien de public pour le moment',
    'collab.empty.books.description': "Les projets publics d'autres auteurs apparaîtront ici.",
    'collab.empty.goals.title': 'Aucune tâche à faire',
    'collab.empty.goals.description': 'Notez une tâche ci-dessus ou ouvrez Objectifs pour planifier votre semaine.',
    'collab.empty.goals.cta': 'Ouvrir Objectifs',
    'collab.tasks.quick_add.placeholder': 'Nouvelle tâche…',
    'collab.tasks.quick_add.aria': 'Capturer une nouvelle tâche',
    'collab.tasks.quick_add.submit': 'Ajouter',
    'collab.tasks.quick_add.done': 'Tâche ajoutée à votre kanban.',
    'collab.tasks.quick_add.failed': "Ajout impossible — réessayez.",
    'collab.tasks.quick_complete.title': 'Marquer comme terminée',
    'collab.tasks.quick_complete.aria': 'Marquer « {title} » comme terminée',
    'collab.tasks.quick_complete.done': 'Tâche terminée.',
    'collab.tasks.quick_complete.failed': "Action impossible — réessayez.",
    'collab.empty.posts.title': 'Aucune discussion',
    'collab.empty.posts.description': 'Posez une question sur le forum pour lancer une discussion.',
    'collab.empty.posts.cta': 'Ouvrir le forum',
    'collab.empty.writers.title': 'Aucun auteur à afficher',
    'collab.empty.writers.description': "Suivez et découvrez d'autres auteurs à mesure que la communauté grandit.",
    'collab.empty.quote.title': 'Pas de citation pour le moment',
    'collab.empty.quote.description': 'Citation indisponible. Réessayez dans un moment.',
    'collab.empty.weather.title': 'Météo indisponible',
    'collab.empty.weather.description': 'Météo ou localisation indisponible.',

    'calendar.prev_month': 'Mois précédent',
    'calendar.next_month': 'Mois suivant',

    'forum.collaboration': 'Collaboration',
    'forum.heading': 'Forum',
    'forum.new_question.title': 'Sujet',
    'forum.new_question.title_placeholder': 'Votre question',
    'forum.new_question.description': 'Description',
    'forum.new_question.description_placeholder': 'Décrivez votre question en détail',
    'forum.new_question.category': 'Catégorie',
    'forum.new_question.category_placeholder': 'Choisir une catégorie',
    'forum.new_question.date': 'Date',
    'forum.new_question.tags': 'Étiquettes',
    'forum.new_question.tags_placeholder': 'Étiquettes séparées par des virgules',
    'forum.new_question.submit': 'Publier',
    'forum.toast.post_published': 'Publié.',
    'forum.toast.post_failed': "Échec de la publication. Vérifiez votre connexion et réessayez.",
    'forum.category.general': 'Général',
    'forum.category.technical': 'Technique',
    'forum.category.other': 'Autre',
    'forum.filter.popular_topics': 'Sujets populaires',
    'forum.filter.topic.writing_tips': "Astuces d'écriture",
    'forum.filter.topic.publishing': 'Conseils de publication',
    'forum.filter.topic.inspiration': 'Inspiration',
    'forum.filter.topic.community': 'Communauté',
    'forum.filter.writing_style': "Style d'écriture",
    'forum.filter.all_categories': 'Toutes les catégories',
    'forum.search.placeholder': 'Rechercher une question…',
    'forum.search.button': 'Rechercher',
    'forum.post.back': 'Retour',
    'forum.post.reply': 'Répondre',
    'forum.post.edit': 'Modifier',
    'forum.post.delete': 'Supprimer',
    'forum.post.delete_confirm': 'La question et toutes ses réponses seront supprimées définitivement.',
    'forum.post.delete_confirm_title': 'Supprimer cette question ?',
    'forum.post.fill_required': 'Renseignez le titre, la description et la catégorie.',
    'forum.reply.heading': 'Réponses',
    'forum.reply.sort.newest': 'Plus récentes',
    'forum.reply.sort.oldest': 'Plus anciennes',
    'forum.reply.sort.popular': 'Plus populaires',
    'forum.reply.add_heading': 'Ajouter une réponse',
    'forum.reply.placeholder': 'Écrivez votre réponse…',
    'forum.reply.send': 'Envoyer',
    'forum.reply.emoji': 'Insérer un emoji',
    'forum.reply.attach_image': 'Joindre une image',
    'forum.reply.attachment_staged': 'Image prête',
    'forum.reply.attachment_clear': "Retirer l'image",
    'forum.reply.attachment_alt': 'Pièce jointe',
    'forum.reply.attachment_link': 'Voir la pièce jointe',
    'forum.reply.like': "J'aime",
    'forum.attachment.upload_failed': "Impossible d'envoyer l'image — réessayez.",
    'forum.reaction.toggle_failed': "Impossible de mettre à jour la réaction — réessayez.",
    'forum.reply.cancel': 'Annuler',
    'forum.reply.update': 'Mettre à jour',
    'forum.modal.new_title': 'Poser une nouvelle question',
    'forum.modal.edit_title': 'Modifier la question',
    'forum.modal.title': 'Titre',
    'forum.modal.title_placeholder': 'Donnez un titre clair',
    'forum.modal.description': 'Description',
    'forum.modal.description_placeholder': 'Décrivez votre question en détail…',
    'forum.modal.category': 'Catégorie',
    'forum.modal.tags': 'Étiquettes',
    'forum.modal.tags_placeholder': 'Étiquettes séparées par des virgules',
    'forum.modal.cancel': 'Annuler',
    'forum.modal.publish': 'Publier',
    'forum.modal.update': 'Mettre à jour',
    'forum.empty.title': 'Aucune question pour le moment',
    'forum.empty.description': "Soyez le premier à poser une question — utilisez le panneau latéral.",

    'editor.save': 'Enregistrer',
    'editor.publish': 'Publier',
    'editor.preview': 'Aperçu',
    'editor.grammar': 'Grammaire',
    'editor.share': 'Partager',
    'editor.chapter_title_placeholder': 'Titre du chapitre',
    'editor.upload_cover': 'Importer une couverture',
    'editor.back_to_projects': 'Retour aux projets',
    'editor.prompt_hint.title': 'Idée de scène prête',
    'editor.prompt_hint.title_named': 'Idée de scène pour {name}',
    'editor.prompt_hint.copy': 'Copier',
    'editor.prompt_hint.copied': 'Idée copiée',
    'editor.prompt_hint.dismiss': 'Ignorer',
    'editor.editing_kicker': 'Édition du projet',
    'editor.part': 'Partie {n}',
    'editor.story_title': "Titre de l'histoire",
    'editor.write_here': 'Écrivez votre histoire ici',
    'editor.grammar_check': 'Grammaire',
    'editor.format': 'Mise en forme',
    'editor.inspiration': 'Inspiration',
    'editor.auto_suggestions': 'Suggestions auto',
    'editor.ai_assistant': 'Assistant IA',

    'book.toc.title': 'Table des matières',
    'book.read_chapter': 'Lire le chapitre',
    'book.next_chapter': 'Chapitre suivant',
    'book.prev_chapter': 'Chapitre précédent',
    'book.by_author': 'Par {author}',
    'book.your_comments': 'Vos commentaires',
    'book.other_comments': 'Autres commentaires',
    'book.comment_placeholder': 'Écrivez votre commentaire',
    'book.guest.signup_prompt': 'Inscrivez-vous pour sauvegarder, noter et commenter.',
    'book.comment.submit_failed': 'Commentaire non publié. Réessayez.',
    'book.guest.section_eyebrow': 'Lecteur',
    'book.guest.section_title': "Vous aimez ce récit ?",
    'book.guest.section_description': "La lecture est libre — aucun compte requis. Inscrivez-vous pour commenter, noter, ajouter à votre liste et suivre cet auteur pour les nouveaux chapitres.",
    'book.guest.signup_action': 'Inscription gratuite',
    'book.guest.login': 'Se connecter',
    'book.guest.do_now': 'Ou continuez sans compte',
    'book.guest.share_copy': 'Copier le lien',
    'book.guest.share_copied': 'Lien copié dans le presse-papiers.',
    'book.guest.share_failed': 'Copie impossible. Réessayez.',
    'book.guest.see_author': "Voir plus de @{handle}",
    'book.guest.browse_more': 'Parcourir plus de récits',
    'book.no_comments': "Aucun commentaire pour l'instant.",
    'book.parts_of': 'Partie {current} sur {total}',
    'book.continue': 'Continuer à la partie suivante',
    'book.mark_finished': 'Marquer comme terminé',
    'book.related.heading': 'Livres du même auteur',
    'book.related.subheading': 'D’autres récits du même écrivain',
    'book.related.view_all': 'Tout afficher',
    'book.bookmark': "Ajouter à l'étagère",
    'book.bookmark.added': "Retirer de l'étagère",
    'book.bookmark.toast.added': "Ajouté à votre étagère.",
    'book.bookmark.toast.removed': "Retiré de votre étagère.",
    'book.bookmark.toast.failed': "Mise à jour de l'étagère impossible. Réessayez.",
    'book.favorite': 'Ajouter aux favoris',
    'book.favorite.added': 'Retirer des favoris',
    'book.favorite.toast.added': 'Ajouté à vos favoris.',
    'book.favorite.toast.removed': 'Retiré de vos favoris.',
    'book.favorite.toast.failed': 'Mise à jour des favoris impossible. Réessayez.',
    'book.alt.hero': 'Image de couverture',
    'book.alt.author': "Avatar de l'auteur",
    'book.alt.rating': 'Note',
    'book.alt.views': 'Vues',
    'book.alt.comments': 'Commentaires',
    'book.alt.send': 'Envoyer le commentaire',
    'book.alt.readers': 'Lecteurs',

    'work.tab.organisations': 'Organisations',
    'work.tab.projects': 'Projets',
    'work.back_to_dashboard': 'Retour au tableau de bord',
    'work.add_new': 'Ajouter',
    'work.header.projects_subtitle': 'Tous les livres que vous écrivez.',
    'work.header.orgs_subtitle': 'Regroupements de projets liés.',
    'work.header.count_aria': '{n} éléments',
    'work.bulk.select_row': 'Sélectionner la ligne',
    'work.bulk.selected_count': '{n} sélectionné(s)',
    'work.bulk.cancel': 'Annuler',
    'work.bulk.delete': 'Supprimer {n}',
    'work.bulk.confirm_delete': 'Confirmer la suppression de {n}',
    'work.bulk.working': 'Suppression…',
    'work.bulk.toast.deleted': '{n} déplacé(s) à la corbeille.',
    'work.bulk.toast.delete_failed': 'Suppression de {n} impossible — réessayez.',
    'work.filter': 'Filtrer',
    'work.view_more': 'Voir plus',
    'work.summary': 'Résumé',
    'work.projects_empty': "Aucun projet pour le moment — cliquez sur Ajouter pour commencer.",
    'work.projects_empty.title': "Aucun projet pour le moment",
    'work.projects_empty.description': 'Lancez votre premier projet pour commencer à écrire. Regroupez-les ensuite dans une organisation.',
    'work.projects_empty.cta': 'Créer un projet',
    'work.orgs_empty.title': 'Aucune organisation pour le moment',
    'work.orgs_empty.description': 'Les organisations regroupent vos projets — par série, par client, ou par thème.',
    'work.orgs_empty.cta': 'Créer une organisation',
    'work.modal.preset.label': 'Ou choisissez une couverture prédéfinie',
    'work.modal.preset.pick': 'Choisir cette couverture',
    'work.modal.preset.hint': "Cliquez sur l'image ci-dessus pour importer la vôtre.",
    'work.modal.ai_cover': "Générer une couverture avec l'IA",
    'work.modal.cover.upload_label': 'Importer la couverture',
    'work.unfiled': 'Sans dossier',
    'work.edit_org_info': "Modifier les informations de l'organisation",
    'work.modal.new_org': 'Nouvelle organisation',
    'work.modal.edit_org': "Modifier l'organisation",
    'work.modal.new_project': 'Nouveau projet',
    'work.modal.edit_project': 'Modifier le projet',
    'work.modal.name.org': "Nom de l'organisation",
    'work.modal.name.project': 'Nom du projet',
    'work.modal.name_placeholder.org': "Saisir le nom de l'organisation",
    'work.modal.name_placeholder.project': 'Saisir le nom du projet',
    'work.modal.description.org': "Description de l'organisation",
    'work.modal.description.project': 'Description du projet',
    'work.modal.description_placeholder.org': "Saisir la description de l'organisation",
    'work.modal.description_placeholder.project': 'Saisir la description du projet',
    'work.modal.save': 'Enregistrer',
    'work.modal.saving': 'Enregistrement…',
    'work.modal.cancel': 'Annuler',
    'work.modal.delete_org': "Supprimer l'organisation",
    'work.modal.delete_project': 'Supprimer le projet',
    'work.confirm.delete_project_title': 'Supprimer ce projet ?',
    'work.confirm.delete_project_message': 'Le projet, ses chapitres et son historique seront définitivement supprimés. Cette action est irréversible.',
    'work.confirm.delete_org_title': 'Supprimer cette organisation ?',
    'work.confirm.delete_org_message': "L'organisation sera supprimée. Les projets qu'elle contient restent intacts et reviennent dans votre liste sans dossier.",
    'work.confirm.delete_action': 'Supprimer',
    'work.share_project': 'Partager le projet',
    'work.share_org': "Partager l'organisation",
    'work.view_history': "Voir l'activité",
    'work.history_modal.title_for': "Activité — {name}",
    'work.shared_with': 'Partagé · {count}',
    'work.shared_bucket.name': 'Partagés avec moi',
    'work.shared_bucket.description':
      "Projets sur lesquels on vous a invité·e, en dehors de vos propres organisations.",
    'work.modal.delete_confirm_org': "Supprimer cette organisation et tous ses projets ? Cette action est irréversible.",
    'work.modal.delete_confirm_project': 'Supprimer ce projet ? Cette action est irréversible.',
    'work.modal.danger.org': "Supprimer définitivement cette organisation.",
    'work.modal.danger.project': 'Supprimer définitivement ce projet.',
    'work.modal.upload_failed': "Envoi de l'image de couverture impossible. Vos modifications n'ont pas été enregistrées. Réessayez sans couverture ou contactez votre administrateur.",
    'work.modal.upload_unavailable': "Les téléversements ne sont pas encore configurés sur ce serveur. Enregistrez sans couverture pour le moment.",

    'create_project.title': 'Nouveau projet',
    'create_project.subtitle':
      "Configurez les bases — vous pourrez tout affiner plus tard.",
    'create_project.project_title': 'Titre du projet',
    'create_project.description': 'Courte description',
    'create_project.genre': 'Genre',
    'create_project.cover': 'Image de couverture',
    'create_project.save_cta': 'Créer le projet',
    'create_project.cancel': 'Annuler',
    'create_project.section.title': 'Titre',
    'create_project.section.description': 'Résumé public',
    'create_project.section.description_hint': "Résumé 4e de couverture vu par les lecteurs dans le catalogue.",
    'create_project.section.characters': 'Personnages principaux',
    'create_project.section.characters_hint': "Les noms ici créent des fiches de personnages dans le plan.",
    'create_project.section.plot_points': "Points d'intrigue",
    'create_project.section.scenarios': 'Scénarios',
    'create_project.section.notes': 'Notes',
    'create_project.section.chapters': 'Chapitres',
    'create_project.section.settings': 'Paramètres',
    'create_project.add_character': 'Ajouter un personnage',
    'create_project.add_chapter': 'Ajouter un chapitre',
    'create_project.save_draft': 'Enregistrer le brouillon',
    'create_project.org': 'Organisation',
    'create_project.proj': 'Projet',
    'create_project.danger_zone': 'Zone sensible',
    'create_project.section.tags': 'Étiquettes',
    'create_project.section.category': 'Catégorie',
    'create_project.section.category_hint': "La grande famille que les lecteurs parcourent (Fiction, Documentaire, Jeunesse…). Le Genre, plus bas, en est la sous-classe narrative (Fantasy, Mystère, Romance…).",
    'create_project.section.target_audience': 'Public cible',
    'create_project.section.language': 'Langue',
    'create_project.section.copyright': 'Droits d’auteur',
    'create_project.section.toc': 'Table des matières',
    'create_project.section.story_note': "Note de l'histoire",
    'create_project.story_details': "Détails de l'histoire",
    'create_project.add_photo': 'Ajouter une photo',
    'create_project.image_placeholder': 'Cliquez pour ajouter une couverture',
    'create_project.add_more_characters': "Ajouter d'autres personnages",
    'create_project.character_placeholder': 'Personnage {index}',
    'create_project.save': 'Enregistrer',
    'create_project.saving': 'Enregistrement…',
    'create_project.add_new': 'Ajouter',
    'create_project.delete_project': 'Supprimer le projet',
    'create_project.danger.description_project': 'Supprimer définitivement ce projet.',
    'create_project.prompt.chapter_name': 'Saisir le nom du chapitre',
    'create_project.prompt.rename_chapter': 'Saisir le nouveau nom du chapitre',
    'create_project.rename_chapter': 'Renommer le chapitre',
    'create_project.delete_chapter': 'Supprimer le chapitre',
    'create_project.confirm_delete': 'Supprimer',
    'create_project.no_chapters.title': 'Aucun chapitre',
    'create_project.no_chapters.description': 'Cliquez sur « Ajouter » pour créer votre premier chapitre.',
    'create_project.reorder_chapter': 'Glissez pour réordonner le chapitre',
    'create_project.chapter_n': 'Chapitre {n}',
    'create_project.bulk.select_row': 'Sélectionner le chapitre',
    'create_project.bulk.selected_count': '{n} chapitre(s) sélectionné(s)',
    'create_project.bulk.cancel': 'Annuler',
    'create_project.bulk.delete': 'Supprimer {n}',
    'create_project.bulk.confirm_delete': 'Confirmer la suppression de {n}',
    'create_project.bulk.working': 'Suppression…',
    'create_project.bulk.toast.deleted': '{n} chapitre(s) déplacé(s) à la corbeille.',
    'create_project.bulk.toast.delete_failed': 'Suppression de {n} chapitre(s) impossible — réessayez.',
    'create_project.toast.chapter_reorder_failed': 'Réorganisation impossible. Réessayez.',
    'create_project.confirm.delete_chapter': 'Supprimer ce chapitre ?',
    'create_project.confirm.delete_project': 'Le projet, ses chapitres et son historique seront définitivement supprimés. Cette action est irréversible.',
    'create_project.confirm.delete_project_title': 'Supprimer ce projet ?',
    'create_project.toast.project_saved': 'Projet mis à jour.',
    'create_project.toast.project_save_failed': 'Enregistrement impossible. Réessayez.',
    'create_project.toast.project_deleted': 'Projet supprimé.',
    'create_project.toast.project_delete_failed': 'Suppression impossible. Réessayez.',
    'create_project.toast.cover_upload_failed': "Couverture non envoyée. Réessayez.",
    'create_project.toast.chapter_added': 'Chapitre ajouté.',
    'create_project.toast.chapter_add_failed': "Ajout impossible. Réessayez.",
    'create_project.toast.chapter_renamed': 'Chapitre mis à jour.',
    'create_project.toast.chapter_rename_failed': 'Mise à jour impossible. Réessayez.',
    'create_project.toast.chapter_deleted': 'Chapitre supprimé.',
    'create_project.toast.chapter_delete_failed': 'Suppression impossible. Réessayez.',
    'create_project.category.biography': 'Biographie',
    'create_project.category.fiction': 'Fiction',
    'create_project.category.non_fiction': 'Non-fiction',
    'create_project.category.novel': 'Roman',
    'create_project.category.short_story': 'Nouvelle',
    'create_project.category.poetry': 'Poésie',
    'create_project.category.fantasy': 'Fantasy',
    'create_project.category.science_fiction': 'Science-fiction',
    'create_project.category.romance': 'Romance',
    'create_project.category.mystery': 'Polar',
    'create_project.category.thriller': 'Thriller',
    'create_project.category.horror': 'Horreur',
    'create_project.category.historical_fiction': 'Roman historique',
    'create_project.category.young_adult': 'Jeune adulte',
    'create_project.category.childrens': 'Livre jeunesse',
    'create_project.category.graphic_novel': 'Roman graphique',
    'create_project.category.drama': 'Drame',
    'create_project.category.screenplay': 'Scénario',
    'create_project.category.memoir': 'Mémoires',
    'create_project.category.autobiography': 'Autobiographie',
    'create_project.category.self_help': 'Développement personnel',
    'create_project.category.history': 'Histoire',
    'create_project.category.essay': 'Essai',
    'create_project.category.science': 'Science',
    'create_project.category.business': 'Affaires',
    'create_project.category.travel': 'Voyage',
    'create_project.category.placeholder': 'Rechercher une catégorie…',
    'create_project.category.clear': 'Effacer la catégorie',
    'create_project.category.no_match': 'Aucune catégorie ne correspond à votre recherche.',
    'create_project.category.show_more': 'Afficher {count} de plus',
    'create_project.audience.children': 'Enfants',
    'create_project.audience.teenagers': 'Adolescents',
    'create_project.audience.adults': 'Adultes',
    'create_project.audience.elders': 'Seniors',
    'create_project.language.english': 'Anglais',
    'create_project.language.french': 'Français',
    'create_project.copyright.all_rights': 'Tous droits réservés',
    'create_project.copyright.creative_commons': 'Creative Commons',

    'comments.title': 'Commentaires',
    'comments.show_resolved': 'Résolus',
    'comments.placeholder': 'Écrire un commentaire…',
    'comments.post': 'Publier',
    'comments.post_reply': 'Répondre',
    'comments.posting': 'Publication…',
    'comments.compose_new': 'Nouveau commentaire',
    'comments.compose_reply': 'Réponse…',
    'comments.cancel_reply': 'Annuler la réponse',
    'comments.replying_to': 'En réponse à',
    'comments.submit_hint': 'Ctrl/⌘ + Entrée pour publier',
    'comments.clear': 'Effacer',
    'comments.reply': 'Répondre',
    'comments.edit': 'Modifier',
    'comments.delete': 'Supprimer',
    'comments.save': 'Enregistrer',
    'comments.cancel': 'Annuler',
    'comments.resolve': 'Résoudre',
    'comments.unresolve': 'Rouvrir',
    'comments.resolved': 'Résolu',
    'comments.unknown_author': 'Inconnu',
    'comments.confirm_delete': 'Le commentaire et toutes les réponses qui en découlent seront supprimés définitivement.',
    'comments.confirm_delete_title': 'Supprimer ce commentaire ?',
    'comments.empty.title': 'Aucun commentaire',
    'comments.empty.description':
      "Sélectionnez du texte dans l'éditeur et cliquez sur l'icône de commentaire pour ouvrir un fil.",
    'comments.toast.create_failed': "Publication du commentaire impossible. Réessayez.",
    'comments.toast.update_failed': 'Mise à jour du commentaire impossible. Réessayez.',
    'comments.toast.delete_failed': 'Suppression du commentaire impossible. Réessayez.',
    'comments.toast.resolve_failed': 'Mise à jour du commentaire impossible. Réessayez.',
    'comments.toast.verification_required': 'Vérifiez votre adresse e-mail pour commenter — touchez pour ouvrir les paramètres.',

    'editor.preview.label': 'Aperçu',
    'editor.preview.back': "Revenir à l'édition",
    'editor.preview.untitled': 'Chapitre sans titre',
    'editor.preview.empty.title': 'Rien à prévisualiser',
    'editor.preview.empty.description':
      "Commencez à écrire dans l'éditeur et votre chapitre s'affichera ici tel qu'un lecteur le verrait.",

    'share.button': 'Partager',
    'share.title': 'Partager le chapitre',
    'share.close': 'Fermer',
    'share.done': 'Terminé',
    'share.search_placeholder': 'Rechercher par nom ou e-mail',
    'share.section.collaborators': 'Collaborateurs',
    'share.section.invite': 'Inviter',
    'share.empty.collaborators': "Aucun collaborateur. Invitez quelqu'un ci-dessous.",
    'share.empty.invite': 'Aucun autre utilisateur à inviter.',
    'share.empty.no_match': 'Aucun résultat.',
    'share.role.editor': 'Éditeur',
    'share.role.reader': 'Commentateur',
    'share.action.make_editor': 'Passer éditeur',
    'share.action.make_reader': 'Passer commentateur',
    'share.action.remove': "Retirer l'accès",
    'share.action.invite_editor': 'Inviter comme éditeur',
    'share.action.invite_reader': 'Inviter comme commentateur',
    'share.toast.fetch_failed': 'Collaborateurs indisponibles. Réessayez.',
    'share.toast.role_updated': 'Accès mis à jour.',
    'share.toast.role_failed': 'Mise à jour impossible. Réessayez.',
    'share.toast.removed': 'Collaborateur retiré.',
    'share.toast.remove_failed': 'Retrait impossible. Réessayez.',
    'share.title.project': 'Partager le projet',
    'share.title.organisation': "Partager l'organisation",

    'publish.button.publish': 'Publier',
    'publish.button.published': 'Publié',
    'publish.close': 'Fermer',
    'publish.cancel': 'Annuler',
    'publish.confirm_publish': 'Publier le chapitre',
    'publish.confirm_unpublish': 'Dépublier',
    'publish.working': 'En cours…',
    'publish.dialog.publish_title': 'Publier ce chapitre ?',
    'publish.dialog.unpublish_title': 'Dépublier ce chapitre ?',
    'publish.dialog.publish_body':
      "Les lecteurs et commentateurs de ce projet pourront lire ce chapitre une fois publié. Vous pouvez le dépublier à tout moment.",
    'publish.dialog.unpublish_body':
      "Ce chapitre repassera en brouillon et disparaîtra de la vue lecteur. Les collaborateurs avec un accès éditeur conservent leur accès.",
    'publish.toast.published': 'Chapitre publié.',
    'publish.toast.unpublished': 'Chapitre remis en brouillon.',
    'publish.toast.failed': 'Mise à jour de la publication impossible. Réessayez.',
    'publish.toast.no_chapter': 'Enregistrez le chapitre avant de le publier.',
    'publish.success.title': 'Chapitre publié',
    'publish.success.body':
      'Partagez le lien de lecture, ouvrez la page publique ou reprenez la rédaction.',
    'publish.success.copy': 'Copier le lien',
    'publish.success.copied_short': 'Copié',
    'publish.success.copied': 'Lien copié dans le presse-papiers.',
    'publish.success.copy_failed': 'Copie du lien impossible. Réessayez.',
    'publish.success.open_read': 'Ouvrir la page de lecture',
    'publish.success.continue_editing': 'Continuer à écrire',

    'versions.title': 'Versions',
    'versions.subtitle': 'Revenir à un brouillon antérieur',
    'versions.loading': 'Recherche des brouillons antérieurs…',
    'versions.empty.title': 'Rien à restaurer pour le moment',
    'versions.empty.hint':
      "Une sauvegarde est créée automatiquement toutes les quelques minutes pendant que vous écrivez — elles apparaîtront ici au fil du temps. Ou cliquez sur Enregistrer maintenant pour marquer ce moment précis.",
    'versions.save_point': 'Enregistrer maintenant',
    'versions.restore': 'Restaurer',
    'versions.confirm_restore': 'Remplacer le brouillon actuel',
    'versions.cancel': 'Garder l’actuel',
    'versions.working': 'Restauration…',
    'versions.author_unknown': 'Auteur inconnu',
    'versions.word_count': '{count} mots',
    'versions.toast.saved': 'Capture enregistrée à cet instant.',
    'versions.toast.save_failed':
      "Enregistrement de la version impossible. Réessayez.",
    'versions.toast.restored':
      "Restauré. Votre brouillon précédent a été conservé comme version, vous pouvez y revenir.",
    'versions.toast.restore_failed':
      "Restauration impossible. Réessayez.",

    'trash.title': 'Corbeille',
    'trash.subtitle': 'Projets et chapitres supprimés. Restaurez ou videz.',
    'trash.open_button': 'Ouvrir la corbeille',
    'trash.loading': 'Chargement de la corbeille…',
    'trash.empty': 'Rien dans la corbeille.',
    'trash.empty_hint': 'Les éléments sont conservés 30 jours, puis supprimés définitivement.',
    'trash.summary_count': '{n} élément(s) dans la corbeille',
    'trash.empty_all': 'Vider la corbeille',
    'trash.confirm_empty_all': 'Supprimer définitivement les {n} ?',
    'trash.bulk.select_all': 'Tout sélectionner dans cette section',
    'trash.bulk.select_row': 'Sélectionner la ligne',
    'trash.bulk.selected_count': '{n} sélectionné(s)',
    'trash.bulk.clear': 'Effacer la sélection',
    'trash.bulk.purge': 'Supprimer {n} définitivement',
    'trash.bulk.confirm_purge': 'Confirmer la suppression de {n}',
    'trash.toast.bulk_purged': 'Suppression définitive de {n} élément(s).',
    'trash.toast.bulk_purge_failed': "Suppression de {n} élément(s) impossible — réessayez.",
    'trash.section.projects': 'Projets · {count}',
    'trash.section.projects_subtitle':
      "La restauration d'un projet rend également ses chapitres à nouveau visibles.",
    'trash.section.chapters': 'Chapitres · {count}',
    'trash.section.chapters_subtitle':
      "Chapitres de projets supprimés : restaurez d'abord le projet.",
    'trash.in_project': 'Dans {project}',
    'trash.parent_in_trash': "Restaurez d'abord le projet parent.",
    'trash.deleted_when': 'Supprimé le {when}',
    'trash.restore': 'Restaurer',
    'trash.purge': 'Supprimer définitivement',
    'trash.confirm_purge': 'Supprimer définitivement',
    'trash.cancel': 'Annuler',
    'trash.working': 'Suppression…',
    'trash.toast.restored': 'Restauré.',
    'trash.toast.restore_failed': "Restauration impossible. Réessayez.",
    'trash.toast.restore_parent_first':
      "Restaurez d'abord le projet parent, puis ce chapitre.",
    'trash.toast.purged': 'Supprimé définitivement.',
    'trash.toast.purge_failed': "Suppression impossible. Réessayez.",

    'editor.save.state.save_now': 'Enregistrer',
    'editor.save.state.saving': 'Enregistrement…',
    'editor.save.state.just_saved': 'Enregistré',
    'editor.save.state.saved': 'Enregistré',
    'editor.save.state.saved_at': 'Enregistré il y a {time}',
    'editor.save.state.error': 'Réessayer',
    'editor.save.toast.failed': "Enregistrement du chapitre impossible. Réessayez.",
    'editor.save.toast.no_chapter': "Le chapitre n'est pas encore enregistré. Rechargez la page.",

    'grammar.title': 'Grammaire et orthographe',
    'grammar.run': 'Vérifier',
    'grammar.running': 'Analyse…',
    'grammar.clean': 'Aucun problème détecté.',
    'grammar.counts': '{grammar} grammaire · {spelling} orthographe',
    'grammar.no_suggestions': "Aucune suggestion — ignorez si c'est voulu.",
    'grammar.empty.title': 'Texte impeccable.',
    'grammar.empty.description':
      "Aucun problème de grammaire ou d'orthographe détecté dans ce chapitre.",
    'grammar.source.grammar': 'Grammaire',
    'grammar.source.spelling': 'Orthographe',
    'grammar.description.possible_typo': "Mot inconnu du dictionnaire.",
    'grammar.action.apply': 'Appliquer « {suggestion} »',
    'grammar.action.dismiss': 'Ignorer',
    'grammar.toast.no_issues': 'Votre texte est propre.',
    'grammar.toast.stale': 'Le texte a changé depuis la dernière analyse — relancez la vérification.',
    'grammar.toast.failed': "Vérification impossible. Réessayez.",

    'format.title': 'Mise en forme',
    'format.subtitle': 'Insérez un modèle ou nettoyez la typographie.',
    'format.close': 'Fermer',
    'format.cancel': 'Annuler',
    'format.apply_template': 'Insérer le modèle',
    'format.preview': 'Aperçu',
    'format.replace_chapter':
      'Remplacer tout le chapitre (par défaut : insérer au curseur)',
    'format.section.templates': 'Modèles',
    'format.section.tidy': 'Nettoyage typographique',
    'format.tidy.selection': 'Nettoyer la sélection',
    'format.tidy.chapter': 'Nettoyer le chapitre',
    'format.tidy.description':
      "Guillemets français, tirets cadratins, points de suspension, espaces superflues. Appliqué au texte sans toucher à la mise en forme.",
    'format.toast.template_applied': 'Modèle inséré.',
    'format.toast.tidied_selection': 'Sélection nettoyée.',
    'format.toast.tidied_chapter': 'Chapitre nettoyé.',
    'format.toast.no_selection': "Sélectionnez d'abord du texte.",

    'format.template.novel_chapter.name': 'Chapitre de roman',
    'format.template.novel_chapter.summary': "Titre, accroche et trois scènes.",
    'format.template.novel_chapter.title': 'Titre du chapitre',
    'format.template.novel_chapter.hook': "Commencez par une accroche — plantez le décor et les enjeux.",
    'format.template.novel_chapter.scene': 'Scène {n}',
    'format.template.novel_chapter.scene_body': 'Écrivez cette scène…',

    'format.template.essay.name': 'Essai',
    'format.template.essay.summary': 'Structure classique en cinq paragraphes.',
    'format.template.essay.title': "Titre de l'essai",
    'format.template.essay.intro': 'Introduction',
    'format.template.essay.intro_body':
      'Présentez le sujet et formulez votre thèse.',
    'format.template.essay.body': 'Argument {n}',
    'format.template.essay.body_prompt': 'Développez un argument ici.',
    'format.template.essay.conclusion': 'Conclusion',
    'format.template.essay.conclusion_body':
      'Reformulez la thèse à la lumière des arguments et concluez par une ouverture.',

    'format.template.dialogue_scene.name': 'Scène de dialogue',
    'format.template.dialogue_scene.summary': "Échange entre deux personnages avec décor.",
    'format.template.dialogue_scene.title': 'Scène',
    'format.template.dialogue_scene.setting': 'Décrivez le décor en une ligne.',
    'format.template.dialogue_scene.char_a': 'Personnage A',
    'format.template.dialogue_scene.char_b': 'Personnage B',
    'format.template.dialogue_scene.line': 'Ligne de dialogue.',

    'format.template.outline.name': 'Plan',
    'format.template.outline.summary': 'Structure en puces imbriquées pour préparer.',
    'format.template.outline.title': 'Plan',
    'format.template.outline.point': 'Idée principale {n}',
    'format.template.outline.detail': 'Détail à développer.',

    'format.template.three_act.name': 'Structure en trois actes',
    'format.template.three_act.summary': 'Exposition, confrontation, résolution.',
    'format.template.three_act.setup': 'Acte I — Exposition',
    'format.template.three_act.setup_body':
      "Présentez le monde, le protagoniste et l'élément déclencheur.",
    'format.template.three_act.confrontation': 'Acte II — Confrontation',
    'format.template.three_act.confrontation_body':
      'Faites monter la tension et atteignez le point central.',
    'format.template.three_act.resolution': 'Acte III — Résolution',
    'format.template.three_act.resolution_body':
      "Livrez l'apogée puis le dénouement.",

    'format.template.blog_post.name': 'Article de blog',
    'format.template.blog_post.summary': 'Accroche, pourquoi, comment, conclusion.',
    'format.template.blog_post.title': "Titre de l'article",
    'format.template.blog_post.hook':
      "Ouvrez avec une phrase qui promet ce que le lecteur va apprendre.",
    'format.template.blog_post.why': "Pourquoi c'est important",
    'format.template.blog_post.why_body':
      "Expliquez le problème et pourquoi le lecteur doit s'y intéresser maintenant.",
    'format.template.blog_post.how': 'Comment ça marche',
    'format.template.blog_post.how_body':
      'Déroulez la méthode en étapes concrètes et ordonnées.',
    'format.template.blog_post.takeaway': 'À retenir',
    'format.template.blog_post.takeaway_body':
      'Résumez la chose à retenir en une phrase.',

    'inspiration.title': 'Inspiration',
    'inspiration.subtitle':
      'Synonymes et citations pour débloquer votre brouillon.',
    'inspiration.close': 'Fermer',
    'inspiration.placeholder': 'Rechercher un mot ou un thème…',
    'inspiration.hint': 'Appuyez sur Entrée pour rechercher.',
    'inspiration.section.synonyms': 'Synonymes',
    'inspiration.section.synonyms_empty':
      'Aucun synonyme trouvé pour ce terme dans cette langue.',
    'inspiration.section.citations': 'Citations',
    'inspiration.section.citations_empty':
      'Aucune citation — essayez un thème plus large.',
    'inspiration.empty.title': 'Cherchez un mot pour commencer',
    'inspiration.empty.description':
      "Ou surlignez un mot dans l'éditeur avant d'ouvrir ce panneau — il alimente la recherche.",
    'inspiration.action.insert': 'Insérer',
    'inspiration.action.copy': 'Copier',
    'inspiration.toast.synonym_inserted': 'Synonyme inséré.',
    'inspiration.toast.quote_inserted': 'Citation insérée en bloc.',
    'inspiration.toast.copied': 'Copié dans le presse-papiers.',
    'inspiration.toast.copy_failed': 'Copie impossible. Réessayez.',
    'inspiration.toast.failed': 'Inspiration indisponible. Réessayez.',

    'chatai.title': 'Assistant IA',
    'chatai.subtitle': 'Poser, continuer, reformuler ou planifier.',
    'chatai.close': 'Fermer',
    'chatai.reset': 'Effacer la conversation',
    'chatai.greeting': 'Choisissez un mode ci-dessus et tapez une demande pour commencer.',
    'chatai.send': 'Envoyer',
    'chatai.sending': 'En cours…',
    'chatai.submit_hint': 'Ctrl/⌘ + Entrée pour envoyer',
    'chatai.use_context': 'Inclure le chapitre comme contexte',
    'chatai.empty_response': '(aucune réponse)',
    'chatai.rewrite_of': 'Reformuler : « {text} »',
    'chatai.continue_of': 'Continuer à partir du curseur',
    'chatai.mode.ask': 'Poser',
    'chatai.mode.continue': 'Continuer',
    'chatai.mode.rewrite': 'Reformuler',
    'chatai.mode.outline': 'Plan',
    'chatai.hint.ask':
      "Question libre. Activez le contexte si la réponse doit s'appuyer sur votre brouillon.",
    'chatai.hint.continue':
      'Génère le prochain paragraphe à partir du curseur. Le champ de saisie est facultatif.',
    'chatai.hint.rewrite':
      "Reformule la sélection surlignée. Ajoutez une indication de ton si besoin.",
    'chatai.hint.outline':
      "Génère un plan structuré pour le sujet saisi ci-dessous.",
    'chatai.placeholder.ask': "Poser une question à l'assistant…",
    'chatai.placeholder.continue': 'Facultatif : ton ou direction…',
    'chatai.placeholder.rewrite': 'Facultatif : ton (plus clair, serré, formel)…',
    'chatai.action.copy': 'Copier',
    'chatai.action.insert': 'Insérer',
    'chatai.pro_lane.label': 'Pro',
    'chatai.pro_lane.tooltip':
      "Réponse générée par Pro — votre abonnement utilise notre modèle le plus précis pour Continuer, Reformuler et Plan.",
    'chatai.toast.inserted': "Inséré dans l'éditeur.",
    'chatai.toast.copied': 'Copié dans le presse-papiers.',
    'chatai.toast.copy_failed': 'Copie impossible. Réessayez.',
    'chatai.toast.select_text': "Surlignez d'abord du texte dans l'éditeur.",
    'chatai.toast.empty_chapter': "Votre chapitre est vide — écrivez d'abord un peu.",
    'chatai.toast.failed': "L'assistant n'a pas pu répondre. Réessayez.",

    'flyingmenu.action.rewrite': 'Reformuler la sélection',
    'flyingmenu.action.continue': 'Continuer après la sélection',
    'flyingmenu.toast.rewrote': 'Sélection reformulée.',
    'flyingmenu.toast.rewrote_pro': 'Reformulé avec Pro.',
    'flyingmenu.toast.continued': 'Suite ajoutée.',
    'flyingmenu.toast.continued_pro': 'Continué avec Pro.',
    'flyingmenu.toast.empty': "Aucune réponse — réessayez.",
    'flyingmenu.toast.failed': 'Requête IA impossible. Réessayez.',

    'goal.kanban.title': 'Tâches',
    'goal.kanban.tab_label': 'Tâches',
    'goal.kanban.subtitle':
      "Glissez les cartes entre les étapes pour suivre votre plan d'écriture.",
    'planner.tab_label': 'Plan',
    'planner.signature.kicker': 'Plan narratif',
    'planner.signature.untitled_project': 'Projet sans titre',
    'planner.signature.coverage_label': 'Temps forts complétés',
    'planner.signature.coverage_tooltip': '{pct}% des temps forts de votre structure ont des notes.',
    // Panneau de suggestions avancées (anciennement « tropes »).
    'planner.tropes.open': 'Suggestions avancées',
    'planner.tropes.panel_title': 'Suggestions avancées',
    'planner.tropes.subtitle_ai': "Personnalisées pour votre projet par l'assistant.",
    'planner.tropes.subtitle_curated': 'Tirées du catalogue.',
    'planner.tropes.ai_badge': 'Personnalisé IA',
    'planner.tropes.refresh': 'Rafraîchir',
    'planner.tropes.loading': "Lecture de votre plan…",
    'planner.tropes.dismiss': 'Masquer cette suggestion',
    'planner.tropes.dismissed_toast': 'Masquée pour ce projet.',
    'planner.tropes.apply': 'Ajouter au plan',
    'planner.tropes.apply_update': "Compléter « {title} »",
    'planner.tropes.apply_update_generic': "Compléter une fiche existante",
    'planner.tropes.apply_delete': "Retirer « {title} »",
    'planner.tropes.apply_delete_generic': "Retirer une fiche existante",
    'planner.tropes.apply_change_kind': "Reclasser « {title} » en {kind}",
    'planner.tropes.apply_change_kind_generic': "Reclasser en {kind}",
    'planner.tropes.apply_merge': "Fusionner « {source} » dans « {target} »",
    'planner.tropes.apply_merge_generic': "Fusionner deux fiches",
    'planner.tropes.applied_toast': "« {title} » ajouté à votre plan.",
    // Slice 7.
    'planner.tropes.applied_pill': "Appliqué à « {title} »",
    'planner.tropes.applied_pill_tooltip': "Cette suggestion est déjà dans votre plan — vous l'avez ajoutée précédemment.",
    'planner.tropes.quota.pill': '{remaining} IA restantes',
    'planner.tropes.quota.tooltip': "Utilisé {used} sur {limit} suggestions IA aujourd'hui.",
    'planner.tropes.reset_dismissed': 'Voir les {count} masqué·e·s',
    'planner.tropes.dismissed_reset': "Liste des masqué·e·s effacée. Appuyez sur Rafraîchir pour les revoir.",
    'planner.tropes.updated_toast': "« {title} » mis à jour.",
    'planner.tropes.deleted_toast': "« {title} » retiré. Restaurez depuis la corbeille sous 30 jours.",
    'planner.tropes.reclassified_toast': "« {title} » reclassé.",
    'planner.tropes.merged_toast': "« {source} » fusionné dans « {target} ».",
    'planner.tropes.undone_toast': 'Annulation effectuée.',
    'planner.tropes.undo_failed': "Annulation impossible — retirez-le manuellement depuis le plan.",
    'planner.tropes.apply_failed': "Ajout impossible — réessayez.",
    'planner.tropes.apply_unsupported_kind': "Cette suggestion demande un ajout en plusieurs étapes — bientôt disponible.",
    'planner.tropes.tier_label': 'Niveau {tier}',
    'planner.tropes.tier_tooltip': 'Niveau {tier} — 1 universel, 2 lié au genre, 3 spécialisé.',
    'planner.tropes.footer_count': "Affichage de {shown} sur {total} suggestions du catalogue.",
    'planner.tropes.empty.title': 'Aucune suggestion pour le moment',
    'planner.tropes.empty.description': "Essayez de rafraîchir, ou ajoutez une accroche et quelques personnages pour donner plus de matière à l'assistant.",
    'planner.tropes.quota.title': "Quota IA atteint pour aujourd'hui",
    'planner.tropes.quota.description': "Vous avez atteint la limite quotidienne. Vous pouvez tout de même parcourir le catalogue sans entamer votre quota.",
    'planner.tropes.quota.show_curated': 'Voir les suggestions du catalogue',
    'planner.tropes.error.title': "Chargement impossible",
    'planner.tropes.error.description': "L'assistant est peut-être occupé. Réessayez dans un instant.",
    'planner.tropes.error.retry': 'Réessayer',
    'planner.tropes.kind.character': 'personnage',
    'planner.tropes.kind.location': 'lieu',
    'planner.tropes.kind.object': 'objet',
    'planner.tropes.kind.event': 'événement',
    'planner.tropes.kind.scene': 'scène',
    'planner.tropes.kind.magic_system': 'système de magie',
    'planner.tropes.kind.structure': 'structure',
    'planner.tropes.kind.theme': 'thème',
    'planner.tropes.kind.relationship': 'relation',
    'planner.snippets.open': 'Bibliothèque',
    'planner.snippets.title': 'Modèles réutilisables',
    'planner.snippets.subtitle': "Importez un archétype, un système de magie ou une scène déjà créés dans ce projet.",
    'planner.snippets.empty.title': "Aucun modèle pour l'instant",
    'planner.snippets.empty.description': "Cliquez « Enregistrer comme modèle » sur n'importe quelle carte pour la réutiliser.",
    'planner.snippets.empty.mine.title': 'Aucun modèle',
    'planner.snippets.empty.mine.description': "Cliquez « Enregistrer comme modèle » sur n'importe quelle carte pour la réutiliser.",
    'planner.snippets.empty.marketplace.title': 'Aucun modèle public',
    'planner.snippets.empty.marketplace.description': "Soyez le premier à publier un archétype réutilisable. Ouvrez un de vos modèles et cliquez sur l'icône de partage pour le proposer à la communauté.",
    'planner.snippets.empty.classics.title': 'Aucun classique dans cette langue',
    'planner.snippets.empty.classics.description': 'Les classiques sont enrichis par langue. Changez la langue de l\'interface ou revenez plus tard.',
    'planner.snippets.tab.mine': 'Modèles',
    'planner.snippets.tab.marketplace': 'Communauté',
    'planner.snippets.tab.classics': 'Classiques',
    'planner.snippets.mine_hint': "Votre bibliothèque privée. Partagez-en un avec la communauté via l'icône de partage.",
    'planner.snippets.marketplace_hint': 'Modèles sélectionnés et communautaires à importer directement dans ce projet.',
    'planner.snippets.classics_hint': "Modules tirés des classiques du domaine public enrichis — personnages, lieux, règles du monde, temps forts — prêts à étudier ou remixer.",
    'planner.snippets.search_placeholder': 'Rechercher par titre, résumé ou œuvre',
    'planner.snippets.search_empty.title': 'Aucun résultat',
    'planner.snippets.search_empty.description': "Aucun résultat pour « {query} ». Essayez une recherche plus courte ou effacez-la pour tout parcourir.",
    'planner.snippets.over_cap_hint': "Affichage des {shown} premiers sur {total} — affinez la recherche pour en voir davantage.",
    'planner.snippets.featured': 'À la une',
    'planner.snippets.yours': 'À vous',
    'planner.snippets.anonymous': 'anonyme',
    'planner.snippets.publish': 'Partager avec la communauté',
    'planner.snippets.unpublish': 'Repasser en privé',
    'planner.snippets.published': 'Partagé avec la communauté.',
    'planner.snippets.unpublished': 'Modèle redevenu privé.',
    'planner.snippets.import': 'Importer ici',
    'planner.snippets.delete': 'Supprimer',
    'planner.snippets.imported': 'Modèle ajouté au projet.',
    'planner.coherence.button': 'Vérifier la cohérence',
    'planner.coherence.tooltip': "Comparer un chapitre à votre plan avec l'IA.",
    'planner.coherence.title': 'Vérification de cohérence',
    'planner.coherence.subtitle': "Compare un chapitre à votre plan pour relever contradictions, dérives par rapport à vos notes, et idées à explorer.",
    'planner.coherence.chapter': 'Chapitre à analyser',
    'planner.coherence.run': "Lancer l'analyse",
    'planner.coherence.running': 'Analyse…',
    'planner.coherence.summary': 'Synthèse',
    'planner.coherence.no_flags': "Aucune anomalie détectée — le chapitre est cohérent avec votre plan.",
    'planner.coherence.no_chapters': 'Aucun chapitre à analyser pour le moment.',
    'planner.coherence.untitled_chapter': 'Chapitre {id}',
    'planner.coherence.failed': "L'analyse a échoué — réessayez dans un instant.",
    'planner.coherence.quota_exceeded': "Vous avez utilisé vos analyses de cohérence du jour.",
    'planner.coherence.truncated': "Le chapitre ou le plan a été tronqué pour l'IA — certains projets longs peuvent manquer de contexte.",
    'planner.coherence.severity.error': 'Contradiction',
    'planner.coherence.severity.warning': 'Dérive',
    'planner.coherence.severity.info': 'Suggestion',
    'planner.coherence.category.character': 'Personnage',
    'planner.coherence.category.voice': 'Voix',
    'planner.coherence.category.tone': 'Ton',
    'planner.coherence.category.pacing': 'Rythme',
    'planner.coherence.category.plot': 'Intrigue',
    'planner.coherence.category.continuity': 'Continuité',
    'planner.coherence.category.worldbuilding': 'Univers',
    'planner.coherence.category.motif': 'Motif',
    'planner.coherence.category.magic_rules': 'Règles de magie',
    'planner.coherence.category.structure': 'Structure',
    'planner.coherence.category.other': 'Autre',
    'planner.coherence.category.parse_fallback': 'Réponse illisible',
    'planner.coherence.parse_fallback_message':
      "Le correcteur a renvoyé une réponse que nous n'avons pas pu analyser correctement. Réessayez — la plupart des erreurs disparaissent au deuxième essai. Le texte brut se trouve dans la zone de preuve ci-dessous.",

    'planner.project_critique.button': 'Critiquer tout le projet',
    'planner.project_critique.tooltip': 'Lance une lecture éditoriale IA sur tous les chapitres et votre plan.',
    'planner.project_critique.title': 'Critique du projet',
    'planner.project_critique.subtitle': "Une lecture éditoriale qui parcourt tous vos chapitres et votre plan. Repère intrigues abandonnées, personnages laissés en plan, étapes structurelles non atteintes et répétitions entre chapitres.",
    'planner.project_critique.pro_badge': 'Pro',
    'planner.project_critique.pro_tooltip': "Fonction Premium — votre abonnement utilise notre modèle le plus précis.",
    'planner.project_critique.run': 'Lancer la critique',
    'planner.project_critique.rerun': 'Relancer',
    'planner.project_critique.running': 'Analyse en cours…',
    'planner.project_critique.wait.title': 'Lecture de votre récit',
    'planner.project_critique.wait.eta': "Environ 15 à 25 secondes",
    'planner.project_critique.wait.stats': "Sur {count} fiches du plan",
    'planner.project_critique.wait.stage_reading': 'Lecture du plan…',
    'planner.project_critique.wait.stage_analysing': 'Analyse des résumés de chapitres…',
    'planner.project_critique.wait.stage_cross_checking': 'Recoupement des fils narratifs…',
    'planner.project_critique.wait.stage_compiling': 'Compilation des observations…',
    'planner.project_critique.preflight.no_preset': "Choisissez un préréglage Genre + Structure dans l'en-tête du plan pour activer les observations structurelles. Sans ça, l'analyse sautera cette catégorie.",
    'planner.project_critique.preflight.thin_corpus': "Seulement {count} chapitres rédigés pour l'instant — l'analyse s'appuiera surtout sur le plan tant que la prose reste légère.",
    // Tier-4 §14
    'planner.project_critique.dna_preflight.title': "Votre ADN d'histoire risque de fausser la critique",
    'planner.project_critique.dna_preflight.intro': 'Points à vérifier',
    'planner.project_critique.dna_preflight.hint': "Essayez « Brouillon depuis les chapitres » dans Notes (gratuit) pour rattraper l'ADN avant de consommer du quota Pro.",
    'planner.project_critique.dna_preflight.proceed': 'Continuer quand même',
    'planner.project_critique.dna_preflight.fix_first': "D'abord corriger",
    'planner.project_critique.dna_preflight.flag.logline_short': 'accroche trop courte',
    'planner.project_critique.dna_preflight.flag.logline_no_period': 'accroche coupée en pleine phrase',
    'planner.project_critique.dna_preflight.flag.synopsis_short': 'synopsis trop léger',
    'planner.project_critique.dna_preflight.flag.synopsis_includes_act_marker': "synopsis contient le plan d'actes",
    'planner.project_critique.dna_preflight.flag.theme_truncated': 'thème coupé en plein mot',
    'planner.project_critique.dna_preflight.flag.tone_empty': 'ton vide',
    'planner.project_critique.summary': 'Synthèse',
    'planner.project_critique.failed': "La critique a échoué — réessayez dans un instant.",
    'planner.project_critique.truncated': "Votre projet a été tronqué pour l'IA — certains chapitres tardifs peuvent manquer de contexte.",
    'planner.project_critique.premium_required_title': "La critique de projet est réservée à Pro.",
    'planner.project_critique.premium_required_body': "Passez Premium pour lancer une lecture éditoriale IA sur tout votre projet, avec dette catégorisée et corrections concrètes.",
    'planner.project_critique.upgrade_cta': 'Voir les tarifs',
    'planner.project_critique.parse_fallback_message': "Le correcteur a renvoyé une réponse illisible. Relancez la critique — la plupart des erreurs disparaissent au deuxième essai.",
    'planner.project_critique.clean_title': "Rien à signaler.",
    'planner.project_critique.clean_body': "Le correcteur n'a relevé aucune dette structurante dans les quatre catégories. Continuez.",
    'planner.project_critique.category.plot_holes': "Trous d'intrigue",
    'planner.project_critique.category.character_debt': 'Dette personnage',
    'planner.project_critique.category.structural_gaps': 'Trous de structure',
    'planner.project_critique.category.pacing_repetition': 'Rythme & répétition',
    'planner.project_critique.category_clean': 'Rien à signaler dans cette catégorie.',
    'planner.project_critique.count': '{n}',
    'planner.project_critique.apply.add_label': 'Ajouter au plan',
    'planner.project_critique.apply.update_label': 'Mettre à jour',
    'planner.project_critique.apply.chapter_label': 'Réécrire le chapitre',
    'planner.project_critique.apply.add_tooltip': 'Ajoute une nouvelle fiche {kind} intitulée « {title} » à votre plan.',
    'planner.project_critique.apply.update_tooltip': 'Met à jour la fiche « {title} » avec la modification suggérée.',
    'planner.project_critique.apply.chapter_tooltip': "Réécrit le chapitre {n} en suivant l'instruction de la critique. Consomme un crédit IA pour la génération de texte.",
    'planner.project_critique.apply.running': 'Application…',
    'planner.project_critique.apply.done': 'Appliqué',
    'planner.project_critique.apply.added': '« {title} » ajouté au plan.',
    'planner.project_critique.apply.updated': '« {title} » mis à jour.',
    'planner.project_critique.apply.chapter_rewritten': "Chapitre {n} réécrit — ouvrez l'éditeur pour relire.",
    'planner.project_critique.apply.failed': "Application impossible ({reason}).",
    'planner.project_critique.scope_chapter': 'Chapitre {n}',
    'planner.project_critique.scope_project': "À l'échelle du projet",
    'planner.project_critique.show_fix': 'Voir le correctif proposé',

    'planner.actions.drag': 'Glisser pour réordonner',
    'planner.card.cross_refs_tooltip': '{count} fiche(s) liée(s)',
    'planner.card.age_unit': 'ans',
    'planner.card.placeholder': 'Ébauche',
    'planner.card.placeholder_tooltip': "Marquée comme ébauche intentionnelle — la vérification « fiches à étoffer » de l'analytique ignore celle-ci.",
    // Tier-6 §17
    'planner.card.appears.count': 'dans {count} chapitres',
    'planner.card.appears.none': "pas encore dans un chapitre",
    'planner.card.appears.tooltip': 'Mentionné·e dans {count} chapitres — cliquez pour voir lesquels.',
    'planner.card.appears.none_tooltip': "Ce personnage n'apparaît dans aucun chapitre pour l'instant.",
    'planner.card.appears.modal_title': "Apparitions de {name}",
    'planner.card.appears.modal_subtitle': '{count} chapitres mentionnent ce personnage. Cliquez sur une ligne pour ouvrir l\'éditeur.',
    'planner.card.appears.untitled_chapter': 'Chapitre {n} (sans titre)',
    'planner.card.appears.close': 'Fermer',
    // [Removed: planner.family_tree.* (FR) — see EN block.]
    'planner.timeline.toggle': 'Chronologie',
    'planner.timeline.title': 'Chronologie du projet',
    'planner.timeline.dated_suffix': 'datés',
    'planner.timeline.undated_suffix': 'sans date',
    'planner.timeline.undated': 'sans date',
    'planner.timeline.hint': "Les cartes alternent au-dessus et en dessous de la frise. Modifiez une carte pour préciser sa date — la chronologie se reconfigure automatiquement.",
    'planner.timeline.empty.title': 'Aucun événement à tracer',
    'planner.timeline.empty.description': "Créez des fiches d'événement, de chronologie, de guerre, de fête, de traité, de catastrophe ou de plan de chapitre avec une date — elles s'aligneront ici dans l'ordre chronologique.",
    'planner.map.toggle': 'Carte',
    'planner.map.title': 'Carte du monde',
    'planner.map.empty.title': 'Épinglez votre monde sur une carte',
    'planner.map.empty.description': "Importez un croquis, une carte dessinée à la main ou n'importe quelle image, puis glissez vos lieux dessus. Les coordonnées s'enregistrent sur chaque fiche de lieu pour survivre quand vous changez la taille de la carte.",
    'planner.map.upload_cta': 'Importer une carte',
    'planner.map.uploading': 'Envoi…',
    'planner.map.uploaded': "Carte importée — commencez à épingler vos lieux.",
    'planner.map.upload_failed': "L'envoi a échoué — essayez une image plus petite.",
    'planner.map.replace': 'Remplacer',
    'planner.map.clear': 'Effacer',
    'planner.map.clear_confirm': "Supprimer la carte actuelle ? Les coordonnées épinglées resteront sur chaque lieu pour qu'une nouvelle carte puisse les réutiliser.",
    'planner.map.pinned_suffix': 'épinglés',
    'planner.map.unplaced': 'Lieux non encore épinglés',
    'planner.map.pin_to_map': 'Épingler sur la carte',
    'planner.map.pin_hint': "Cliquez sur une puce pour déposer l'épingle au centre, puis glissez-la à la bonne position.",
    // [Removed: planner.graph.* (FR) — see EN block.]
    'planner.corkboard.toggle': "Tableau d'épingles",
    'planner.corkboard.list_view': 'Liste',
    'planner.corkboard.scene_index': 'Scène {index}',
    'planner.corkboard.empty.title': "Aucune scène épinglée",
    'planner.corkboard.empty.description': "Ajoutez des scènes dans Narration > Scène — elles apparaîtront sur le tableau, déplacez-les pour les réordonner.",
    'planner.loading': 'Chargement du plan…',
    'planner.loading_modules': 'Chargement des fiches…',
    'planner.search.placeholder': 'Filtrer les cartes de cette catégorie…',
    'planner.module.untitled': 'Sans titre',
    'planner.category.rail': 'Catégories du plan',
    'planner.category.switch_aria': 'Changer de catégorie',
    'planner.category.pick': 'Choisir une catégorie',
    'planner.category.world': 'Monde & Géographie',
    'planner.category.society': 'Civilisation & Société',
    'planner.category.characters': 'Personnages',
    'planner.category.creatures': 'Créatures & Nature',
    'planner.category.systems': 'Systèmes',
    'planner.category.narration': 'Narration',
    'planner.category.objects': 'Objets & Inventaire',
    'planner.category.lore': 'Documentation & Savoirs',
    'planner.category.art_direction': 'Direction artistique & Médias',
    'planner.category.metadata': 'Métadonnées',
    'planner.category.unknown': 'Autre',
    'planner.empty_project.title': 'Choisissez un projet pour commencer',
    'planner.empty_project.description':
      "Le plan narratif est lié à un projet — sélectionnez-en un pour voir son monde, ses personnages et sa narration au même endroit.",
    'planner.empty_project.no_projects_title': 'Créez votre premier projet',
    'planner.empty_project.no_projects_description':
      'Le plan se rattache à un projet. Créez-en un et revenez ici, la toile sera prête.',
    'planner.empty_project.create_cta': 'Créer un nouveau projet',
    'planner.project.switch': 'Projet',
    'planner.empty_category.title': 'Rien ici pour l’instant',
    'planner.empty_category.title_named': '{category} est vide',
    'planner.empty_category.description':
      "Touchez une puce ci-dessous pour ajouter votre première carte, importez un modèle enregistré, ou laissez l'IA démarrer cette catégorie.",
    'planner.empty_category.suggest': 'Proposer un point de départ',
    'planner.empty_category.add_first': 'Ajouter votre premier {kind}',
    'planner.kind.empty': 'Aucun {kind} pour le moment.',
    // Bande d'ajout en bas de chaque vue de catégorie — remplace les
    // lignes « vide » par type. Les types vides disparaissent ;
    // cette bande conserve l'affordance « + Ajouter » pour chacun.
    'planner.add_more_kinds.label': 'Ajouter un autre type',
    'planner.add_more_kinds.hint': '{count} autres disponibles pour cette catégorie',
    'planner.project.pick': 'Choisir un projet',
    'planner.preset.pick': 'Choisir un type d’écriture',
    'planner.preset.title': 'Appliquer un préréglage',
    'planner.preset.subtitle':
      "Chaque type d'écriture active les catégories les plus utiles et oriente l'IA. Choisissez une structure pour générer les temps forts narratifs verrouillés.",
    'planner.preset.apply': 'Appliquer',
    'planner.preset.genre': 'Type d’écriture',
    'planner.preset.structure': 'Structure narrative',
    'planner.preset.literary': 'Fiction littéraire',
    'planner.preset.fantasy': 'Fantasy',
    'planner.preset.scifi': 'Science-fiction',
    'planner.preset.mystery': 'Polar / Suspense',
    'planner.preset.romance': 'Romance',
    'planner.preset.memoir': 'Mémoires / Non-fiction',
    'planner.preset.screenplay': 'Scénario',
    'planner.preset.poetry': 'Poésie',
    'planner.preset.children': 'Jeunesse',
    'planner.preset.essay': 'Essai / Article',
    'planner.structure.pick': 'Structure narrative',
    'planner.structure.three_act': 'Trois actes',
    'planner.structure.five_act': 'Cinq actes',
    'planner.structure.heros_journey': 'Voyage du héros',
    'planner.structure.save_the_cat': 'Save the Cat',
    'planner.structure.freytag': 'Pyramide de Freytag',
    'planner.structure.kishotenketsu': 'Kishōtenketsu',
    'planner.structure.snowflake': 'Méthode du flocon',
    'planner.structure.in_media_res': 'In media res',
    'planner.structure.fichtean_curve': 'Courbe fichtéenne',
    'planner.structure.seven_point': 'Sept points',
    // Tier-5 §16
    'planner.structure.custom': 'Structure personnalisée',
    'planner.preset.custom.label': 'Vos actes et temps forts',
    'planner.preset.custom.placeholder': 'Décrivez votre structure narrative — un acte ou un temps fort par ligne. Exemple :\n\n1. Mise en place — le protagoniste dans son monde\n2. Élément déclencheur — le dragon magique attaque\n3. Action montante — recrutement des alliés\n4. Apogée — le sortilège final',
    'planner.preset.custom.hint': '{used} / {max} caractères. La critique lit ce texte tel quel et compare vos chapitres au cadre que vous avez défini.',
    'planner.dna.logline': 'Accroche',
    'planner.dna.logline_placeholder': "L'accroche en une phrase.",
    'planner.dna.synopsis': 'Synopsis',
    'planner.dna.synopsis_placeholder': "Un court paragraphe qui résume la colonne vertébrale du récit.",
    'planner.dna.theme': 'Thème',
    'planner.dna.theme_placeholder': "Ce que l'histoire raconte vraiment.",
    'planner.dna.tone': 'Ton',
    'planner.dna.tone_placeholder': 'Doux-amer, angoissant, lumineux…',
    'planner.dna.target_words': 'Objectif de mots',
    'planner.dna.toggle': 'ADN du récit',
    'planner.dna.toggle_aria': "Afficher ou masquer l'accroche, le synopsis, le thème et le ton.",
    'planner.logline_strip.aria': "Accroche du projet. Cliquez pour modifier dans l'ADN du récit.",
    'planner.logline_strip.empty_cta': "Ajoutez une accroche — une phrase qui capture votre histoire.",
    'planner.logline_strip.empty_tooltip': "Une accroche d'une phrase. Visible partout dans le plan pour garder l'épine dorsale de votre histoire en vue.",
    'planner.status.stub': 'Ébauche — titre seul',
    'planner.status.draft': 'Brouillon — partiellement rempli',
    'planner.status.complete': 'Complet — résumé, corps et métadonnées remplis',
    'planner.filter.all': 'Tous',
    'planner.filter.stub': 'Ébauches',
    'planner.filter.draft': 'Brouillons',
    'planner.filter.complete': 'Complets',
    'planner.filter.aria': 'Filtrer les fiches par état de complétion',
    'planner.spotlight.aria': 'Prochaines actions suggérées pour ce projet',
    'planner.spotlight.heading': 'Prochaines actions :',
    'planner.spotlight.no_logline': 'Ajouter une accroche',
    'planner.spotlight.no_preset': 'Choisir un genre et un préréglage de structure',
    'planner.spotlight.stubs': '{count} ébauches à enrichir',
    'planner.spotlight.low_coverage': 'Couverture des temps forts à {pct}% — remplir d\'autres temps forts',
    'planner.ai.collapse': "Réduire l'assistant IA",
    'planner.ai.expand': "Déplier l'assistant IA",
    'planner.search.clear': 'Effacer la recherche',
    'planner.density.aria': "Densité d'affichage des fiches",
    'planner.density.grid': 'Vue grille',
    'planner.density.list': 'Vue liste',
    'planner.import.button': 'Importer',
    'planner.import.title': 'Importer un manuscrit',
    'planner.import.title.manuscript': 'Importer un manuscrit',
    'planner.import.title.side_doc': 'Importer une documentation annexe',
    'planner.import.intro': "Déposez un document Word ou un PDF — on le lit et on en extrait les chapitres, personnages et lieux détectés. Vous décidez ce qui sera importé.",
    'planner.import.kind.label': "Quel type de document est-ce ?",
    'planner.import.kind.manuscript.label': 'Manuscrit',
    'planner.import.kind.manuscript.desc': "Prose avec chapitres. On en extrait les chapitres, personnages, lieux et l'ADN du projet.",
    'planner.import.kind.side_doc.label': 'Document de référence',
    'planner.import.kind.side_doc.desc': "Notes, fiches de référence, documents d'univers, systèmes magiques, factions. On découpe le texte en sections et chacune est classée dans la bonne catégorie du plan.",
    'planner.import.kind.multifile_lock': "Quand vous déposez plusieurs fichiers, chacun est traité comme un chapitre du manuscrit.",
    'planner.import.review_intro_sheet': "Cochez les entrées à importer. Chacune devient une fiche du plan dans sa catégorie détectée, avec son résumé, son contenu et son rôle.",
    'planner.import.entry_kind_tooltip': "Type de fiche détecté. Modifiable plus tard depuis la fiche elle-même.",
    'planner.import.chars': 'car.',
    'planner.import.entry_category.characters': 'Personnages',
    'planner.import.entry_category.world': 'Monde et lieux',
    'planner.import.entry_category.society': 'Société et cultures',
    'planner.import.entry_category.systems': 'Systèmes et magie',
    'planner.import.entry_category.creatures': 'Créatures',
    'planner.import.entry_category.narration': 'Narration et notes',
    'planner.import.entry_category.objects': 'Objets et accessoires',
    'planner.import.entry_category.lore': 'Documentation et histoire',
    'planner.import.entry_category.art_direction': 'Direction artistique',
    'planner.import.entry_category.metadata': 'Métadonnées',
    'planner.import.entry_category._other': 'Autre ({cat})',
    'planner.import.drop_prompt': 'Déposez un fichier ici, ou cliquez pour parcourir',
    'planner.import.drop_more_or_click': "Déposez d'autres fichiers, ou cliquez pour ajouter",
    'planner.import.multi_hint': "Chaque fichier devient un chapitre, dans l'ordre alphabétique.",
    'planner.import.accepts': 'Accepte : {accepted}',
    'planner.import.files_picked': 'Fichiers sélectionnés ({n})',
    'planner.import.analyze_n_files': 'Analyser {n} fichiers',
    'planner.import.analyzing_n': 'Analyse {current}/{total}…',
    'planner.import.analyzing_file': 'Lecture de {filename} ({current}/{total})',
    'planner.import.remove_file': 'Retirer le fichier',
    'planner.import.or': 'ou',
    'planner.import.paste_label': 'Coller du texte directement',
    'planner.import.paste_placeholder': 'Collez votre brouillon, plan ou notes de référence ici…',
    'planner.import.analyze': 'Analyser',
    'planner.import.analyzing': 'Analyse en cours…',
    'planner.import.cancel': 'Annuler',
    'planner.import.back': 'Retour',
    'planner.import.close': 'Fermer',
    'planner.import.commit_n': 'Importer {n} éléments',
    'planner.import.committing': 'Import en cours…',
    'planner.import.review_intro': "Cochez ce qu'il faut garder. Tout est sélectionné par défaut.",
    'planner.import.section.metadata': 'Détails du projet',
    'planner.import.section.chapters': 'Chapitres',
    'planner.import.section.characters': 'Personnages',
    'planner.import.section.locations': 'Lieux',
    'planner.import.metadata_hint': "Détecté à partir du texte précédant le premier chapitre. Les champs cochés remplissent votre projet ; les autres restent tels quels.",
    'planner.import.meta.title': 'Titre',
    'planner.import.meta.logline': 'Accroche',
    'planner.import.meta.synopsis': 'Synopsis',
    'planner.import.meta.genre': 'Genre',
    'planner.import.meta.theme': 'Thème',
    'planner.import.meta.has_current': "Votre projet a déjà une valeur — cocher écrasera : « {current} »",
    'planner.import.role.protagonist': 'Protagoniste',
    'planner.import.role.supporting': 'Personnage secondaire',
    'planner.import.role.antagonist': 'Antagoniste',
    'planner.import.role.mentor': 'Mentor',
    'planner.import.role.rival': 'Rival',
    'planner.import.role.ally': 'Allié',
    'planner.import.role.foil': 'Faire-valoir',
    'planner.import.role.deuteragonist': 'Deutéragoniste',
    'planner.import.role.love interest': 'Intérêt amoureux',
    'planner.import.section_empty': 'Rien détecté dans cette catégorie.',
    'planner.import.no_entries_title': "Aucune entrée détectée dans ce document.",
    'planner.import.no_entries_hint': "Aucune section exploitable n'a été repérée. Revenez en arrière et essayez un autre fichier, ou collez directement l'extrait pertinent.",
    'planner.import.sidedoc_fallback': "Nous n'avons pas pu classer ce document comme documentation annexe, mais nous y avons trouvé du contenu de type manuscrit (chapitres, personnages, lieux). Cochez parmi les éléments ci-dessous pour les importer comme un manuscrit.",
    'planner.import.ai_uplift_button': "Renfort IA : extraire les entrées du plan",
    'planner.import.ai_uplift_running': "Extraction des entrées…",
    'planner.import.ai_uplift_done': "Le renfort IA a trouvé {n} entrées. Vérifiez et cochez celles à conserver.",
    'planner.import.ai_uplift_no_entries': "Le renfort IA n'a pas pu extraire d'entrées structurées de ce texte.",
    'planner.import.ai_uplift_no_text': "Aucun texte source à analyser. Collez du texte ou réimportez le fichier.",
    'planner.import.ai_uplift_error': "Le renfort IA a échoué. Réessayez.",
    'planner.import.ai_uplift_quota': "Quota IA du plan épuisé — réessayez plus tard ou passez à un forfait supérieur.",
    'planner.import.select_all': 'Tout sélectionner',
    'planner.import.clear_all': 'Effacer',
    'planner.import.words': 'mots',
    'planner.import.mentions': 'mentions',
    'planner.import.truncated_warning': "Votre document était très long — on a lu les 2 premiers millions de caractères. Réimportez le reste séparément au besoin.",
    'planner.import.done_title': 'Importé',
    'planner.import.done_summary': '{chapters} chapitre(s), {characters} personnage(s) et {locations} lieu(x) ajoutés.',
    'planner.import.error_missing_input': "Déposez un fichier ou collez du texte d'abord.",
    'planner.import.error_unsupported_format': 'Format non pris en charge. Accepte : {accepted}.',
    'planner.import.error_too_large': 'Fichier trop volumineux (max {max_mb} Mo).',
    'planner.import.error_analyze': "Fichier illisible. Essayez un autre format ou collez le texte directement.",
    'planner.import.error_commit': "Échec de l'enregistrement — réessayez dans un instant.",
    'planner.import.error_nothing_picked': "Cochez au moins un élément à importer.",
    'planner.import.mark_study.label': "Marquer comme projet d'étude / recherche",
    'planner.import.mark_study.description': "Garde ce projet hors de votre liste de travaux principaux. Utile pour importer un livre publié (le vôtre ou d'un autre auteur) afin d'en étudier la structure.",
    'planner.import.mark_study.already': "Ce projet est déjà marqué comme projet d'étude.",
    'planner.import.structure.title': 'Structure',
    'planner.import.structure.intro': "Un aperçu rapide de la forme du manuscrit avant l'import.",
    'planner.import.structure.chapters': 'Chapitres',
    'planner.import.structure.total_words': 'Mots au total',
    'planner.import.structure.mean_chapter': 'Moyenne / chapitre',
    'planner.import.structure.range': 'Étendue',
    'planner.import.structure.words_unit': 'mots',
    'planner.import.structure.pace_label': 'Rythme des chapitres :',
    'planner.import.structure.pace_even': 'assez régulier',
    'planner.import.structure.pace_mixed': 'mitigé',
    'planner.import.structure.pace_uneven': 'très irrégulier',
    'planner.import.structure.coverage_label': 'Présence par personnage',
    'planner.import.structure.coverage_row': '{present}/{total} · {pct}%',
    'planner.import.fallback_chapter_title': 'Chapitre importé',
    'planner.import.diff_summary': '{new} nouveau(x) · {dup} déjà dans le projet',
    'planner.import.already_imported': 'Déjà dans le projet',
    'planner.import.enrich_prompt.character':
      'Décris le personnage « {name} » en un court paragraphe : qui il est, son rôle apparent, et les traits qui ressortent. Ne te sers que de ce qui est étayé par cet extrait du manuscrit source ; si l\'extrait ne le dit pas, n\'invente rien.\n\nExtrait :\n{excerpt}',
    'planner.import.enrich_prompt.location':
      'Décris le lieu « {name} » en un court paragraphe évocateur. Ne te sers que de ce qui est étayé par cet extrait du manuscrit source ; si l\'extrait ne le dit pas, n\'invente rien.\n\nExtrait :\n{excerpt}',
    'planner.import.enrich_title': "Enrichir avec l'IA",
    'planner.import.enrich_description': "Ajoute un paragraphe généré à chaque fiche importée, en s'appuyant sur le texte de votre manuscrit. Coûte un crédit IA par fiche.",
    'planner.import.enrich_cap_note': "Limité à {cap} fiches par exécution — ouvrez l'assistant IA pour enrichir les autres ensuite.",
    'planner.import.enrich_n': "Enrichir {n} avec l'IA",
    'planner.import.enrich_progress': 'Enrichissement {current} sur {total}…',
    'planner.import.enrich_cancel': 'Arrêter',
    'planner.import.enrich_done': 'Enrichissement terminé',
    'planner.import.enrich_stopped_quota': 'Quota IA atteint',
    'planner.import.enrich_summary': '{success} enrichi(s), {failed} ignoré(s).',
    'planner.header.preset_combined': 'Modèle : {genre} · {structure}',
    'planner.header.preset_pick': 'Choisir un modèle',
    'planner.actions.save': 'Enregistrer',
    'planner.actions.delete': 'Mettre à la corbeille',
    'planner.actions.add_kind': 'Ajouter {kind}',
    'planner.actions.save_as_snippet': 'Enregistrer comme modèle',
    'planner.actions.export': 'Exporter le plan',
    'planner.actions.export_md': 'Markdown (.md)',
    'planner.actions.export_txt': 'Texte brut (.txt)',
    'planner.actions.export_doc': 'Word (.doc)',
    'planner.actions.export_pdf': 'PDF',
    'planner.actions.export_html': 'HTML',
    'planner.actions.export_failed': "Export du plan impossible.",
    'planner.actions.export_xrefs.label': "Inclure les renvois",
    'planner.actions.export_xrefs.hint': "Ajoute un bloc Références / Mentionné par sous chaque module.",
    'planner.actions.export_appendix.label': "Ajouter l'annexe de connectivité",
    'planner.actions.export_appendix.hint': "Ajoute les modules-pivots, les liens manquants et la carte d'adjacence complète à la fin.",
    'planner.actions.export_notes.label': "Inclure les notes du projet",
    'planner.actions.export_notes.hint': "Ajoute le contenu libre de l'onglet Notes près du début de l'export.",
    'planner.search.empty': 'Aucun résultat.',
    'planner.drawer.new': 'Nouveau {kind}',
    'planner.drawer.edit': 'Modifier {kind}',
    'planner.drawer.title': 'Titre',
    'planner.drawer.dupe.warning': "Ce titre existe déjà sous :",
    'planner.drawer.dupe.open': "Ouvrir la fiche existante",
    'planner.drawer.summary': 'Résumé',
    'planner.drawer.summary_placeholder': 'Une ligne affichée sur la carte.',
    'planner.drawer.body': 'Corps',
    'planner.drawer.metadata': 'Détails',
    'planner.metadata.list_placeholder': 'Un élément par ligne',
    'planner.metadata.ref_field_hint': "Lien vers un autre module du plan — saisissez un titre ou utilisez Choisir.",
    'planner.metadata.ref_field_placeholder': "Saisissez le titre d'un module à lier…",
    'planner.metadata.ref_field_list_placeholder': "Un titre de module par ligne — chacun devient un lien.",
    'planner.metadata.ref_field_pick': "Choisir",
    'planner.health.empty_state.title': "Démarrer avec les renvois",
    'planner.health.empty_state.body': "Votre plan contient des modules mais aucun renvoi pour le moment — chaque module est isolé. Les renvois tissent un réseau : ouvrez un module, repérez un champ marqué d'une icône de lien (mentor, rival, faction, lieu…) et saisissez le titre du module lié.",
    'planner.health.empty_state.example': "Exemple : dans le module de votre protagoniste, remplissez le champ « mentor » avec le titre d'un autre personnage. Cette seule modification alimente la Carte du projet, le panneau « Mentionné par » des deux modules, et les indicateurs de santé ci-dessous.",
    'planner.health.empty_state.next_step': "Astuce : les personnages, factions et lieux comportent le plus de champs de référence. Commencez par eux et le réseau s'étoffera tout seul.",
    'planner.health.xref_detect.title': "Détecter les renvois automatiquement",
    'planner.health.xref_detect.subtitle': "Analyse le titre, résumé et corps de chaque module à la recherche d'autres titres mentionnés, puis propose des valeurs de champ de référence pour les relier. Sans quota IA — simple analyse de motifs.",
    'planner.health.xref_detect.button': "Détecter les renvois",
    'planner.health.xref_detect.scanning': "Analyse…",
    'planner.health.xref_detect.modal_title': "Propositions de renvois",
    'planner.health.xref_detect.summary': "{values} valeurs proposées sur {modules} modules (sur {scanned} analysés).",
    'planner.health.xref_detect.summary_empty': "Aucun nouveau renvoi détecté dans la prose. {scanned} modules analysés.",
    'planner.health.xref_detect.sample_label': "Aperçu ({shown} sur {total})",
    'planner.health.xref_detect.apply_hint': "Appliquer écrit l'ensemble du plan dans les métadonnées de vos modules. Vous pouvez annuler une modification en éditant le module directement.",
    'planner.health.xref_detect.apply': "Tout appliquer ({count})",
    'planner.health.xref_detect.applying': "Application…",
    'planner.health.xref_detect.applied': "{fields} renvois écrits sur {modules} modules.",
    'planner.health.xref_detect.scan_failed': "Analyse du plan impossible — réessayez dans un instant.",
    'planner.health.xref_detect.apply_failed': "Certains modules n'ont pas été mis à jour. Actualisez et vérifiez les indicateurs.",
    // Cross-linking (Slice B chips) — états et infobulles.
    'planner.cross_linking.chip.folded_hint': "Lien automatique (confiance faible — pliage d'accent ou ligature)",
    'planner.cross_linking.chip.ambiguous_hint': '{count} correspondances possibles — choisissez-en une',
    'planner.cross_linking.chip.red_hint': "Aucun module ne correspond — pourrait devenir un {kinds}",
    'planner.cross_linking.chip.repin_hint': "Changer l'épingle ou choisir un autre module",
    // Slice C — étiquettes du panneau « Mentionné par ».
    'planner.cross_linking.mentioned_by.title': 'Mentionné par',
    'planner.cross_linking.mentioned_by.uncategorised': 'Autre',
    'planner.cross_linking.mentioned_by.via': 'via {via}',
    // Slice D — sélecteur de référence.
    'planner.cross_linking.picker.title_resolved': 'Changer le lien',
    'planner.cross_linking.picker.title_ambiguous': 'Choisir un module',
    'planner.cross_linking.picker.title_red': 'Lier ou créer un module',
    'planner.cross_linking.picker.close': 'Fermer',
    'planner.cross_linking.picker.candidates': 'Correspondances possibles',
    'planner.cross_linking.picker.search_label': 'Choisir un module existant',
    'planner.cross_linking.picker.search_placeholder': 'Rechercher par titre…',
    'planner.cross_linking.picker.no_results': "Aucune correspondance dans ce projet pour ces types.",
    'planner.cross_linking.picker.create_label': "Créer un nouveau module à la place",
    'planner.cross_linking.picker.create_title_placeholder': 'Titre du nouveau module',
    'planner.cross_linking.picker.create_as': 'Créer en {kind}',
    'planner.cross_linking.picker.title_required': "Un titre est requis pour créer un module.",
    'planner.cross_linking.picker.created': "Créé — enregistré et lié.",
    'planner.cross_linking.picker.create_failed': "Création du module impossible. Réessayez.",
    'planner.cross_linking.picker.pinned': "Épinglé. Enregistrez le panneau pour valider.",
    // Slice E — suggestion de relation, action en ligne dans le
    // panneau « Mentionné par ».
    'planner.cross_linking.suggest_rel.cta': 'Créer rel.',
    'planner.cross_linking.suggest_rel.hint': "Ces deux personnages se référencent — créer un module « relation »",
    'planner.cross_linking.suggest_rel.created': "Relation créée — ouvrez-la pour la renommer et la décrire.",
    'planner.cross_linking.suggest_rel.failed': "Création de la relation impossible. Réessayez.",
    // Slice F — vue carte du projet.
    'planner.view.cards': 'Grille',
    'planner.view.map': 'Carte',
    'planner.tab.aria': 'Vue du plan',
    'planner.tab.modules': 'Modules',
    'planner.tab.connections': 'Analytique',
    'planner.tab.notes': 'Notes',
    'planner.connections.sub_aria': 'Vue des connexions',
    'planner.connections.tab_intro': "Ce qui cloche dans le plan, comment les fiches se relient, et par où commencer.",
    'planner.connections.sub.map': 'Carte',
    'planner.connections.sub.health': 'Santé',
    'planner.connections.sub.orphans': 'Fiches isolées',
    'planner.connections.sub.red_links': 'Mentions cassées',
    'planner.connections.loading': "Analyse des connexions du projet…",
    'planner.connections.error_title': "Connexions indisponibles",
    'planner.connections.error_hint': "Essayez de rafraîchir — le reste du plan continue de fonctionner.",
    'planner.overflow.title': 'Plus d\'outils',
    'planner.overflow.snippets': 'Bibliothèque de modèles',
    'planner.overflow.tropes': 'Suggestions avancées',
    'planner.overflow.coherence': 'Vérifier la cohérence',
    'planner.overflow.critique': 'Critique du projet',
    'planner.overflow.import': 'Importer…',
    'planner.notes.title': 'Notes du plan',
    'planner.notes.body': "Ouvrez l'éditeur ADN du récit dans l'en-tête pour modifier votre accroche, synopsis, thème et tonalité.",
    'planner.notes.open_editor': "Ouvrir l'éditeur ADN du récit",
    'planner.notes.dna.title': 'ADN du récit',
    'planner.notes.dna.subtitle': "La colonne vertébrale du projet — les quatre lignes sur lesquelles l'IA s'appuie le plus.",
    'planner.notes.field.logline': 'Accroche',
    'planner.notes.field.logline_placeholder': "Une phrase : le protagoniste, son objectif, et ce qui s'y oppose.",
    'planner.notes.field.synopsis': 'Synopsis',
    'planner.notes.field.synopsis_placeholder': "Un ou deux paragraphes — l'histoire de bout en bout.",
    'planner.notes.field.theme': 'Thème',
    'planner.notes.field.theme_placeholder': "L'idée que l'histoire travaille (amour, trahison, identité…).",
    'planner.notes.field.tone': 'Tonalité',
    'planner.notes.field.tone_placeholder': "Ce que devrait être l'effet de lecture (lyrique, sobre, urgent…).",
    'planner.notes.freeform.title': 'Notes du plan',
    'planner.notes.freeform.subtitle': "Le bloc fourre-tout — pensées en cours, questions ouvertes, choses à faire. Enregistré au fil de l'eau.",
    'planner.notes.freeform.label': 'Notes',
    'planner.notes.freeform.placeholder': "Qu'avez-vous en tête pour ce projet ?",
    'planner.notes.freeform.hint': "Markdown pris en charge · enregistrement automatique",
    'planner.notes.saving': 'Enregistrement…',
    'planner.notes.saved': 'Enregistré',
    'planner.notes.save_failed': "Enregistrement impossible — réessayez.",
    'planner.notes.draft.button': "Brouillon depuis mes chapitres",
    'planner.notes.draft.tooltip': "Génère un candidat à partir de votre prose et de votre plan. Consomme un crédit IA.",
    'planner.notes.draft.candidate_label': "Brouillon IA",
    'planner.notes.draft.apply': "Appliquer",
    'planner.notes.draft.discard': "Ignorer",
    'planner.notes.draft.applied': "Appliqué — votre brouillon remplace la valeur du champ.",
    'planner.notes.draft.failed': "Échec du brouillon — réessayez dans un instant.",
    'planner.notes.draft.quota_exceeded': "Limite quotidienne IA atteinte. Vous pouvez toujours écrire le champ vous-même.",
    'planner.drawer.more_details': '▸ Plus de détails ({count})',
    'planner.first_steps.title': 'Bienvenue dans votre plan',
    'planner.first_steps.body': "Le plan garde ce que vous avez en tête — personnages, lieux, règles magiques — pour que vous puissiez écrire sans rien oublier. Choisissez un point de départ :",
    'planner.first_steps.add_protagonist': 'Ajouter votre protagoniste',
    'planner.first_steps.add_setting': 'Ajouter le décor principal',
    'planner.first_steps.start_preset': 'Partir d\'une structure prédéfinie',
    'planner.first_steps.dismiss_aria': 'Fermer ce panneau de bienvenue',
    'planner.at_scale.title': "{modules} modules · {chapters} chapitres",
    'planner.at_scale.bottom_title': "Dans votre plan — {modules} modules, {chapters} chapitres",
    'planner.at_scale.loading': "Analyse des liens manquants…",
    'planner.at_scale.clean': "Tout est connecté — aucun module orphelin ni lien cassé.",
    'planner.at_scale.loose_ends': "{count} points à traiter ({orphans} orphelins, {red_links} liens cassés).",
    'planner.at_scale.loose_ends_short': "{count} à traiter — {orphans} isolées · {red_links} cassées",
    'planner.at_scale.show_loose': "Voir ce qui manque",
    'planner.at_scale.review_loose': "Voir les points à traiter",
    'planner.cross_refs_strip.title': "Vos modules ne sont pas encore reliés",
    'planner.cross_refs_strip.body': "Les renvois transforment vos modules en un réseau connecté — ouvrez Connexions pour les détecter depuis votre prose ou les ajouter à la main.",
    'planner.cross_refs_strip.cta': "Ouvrir Connexions",
    'planner.continue.label': "Récemment édité",
    'planner.footer.heading': "Quelque chose à terminer ?",
    'planner.health.title': "{count} incohérences à vérifier dans le plan",
    'planner.health.clean_title': "Plan sans incohérences",
    'planner.health.clean_hint': "Toutes les vérifications passent. Ajoutez ou modifiez une fiche pour faire émerger de nouveaux signaux.",
    'planner.health.open': "Ouvrir cette fiche",
    'planner.health.more': "+ {count} de plus",
    'planner.health.section.duplicates': "Doublons",
    'planner.health.duplicates.hint': "Même titre, plusieurs types — probablement des doublons.",
    'planner.health.duplicates.across': "→",
    'planner.health.section.role_conflicts': "Conflits de rôles",
    'planner.health.role_conflicts.hint': "Confirmez si voulu, ou modifiez une fiche.",
    'planner.health.role_conflicts.line': "{count} partagent « {role} » (max {expected}).",
    'planner.health.section.stale_orphans': "Orphelins anciens",
    'planner.health.stale_orphans.hint': "Aucune référence, aucune édition récente.",
    'planner.health.stale_orphans.tooltip': "Aucune référence · il y a {days} jours",
    'planner.health.section.unmentioned_characters': "Personnages absents ou sous-utilisés",
    'planner.health.unmentioned_characters.hint': "Personnages planifiés qui n'apparaissent pas dans la prose, et rôles importants en dessous du seuil attendu.",
    // Tier-6 §17
    'planner.health.unmentioned.underused_badge': '{count} ch.',
    'planner.health.unmentioned.underused_tooltip': "Apparaît dans {count} chapitre(s) — en dessous du seuil attendu pour un rôle {role}.",
    'planner.health.section.metadata_gaps': "Champs critiques manquants",
    'planner.health.metadata_gaps.hint': "Voix, relations et motivations nourrissent chaque critique.",
    'planner.health.metadata_gaps.missing': "manquant",
    'planner.health.metadata_gaps.key.voice_sample': "voix",
    'planner.health.metadata_gaps.key.relationships': "relations",
    'planner.health.metadata_gaps.key.motivations': "motivations",
    'planner.health.section.thin_modules': "Fiches à étoffer",
    'planner.health.thin_modules.hint': "Corps vide et résumé court.",
    'planner.health.thin_modules.tooltip': "Corps {body}c · Résumé {summary}c",
    'planner.health.tile.duplicates': "Doublons",
    'planner.health.tile.semantic_dupes': "Résumé identique",
    'planner.health.section.semantic_dupes': "Résumés identiques ({count})",
    'planner.health.semantic_dupes.hint': "Titres différents, résumé identique mot pour mot — souvent un copier-coller. Ouvrez-en un pour différencier.",
    // Tier-4
    'planner.health.tile.orphan_merges': "À fusionner",
    'planner.health.section.orphan_merges': "Orphelins fusionnables ({count})",
    'planner.health.orphan_merges.hint': "Ces orphelins partagent un fort recoupement de contenu avec un module référencé du même type. Ouvrez les deux pour fusionner ou différencier.",
    'planner.health.orphan_merges.arrow': "{overlap}% en commun →",
    'planner.health.tile.narration_names': "Dans la prose seule",
    'planner.health.section.narration_names': "Noms dans la prose, absents du plan ({count})",
    'planner.health.narration_names.hint': "Ces noms en majuscule reviennent dans vos chapitres mais n'existent pas comme cartes du plan. Ajoutez les plus récurrents pour que les liens et la critique les voient.",
    'planner.health.narration_names.tooltip': "{mentions} mentions sur {chapters} chapitres",
    'planner.health.tile.role_conflicts': "Rôles",
    'planner.health.tile.unmentioned': "Absents",
    'planner.health.tile.metadata_gaps': "Champs vides",
    'planner.health.tile.thin_modules': "À étoffer",
    'planner.health.tile.stale_orphans': "Anciens",
    'planner.health.bulk_archive.select': "Sélectionner pour archivage groupé",
    'planner.health.bulk_archive.action': "Archiver {count}",
    'planner.health.bulk_archive.done': "{count} fiche(s) archivée(s).",
    'planner.health.bulk_archive.failed': "Archivage impossible — réessayez dans un instant.",
    'planner.health.merge.action': "Fusionner",
    'planner.health.merge.multi_action': "Fusionner {count} dans la 1ʳᵉ",
    'planner.health.merge.multi_tooltip': "Fond les {count} fiches suivantes du groupe dans la première listée ({target}). En une seule opération.",
    'planner.health.merge.tooltip': "Fond la fiche {from} dans la fiche {into}. Les références sont mises à jour, la fiche {from} est archivée.",
    'planner.health.merge.done': "Fusion réussie — références mises à jour, ancienne fiche archivée.",
    'planner.health.merge.failed': "Fusion impossible — réessayez dans un instant.",
    'planner.health.role_conflicts.dismiss': "Intentionnel",
    'planner.health.role_conflicts.dismiss_tooltip': "Marquer cette répartition de rôles comme intentionnelle — l'alerte disparaît pour ce projet.",
    'planner.health.write_scene.action': "Écrire une scène",
    'planner.health.write_scene.tooltip': "Ouvrir l'espace d'écriture et démarrer une scène avec {name}.",
    'planner.health.write_scene.prompt': "Écris une scène où {name} ({role}) entre dans l'histoire pour la première fois. Garde la voix et les motivations du plan ; présente ce personnage au lecteur par l'action, pas par l'exposition.",
    'planner.health.write_scene.clipboard': "Invite de scène copiée — collez-la dans l'assistant IA quand vous ouvrez un chapitre.",
    'planner.health.fill.action': "Remplir IA",
    'planner.health.fill.tooltip_metadata': "Ouvrir cette fiche personnage et auto-remplir voix, relations et motivations avec l'IA.",
    'planner.health.fill.tooltip_thin': "Ouvrir cette fiche et auto-remplir chaque champ vide avec l'IA.",
    'planner.continue.title': "Ouvrir le dernier module que vous éditiez",
    'planner.continue.edited_ago': "édité {ago}",
    'planner.signature.untitled_module': "Module sans titre",
    'planner.overflow.coherence_disabled': "Ajoutez {count} chapitre(s) supplémentaire(s) avant de lancer la vérification de cohérence — elle s'appuie sur la prose pour détecter les écarts.",
    'planner.overflow.critique_disabled': "Ajoutez {count} chapitre(s) supplémentaire(s) avant de lancer Critique — elle a besoin de chapitres réels pour signaler les manques structurels.",
    'planner.category.section_label': 'Catégories',
    'planner.group_by_kind.label': 'Grouper par type',
    'planner.select_mode.label': 'Sélectionner',
    'planner.select_mode.off_tooltip': "Passer en mode sélection — cochez plusieurs fiches pour les archiver d'un coup.",
    'planner.select_mode.on_tooltip': "Quitter le mode sélection et vider la sélection.",
    'planner.bulk_archive.selected_count': '{count} sélectionnée(s)',
    'planner.bulk_archive.clear': 'Vider',
    'planner.bulk_archive.action': 'Archiver {count}',
    'planner.bulk_archive.done': '{count} fiche(s) archivée(s). Restaurez depuis la corbeille à tout moment.',
    'planner.bulk_archive.failed': "Archivage impossible — réessayez dans un instant.",
    'planner.group_by_kind.on_tooltip': 'Une section par type — cliquez pour revenir à la grille unique',
    'planner.group_by_kind.off_tooltip': 'Grille triée par activité récente — cliquez pour grouper par type',
    'planner.kind_picker.button_label': 'Nouveau',
    'planner.kind_picker.button_title': 'Ajouter un module — recherchez ou choisissez un type',
    'planner.kind_picker.title': 'Ajouter un module',
    'planner.kind_picker.search_placeholder': "Décrivez ce que vous voulez ajouter — personnage, ville, scène…",
    'planner.kind_picker.clear': 'Effacer la recherche',
    'planner.kind_picker.results': 'Résultats ({count})',
    'planner.kind_picker.no_results': "Aucun type ne correspond. Parcourez les catégories ci-dessous ou changez de mot-clé.",
    'planner.kind_picker.popular': 'Populaire pour ce projet',
    'planner.kind_picker.all_categories': 'Toutes les catégories',
    'planner.kind_picker.category_count': '{count} types',
    // [Removed: planner.project_map.* + planner.map.loading/error (FR) — see EN block.]
    // Slice H — vue santé du projet.
    'planner.view.health': 'Santé',
    'planner.health.loading': 'Lecture du pouls du plan…',
    'planner.health.error_title': 'Santé du projet indisponible',
    'planner.health.error_hint': "Réessayez — le plan fonctionne toujours en mode grille.",
    'planner.health.metric.resolution_rate': 'Liens établis',
    'planner.health.metric.resolution_rate_sub': '{resolved} mentions sur {instances} sont reliées',
    'planner.health.metric.edges_per_module': 'Liens par fiche',
    'planner.health.metric.edges_per_module_sub': "Plus haut = un réseau plus riche",
    'planner.health.metric.orphans': 'Fiches isolées',
    'planner.health.metric.orphans_sub': 'Personne ne les mentionne',
    'planner.health.metric.red_links': 'Mentions cassées',
    'planner.health.metric.red_links_sub': "Noms qui ne correspondent à aucune fiche",
    'planner.health.orphans.title': 'Modules sans aucune référence entrante',
    'planner.health.orphans.empty': "Tous les modules sont référencés par au moins un autre — la toile est entièrement connectée.",
    'planner.health.orphans.show_more': '+ {count} de plus — tout afficher',
    'planner.health.orphans.show_more_categories': '+ {categories} catégories supplémentaires ({orphans} orphelins) — tout afficher',
    'planner.health.red_links.title': 'Références non résolues (liste de travail)',
    'planner.health.red_links.empty': "Toutes les références pointent vers un module réel — aucune tâche.",
    'planner.health.red_links.open_referrer': "Ouvrir le module référant pour choisir ou créer la cible",
    'planner.health.red_links.more': '+ {count} autres références — ouvrez-en une pour voir le détail.',
    'planner.health.red_links.why_aria': 'Pourquoi cette référence échoue-t-elle ?',
    'planner.health.red_links.why.not_found': "Aucun module ne porte ce nom — créez-en un avec le type {kinds} depuis le module référant.",
    'planner.health.red_links.why.kind_mismatch': "Un module nommé « {suggestion_title} » existe, mais son type ({suggestion_kind}) n’est pas dans {kinds}.",
    'planner.health.red_links.why.too_short': "Trop court pour la correspondance automatique — le solveur n’apparie que les noms de 4 caractères ou plus.",
    'planner.health.red_links.why.open_suggestion': 'Ouvrir « {title} »',
    'planner.metadata.setting': 'Lieu',
    'planner.metadata.pov': 'Point de vue',
    // Auto-added planner.metadata.* — covers every field id
    // declared in writeon-django planner_presets._field. Without
    // these, missing keys fall through to the humanized id
    // (looks English even when locale=fr).
    'planner.metadata.abilities': 'Capacités',
    'planner.metadata.access_rules': 'Règles d\'accès',
    'planner.metadata.activation': 'Activation',
    'planner.metadata.aftermath': 'Conséquences',
    'planner.metadata.age': 'Âge',
    'planner.metadata.anchor_date': 'Date de référence',
    'planner.metadata.answer': 'Réponse',
    'planner.metadata.appearance': 'Apparence',
    'planner.metadata.attributed_to': 'Attribué à',
    'planner.metadata.author': 'Auteur',
    'planner.metadata.authority': 'Autorité',
    'planner.metadata.axioms': 'Axiomes',
    'planner.metadata.behavior': 'Comportement',
    'planner.metadata.biography': 'Biographie',
    'planner.metadata.biome': 'Biome',
    'planner.metadata.capital': 'Capitale',
    'planner.metadata.cause': 'Cause',
    'planner.metadata.character': 'Personnage',
    'planner.metadata.clergy': 'Clergé',
    'planner.metadata.climax': 'Apogée',
    'planner.metadata.conflict': 'Conflit',
    'planner.metadata.context': 'Contexte',
    'planner.metadata.costs': 'Coûts',
    'planner.metadata.creation_myth': 'Mythe de création',
    'planner.metadata.culture': 'Culture',
    'planner.metadata.currency': 'Devise',
    'planner.metadata.daily_life': 'Vie quotidienne',
    'planner.metadata.damage_kind': 'Type de dégâts',
    'planner.metadata.date': 'Date',
    'planner.metadata.description': 'Description',
    'planner.metadata.districts': 'Quartiers',
    'planner.metadata.effects': 'Effets',
    'planner.metadata.emotional_states': 'États émotionnels',
    'planner.metadata.end_state': 'État final',
    'planner.metadata.evolution': 'Évolution',
    'planner.metadata.exceptions': 'Exceptions',
    'planner.metadata.festivals': 'Festivals',
    'planner.metadata.flaws': 'Défauts',
    'planner.metadata.form': 'Forme',
    'planner.metadata.founder': 'Fondateur',
    'planner.metadata.function': 'Fonction',
    'planner.metadata.giver': 'Donneur',
    'planner.metadata.goal': 'Objectif',
    'planner.metadata.government': 'Gouvernement',
    'planner.metadata.habitat': 'Habitat',
    'planner.metadata.hazards': 'Dangers',
    'planner.metadata.heroes': 'Héros',
    'planner.metadata.history': 'Histoire',
    'planner.metadata.image_url': 'URL de l\'image',
    'planner.metadata.impact': 'Impact',
    'planner.metadata.inciting_incident': 'Élément déclencheur',
    'planner.metadata.inhabitants': 'Habitants',
    'planner.metadata.kind': 'Type',
    'planner.metadata.landmarks': 'Sites remarquables',
    'planner.metadata.languages': 'Langues',
    'planner.metadata.leadership': 'Direction',
    'planner.metadata.limitations': 'Limitations',
    'planner.metadata.limits': 'Limites',
    'planner.metadata.link': 'Lien',
    'planner.metadata.locked': 'Verrouillé',
    'planner.metadata.logline': 'Accroche (une phrase)',
    'planner.metadata.manifestations': 'Manifestations',
    'planner.metadata.members': 'Membres',
    'planner.metadata.methods': 'Méthodes',
    'planner.metadata.months': 'Mois',
    'planner.metadata.motivations': 'Motivations',
    'planner.metadata.nature': 'Nature',
    'planner.metadata.notable_features': 'Caractéristiques notables',
    'planner.metadata.notable_members': 'Membres notables',
    'planner.metadata.objective': 'Objectif',
    'planner.metadata.origin': 'Origine',
    'planner.metadata.origin_myth': 'Mythe d\'origine',
    'planner.metadata.outcome': 'Issue',
    'planner.metadata.pantheon': 'Panthéon',
    'planner.metadata.penalty': 'Pénalité',
    'planner.metadata.period': 'Période',
    'planner.metadata.physiology': 'Physiologie',
    'planner.metadata.population': 'Population',
    'planner.metadata.portrait_url': 'URL du portrait',
    'planner.metadata.powers': 'Pouvoirs',
    'planner.metadata.practices': 'Pratiques',
    'planner.metadata.practitioners': 'Pratiquants',
    'planner.metadata.present_characters': 'Personnages présents',
    'planner.metadata.primary_industries': 'Industries principales',
    'planner.metadata.psychology': 'Psychologie',
    'planner.metadata.purpose': 'But',
    'planner.metadata.reputations': 'Réputations',
    'planner.metadata.requires': 'Requiert',
    'planner.metadata.research_state': 'État de la recherche',
    'planner.metadata.resolution': 'Résolution',
    'planner.metadata.reward': 'Récompense',
    'planner.metadata.rival_factions': 'Factions rivales',
    'planner.metadata.role': 'Rôle',
    'planner.metadata.ruler': 'Dirigeant',
    'planner.metadata.rules': 'Règles',
    'planner.metadata.sample_phrases': 'Phrases d\'exemple',
    'planner.metadata.scope': 'Portée',
    'planner.metadata.script': 'Écriture',
    'planner.metadata.seasons': 'Saisons',
    'planner.metadata.secrets': 'Secrets',
    'planner.metadata.side_effects': 'Effets secondaires',
    'planner.metadata.sides': 'Camps',
    'planner.metadata.source': 'Source',
    'planner.metadata.speakers': 'Locuteurs',
    'planner.metadata.special': 'Spécial',
    'planner.metadata.stages': 'Étapes',
    'planner.metadata.stakes': 'Enjeux',
    'planner.metadata.start_state': 'État initial',
    'planner.metadata.statement': 'Énoncé',
    'planner.metadata.succession': 'Succession',
    'planner.metadata.summary': 'Résumé',
    'planner.metadata.system': 'Système',
    'planner.metadata.taboos': 'Tabous',
    'planner.metadata.threat_level': 'Niveau de menace',
    'planner.metadata.trade_routes': 'Routes commerciales',
    'planner.metadata.traits': 'Traits',
    'planner.metadata.trajectory': 'Trajectoire',
    'planner.metadata.turning_points': 'Points de bascule',
    'planner.metadata.type': 'Type',
    'planner.metadata.value': 'Valeur',
    'planner.metadata.values': 'Valeurs',
    'planner.metadata.vibe': 'Ambiance',
    'planner.metadata.voice_sample': 'Échantillon de voix',
    'planner.metadata.weather_patterns': 'Climat',
    'planner.metadata.word_target': 'Objectif de mots',
    'planner.chip.role': 'rôle',
    'planner.chip.age': 'âge',
    'planner.chip.type': 'type',
    'planner.chip.pov': 'POV',
    'planner.chip.setting': 'lieu',
    'planner.chip.population': 'pop.',
    'planner.chip.rules': 'règles',
    'planner.chip.motivations': 'buts',
    'planner.chip.flaws': 'défauts',
    'planner.chip.locked': 'étape',
    // [Removed: planner.family_tree.{dynasty,family} (FR) — see EN block.]
    'planner.ai.title': 'Assistant IA',
    'planner.ai.fab': 'IA',
    'planner.ai.pill_label': 'Assistant',
    'planner.ai.quota_label': "Utilisation IA aujourd'hui",
    'planner.ai.suggest': 'Avancée',
    'planner.ai.suggest_for_kind': 'Obtenir une suggestion avancée',
    'planner.ai.run_freeform': 'Lancer une requête libre',
    'planner.ai.freeform_placeholder': 'Décrivez ce qu’il vous faut (par ex. « un mentor qui défie le protagoniste »).',
    'planner.ai.active_kind': 'Catégorie active : {kind}',
    'planner.ai.no_kind': "Choisissez une catégorie pour activer les suggestions IA.",
    'planner.ai.no_presets': "Aucun préréglage IA pour cette catégorie — utilisez la requête libre ci-dessous.",
    'planner.ai.result': 'Suggestion',
    'planner.ai.insert': 'Insérer comme nouvelle carte',
    'planner.ai.dismiss': 'Ignorer',
    'planner.ai.pick_project': "Choisissez un projet pour activer l'IA.",
    'planner.ai.quota_exceeded': "Vous avez atteint votre quota IA pour aujourd'hui.",
    'planner.ai.failed': "La suggestion IA a échoué — réessayez dans un instant.",
    'planner.ai.kind_armed': "IA prête pour {kind} — ouvrez l'assistant à droite.",
    'planner.ai.try_preset': "Ouvrez l'assistant IA à droite pour démarrer dans cette catégorie.",
    'planner.ai.suggested_title': 'Suggestion avancée : {kind}',
    'planner.ai.preset.character_quick_sketch': 'Esquisse rapide de personnage',
    'planner.ai.preset.character_foil': "Générer un faire-valoir",
    'planner.ai.preset.character_voice_sample': 'Échantillon de voix',
    'planner.ai.preset.character_backstory': 'Paragraphe de passé',
    'planner.ai.preset.character_secret': 'Secret caché',
    'planner.ai.preset.city_describe': 'Décrire avec des détails sensoriels',
    'planner.ai.preset.region_expand': 'Développer la région',
    'planner.ai.preset.magic_rules_design': 'Concevoir les règles de magie',
    'planner.ai.preset.magic_cost': 'Choisir un coût (magie)',
    'planner.ai.preset.technology_rules_design': 'Concevoir les règles technologiques',
    'planner.ai.preset.plot_raise_stakes': 'Augmenter les enjeux',
    'planner.ai.preset.plot_twist': 'Suggérer un retournement',
    'planner.ai.preset.scene_punchy_opener': "Ouverture percutante",
    'planner.ai.preset.scene_transition': 'Transition vers la prochaine scène',
    'planner.toast.saved': 'Enregistré.',
    'planner.toast.save_failed': "L'enregistrement a échoué — réessayez.",
    'planner.toast.trashed': 'Déplacé dans la corbeille (30 jours pour le restaurer).',
    'planner.toast.delete_failed': "La suppression a échoué — réessayez.",
    'planner.toast.conflict': "Quelqu'un d'autre a modifié cette carte — version la plus récente chargée.",
    'planner.toast.snippet_saved': 'Enregistré dans votre bibliothèque de modèles.',
    'planner.toast.snippet_failed': "Enregistrement du modèle impossible — réessayez.",
    'planner.toast.load_failed': 'Chargement du plan impossible — actualisez la page.',
    'planner.toast.preset_applied': 'Préréglage appliqué.',
    'planner.toast.preset_failed': "Application du préréglage impossible — réessayez.",
    'planner.confirm.delete': 'Mettre cette carte à la corbeille ? Vous pourrez la restaurer pendant 30 jours.',
    'planner.confirm.delete_title': 'Mettre cette carte à la corbeille ?',
    'planner.confirm.delete_action': 'Mettre à la corbeille',
    'planner.snippets.confirm.delete_title': 'Supprimer ce modèle ?',
    'planner.snippets.confirm.delete_message': 'Ce modèle sera définitivement retiré de votre bibliothèque. Les cartes déjà importées dans des projets restent intactes.',
    'planner.map.clear_confirm_title': 'Supprimer la carte actuelle ?',
    'planner.map.clear_action': 'Supprimer la carte',
    'planner.image.prompt_placeholder': "Décrivez l'image à générer…",
    'planner.image.generate': 'Générer',
    'planner.image.hint': "Téléversez un fichier ou générez-en un — vous choisissez.",
    'planner.image.generated': 'Image générée et attachée.',
    'planner.image.failed': "La génération a échoué — essayez une autre consigne.",
    'planner.image.quota_exceeded': "Vous avez utilisé vos générations d'images du jour.",
    'planner.image.upload_cta': "Téléverser une image",
    'planner.image.upload_hint': "PNG, JPEG, WebP ou GIF.",
    'planner.image.upload_button': "Téléverser un fichier",
    'planner.image.replace_cta': "Remplacer",
    'planner.image.uploading': "Téléversement…",
    'planner.image.uploaded': "Image téléversée.",
    'planner.image.upload_failed': "Téléversement impossible — réessayez.",
    'planner.image.remove': "Retirer l'image",
    // --- Lecteur de chapitre dans la page (synthèse vocale).
    'reader.button.open': 'Ouvrir le lecteur',
    'reader.button.play': 'Lire à voix haute',
    'reader.button.pause': 'Mettre en pause',
    'reader.button.resume': 'Reprendre la lecture',
    'reader.button.stop': 'Arrêter',
    'reader.button.prev': 'Phrase précédente',
    'reader.button.next': 'Phrase suivante',
    'reader.unsupported': "Votre navigateur ne prend pas encore en charge la lecture vocale.",
    'reader.panel.title': 'Lecteur',
    'reader.panel.status.idle': "Prêt — choisissez où commencer.",
    'reader.panel.status.playing': 'Lecture en cours…',
    'reader.panel.status.paused': 'En pause.',
    'reader.label.speed': 'Vitesse',
    'reader.label.voice': 'Narrateur',
    'reader.label.markers': 'Repères dans ce chapitre',
    'reader.label.chapters': 'Chapitres',
    'reader.marker.beginning': 'Début',
    'reader.marker.end': 'Fin',
    'reader.marker.chapter': 'Chapitre {n}',
    // Bandeau d'installation — affiché quand le moteur n'a aucune
    // voix dans la langue du chapitre.
    'reader.install.title': "Aucune voix {language} installée",
    'reader.install.body': "Le lecteur utilise votre voix par défaut — elle prononcera mal cette langue. Pour obtenir une vraie voix {language} sur cet appareil :",
    'reader.install.dismiss': 'Masquer cet avis',
    // Instructions volontairement génériques — les chemins de menu
    // précis varient selon la version de l'OS, la langue et la
    // marque du téléphone. Nous nommons la bonne application de
    // réglages et invitons à utiliser sa recherche intégrée.
    'reader.install.steps.windows': "Ouvrez les Paramètres Windows et utilisez la barre de recherche pour trouver « voix » ou « synthèse vocale ». Cherchez la section permettant d'ajouter une voix dans la langue cible. Une fois le téléchargement terminé, redémarrez votre navigateur.",
    'reader.install.steps.mac': "Ouvrez les Réglages Système et tapez « contenu énoncé » ou « voix » dans la recherche. Choisissez une voix dans la langue cible, laissez macOS la télécharger, puis rechargez cette page.",
    'reader.install.steps.ios': "Ouvrez l'app Réglages et cherchez « voix » ou « contenu énoncé ». Trouvez la langue cible, choisissez une voix et téléchargez-la. Revenez à votre navigateur.",
    'reader.install.steps.android': "Ouvrez les Paramètres Android et cherchez « synthèse vocale ». Ouvrez votre moteur actif et regardez s'il propose d'installer ou de mettre à jour des données vocales — les libellés exacts varient selon le constructeur.",
    'reader.install.steps.linux': "Installez un paquet de moteur de synthèse vocale qui contient des voix dans la langue cible. ``espeak-ng`` ou ``speech-dispatcher`` avec les voix ``mbrola`` sont des points de départ courants ; le gestionnaire de paquets de votre distribution s'occupe du reste.",
    'reader.install.steps.generic': "Ouvrez les réglages de votre appareil et cherchez « voix » ou « synthèse vocale ». Repérez l'option d'installer une voix dans la langue cible, puis rechargez cette page.",
    // Narrateur Plumescript — libellé de marque pour la voix
    // canonique (Aria/Sylvie/Samantha/Amélie selon plateforme +
    // langue). Lorsque la voix canonique est active, un réglage
    // d'élocution adapté au récit s'applique automatiquement
    // (légèrement plus lent, ton plus chaud) — le curseur de
    // l'auteur reste prioritaire.
    // FR — Personal voice clone settings panel
    'settings.personal_voice.title': 'Voix personnelle',
    'settings.personal_voice.subtitle': 'Enregistrez un échantillon : le narrateur lira vos textes avec votre voix. Compte sur votre quota de narration quotidien.',
    'settings.personal_voice.open': 'Ouvrir les échantillons vocaux',
    'settings.personal_voice.open_hint': 'Enregistrez, renommez ou supprimez vos échantillons.',
    'settings.personal_voice.your_samples': 'Vos échantillons',
    'settings.personal_voice.empty': "Aucun échantillon. Ajoutez-en un pour activer le narrateur personnel.",
    'settings.personal_voice.loading': 'Chargement des échantillons…',
    'settings.personal_voice.add_title': 'Ajouter un échantillon',
    'settings.personal_voice.add_hint': 'Enregistrez 10 à 20 secondes de parole claire (une seule voix, sans bruit). Nous clonerons votre timbre pour vos prochains chapitres.',
    'settings.personal_voice.audio_hint': "Aucun fichier sélectionné. Déposez un .wav, .mp3 ou .webm.",
    'settings.personal_voice.default_name': 'Échantillon',
    'settings.personal_voice.confirm_delete': "Supprimer cet échantillon ? Cette action est définitive.",
    'settings.personal_voice.field.reference_text': 'Ce que vous dites dans l’enregistrement',
    'settings.personal_voice.field.reference_text_optional': '(facultatif)',
    'settings.personal_voice.field.reference_text_placeholder': "Laissez vide — nous le transcrirons pour vous",
    'settings.personal_voice.field.reference_text_hint': "Saisir vos mots exacts donne le meilleur résultat. Sinon, transcription automatique à l'enregistrement (~30 s de plus).",
    'settings.personal_voice.field.name': 'Nom (facultatif)',
    'settings.personal_voice.field.name_placeholder': 'ex. Micro studio, Pièce calme',
    'settings.personal_voice.field.audio': 'Fichier audio',
    'settings.personal_voice.action.pick_file': 'Choisir un fichier audio',
    'settings.personal_voice.action.record': 'Enregistrer',
    'settings.personal_voice.action.stop_recording': "Arrêter l'enregistrement",
    'settings.personal_voice.action.clear': 'Effacer',
    'settings.personal_voice.action.save': 'Enregistrer',
    'settings.personal_voice.action.saving': 'Enregistrement…',
    'settings.personal_voice.action.saved': 'Enregistré',
    'settings.personal_voice.action.rename': 'Renommer',
    'settings.personal_voice.action.save_name': 'Enregistrer le nom',
    'settings.personal_voice.action.cancel': 'Annuler',
    'settings.personal_voice.action.delete': "Supprimer l'échantillon",
    'settings.personal_voice.error.load': 'Échantillons indisponibles. Réessayez.',
    'settings.personal_voice.error.submit': "Échantillon non enregistré. Vérifiez votre connexion et réessayez.",
    'settings.personal_voice.error.file_too_large': 'Fichier trop volumineux (10 Mo maximum).',
    'settings.personal_voice.error.mic_denied': 'Accès au microphone refusé. Autorisez-le dans les paramètres du navigateur ou importez un fichier audio.',
    'settings.personal_voice.error.record_unsupported': "Enregistrement indisponible dans ce navigateur. Importez un fichier audio.",
    'settings.personal_voice.error.record_failed': "Démarrage impossible. Réessayez ou importez un fichier.",
    'settings.personal_voice.disabled.no_audio': 'Enregistrez ou importez un échantillon audio.',
    'settings.personal_voice.disabled.no_reference_text': 'Ajoutez le texte de référence (les mots dits dans le clip).',
    // FR — Planner AI-Update
    'planner.ai_update.button': "Mettre à jour avec l'IA",
    'planner.ai_update.running': 'Mise à jour…',
    'planner.ai_update.applied': "IA appliquée — {filled} complétés, {updated} synchronisés. Vérifiez puis Enregistrez.",
    'planner.ai_update.no_change': "L'IA n'a rien à ajouter — votre module est déjà cohérent avec le plan.",
    'planner.ai_update.failed': "Planificateur indisponible. Réessayez.",
    'planner.ai_update.quota_exceeded': "Quota IA du plan atteint. Passez à l'offre supérieure ou réessayez demain.",
    'planner.ai_update.badge.filled': 'Rempli par IA',
    'planner.ai_update.badge.updated': 'Synchronisé par IA',
    'planner.ai_update.badge.filled_tooltip': "L'IA a proposé cette valeur pour un champ vide. Modifiez ou Enregistrez pour conserver.",
    'planner.ai_update.badge.updated_tooltip': "L'IA a proposé cette mise à jour pour rester cohérent avec vos chapitres. Modifiez ou Enregistrez pour conserver.",
    'planner.ai_update.hint': "Lit la structure de votre plan, les modules voisins et les chapitres qui mentionnent cette entrée — puis complète les champs vides et met à jour les existants. Soumis à votre quota IA quotidien.",
    'planner.ai_update.per_field_hint': "Cliquez sur ✨ à côté d'un champ pour limiter l'IA à ce champ.",
    'planner.ai_update.improve_field': "Améliorer ce champ avec l'IA",
    'planner.ai_update.field_improved': "L'IA a affiné ce champ. Vérifiez puis Enregistrez.",
    'planner.ai_update.no_context': "Ajoutez d'abord du contenu au plan ou des résumés de chapitres — l'IA n'a rien sur quoi s'appuyer.",
    'planner.ai_update.diff.was': 'Avant :',
    'planner.ai_update.diff.empty': 'vide',
    'planner.ai_update.diff.more': 'plus',
    'planner.ai_update.diff.less': 'moins',
    'reader.tier.label': 'Narration',
    'reader.tier.browser': 'Standard',
    'reader.tier.browser_hint': "Utilise les voix déjà installées sur cet appareil — instantané, gratuit, hors ligne.",
    'reader.tier.pro': 'Pro',
    'reader.tier.pro_hint': "Narration de qualité studio. Soumise à votre quota de narration quotidien.",
    'reader.tier.personal': 'Ma voix',
    'reader.tier.personal_hint': "Votre propre voix, clonée à partir d'un échantillon dans Paramètres. Soumise à votre quota de narration quotidien.",
    'reader.tier.pro_preview.title': 'Narration de qualité studio',
    'reader.tier.pro_preview.body':
      "Les voix Pro offrent une prosodie, un accent et un rythme bien au-delà du synthétiseur de votre appareil. Disponible avec le plan Premium.",
    'reader.tier.pro_preview.sample':
      "« Au sommet de la colline, le vent murmurait des histoires anciennes — et Ragaboum écoutait, le cœur battant. »",
    'reader.tier.pro_preview.cta_premium': 'Passer à Premium',
    'reader.tier.pro_preview.cta_login': 'Connectez-vous pour écouter',
    'reader.personal.download.aria': 'Télécharger l’audio de la narration',
    'reader.personal.download.title': 'Enregistrer cette narration en MP3',
    'reader.personal.label': 'Échantillon vocal',
    'reader.personal.empty': 'Ajoutez un échantillon dans Paramètres → Voix personnelle pour activer ce mode.',
    'reader.personal.tip.proper_nouns': "Astuce : les noms propres d'origine étrangère (ex. « Fuego ») gardent leur accent natif. Pour forcer l'accent de votre voix clonée, écrivez-les phonétiquement (ex. « Fouégo »).",
    'reader.personal.bg.badge.title': 'Génération en cours…',
    'reader.personal.bg.badge.hint': 'naviguez librement — un avis vous parviendra',
    'reader.personal.bg.badge.return': 'Retour au lecteur',
    'reader.personal.bg.badge.cancel': 'Annuler la génération',
    'reader.personal.bg.toast.ready': 'Votre narration est prête — ouvrez le lecteur pour écouter.',
    'reader.personal.bg.toast.timeout': 'Narration interrompue après 20 min. Réessayez.',
    'reader.personal.bg.toast.failed': 'Génération impossible — réessayez.',
    'reader.personal.bg.toast.quota': 'Quota de narration personnelle quotidien atteint.',
    'reader.personal.bg.pending.title': 'Génération en arrière-plan',
    'reader.personal.bg.pending.subtitle': "Le traitement continue même si vous quittez cette page. Un avis vous parviendra dès que c'est prêt.",
    'reader.personal.bg.ready.title': 'Narration prête',
    'reader.personal.bg.ready.subtitle': 'Appuyez sur lecture pour écouter.',
    'reader.personal.bg.failed.timeout': 'Délai dépassé après 20 min. Réessayez.',
    'reader.personal.bg.failed.generic': 'Génération impossible. Réessayez.',
    'reader.personal.bg.failed.retry': 'Réessayer',
    'reader.personal.bg.cancel': 'Annuler',
    'reader.personal.bg.dismiss': 'Fermer',
    'reader.pro.label': 'Voix Pro',
    'reader.pro.loading_voices': "Chargement des voix…",
    'reader.pro.no_voices': "Aucune voix Pro disponible pour l'instant.",
    'reader.pro.no_locale_match': "Aucune voix Pro ne correspond à la langue de ce chapitre — affichage de toutes les voix. La qualité peut en pâtir.",
    'reader.pro.no_locale_match_plan': "Aucune voix Pro pour la langue de ce chapitre n'est incluse dans votre offre — affichage de l'ensemble de voix de votre offre.",
    'reader.pro.plan_locked': "Les voix pour cette langue nécessitent une offre supérieure. Mettez à niveau pour déverrouiller les narrateurs studio.",
    'reader.pro.sample.cta': "Essayer une voix Pro — gratuit, une fois",
    'reader.pro.download.label': "Télécharger le chapitre en MP3",
    'reader.pro.download.started': "Téléchargement démarré — votre chapitre audio arrive.",
    'reader.pro.download.failed': "Téléchargement du chapitre impossible. Réessayez.",
    'reader.pro.download.quota_exhausted': "Limite de téléchargements du jour atteinte. Réinitialisation à minuit (heure de l'Est) ; Premium en obtient plus.",
    'reader.pro.engine.generative': "Studio (Generative) — le plus réaliste à la phrase",
    'reader.pro.engine.neural': "Direct (Neural) — prosodie naturelle",
    'reader.pro.engine.long_form': "Conteur (Long-form) — recommandé pour la narration d'un chapitre",
    'reader.pro.engine.standard': "Standard — le plus rapide, qualité moindre",
    'reader.pro.engine.legend': "Conteur est recommandé pour la lecture d'un chapitre — rythme cohérent sur plusieurs minutes, conçu pour la narration de livre audio. Studio est la voix la plus réaliste à la phrase mais se réinitialise entre les segments. Direct est le choix courant ; Standard est la plus rapide.",
    'profile.tier.premium_title': "Membre Premium",
    'profile.tier.basic_title': "Membre",
    'profile.role.admin_title': "Administration Plumescript · compte officiel",
    'profile.role.admin_aria': "Administration Plumescript",
    'profile.role.team_title': "Équipe Plumescript · compte officiel",
    'profile.role.team_aria': "Membre de l'équipe Plumescript",
    'settings.appearance.theme.warm': 'Chaud',
    'settings.appearance.theme.warm.desc': "Noyer rouge profond et tan terra cotta éclairés de sienne brûlée et de prune épicée, sur typo crème vélin — une édition reliée de rouge d'une presse littéraire, déclinée en rouge-orange-brun pour une chaleur maximale.",
    'settings.appearance.theme.charbon': 'Charbon',
    'settings.appearance.theme.charbon.desc': "Esquisse au fusain — canevas papier-charbon sombre avec CTA rouge charbon ardent, accent laiton vieilli et encre craie ivoire. Le carnet à croquis d'un artiste dans un atelier éclairé à la lampe : papier suieux sous un halo de braise chaude qui luit dans le coin inférieur droit.",
    'settings.appearance.theme.ocean': 'Océan',
    'settings.appearance.theme.ocean.desc': "Mer profonde — canevas bleu saturé mi-ton avec CTA vague-céruléen brillant, accent turquoise-récif et encre écume de mer. Immergé à mi-profondeur dans l'eau tropicale : la lumière du soleil filtre d'en haut (halo céruléen brillant en haut), un bleu plus profond fond en dessous. Bleu et sombre avec assurance, mais pas abyssal-presque-noir — une profondeur intermédiaire.",
    'settings.appearance.theme.nature': 'Nature',
    'settings.appearance.theme.nature.desc': "Mossbank — canevas vert forêt profond tacheté de soleil filtré par la canopée, accent bronze automnal et encre crème parchemin. La cabane d'un écrivain au cœur des bois.",
    'settings.appearance.theme.premium_locked': "{label} fait partie de Premium — passez à l'offre supérieure dans Paramètres → Facturation pour débloquer.",
    'reader.pro.sample.pending': "Déverrouillage de votre échantillon…",
    'reader.pro.sample.granted': "✓ Premium débloqué pour 10 minutes. Appuyez sur lecture pour entendre la voix Pro.",
    'reader.pro.sample.unavailable': "L'échantillon n'est pas disponible — le lien d'abonnement fonctionne toujours.",
    'reader.pro.status.loading': "Préparation de l'audio…",
    'reader.pro.status.long_wait': "Génération en cours ({seconds}s)…",
    'reader.pro.status.cached': "En cache — lecture instantanée",
    'reader.pro.status.cached_short': "En cache",
    'reader.pro.status.cached_tooltip': "Cette narration était déjà prête — lecture instantanée, sans coût de synthèse.",
    'reader.pro.status.error': "Narrateur indisponible. Réessayez.",
    'reader.pro.status.quota': "Quota de narration du jour atteint. Passez à l'offre supérieure ou réessayez demain.",
    'reader.pro.status.now_playing': "Lecture en cours",
    'reader.pro.status.paused': "En pause",
    'reader.pro.stage.queued': "En file",
    'reader.pro.stage.synthesizing': "Synthèse de la voix…",
    'reader.pro.stage.almost_ready': "Presque prêt — finalisation",
    'reader.pro.scrubber': "Avancer dans la narration",
    'reader.plumescript_narrator': 'Narrateur Plumescript',
    'reader.plumescript_badge_tooltip': "Voix soignée pensée pour le récit — vos curseurs restent toujours prioritaires.",
    'reader.plumescript_narrator_hint': "Choisi pour le récit sur cet appareil — ajustez la vitesse à votre goût.",
    // --- Étiquettes des « kinds » du catalogue Planner (94).
    'planner.kind.universe': 'Univers',
    'planner.kind.dimension': 'Dimension',
    'planner.kind.kingdom': 'Royaume',
    'planner.kind.region': 'Région',
    'planner.kind.country': 'Pays',
    'planner.kind.city': 'Ville',
    'planner.kind.district': 'Quartier',
    'planner.kind.location': 'Lieu',
    'planner.kind.map': 'Carte',
    'planner.kind.climate': 'Climat',
    'planner.kind.calendar': 'Calendrier',
    'planner.kind.environmental_condition': 'Condition environnementale',
    'planner.kind.culture': 'Culture',
    'planner.kind.religion': 'Religion',
    'planner.kind.mythology': 'Mythologie',
    'planner.kind.language': 'Langue',
    'planner.kind.custom': 'Coutume',
    'planner.kind.tradition': 'Tradition',
    'planner.kind.law': 'Loi',
    'planner.kind.economy': 'Économie',
    'planner.kind.trade': 'Commerce',
    'planner.kind.political_system': 'Système politique',
    'planner.kind.organisation': 'Organisation',
    'planner.kind.faction': 'Faction',
    'planner.kind.alliance': 'Alliance',
    'planner.kind.war': 'Guerre',
    'planner.kind.treaty': 'Traité',
    'planner.kind.catastrophe': 'Catastrophe',
    'planner.kind.festival': 'Festival',
    'planner.kind.character': 'Personnage',
    'planner.kind.family': 'Famille',
    'planner.kind.dynasty': 'Dynastie',
    'planner.kind.relationship': 'Relation',
    'planner.kind.social_class': 'Classe sociale',
    'planner.kind.race': 'Race',
    'planner.kind.power': 'Pouvoir',
    'planner.kind.ability': 'Capacité',
    'planner.kind.skill': 'Compétence',
    'planner.kind.creature': 'Créature',
    'planner.kind.fauna': 'Faune',
    'planner.kind.flora': 'Flore',
    'planner.kind.ecosystem': 'Écosystème',
    'planner.kind.natural_resource': 'Ressource naturelle',
    'planner.kind.magic_system': 'Système de magie',
    'planner.kind.scientific_system': 'Système scientifique',
    'planner.kind.technology': 'Technologie',
    'planner.kind.world_rule': 'Règle du monde',
    'planner.kind.energy': 'Énergie',
    'planner.kind.resource_system': 'Système de ressources',
    'planner.kind.balance': 'Équilibre',
    'planner.kind.note': 'Note',
    'planner.kind.chronology': 'Chronologie',
    'planner.kind.event': 'Événement',
    'planner.kind.plot': 'Intrigue',
    'planner.kind.conflict': 'Conflit',
    'planner.kind.story_arc': 'Arc narratif',
    'planner.kind.beat': 'Temps fort',
    'planner.kind.chapter_plan': 'Plan de chapitre',
    'planner.kind.quest': 'Quête',
    'planner.kind.mission': 'Mission',
    'planner.kind.dialogue': 'Dialogue',
    'planner.kind.scene': 'Scène',
    'planner.kind.alternative_ending': 'Fin alternative',
    'planner.kind.narrative_variant': 'Variante narrative',
    'planner.kind.narrative_flow': 'Flux narratif',
    'planner.kind.object': 'Objet',
    'planner.kind.artefact': 'Artefact',
    'planner.kind.magical_object': 'Objet magique',
    'planner.kind.inventory': 'Inventaire',
    'planner.kind.equipment': 'Équipement',
    'planner.kind.weapon': 'Arme',
    'planner.kind.armor': 'Armure',
    'planner.kind.vehicle': 'Véhicule',
    'planner.kind.document': 'Document',
    'planner.kind.letter': 'Lettre',
    'planner.kind.in_world_book': 'Livre du monde',
    'planner.kind.archive': 'Archive',
    'planner.kind.journal': 'Journal',
    'planner.kind.quote': 'Citation',
    'planner.kind.proverb': 'Proverbe',
    'planner.kind.riddle': 'Énigme',
    'planner.kind.code': 'Code',
    'planner.kind.philosophical_inspiration': 'Inspiration philosophique',
    'planner.kind.illustration': 'Illustration',
    'planner.kind.concept_art': 'Concept art',
    'planner.kind.visual_reference': 'Référence visuelle',
    'planner.kind.inspiration': 'Inspiration',
    'planner.kind.music': 'Musique',
    'planner.kind.sound_ambiance': 'Ambiance sonore',
    'planner.kind.media': 'Média',
    'planner.kind.tag': 'Étiquette',
    'planner.kind.keyword': 'Mot-clé',
    'planner.kind.collection': 'Collection',
    'planner.kind.statistic': 'Statistique',
    // --- Valeurs énumérées (rôle d'un personnage, type d'un lieu,
    // niveau de menace d'une créature, etc.).
    'planner.options.role.protagonist': 'Protagoniste',
    'planner.options.role.antagonist': 'Antagoniste',
    'planner.options.role.mentor': 'Mentor',
    'planner.options.role.ally': 'Allié',
    'planner.options.role.foil': 'Faire-valoir',
    'planner.options.role.supporting': 'Personnage secondaire',
    'planner.options.role.minor': 'Personnage mineur',
    'planner.options.role.rival': 'Rival',
    'planner.options.role.deuteragonist': 'Deutéragoniste',
    'planner.options.role.love interest': 'Intérêt amoureux',
    'planner.options.location.poi': "Point d'intérêt",
    'planner.options.location.dungeon': 'Donjon',
    'planner.options.location.interior': 'Intérieur',
    'planner.options.location.building': 'Bâtiment',
    'planner.options.location.natural': 'Naturel',
    'planner.options.threat.harmless': 'Inoffensif',
    'planner.options.threat.wary': 'Prudent',
    'planner.options.threat.dangerous': 'Dangereux',
    'planner.options.threat.deadly': 'Mortel',
    'planner.options.rel.friendship': 'Amitié',
    'planner.options.rel.love': 'Amour',
    'planner.options.rel.rivalry': 'Rivalité',
    'planner.options.rel.enemy': 'Ennemi',
    'planner.options.rel.kin': 'Famille',
    'planner.options.rel.mentor': 'Mentor',
    'planner.options.conflict.cosmic': 'Cosmique',
    'planner.options.conflict.environmental': 'Environnemental',
    'planner.options.conflict.internal': 'Interne',
    'planner.options.conflict.interpersonal': 'Interpersonnel',
    'planner.options.conflict.societal': 'Sociétal',
    'planner.options.locked.no': 'Non',
    'planner.options.locked.yes': 'Oui',
    'goal.kanban.stage.todo': 'À faire',
    'goal.kanban.stage.pending': 'En attente',
    'goal.kanban.stage.progress': 'En cours',
    'goal.kanban.stage.done': 'Terminé',
    'goal.kanban.add_task': 'Ajouter une tâche',
    'goal.kanban.empty': "Aucune tâche pour l'instant.",
    'goal.kanban.empty.title': 'Aucune tâche ici',
    'goal.kanban.empty.description': 'Ajoutez une tâche dans cette colonne pour organiser votre travail.',
    'goal.kanban.loading': 'Chargement des tâches…',
    'goal.kanban.error.title_required': 'Un titre est requis pour enregistrer la tâche.',
    'goal.kanban.error.save_failed': 'Une erreur est survenue. Réessayez.',
    'goal.kanban.modal.title': 'Titre',
    'goal.kanban.modal.title_placeholder': 'Saisir le titre de la tâche',
    'goal.kanban.modal.tags': 'Étiquettes',
    'goal.kanban.modal.tags_placeholder': 'Saisir les étiquettes séparées par des espaces',
    'goal.kanban.modal.progress': 'Progression',
    'goal.kanban.modal.description': 'Description',
    'goal.kanban.modal.description_placeholder': 'Saisir la description de la tâche',
    'goal.kanban.modal.due_date': "Date d'échéance",
    'goal.kanban.modal.saving': 'Enregistrement…',
    'goal.kanban.modal.save_task': 'Créer la tâche',
    'goal.kanban.modal.save_changes': 'Enregistrer les modifications',
    'goal.kanban.modal.close': 'Fermer',
    'goal.kanban.card.in_list': 'Dans la liste',
    'goal.kanban.card.due_date': 'Échéance',
    'goal.kanban.card.edit': 'Modifier la carte',
    'goal.kanban.card.delete': 'Supprimer la carte',
    'goal.kanban.card.no_deadline': 'Sans date',

    'image_generation.title': 'Générer une image avec l’IA',
    'image_generation.subtitle': 'Décrivez votre image, puis ajoutez éventuellement un texte lisible.',
    'image_generation.action.open': 'Générer avec l’IA',
    'image_generation.prompt.label': 'Description',
    'image_generation.prompt.placeholder': 'Un port brumeux au crépuscule, style peinture à l’huile…',
    'image_generation.image_type.label': 'Type',
    'image_generation.image_type.book_cover': 'Couverture de livre',
    'image_generation.image_type.chapter_cover': 'Image de chapitre',
    'image_generation.image_type.custom': 'Personnalisée',
    'image_generation.aspect.label': 'Format',
    'image_generation.aspect.auto': 'Par défaut selon le type',
    'image_generation.action.generate': 'Générer',
    'image_generation.action.regenerate': 'Regénérer',
    'image_generation.action.use': 'Utiliser cette image',
    'image_generation.action.cancel': 'Annuler',
    'image_generation.empty.title': 'Aucune image pour le moment',
    'image_generation.empty.description': "Décrivez la couverture, la scène ou le personnage que vous voulez, puis cliquez sur Générer pour voir un aperçu ici. Le modèle ne produit que l'image ; les informations du projet s'ajoutent à l'étape suivante.",
    'image_generation.generating.title': 'Génération de votre image…',
    'image_generation.generating.description': "Cela prend généralement quelques secondes. Patientez pendant que le modèle travaille.",
    'image_generation.overlay.toggle': "Ajouter les informations du projet sur l'image",
    'image_generation.overlay.title.label': 'Titre',
    'image_generation.overlay.title.placeholder': 'Ligne principale (ex. titre du livre)',
    'image_generation.overlay.subtitle.label': 'Sous-titre',
    'image_generation.overlay.subtitle.placeholder': "Seconde ligne facultative (ex. auteur)",
    'image_generation.overlay.clear': 'Effacer le texte',
    'image_generation.overlay.position.label': 'Position',
    'image_generation.overlay.position.top': 'Haut',
    'image_generation.overlay.position.center': 'Centre',
    'image_generation.overlay.position.bottom': 'Bas',
    'image_generation.overlay.color.label': 'Couleur du texte',
    'image_generation.overlay.color.auto.toggle': 'Adapter à la couverture',
    'image_generation.overlay.color.auto.hint': 'Choisit automatiquement une teinte contrastée selon la couverture sous le texte',
    'image_generation.overlay.background.toggle': 'Ajouter un bandeau',
    'image_generation.overlay.background.mode.label': 'Fond du texte',
    'image_generation.overlay.background.mode.gradient': 'Dégradé doux',
    'image_generation.overlay.background.mode.solid': 'Bandeau plein',
    'image_generation.overlay.background.mode.none': 'Aucun',
    'image_generation.overlay.background.color.label': 'Teinte du fond',
    'image_generation.toast.failed': 'Génération impossible. Réessayez.',
    'image_generation.toast.empty_prompt': 'Écrivez d’abord une description.',
    'image_generation.toast.applied': 'Image ajoutée.',
    'image_generation.clean.action': 'Nettoyer tout texte résiduel',
    'image_generation.clean.hint':
      "Détecte les zones en forme de lettres et les efface. Sans appel IA supplémentaire, sans coût.",
    'image_generation.toast.clean_applied': 'Image nettoyée.',
    'image_generation.toast.clean_noop': "L'image semble déjà propre — rien à supprimer.",
    'image_generation.toast.clean_failed': "Nettoyage impossible. Réessayez.",
    'image_generation.note.cost':
      "Chaque image consomme environ une recharge de téléphone en électricité, plus un verre d'eau pour refroidir le centre de données. À générer pour une couverture que vous comptez garder.",

    'games.page.title': 'Jeux pour l’esprit',
    'games.page.subtitle':
      "Quelques petits jeux de mots pour s'échauffer avant une session d'écriture. Tout se joue dans le navigateur, sans compte ni classement — juste un coup de neuf rapide.",
    'games.back': 'Retour aux jeux',
    'games.scramble.title': 'Lettres mêlées',
    'games.scramble.desc': 'Remettez en ordre des mots autour de l’écriture. Enchaînez les bonnes réponses.',
    'games.typing.title': 'Sprint de frappe',
    'games.typing.desc': 'Tapez une phrase en 30 secondes — mesurez votre vitesse et votre précision.',
    'games.rhyme.title': 'Chasse aux rimes',
    'games.rhyme.desc': 'Trouvez autant de mots que possible qui riment avec la cible.',
    'games.literary_devices.title': 'Figures de style',
    'games.literary_devices.desc': 'Lisez une courte phrase et identifiez la figure utilisée.',
    'games.charade.title': 'Devinette',
    'games.charade.desc': "Résolvez une courte devinette d'écrivain en un mot.",

    'curiosities.page.title': "L'étude du métier",
    'curiosities.page.kicker': 'Aiguiser la plume',
    'curiosities.page.subtitle': "De petites leçons quotidiennes pour affûter l'oreille de l'écrivain. Quelques minutes, répétées souvent — c'est ainsi que le métier se construit.",
    'curiosities.philosophy': "La pratique creuse le métier. Chaque leçon prend quelques minutes et se rejoue volontiers — le but n'est pas de la finir, c'est de continuer à observer.",
    'curiosities.lesson_kicker': 'Leçon {number}',
    'curiosities.duration_short': '~ 3 min',
    'curiosities.start_lesson': 'Commencer la leçon',
    'curiosities.more_soon': "D'autres leçons arriveront à mesure que la rubrique grandira.",
    'curiosities.back': 'Retour aux leçons',

    // Interface partagée du quiz
    'curiosities.quiz.next': 'Suivant',
    'curiosities.quiz.skip': 'Passer',
    'curiosities.quiz.wrong_with_answer': "Pas tout à fait — la réponse était : {answer}",

    // Leçon 1 : Figures de style
    'curiosities.lesson.literary_devices.title': 'Figures de style',
    'curiosities.lesson.literary_devices.curriculum': "Lisez une phrase ; nommez la figure de style qui s'y cache. Construit le vocabulaire utile à la révision — métaphore, personnification, allitération, oxymore, et plus.",

    // Leçon 2 : Voix active vs passive
    'curiosities.lesson.active_voice.title': 'Voix active vs voix passive',
    'curiosities.lesson.active_voice.curriculum': "À partir d'une phrase à la voix passive, choisissez la réécriture qui la bascule à la voix active, plus directe et plus vive. Le réflexe de révision au plus fort rendement.",
    'curiosities.lesson.active_voice.prompt': 'Choisissez la réécriture à la voix active :',

    // Leçon 3 : Incipits
    'curiosities.lesson.first_lines.title': 'Incipits célèbres',
    'curiosities.lesson.first_lines.curriculum': "Reconnaissez le roman à sa première phrase. Affine l'oreille pour le rythme et la concision propres aux grandes ouvertures.",
    'curiosities.lesson.first_lines.prompt': 'Quel roman ouvre cette phrase ?',

    // Leçon 4 : Verbes forts
    'curiosities.lesson.verb_strength.title': 'Verbes plus forts',
    'curiosities.lesson.verb_strength.curriculum': "Remplacez un verbe accompagné d'un adverbe par un seul verbe plus vif. Le moyen le plus rapide de resserrer une prose plate.",
    'curiosities.lesson.verb_strength.prompt': 'Choisissez le verbe simple le plus fort :',

    // Leçon 5 : Montrer, pas raconter
    'curiosities.lesson.show_dont_tell.title': 'Montrer, pas raconter',
    'curiosities.lesson.show_dont_tell.curriculum': "À partir d'une phrase qui se contente de raconter, choisissez la version qui transmet la même chose par l'action ou la sensation.",
    'curiosities.lesson.show_dont_tell.prompt': 'Quelle version montre plus qu’elle ne raconte ?',

    // Leçon 6 : Point de vue
    'curiosities.lesson.pov.title': 'Point de vue',
    'curiosities.lesson.pov.curriculum': "Identifiez le point de vue narratif employé. Première personne, deuxième personne, troisième limitée, troisième omnisciente — chacune engage une promesse différente envers le lecteur.",
    'curiosities.lesson.pov.prompt': 'À quel point de vue cette phrase est-elle écrite ?',
    'curiosities.pov.first': 'Première personne',
    'curiosities.pov.second': 'Deuxième personne',
    'curiosities.pov.third_limited': 'Troisième personne — limitée',
    'curiosities.pov.third_omni': 'Troisième personne — omnisciente',

    // Leçon 7 : Rythme de la ponctuation
    'curiosities.lesson.punctuation.title': 'Rythme de la ponctuation',
    'curiosities.lesson.punctuation.curriculum': "Choisissez le signe de ponctuation qui sert le mieux un effet rhétorique. Rythme, emphase, voix — tout passe par ces petits signes.",
    'curiosities.lesson.punctuation.prompt': 'Quel signe convient le mieux ?',
    'curiosities.punct.comma': 'Virgule  ,',
    'curiosities.punct.commas': 'Virgules  , ,',
    'curiosities.punct.semicolon': 'Point-virgule  ;',
    'curiosities.punct.colon': 'Deux-points  :',
    'curiosities.punct.em_dash': 'Tiret cadratin  —',
    'curiosities.punct.em_dashes': 'Tirets cadratins  — —',
    'curiosities.punct.parentheses': 'Parenthèses  ( )',
    'curiosities.punct.period': 'Point  .',
    'curiosities.punct.ellipsis': 'Points de suspension  …',
    'curiosities.punct.exclamation': "Point d'exclamation  !",
    'curiosities.punct.question_mark': "Point d'interrogation  ?",

    // Leçon 8 : Précision des synonymes
    'curiosities.lesson.synonym_precision.title': 'Précision des synonymes',
    'curiosities.lesson.synonym_precision.curriculum': "Choisissez le synonyme qui s'ajuste au plus près du contexte. Affûte l'oreille pour le registre, l'intensité et la connotation — aucun synonyme n'est vraiment interchangeable.",
    'curiosities.lesson.synonym_precision.prompt': "Quel mot complète le mieux la phrase ?",

    // Leçon 9 : Verbes de dialogue
    'curiosities.lesson.dialogue_tags.title': 'Verbes de dialogue',
    'curiosities.lesson.dialogue_tags.curriculum': "Choisissez le verbe de dialogue qui sert le mieux la réplique. Un simple « dit » reste souvent le meilleur choix invisible ; un verbe précis ne mérite sa place que s'il ajoute ce que la réplique ne porte pas déjà.",
    'curiosities.lesson.dialogue_tags.prompt': 'Quel verbe de dialogue convient le mieux ?',

    // Leçon 10 : Phrases de clôture
    'curiosities.lesson.closing_lines.title': 'Phrases de clôture',
    'curiosities.lesson.closing_lines.curriculum': "Reconnaissez l'œuvre à sa dernière phrase. Pendant naturel des incipits — l'ouverture pose le contrat, la clôture l'honore.",
    'curiosities.lesson.closing_lines.prompt': "Quelle œuvre se termine par cette phrase ?",
    // Leçons dérivées de Ecriture_en_jeux.docx
    // Leçons 11-14 — suite cohérente du curriculum fondamental.
    // Chaque pool ChoiceQuiz comporte une entrée à la saveur d'un
    // ancien jeu (virelangue, charade, énigme, chiffre) pour
    // préserver l'esprit ludique comme variante du geste de craft.
    'curiosities.lesson.rhythm.title': 'Rythme',
    'curiosities.lesson.rhythm.curriculum': "Les phrases courtes courent. Les phrases longues s'attardent. La longueur d'une phrase est un bouton de vitesse — trois mots à la file mettent le lecteur dans une course ; une seule longue phrase sans souffle coupé le retient dans une prise de conscience lente. Le moyen le plus sûr d'écrire une scène qui traîne, c'est de la remplir de phrases toutes de la même taille. Variez la longueur exprès, et la page se met à respirer.",
    'curiosities.lesson.rhythm.prompt': 'Quelle version correspond au moment ?',
    'curiosities.lesson.concrete_nouns.title': 'Noms concrets',
    'curiosities.lesson.concrete_nouns.curriculum': "La moitié « nom » du couple mots-forts (la leçon 04 a traité les verbes). Oiseau → cardinal ; arbre → chêne ; boisson → gin sur le glaçon. La précision sépare la description qui s'efface de celle qui reste — la chose nommée gagne sa place.",
    'curiosities.lesson.concrete_nouns.prompt': 'Choisissez la version qui nomme la chose :',
    'curiosities.lesson.subtext.title': 'Sous-texte',
    'curiosities.lesson.subtext.curriculum': "La leçon 09 traitait l'attribution du dialogue. Celle-ci va plus loin : ce que la réplique VEUT DIRE. Le sous-texte est l'écart entre les mots et l'intention — sarcasme, esquive, jugement à demi voilé. Entraîne l'oreille à entendre la seconde voix.",
    'curiosities.lesson.subtext.prompt': 'Choisissez ce qui est réellement dit :',
    'curiosities.lesson.scene_vs_summary.title': 'Scène vs résumé',
    'curiosities.lesson.scene_vs_summary.curriculum': "Le levier macro de structure. La leçon 10 ferme un chapitre ; celle-ci interroge la liaison entre scènes. Scène = dramatiser, vivre le moment. Résumé = compresser, faire vieillir la routine. C'est le levier qui sépare un roman gonflé d'un roman propulsé.",
    'curiosities.lesson.scene_vs_summary.prompt': 'Choisissez le bon mode pour ce moment :',
    'games.anagram.title': 'Chasse aux anagrammes',
    'games.anagram.desc': 'Combien de vrais mots pouvez-vous former avec ces lettres ?',
    'games.chain.title': 'Chaîne de mots',
    'games.chain.desc': 'Chaque mot commence par la dernière lettre du précédent.',
    'games.memory.title': 'Phrases-mémoire',
    'games.memory.desc': 'Lisez une phrase — puis retapez-la de mémoire.',
    'games.synonym.title': 'Paires de synonymes',
    'games.synonym.desc': 'Associez chaque mot à son synonyme, contre la montre.',
    'games.rsvp.title': 'Lecture rapide',
    'games.rsvp.desc': 'Les mots défilent un par un — répondez à une question de compréhension.',
    'games.cubes.title': 'Dés à histoires',
    'games.cubes.desc': 'Tirez trois mots au hasard et écrivez une phrase qui les contient tous.',

    'games.common.score': 'Score',
    'games.common.streak': 'Série',
    'games.common.streak_value': 'Série : {streak}',
    'games.common.skip': 'Passer',
    'games.common.add': 'Ajouter',
    'games.common.start': 'Démarrer',
    'games.common.submit': 'Valider',
    'games.common.play_again': 'Rejouer',
    'games.common.try_again': 'Réessayer',
    'games.common.reset': 'Réinitialiser',
    'games.common.new_round': 'Nouvelle manche',

    'games.feedback.correct': 'Correct',
    'games.feedback.added': 'Ajouté',
    'games.feedback.linked': 'Enchaîné',
    'games.feedback.try_again': 'Réessayer',

    'games.scramble.placeholder': 'Tapez le mot dans le bon ordre',
    'games.scramble.check': 'Vérifier',

    'games.typing.time_left': 'Temps restant',
    'games.typing.hint': 'Tapez la phrase ci-dessous',
    'games.typing.placeholder': 'Commencez à taper ici…',
    'games.typing.result_summary': 'MPM {wpm} · {acc} % de précision',

    'games.literary_devices.prompt': 'Quelle figure cette phrase utilise-t-elle ?',
    'games.literary_devices.feedback.correct': 'Correct.',
    'games.literary_devices.feedback.wrong': "Pas tout à fait — réponse : {device}.",
    'games.literary_devices.device.metaphor': 'Métaphore',
    'games.literary_devices.device.simile': 'Comparaison',
    'games.literary_devices.device.personification': 'Personnification',
    'games.literary_devices.device.alliteration': 'Allitération',
    'games.literary_devices.device.hyperbole': 'Hyperbole',
    'games.literary_devices.device.onomatopoeia': 'Onomatopée',
    'games.literary_devices.device.oxymoron': 'Oxymore',
    'games.literary_devices.device.repetition': 'Répétition',
    'games.literary_devices.device.irony': 'Ironie',
    'games.literary_devices.device.anaphora': 'Anaphore',
    'games.literary_devices.device.assonance': 'Assonance',
    'games.literary_devices.device.litotes': 'Litote',
    'games.literary_devices.device.metonymy': 'Métonymie',
    // ``understatement`` is the broader EN category (minimisation of a
    // big thing — "Losing the house was just a minor inconvenience").
    // ``litotes`` is the narrower negation-based subset ("not
    // unhappy"). FR uses "Litote" for the narrow case, so the broad
    // category is rendered as "Atténuation" to avoid a label
    // collision with ``litotes`` above.
    'games.literary_devices.device.understatement': 'Atténuation',
    'games.literary_devices.device.rhetorical_question': 'Question rhétorique',
    'games.literary_devices.device.symbolism': 'Symbole',
    'games.literary_devices.device.allusion': 'Allusion',

    'games.charade.prompt': 'Qui suis-je ?',
    'games.charade.placeholder': 'Votre réponse…',
    'games.charade.reveal': 'Révéler',
    'games.charade.feedback.correct': 'Trouvé.',
    'games.charade.feedback.wrong': 'Pas ça — essayez un autre mot.',
    'games.charade.feedback.revealed': "La réponse était « {answer} ».",

    'games.rhyme.target_label': 'Mot cible',
    'games.rhyme.found': 'Trouvés',
    'games.rhyme.hint_label': 'mots qui riment avec la cible',
    'games.rhyme.placeholder': "Un mot qui rime avec « {word} »",
    'games.rhyme.new_target': 'Nouvelle cible',
    'games.rhyme.hint': 'Indice',
    'games.rhyme.abandon': 'Abandonner',
    'games.rhyme.complete': 'Vous les avez tous trouvés.',
    'games.rhyme.error.same_word': 'Un mot ne rime pas avec lui-même.',
    'games.rhyme.error.invalid': 'Lettres uniquement, 2 caractères minimum.',
    'games.rhyme.error.duplicate': 'Déjà trouvé.',
    'games.rhyme.error.not_a_rhyme': "Ne rime pas — ou n'est pas dans la liste.",

    'games.anagram.found': 'Trouvés',
    'games.anagram.hint': 'Composez des mots avec les lettres ci-dessous',
    'games.anagram.placeholder': 'Tapez un mot…',
    'games.anagram.new_rack': 'Nouveau tirage',
    'games.anagram.error.duplicate': 'Déjà trouvé',
    'games.anagram.error.invalid_letters': 'Lettres absentes du tirage',
    'games.anagram.error.not_in_list': 'Pas dans la liste de ce puzzle',

    'games.chain.status': 'Temps : {remaining} s · Prochaine lettre : {letter}',
    'games.chain.so_far': 'Chaîne actuelle :',
    'games.chain.placeholder': 'Un mot commençant par « {letter} »',
    'games.chain.error.letters_only': 'Lettres uniquement, 2 caractères minimum',
    'games.chain.error.duplicate': 'Déjà utilisé',
    'games.chain.error.wrong_start': 'Doit commencer par « {letter} »',
    'games.chain.error.not_a_word': "Ce mot ne semble pas exister — essayez-en un autre.",

    'games.memory.intro': 'Une phrase va apparaître quelques secondes. Lisez-la, puis retapez-la de mémoire.',
    'games.memory.placeholder': 'Tapez la phrase que vous venez de voir…',
    'games.memory.accuracy': 'Précision',
    'games.memory.match_kind': 'Correspondance lettre par lettre',
    'games.memory.original': 'Original :',
    'games.memory.yours': 'Votre version :',
    'games.memory.empty': '(vide)',
    'games.memory.helps_remember': 'Aide à retenir :',
    'games.memory.phase.memorize': 'Mémorisez',
    'games.memory.phase.recall': 'De mémoire',

    'games.synonym.pairs': 'Paires',
    'games.synonym.hint': 'Cliquez sur un mot, puis sur son synonyme',
    'games.synonym.done_in': 'Terminé en {seconds} s',

    'games.rsvp.speed': 'Vitesse :',
    'games.rsvp.wpm_option': '{value} mpm',
    'games.rsvp.intro': 'Les mots défilent un par un. Après la phrase, choisissez le mot qui est apparu.',
    'games.rsvp.question': 'Quel mot figurait dans la phrase ?',
    'games.rsvp.correct': 'Correct.',
    'games.rsvp.incorrect': 'Pas tout à fait — la bonne réponse était « {word} ».',
    'games.rsvp.another': 'Une autre phrase',

    'games.cubes.intro': 'Écrivez une phrase (ou deux) en utilisant les trois mots.',
    'games.cubes.placeholder': 'Il était une fois une lanterne, le détective…',
    'games.cubes.word_count': '{count} mots',
    'games.cubes.all_used': '· les trois mots utilisés ✓',
    'games.cubes.reroll': 'Relancer',
    'games.cubes.validate': 'Vérifier les mots',
    'games.cubes.validating': 'Vérification…',
    'games.cubes.unknown_intro': "Mots absents de notre dictionnaire (à vous de décider de les garder) :",
  },
};

export const LOCALES = [
  { id: 'en', label: 'English' },
  { id: 'fr', label: 'Français' },
];

export const DEFAULT_LOCALE = 'en';
